/*
  Ever Leaf Personalizer — state + UI wiring (Phase 4 CORRECTION PASS: local
  file-first architecture; Mini/FilePond is invoked ONLY at the final Add to
  Cart / Buy Now commit moment, never during editing).

  This file owns DOM/interaction/state/caching concerns only. All actual
  pixel processing (grayscale, tonal curve, masking, compositing) lives in
  ever-leaf-image-renderer.js and is untouched here, per that file's own
  header — kept separate so the renderer stays reusable by a later full-lamp
  preview without dragging UI code along with it.

  Upload / Change / Remove / drag / arrows / zoom / pinch / Reset Position /
  text input are handled ENTIRELY by: our own hidden native
  <input type="file"> (see EverLeafCustomerControls below), the browser File
  it produces, PersonalizerState, and the Canvas renderer. Mini/FilePond has
  no role in any of that — window.EverLeafMiniUploadAdapter.finalizeUpload()
  is called exactly once per commit attempt, from the Add to Cart submit
  interception or the Buy Now click interception near the bottom of this
  file, and nowhere else.

  Movement rule (do not relax without explicit approval): Photo is the ONLY
  layer the customer can move/scale. Back, Leaf Base, Photo_Mask, Text, and
  Light are always fixed.

  Coordinate convention (Pass 3A interaction-model correction, extended by
  the Pass 3A targeted correction below): the CANONICAL stored interactive
  state is `photoCenterX`/`photoCenterY`/`photoScale` — the photo's
  normalized CENTER position and its drawn-width/canvasSize scale, in
  Personalization State Space. Scale and Center remain two independently
  CLAMPED fields (ever-leaf-image-renderer.js's clampScaleValue/
  clampCenterValue — no mask geometry, no automatic "always cover"
  correction anywhere in this path). Move/Reset/drag never touch Scale.
  Zoom (buttons, wheel, pinch) touches Scale and, ONLY when a merchant has
  explicitly configured Zoom Pivot Mode = Custom Pivot, also moves Center
  to keep a fixed pivot point visually anchored — see applyZoom()/
  getZoomPivot() and computePivotPreservingCenter() in
  ever-leaf-image-renderer.js. The default ('image_center') mode leaves
  Center untouched by Zoom, identical to the prior pass's behavior. Either
  way, Scale is always clamped FIRST — a Zoom request that doesn't actually
  change the clamped Scale is a complete no-op, and pivot math never runs
  on a no-op. The legacy TOP-LEFT convention
  (`_ever_leaf_photo_x/y`, historically `photoX`/`photoY`) still exists —
  it is the canonical CART/serialization format and is derived from Center
  state on demand via renderer.centerToTopLeft(), never stored directly.
  See legacyTopLeftFromState() below, the ONE place that conversion
  happens for cart purposes (rendering has its own separate
  scene-space conversion, sceneTransformFromState()).

  TWO-TIER RASTER ARCHITECTURE (locked, preserved from the prior pass):
  - INTERACTIVE: a small (~320-640px) render context, rebuilt only when the
    display size changes meaningfully. Used for every drag/zoom frame via
    renderer.renderInteractive() — zero pixel readback, zero allocation.
  - HQ: the full 1060x1060 pipeline (renderer.renderPersonalizedLeaf()), run
    once whenever interaction settles.

  TONAL CALIBRATION (this pass): brightness/contrast/gamma/blackPoint/
  whitePoint are DEV-tunable Photo preparation settings (state.photoTonal).
  Changing any of them rebuilds preparedPhotoInteractive/HQ exactly ONCE
  (one pixel loop each) — never during drag/zoom, which only ever transforms
  the already-prepared cached source. Photo opacity stays a separate
  layer-level setting (state.layerSettings.photo.opacity), never baked into
  these pixels.

  PERFORMANCE CONTRACT: no console logging in the hot path (opt-in verbose
  toggle only), no debug-stage computation during drag/zoom or while the
  debug grid is hidden (hidden by default), no getImageData/putImageData or
  File decoding outside of upload-time/tonal-change preparation.
*/
(function () {
  'use strict';

  if (window.EverLeafPersonalizerState) return;

  var MIN_INTERACTIVE_SIZE = 320;
  /* Mobile Frame-Owned migration: these now bound the LONGER edge of a
     rectangular destination, and the square offscreen Scene composite is
     bounded separately. The Scene caps matter most: the current artwork
     places the product at its original pixel size inside a doubled canvas,
     so the Close-Up camera is a ~0.13-wide crop and an UNCAPPED working
     canvas would be ~3900px (interactive) / ~8100px (HQ) square - past iOS
     Safari's single-canvas area limit and far past a sane mobile budget.
     Capping the Scene raster costs a little upscale on the crop and is still
     dramatically sharper than the pre-migration DOM magnification it
     replaces. */
  var MAX_INTERACTIVE_SIZE = 900;
  var MIN_INTERACTIVE_SHORT_EDGE = 280;
  var MAX_INTERACTIVE_WORKING_SIZE = 1800;
  var MAX_HQ_LONG_EDGE = 1400;
  var MAX_HQ_WORKING_SIZE = 3000;

  /*
    INTERNAL CONFIGURATION ARCHITECTURE pass — Visualizer Calibration /
    Dedication Text values now come from Shopify Theme Editor (two config
    blocks on the Ever Leaf Product section: "Visualizer Calibration" and
    "Dedication / Text"), rendered server-side into one JSON script tag,
    `[data-ever-leaf-product-config]` (see sections/ever-leaf-product.liquid).
    Shopify's own Save button is the only persistence mechanism now — the
    old DEV Calibration/Text Sizes windows and their localStorage-backed
    auto-save are retired. CALIBRATION_DEFAULTS below doubles as the Liquid
    template's own fallback values AND this file's safety net if the config
    script is ever missing/malformed (e.g. a merchant deleted one of the two
    config blocks) — every field is independently validated, so one corrupt
    value never blocks Visualizer init or throws.
  */
  var CALIBRATION_DEFAULTS = {
    photoOpacity: 1,
    tonalBrightness: 0,
    tonalContrast: 0,
    tonalGamma: 1,
    tonalBlackPoint: 0,
    tonalWhitePoint: 255,
    textLayerVisible: true,
    lightLayerVisible: true,
    lightOpacity: 0.47,
    // Pass 3A interaction-model correction — the ONE authoritative Scale/
    // Center model. scaleMin/scaleMax bound Scale only; centerMinX/MaxX/
    // MinY/MaxY bound Center only; the two are never coupled, and neither
    // depends on mask geometry or a scale pivot (all retired — see the
    // top-of-file header comment). moveStep/zoomStep reproduce the
    // originally-hardcoded MOVE_STEP (0.03) / ZOOM_STEP (0.15). Center
    // defaults (0.1-0.9 both axes) are generous but bounded — comfortably
    // include the default upload center (~0.52, ~0.62) while still being a
    // real limit for an unconfigured store, rather than defaulting to
    // unlimited movement.
    scaleMin: 0.2,
    scaleMax: 4,
    moveStep: 0.03,
    zoomStep: 0.15,
    centerMinX: 0.1,
    centerMaxX: 0.9,
    centerMinY: 0.1,
    centerMaxY: 0.9,
    // Pass 3A targeted correction — restored OPTIONAL Custom Pivot Point
    // zoom mode, layered strictly on top of the (now-passing) Center model
    // (never a return to the old scale-pivot + mask-cover architecture —
    // see computePivotPreservingCenter() in ever-leaf-image-renderer.js).
    // 'image_center' (default) reproduces exactly the already-passing
    // behavior: Center is never touched by Zoom. 'custom_pivot' keeps the
    // configured pivot point visually fixed while Scale changes. Pivot X/Y
    // are audited-compatible defaults in Personalization State Space (same
    // space as Center/text-layout), independent of scaleMin/scaleMax and
    // centerMinX/MaxX/MinY/MaxY.
    zoomPivotMode: 'image_center',
    zoomPivotX: 0.5,
    zoomPivotY: 0.4,
    // PASS 3A FINAL CONSOLIDATED CALIBRATION + FRAMING CORRECTION, Section
    // 38-52 — Close-Up Vertical Offset. A pure VIEW-space value (percent,
    // relative to the Close-Up viewport preset's own height) — never
    // touches Personalization State Space, never reprocesses the photo.
    // See lampScene.computeEffectiveCamera()/getCurrentViewport() in this
    // file's init() for where this is actually applied.
    closeUpVerticalOffsetPercent: 0,
    closeUpHorizontalOffsetPercent: 0,
    closeUpVerticalOffsetPercentMobile: 0,
    // Mobile Frame-Owned migration: Close-Up CAMERA zoom (1 = the calibrated
    // base Close-Up rect). Replaces the deleted outer DOM surface multiplier;
    // the stored merchant value carries over unchanged and reproduces the
    // same visible composition, because the old surface clip collapses into
    // exactly this factor (see lampScene.computeEffectiveCamera).
    closeUpZoomMobile: 1,
    closeUpHorizontalOffsetPercentMobile: 0,
    // DESKTOP PRODUCT SECTION PASS — pure post-processing multiplier applied
    // on top of the EXISTING, otherwise-unchanged Desktop Close-Up camera
    // rect (see lampScene.applyScaleMultiplier + getViewportEndpoints()
    // below). 1 = current Desktop framing, byte-identical to before this
    // setting existed. Never touches Mobile, X/Y, or existing Size settings.
    closeUpScaleDesktop: 1,
    /* Mobile Full Lamp now uses the SAME model as Close-Up: Zoom +
       composition offsets, against an aspect-fit base. The previous
       top-left X/Y + long-axis Size trio is gone from Mobile (Desktop keeps
       it unchanged). */
    fullLampZoomMobile: 1,
    fullLampHorizontalOffsetPercentMobile: 0,
    fullLampVerticalOffsetPercentMobile: 0,
    // DESKTOP PRODUCT SECTION PASS — same reasoning as closeUpScaleDesktop
    // above, applied to the Desktop Full Lamp viewport instead.
    fullLampScaleDesktop: 1,
    // SINGLE-VIEWPORT ARCHITECTURE (Pass 3B) — one Scene Space, one
    // viewport rectangle, two presets: Close-Up and Full Lamp. `viewMode`
    // selects which preset is the current viewport ('close_up', the real
    // customer default, or 'full_lamp', a developer/QA-only preview driven
    // by the "Static Scene Preview" Theme Editor select — see Visualizer
    // Calibration schema). Purely VIEW-space, same reasoning as
    // closeUpVerticalOffsetPercent above: never touches Personalization
    // State Space, never reprocesses the photo, never affects the real
    // Shopify selected variant or upload state. fullLampViewportXPercent/
    // YPercent/SizePercent are the Full Lamp preset's OWN independent
    // calibration (Section 67 — deliberately NOT derived from or shared
    // with Close-Up Vertical Offset, which only ever applies to the
    // Close-Up preset). Default (0, 0, 100) is the identity viewport — the
    // full, uncropped 2048 scene — chosen from real inspection of the
    // current Gray Sharp/Blur assets (see lampScene.
    // FULL_LAMP_VIEWPORT_DEFAULT_NORMALIZED's own comment).
    viewMode: 'close_up',
    fullLampViewportXPercent: 0,
    fullLampViewportYPercent: 0,
    fullLampViewportSizePercent: 100,
    // PASS 3C — one shared duration drives BOTH the viewport interpolation
    // and the Blur/Sharp crossfade (Section 48/49 — one eased progress,
    // never desynchronized). Read live via getViewTransitionDurationMs()
    // (same "live read, never cache" convention every other calibration
    // value in this object already uses), so a Theme Editor change takes
    // effect on the NEXT transition without a Save/reload.
    viewTransitionDurationMs: 850,
    // PASS 3A MANUAL QA CORRECTION PASS, Section 39-45 — Photo Interaction
    // Bounding Box calibration. Geometry/activation/hide-timing are LOCKED
    // (untouched this pass) — only color and appear-speed are
    // merchant-tunable.
    // WAVE 3C — no JS-side hardcoded default: blank must reach
    // updatePhotoBoundingBoxAppearance() as blank so it can removeProperty()
    // and let the CSS var(--ever-leaf-photo-outline-color, var(--el-control-surface))
    // fallback resolve from the Product semantic layer ("blank = Theme").
    // The hex-format CALIBRATION_VALIDATORS check below still protects
    // against a malformed persisted value; it just no longer has anything
    // to silently fall back to.
    photoOutlineColor: '',
    photoOutlineFadeInMs: 100,
    // PHOTO OUTLINE TIMING MICRO-PASS — the two remaining outline timing
    // phases, matching the SAME default values the previously-hardcoded
    // constants already used (400ms hide delay, 220ms fade-out) so adding
    // these controls changes nothing for a merchant who never touches them.
    photoOutlinePauseMs: 400,
    photoOutlineFadeOutMs: 220,
    // PASS 3A FINAL CONSOLIDATED UI CORRECTION — Two-Line Dedication model.
    // Retires the old length-based Short/Medium/Long size-BAND settings
    // (textSizeShort/Medium/Long, textThresholdShort/Medium) entirely — the
    // customer now controls line breaks explicitly via two inputs instead
    // of this code guessing a band from content length. dedicationOpacity
    // is NEW (Section 24-25 — separate from textLayerVisible above: opacity
    // controls translucency, textLayerVisible controls whether the layer
    // draws at all) — FROZEN this pass, value/architecture unchanged.
    // dedicationCenterX is a ratio in the same 0..1 Personalization State
    // Space Center X already uses. line1FontSize/line2FontSize/textSizeMin
    // are raw 1060/Leaf-Local-Space px, same convention textSizeShort/
    // Medium/Min always used — kept as `textSizeMin` (not renamed) since
    // it is the SAME setting id/shape, just reused as one shared "Minimum
    // Fitted Size" floor for both lines (one shared minimum is materially
    // simpler than two, and explicitly allowed) — FROZEN this pass.
    dedicationOpacity: 0.8,
    // DEDICATION TEXT CALIBRATION MICRO-PASS — blank/empty string means
    // "not configured", matching the SAME convention as photoOutlineColor
    // above: the validators below reject '' (color: fails the hex regex;
    // blendMode: not in the allowed enum), so `result[key]` never gets set
    // for a blank merchant setting, and the renderer's own existing
    // fallbacks (drawDedicationLayer(): `options.color || '#ffffff'`,
    // `options.blendMode || 'overlay'`) supply the exact current
    // hardcoded appearance with zero wiring needed here beyond passing
    // the raw value through.
    dedicationColor: '',
    dedicationBlendMode: '',
    dedicationCenterX: (431 + 628) / 2 / 1060,
    // PASS 3A FINAL DEDICATION POSITIONING MODEL — retires Group Y + Line
    // Gap entirely (Section 2/11/12). Fully independent Y ratios, matching
    // renderer.TEXT_CANONICAL's own derivation exactly (see that file's
    // comment for the 80%/75%/15% geometry audit). No field here is ever
    // derived from another.
    dedicationLine1YSingle: 0.8103773584905661, // Line 1's position (from top) when Line 2 is empty
    // PASS 3A FINAL UX PASS, Section 6/7/17-18 — the old single two-line
    // pair (Line 1 Y when Line 2 has text / Line 2 Y from bottom) is itself
    // now superseded by two independent customer-facing PRESETS, Together
    // and Split (see dedicationLayout below) — each preset resolves to its
    // own pair of these same-shaped values. Defaults: Together keeps the
    // original PSD-calibrated tight pairing (75%/15%-from-bottom, only 10%
    // apart — see renderer.TEXT_CANONICAL); Split is a fresh, visually
    // reasonable upper/lower starting arrangement (Section 18 explicitly
    // allows fresh Split defaults) — actual per-store tuning lives in
    // Shopify (see the DEV product.json migration in the handoff for how
    // this store's own real two-line values were carried over).
    dedicationTogetherLine1Y: 0.7803773584905661,
    dedicationTogetherLine2YFromBottom: 0.1596226415094339,
    dedicationSplitLine1Y: 0.25,
    dedicationSplitLine2YFromBottom: 0.15,
    line1FontSize: 52,
    line2FontSize: 42,
    textSizeMin: 22,
    // PASS 3A FINAL DEDICATION POSITIONING MODEL, Section 16-17 — split,
    // independent per-line character maximums, replacing the old shared
    // textMaxDedication.
    line1MaxChars: 12,
    line2MaxChars: 12,
    // PASS 3A MANUAL QA CORRECTION PASS, Section 27-33 — True Continuous
    // Adaptive Sizing. Root cause of "changing text length produces no
    // visible size change": the fit ALGORITHM itself
    // (renderer.computeDedicationLineFit) was already mathematically a
    // continuous fit-to-target-width computation — proven identical output
    // to a literal "scale toward target, clamp to [min, configured]"
    // formula for every input — but the WIDTH TARGET it aimed for
    // (renderer.TEXT_CANONICAL.maxWidthRatio, 0.55 — 55% of the
    // Personalization Reference width) was hardcoded and never
    // merchant-tunable. At realistic per-line Maximum Characters, ordinary
    // dedication text essentially never reaches 55% width before hitting
    // its character limit, so the ceiling clamp wins for the ENTIRE
    // practical range and the customer never sees any size change at all —
    // confirmed by direct harness measurement (typing progressively longer
    // text at this store's own real Font Size/Maximum Characters produced
    // the identical rendered size every time), not assumed. Fixed by
    // exposing each line's own Target Width as an explicit, independent
    // setting (Section 32 — genuinely independent, not shared, since Line
    // 1/Line 2 already have independent Font Size/Maximum Characters).
    // 40% is a fresh, tighter starting default than the old 55% (there is
    // no prior merchant-tuned value to preserve for a setting that didn't
    // exist before this pass) — chosen so a typical short name still sits
    // at the Font Size ceiling while text approaching the configured
    // Maximum Characters visibly shrinks; needs the merchant's own live
    // Theme Editor preview to fine-tune per their actual Font Size/Maximum
    // Characters choices. Percent of the Personalization Reference width,
    // same convention as every other ratio setting in this file.
    line1TargetWidth: 0.4,
    line2TargetWidth: 0.4
  };
  // Per-field validators — an invalid/out-of-range value in the rendered
  // config falls back to the default for that ONE field only, never the
  // whole object. Ranges mirror each Shopify setting's own min/max in
  // sections/ever-leaf-product.liquid.
  var CALIBRATION_VALIDATORS = {
    photoOpacity: function (v) { return typeof v === 'number' && isFinite(v) && v >= 0 && v <= 1; },
    tonalBrightness: function (v) { return typeof v === 'number' && isFinite(v) && v >= -100 && v <= 100; },
    tonalContrast: function (v) { return typeof v === 'number' && isFinite(v) && v >= -100 && v <= 100; },
    tonalGamma: function (v) { return typeof v === 'number' && isFinite(v) && v >= 0.2 && v <= 3; },
    tonalBlackPoint: function (v) { return typeof v === 'number' && isFinite(v) && v >= 0 && v <= 255; },
    tonalWhitePoint: function (v) { return typeof v === 'number' && isFinite(v) && v >= 0 && v <= 255; },
    textLayerVisible: function (v) { return typeof v === 'boolean'; },
    lightLayerVisible: function (v) { return typeof v === 'boolean'; },
    lightOpacity: function (v) { return typeof v === 'number' && isFinite(v) && v >= 0 && v <= 1; },
    // scaleMin/scaleMax are plain Shopify "number" settings (not "range"),
    // deliberately — see the section schema comment — so validation here is
    // just sanity bounds (positive, finite), not a mirrored range. The
    // scaleMin <= scaleMax cross-field check happens once, after both are
    // loaded — see loadProductConfig() below.
    scaleMin: function (v) { return typeof v === 'number' && isFinite(v) && v > 0 && v <= 10; },
    scaleMax: function (v) { return typeof v === 'number' && isFinite(v) && v > 0 && v <= 10; },
    moveStep: function (v) { return typeof v === 'number' && isFinite(v) && v >= 0.01 && v <= 0.15; },
    zoomStep: function (v) { return typeof v === 'number' && isFinite(v) && v >= 0.02 && v <= 0.4; },
    centerMinX: function (v) { return typeof v === 'number' && isFinite(v) && v >= -3 && v <= 3; },
    centerMaxX: function (v) { return typeof v === 'number' && isFinite(v) && v >= -3 && v <= 3; },
    centerMinY: function (v) { return typeof v === 'number' && isFinite(v) && v >= -3 && v <= 3; },
    centerMaxY: function (v) { return typeof v === 'number' && isFinite(v) && v >= -3 && v <= 3; },
    zoomPivotMode: function (v) { return v === 'image_center' || v === 'custom_pivot'; },
    zoomPivotX: function (v) { return typeof v === 'number' && isFinite(v) && v >= -3 && v <= 3; },
    zoomPivotY: function (v) { return typeof v === 'number' && isFinite(v) && v >= -3 && v <= 3; },
    closeUpVerticalOffsetPercent: function (v) { return typeof v === 'number' && isFinite(v) && v >= -100 && v <= 100; },
    closeUpHorizontalOffsetPercent: function (v) { return typeof v === 'number' && isFinite(v) && v >= -100 && v <= 100; },
    viewMode: function (v) { return v === 'close_up' || v === 'full_lamp'; },
    fullLampViewportXPercent: function (v) { return typeof v === 'number' && isFinite(v) && v >= 0 && v <= 100; },
    fullLampViewportYPercent: function (v) { return typeof v === 'number' && isFinite(v) && v >= 0 && v <= 100; },
    fullLampViewportSizePercent: function (v) { return typeof v === 'number' && isFinite(v) && v > 0 && v <= 100; },
    closeUpVerticalOffsetPercentMobile: function (v) { return typeof v === 'number' && isFinite(v) && v >= -100 && v <= 100; },
    closeUpZoomMobile: function (v) { return typeof v === 'number' && isFinite(v) && v > 0 && v <= 10; },
    closeUpHorizontalOffsetPercentMobile: function (v) { return typeof v === 'number' && isFinite(v) && v >= -100 && v <= 100; },
    closeUpScaleDesktop: function (v) { return typeof v === 'number' && isFinite(v) && v > 0 && v <= 10; },
    fullLampZoomMobile: function (v) { return typeof v === 'number' && isFinite(v) && v > 0 && v <= 10; },
    fullLampHorizontalOffsetPercentMobile: function (v) { return typeof v === 'number' && isFinite(v) && v >= -100 && v <= 100; },
    fullLampVerticalOffsetPercentMobile: function (v) { return typeof v === 'number' && isFinite(v) && v >= -100 && v <= 100; },
    fullLampScaleDesktop: function (v) { return typeof v === 'number' && isFinite(v) && v > 0 && v <= 10; },
    viewTransitionDurationMs: function (v) { return typeof v === 'number' && isFinite(v) && v >= 300 && v <= 1600; },
    photoOutlineColor: function (v) { return typeof v === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(v); },
    photoOutlineFadeInMs: function (v) { return typeof v === 'number' && isFinite(v) && v >= 0 && v <= 2000; },
    photoOutlinePauseMs: function (v) { return typeof v === 'number' && isFinite(v) && v >= 0 && v <= 2000; },
    photoOutlineFadeOutMs: function (v) { return typeof v === 'number' && isFinite(v) && v >= 0 && v <= 2000; },
    dedicationOpacity: function (v) { return typeof v === 'number' && isFinite(v) && v >= 0 && v <= 1; },
    dedicationColor: function (v) { return typeof v === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(v); },
    dedicationBlendMode: function (v) {
      return typeof v === 'string' && ['source-over', 'multiply', 'screen', 'overlay', 'darken', 'lighten', 'hard-light', 'soft-light'].indexOf(v) !== -1;
    },
    dedicationCenterX: function (v) { return typeof v === 'number' && isFinite(v) && v >= -3 && v <= 3; },
    dedicationLine1YSingle: function (v) { return typeof v === 'number' && isFinite(v) && v >= -3 && v <= 3; },
    dedicationTogetherLine1Y: function (v) { return typeof v === 'number' && isFinite(v) && v >= -3 && v <= 3; },
    dedicationTogetherLine2YFromBottom: function (v) { return typeof v === 'number' && isFinite(v) && v >= -3 && v <= 3; },
    dedicationSplitLine1Y: function (v) { return typeof v === 'number' && isFinite(v) && v >= -3 && v <= 3; },
    dedicationSplitLine2YFromBottom: function (v) { return typeof v === 'number' && isFinite(v) && v >= -3 && v <= 3; },
    line1FontSize: function (v) { return typeof v === 'number' && isFinite(v) && v >= 10 && v <= 120; },
    line2FontSize: function (v) { return typeof v === 'number' && isFinite(v) && v >= 10 && v <= 120; },
    textSizeMin: function (v) { return typeof v === 'number' && isFinite(v) && v >= 8 && v <= 100; },
    line1MaxChars: function (v) { return typeof v === 'number' && isFinite(v) && v >= 1 && v <= 60; },
    line2MaxChars: function (v) { return typeof v === 'number' && isFinite(v) && v >= 1 && v <= 60; },
    line1TargetWidth: function (v) { return typeof v === 'number' && isFinite(v) && v >= 0.1 && v <= 1; },
    line2TargetWidth: function (v) { return typeof v === 'number' && isFinite(v) && v >= 0.1 && v <= 1; }
  };
  function getProductConfigElement() {
    return document.querySelector('[data-ever-leaf-product-config]');
  }
  /*
    Reads the Shopify-rendered config script tag: code defaults first, then
    ONLY the fields that (a) are a recognized key in CALIBRATION_DEFAULTS
    and (b) pass that field's own validator win over the default. A missing
    element, malformed JSON, or an individually invalid field safely yields
    (partial) defaults — never throws, never blocks Visualizer init. This
    is the ONE place these values enter the runtime; nothing else reads
    the config script tag directly.
  */
  function loadProductConfig() {
    var result = Object.assign({}, CALIBRATION_DEFAULTS);
    try {
      var el = getProductConfigElement();
      if (!el) return result;
      var parsed = JSON.parse(el.textContent);
      if (!parsed) return result;
      var vc = parsed.visualizerCalibration || {};
      var dt = parsed.dedicationText || {};
      var flat = {
        photoOpacity: vc.photoOpacity,
        tonalBrightness: vc.tonalBrightness,
        tonalContrast: vc.tonalContrast,
        tonalGamma: vc.tonalGamma,
        tonalBlackPoint: vc.tonalBlackPoint,
        tonalWhitePoint: vc.tonalWhitePoint,
        textLayerVisible: vc.textLayerVisible,
        lightLayerVisible: vc.lightLayerVisible,
        lightOpacity: vc.lightOpacity,
        scaleMin: vc.scaleMin,
        scaleMax: vc.scaleMax,
        moveStep: vc.moveStep,
        zoomStep: vc.zoomStep,
        centerMinX: vc.centerMinX,
        centerMaxX: vc.centerMaxX,
        centerMinY: vc.centerMinY,
        centerMaxY: vc.centerMaxY,
        zoomPivotMode: vc.zoomPivotMode,
        zoomPivotX: vc.zoomPivotX,
        zoomPivotY: vc.zoomPivotY,
        closeUpVerticalOffsetPercent: vc.closeUpVerticalOffsetPercent,
        closeUpHorizontalOffsetPercent: vc.closeUpHorizontalOffsetPercent,
        viewMode: vc.staticScenePreview,
        fullLampViewportXPercent: vc.fullLampViewportXPercent,
        fullLampViewportYPercent: vc.fullLampViewportYPercent,
        fullLampViewportSizePercent: vc.fullLampViewportSizePercent,
        closeUpVerticalOffsetPercentMobile: vc.closeUpVerticalOffsetPercentMobile,
        closeUpZoomMobile: vc.closeUpZoomMobile,
        closeUpHorizontalOffsetPercentMobile: vc.closeUpHorizontalOffsetPercentMobile,
        closeUpScaleDesktop: vc.closeUpScaleDesktop,
        fullLampZoomMobile: vc.fullLampZoomMobile,
        fullLampHorizontalOffsetPercentMobile: vc.fullLampHorizontalOffsetPercentMobile,
        fullLampVerticalOffsetPercentMobile: vc.fullLampVerticalOffsetPercentMobile,
        fullLampScaleDesktop: vc.fullLampScaleDesktop,
        viewTransitionDurationMs: vc.viewTransitionDurationMs,
        photoOutlineColor: vc.photoOutlineColor,
        photoOutlineFadeInMs: vc.photoOutlineFadeInMs,
        photoOutlinePauseMs: vc.photoOutlinePauseMs,
        photoOutlineFadeOutMs: vc.photoOutlineFadeOutMs,
        dedicationOpacity: dt.opacity,
        dedicationColor: dt.color,
        dedicationBlendMode: dt.blendMode,
        dedicationCenterX: dt.centerXRatio,
        dedicationLine1YSingle: dt.line1YSingleRatio,
        dedicationTogetherLine1Y: dt.togetherLine1YRatio,
        dedicationTogetherLine2YFromBottom: dt.togetherLine2YFromBottomRatio,
        dedicationSplitLine1Y: dt.splitLine1YRatio,
        dedicationSplitLine2YFromBottom: dt.splitLine2YFromBottomRatio,
        line1FontSize: dt.line1FontSize,
        line2FontSize: dt.line2FontSize,
        textSizeMin: dt.minFittedSize,
        line1MaxChars: dt.line1MaxChars,
        line2MaxChars: dt.line2MaxChars,
        line1TargetWidth: dt.line1TargetWidthRatio,
        line2TargetWidth: dt.line2TargetWidthRatio
      };
      Object.keys(CALIBRATION_DEFAULTS).forEach(function (key) {
        var v = flat[key];
        if (v !== undefined && CALIBRATION_VALIDATORS[key](v)) {
          result[key] = v;
        }
      });
      // Cross-field safety — each field above already passed its OWN
      // validator, but a merchant could still configure an inverted pair
      // (e.g. Minimum scale 4, Maximum scale 2). Falls back to the exact
      // same safe single-point collapse clampScaleValue()/
      // clampCenterValue() themselves already use for this degenerate
      // case, rather than silently swapping values a merchant didn't ask
      // to swap.
      if (result.scaleMin > result.scaleMax) {
        result.scaleMin = result.scaleMax;
      }
      if (result.centerMinX > result.centerMaxX) {
        var midX = (result.centerMinX + result.centerMaxX) / 2;
        result.centerMinX = result.centerMaxX = midX;
      }
      if (result.centerMinY > result.centerMaxY) {
        var midY = (result.centerMinY + result.centerMaxY) / 2;
        result.centerMinY = result.centerMaxY = midY;
      }
      // No-code Scene Asset management — `sceneAssets` is a nested object,
      // not a flat calibration scalar, so it's validated separately rather
      // than through CALIBRATION_VALIDATORS. The URLs themselves are
      // already fully source-resolved server-side (Shopify Content
      // image_picker or file_url, 2048x2048-validated where applicable) by
      // sections/ever-leaf-product.liquid — this is just a shape/sanity
      // check on the JSON, not a re-validation of the images. An invalid/
      // missing sceneAssets object safely yields `null`; ever-leaf-lamp-
      // scene.js's loader already fails closed (rejects, Visualizer simply
      // never renders) exactly like a missing legacy asset URL did before
      // Pass 3 — no new fragility introduced.
      //
      // PHASE 45B — no more top-level `key`/`lampBlurUrl`/`lampSharpUrl`
      // (Gray is no longer a special always-present default); this object
      // now only carries the two genuinely SHARED assets plus
      // `variantScenes`, a JSON object keyed by real Shopify variant ID
      // (see sections/ever-leaf-product.liquid's PHASE 45B comment for the
      // full architecture). A missing/malformed `variantScenes` object
      // (e.g. a pre-PHASE-45B cached config) safely yields `null` here —
      // getVariantSceneUrls() already treats an absent entry as "no Scene
      // for this variant" and blocks initialization cleanly rather than
      // crashing or substituting another variant's artwork.
      var sa = parsed.sceneAssets;
      if (
        sa &&
        typeof sa.photoMaskUrl === 'string' && sa.photoMaskUrl &&
        typeof sa.lightsOverlayUrl === 'string' && sa.lightsOverlayUrl
      ) {
        result.sceneAssets = {
          photoMaskUrl: sa.photoMaskUrl,
          lightsOverlayUrl: sa.lightsOverlayUrl,
          variantScenes: sa.variantScenes && typeof sa.variantScenes === 'object' ? sa.variantScenes : null,
          // Design-mode-only diagnostics (Pass 3A targeted correction,
          // Section 30) — `sources`/`dimensions`/`warnings` are only
          // emitted into the config JSON at all inside
          // {% if request.design_mode %} server-side (see
          // sections/ever-leaf-product.liquid), so these are simply
          // `undefined` on the real storefront — never rendered, never
          // computed there. Threaded through here (not just console.warn'd)
          // so renderSceneAssetsDiagnostic() below can show them in a
          // genuinely visible on-page panel, not just devtools.
          sources: sa.sources || null,
          dimensions: sa.dimensions || null,
          warnings: Array.isArray(sa.warnings) ? sa.warnings : []
        };
        if (sa.warnings && sa.warnings.length) {
          sa.warnings.forEach(function (w) {
            if (window.console && console.warn) console.warn('[Ever Leaf Scene Assets] ' + w);
          });
        }
      } else {
        result.sceneAssets = null;
      }
    } catch (e) {
      // Missing/malformed config script — defaults already assigned above.
    }
    return result;
  }

  /*
    Theme Editor live-calibration-preview correction — shape of the
    config-DERIVED state fields only (never customer-entered/session state:
    no customerFile/customerSource/prepared photos/photoAspect/photoX/Y/
    Scale/text/selectedVariant/uploadStatus/uploadError). Shared by
    createPersonalizerState()'s one-time initial build below and
    refreshFromConfig()'s Theme-Editor-only re-sync (same function, so the
    two can never drift out of sync with each other).

    Root cause this fixes: loadProductConfig() was previously read exactly
    ONCE per real page load (inside createPersonalizerState(), itself
    guarded to run once) — correct for the CUSTOMER storefront (one config
    snapshot per page load is exactly right), but wrong for the Theme
    Editor: a Shopify Theme Editor settings change replaces this section's
    DOM and re-executes this script (the file-level `if (window.X) return`
    guards exist specifically because of this — see
    ever-leaf-editor-preview.js's own header for the empirically-confirmed
    citation), yet the file-level state-singleton guard meant the NEWLY
    rendered config JSON (with the merchant's just-changed slider value)
    was never actually re-read — only Gallery/Visualizer visibility and
    customer-entered state were already being correctly re-synced (see
    syncGalleryVisualizerState()), not calibration VALUES themselves. This
    is why an unsaved Brightness/Contrast/scale-limit/Scene-Asset-picker
    change did not visibly update the already-uploaded test photo.
  */
  function buildConfigDerivedPatch(persistedCal) {
    return {
      photoTonal: {
        brightness: persistedCal.tonalBrightness,
        contrast: persistedCal.tonalContrast,
        gamma: persistedCal.tonalGamma,
        blackPoint: persistedCal.tonalBlackPoint,
        whitePoint: persistedCal.tonalWhitePoint
      },
      imageLimits: {
        scaleMin: persistedCal.scaleMin,
        scaleMax: persistedCal.scaleMax,
        moveStep: persistedCal.moveStep,
        zoomStep: persistedCal.zoomStep,
        centerMinX: persistedCal.centerMinX,
        centerMaxX: persistedCal.centerMaxX,
        centerMinY: persistedCal.centerMinY,
        centerMaxY: persistedCal.centerMaxY
      },
      // Pass 3A targeted correction — config-derived, like imageLimits
      // (NOT part of the IndexedDB Preview Session; a Theme Editor section
      // re-render must pick up a merchant's just-changed pivot mode/point).
      zoomPivot: {
        mode: persistedCal.zoomPivotMode,
        x: persistedCal.zoomPivotX,
        y: persistedCal.zoomPivotY
      },
      // PASS 3A FINAL CONSOLIDATED CALIBRATION + FRAMING CORRECTION,
      // Section 38-52 — config-derived, same reasoning as zoomPivot above.
      // Purely VIEW-space (see getCurrentViewport() in init()) — never
      // read by anything that touches Personalization State Space or
      // triggers photo reprocessing.
      closeUpVerticalOffsetPercent: persistedCal.closeUpVerticalOffsetPercent,
      closeUpHorizontalOffsetPercent: persistedCal.closeUpHorizontalOffsetPercent,
      closeUpVerticalOffsetPercentMobile: persistedCal.closeUpVerticalOffsetPercentMobile,
      closeUpZoomMobile: persistedCal.closeUpZoomMobile,
      closeUpHorizontalOffsetPercentMobile: persistedCal.closeUpHorizontalOffsetPercentMobile,
      closeUpScaleDesktop: persistedCal.closeUpScaleDesktop,
      fullLampScaleDesktop: persistedCal.fullLampScaleDesktop,
      fullLampViewportMobile: {
        zoom: persistedCal.fullLampZoomMobile,
        horizontalOffsetPercent: persistedCal.fullLampHorizontalOffsetPercentMobile,
        verticalOffsetPercent: persistedCal.fullLampVerticalOffsetPercentMobile
      },
      // PASS 3B — config-derived, same reasoning as closeUpVerticalOffsetPercent
      // above: purely VIEW-space (see getCurrentViewport() in init()), never
      // touches Personalization State Space, never touches the real
      // Shopify selected variant or upload state (Section 17/31/32/68/69).
      viewMode: persistedCal.viewMode,
      fullLampViewport: {
        xPercent: persistedCal.fullLampViewportXPercent,
        yPercent: persistedCal.fullLampViewportYPercent,
        sizePercent: persistedCal.fullLampViewportSizePercent
      },
      // PASS 3C — config-derived, same reasoning as fullLampViewport above;
      // kept identical in both createPersonalizerState() and
      // buildConfigDerivedPatch() so they can never drift.
      viewTransitionDurationMs: persistedCal.viewTransitionDurationMs,
      // PASS 3A MANUAL QA CORRECTION PASS, Section 39-45 — config-derived,
      // same reasoning as closeUpVerticalOffsetPercent above (pure CSS
      // presentation, never touches Personalization State Space, never
      // triggers photo reprocessing — see updatePhotoBoundingBoxAppearance()
      // in init()).
      photoOutlineColor: persistedCal.photoOutlineColor,
      photoOutlineFadeInMs: persistedCal.photoOutlineFadeInMs,
      photoOutlinePauseMs: persistedCal.photoOutlinePauseMs,
      photoOutlineFadeOutMs: persistedCal.photoOutlineFadeOutMs,
      // PASS 3A FINAL DEDICATION POSITIONING MODEL — config-derived
      // Position/Line 1/Line 2/Fit/Shared settings from the "Dedication
      // Text Config" block, replacing Group Y + Line Gap entirely with
      // three fully independent Y ratios (no field ever derived from
      // another). centerXRatio/line1YSingleRatio/line1YTwoRatio/
      // line2YFromBottomRatio are ratios; line1FontSize/line2FontSize/
      // minFontSize are raw 1060-space px (same convention the old
      // shortSize/mediumSize/minSize always used) — converted to a ratio
      // only inside renderer.computeDedicationLineFit()/
      // drawDedicationLayer(). line1MaxChars/line2MaxChars are independent
      // per-line character maximums (Section 16-17), read LIVE by
      // getMaxCharsForLine1()/getMaxCharsForLine2() — never cached, which
      // is what makes them update with no Save required.
      dedicationConfig: {
        centerXRatio: persistedCal.dedicationCenterX,
        line1YSingleRatio: persistedCal.dedicationLine1YSingle,
        togetherLine1YRatio: persistedCal.dedicationTogetherLine1Y,
        togetherLine2YFromBottomRatio: persistedCal.dedicationTogetherLine2YFromBottom,
        splitLine1YRatio: persistedCal.dedicationSplitLine1Y,
        splitLine2YFromBottomRatio: persistedCal.dedicationSplitLine2YFromBottom,
        line1FontSize: persistedCal.line1FontSize,
        line2FontSize: persistedCal.line2FontSize,
        minFontSize: persistedCal.textSizeMin,
        line1MaxChars: persistedCal.line1MaxChars,
        line2MaxChars: persistedCal.line2MaxChars,
        line1TargetWidthRatio: persistedCal.line1TargetWidth,
        line2TargetWidthRatio: persistedCal.line2TargetWidth
      },
      sceneAssets: persistedCal.sceneAssets || null,
      layerSettings: {
        photo: { opacity: persistedCal.photoOpacity },
        text: { visible: persistedCal.textLayerVisible, opacity: persistedCal.dedicationOpacity, color: persistedCal.dedicationColor, blendMode: persistedCal.dedicationBlendMode },
        light: { visible: persistedCal.lightLayerVisible, opacity: persistedCal.lightOpacity }
      }
    };
  }

  /*
    Two-Line Dedication model, Section 15 — combines the two explicit
    customer-entered lines into the ONE canonical `Dedication` cart
    property value, ONLY at this serialization boundary (never earlier —
    Section 13). If only one line has content, the result is that line
    alone (byte-identical to the pre-two-line single-Dedication behavior).
    If both have content, joined with a real newline. If Line 1 is blank
    but Line 2 has content, the non-empty content moves into the result
    with no meaningless leading newline (filters blanks before joining,
    rather than always joining exactly two slots).
  */
  function serializeDedication(line1, line2) {
    return [line1, line2]
      .filter(function (line) {
        return line != null && line !== '';
      })
      .join('\n');
  }

  /*
    Two-Line Dedication model (PASS 3A FINAL CONSOLIDATED UI CORRECTION) —
    per-line text fitting (measureText) is cached here, computed ONCE per
    content/config change, never during Photo drag/zoom. Each line's fitted
    ratio is stored independently on layerSettings.text so both the
    interactive and HQ renderers draw at an identical relative size and a
    change to one line's fit never touches the other's (Section 20). A
    top-level function (not nested in init()) — called both from the
    normal typing path (EverLeafPersonalizer.init's onDedicationChange) and
    from restoreFromPreviewSession() below, matching that function's own
    convention of reading window.EverLeafImageRenderer/
    window.EverLeafPersonalizerState directly rather than closing over a
    local var.
  */
  function refitDedicationLines() {
    var renderer = window.EverLeafImageRenderer;
    var state = window.EverLeafPersonalizerState;
    if (!renderer || !state) return;
    var s = state.get();
    var cfg = s.dedicationConfig || {};
    // PASS 3A MANUAL QA CORRECTION PASS, Section 31-33 — each line's own
    // Target Width, when configured, overrides
    // renderer.TEXT_CANONICAL.maxWidthRatio for THIS call only (never
    // mutates the canonical default) via computeDedicationLineFit's own
    // pre-existing options-merge mechanism — no renderer-level code change
    // needed, since that function already merges `options` over
    // TEXT_CANONICAL and already reads `options.maxWidthRatio`. Omitting
    // the key entirely (rather than passing `maxWidthRatio: undefined`)
    // when unset is deliberate — Object.assign would otherwise overwrite
    // the canonical default with `undefined`.
    var line1Options = cfg.line1TargetWidthRatio != null ? { maxWidthRatio: cfg.line1TargetWidthRatio } : undefined;
    var line2Options = cfg.line2TargetWidthRatio != null ? { maxWidthRatio: cfg.line2TargetWidthRatio } : undefined;
    var fit1 = renderer.computeDedicationLineFit(s.dedicationLine1, cfg.line1FontSize, cfg.minFontSize, line1Options);
    var fit2 = renderer.computeDedicationLineFit(s.dedicationLine2, cfg.line2FontSize, cfg.minFontSize, line2Options);
    state.updateLayer('text', {
      line1FontSizeRatio: fit1.fontSizeRatio,
      line2FontSizeRatio: fit2.fontSizeRatio
    });
  }

  /*
    Minimal shared state container (single source of truth for Desktop and
    Mobile UIs alike — see brief). Plain pub/sub: update(partial) merges and
    notifies every subscriber; no framework dependency. Every reader below
    calls .get() live at read time — nothing destructures/caches a stale
    snapshot anywhere in this file.
  */
  function createPersonalizerState() {
    // Load Shopify-configured defaults (already validated/sanitized above),
    // so the state object below is built ALREADY hydrated from the
    // Visualizer Calibration / Dedication Text config blocks. Since this
    // whole function only ever runs ONCE per page (guarded at the top of
    // the file), a Theme Editor section reload simply never touches this
    // again, reading the same in-memory object instead.
    var persistedCal = loadProductConfig();
    var state = {
      customerFile: null,
      customerSource: null, // original decoded (ImageBitmap/HTMLImageElement) — kept only for the "Original" debug preview
      preparedPhotoInteractive: null, // downscaled + tonally-prepared, used for every drag/zoom frame
      preparedPhotoHQ: null, // full-resolution + tonally-prepared, used only for the settled HQ render
      photoTonal: {
        brightness: persistedCal.tonalBrightness,
        contrast: persistedCal.tonalContrast,
        gamma: persistedCal.tonalGamma,
        blackPoint: persistedCal.tonalBlackPoint,
        whitePoint: persistedCal.tonalWhitePoint
      },
      // The ORIGINAL decoded source's natural width/height ratio — computed
      // once at file-selection time and threaded verbatim into every render
      // call (renderer.renderInteractive/renderPersonalizedLeaf) as the
      // canonical `photoAspect`, so interactive and HQ always agree on
      // geometry regardless of any prepared-canvas downscale rounding
      // (Section 19 fix — see ever-leaf-image-renderer.js).
      photoAspect: null,
      // Pass 3A interaction-model correction — canonical stored transform
      // is now CENTER (photoCenterX/Y), not top-left. null = "no photo
      // positioned yet". Legacy top-left (`_ever_leaf_photo_x/y`) is
      // derived on demand via renderer.centerToTopLeft() — see
      // legacyTopLeftFromState()/sceneTransformFromState() below — never
      // stored directly.
      photoCenterX: null,
      photoCenterY: null,
      photoScale: null,
      // Pass 3A interaction-model correction — Scale and Center limits/
      // sensitivity, same window-scoped-singleton reasoning as photoTonal
      // (a Theme Editor section re-render must pick up the current value,
      // not silently reset to a hardcoded literal). This is now the ONE
      // authoritative model — no automatic/manual mode, no mask-cover
      // fallback, no scale pivot: Scale is bounded by scaleMin/scaleMax
      // only, Center is bounded by centerMinX/MaxX/MinY/MaxY only, and the
      // two are never coupled to each other or to mask geometry.
      imageLimits: {
        scaleMin: persistedCal.scaleMin,
        scaleMax: persistedCal.scaleMax,
        moveStep: persistedCal.moveStep,
        zoomStep: persistedCal.zoomStep,
        centerMinX: persistedCal.centerMinX,
        centerMaxX: persistedCal.centerMaxX,
        centerMinY: persistedCal.centerMinY,
        centerMaxY: persistedCal.centerMaxY
      },
      // Pass 3A targeted correction — optional Custom Pivot Point zoom
      // mode, layered on top of the Center model (see
      // computePivotPreservingCenter() in ever-leaf-image-renderer.js and
      // resolveZoomTransform()/applyZoom() below). Config-derived, same
      // reasoning as imageLimits above — see buildConfigDerivedPatch().
      zoomPivot: {
        mode: persistedCal.zoomPivotMode,
        x: persistedCal.zoomPivotX,
        y: persistedCal.zoomPivotY
      },
      // PASS 3A FINAL CONSOLIDATED CALIBRATION + FRAMING CORRECTION —
      // see buildConfigDerivedPatch() above for the field shape/reasoning;
      // kept identical here since createPersonalizerState()'s initial
      // build and refreshFromConfig()'s re-sync must never drift.
      closeUpVerticalOffsetPercent: persistedCal.closeUpVerticalOffsetPercent,
      closeUpHorizontalOffsetPercent: persistedCal.closeUpHorizontalOffsetPercent,
      closeUpVerticalOffsetPercentMobile: persistedCal.closeUpVerticalOffsetPercentMobile,
      closeUpZoomMobile: persistedCal.closeUpZoomMobile,
      closeUpHorizontalOffsetPercentMobile: persistedCal.closeUpHorizontalOffsetPercentMobile,
      closeUpScaleDesktop: persistedCal.closeUpScaleDesktop,
      fullLampScaleDesktop: persistedCal.fullLampScaleDesktop,
      fullLampViewportMobile: {
        zoom: persistedCal.fullLampZoomMobile,
        horizontalOffsetPercent: persistedCal.fullLampHorizontalOffsetPercentMobile,
        verticalOffsetPercent: persistedCal.fullLampVerticalOffsetPercentMobile
      },
      // PASS 3B — see buildConfigDerivedPatch() above for the field shape/
      // reasoning; kept identical here since createPersonalizerState()'s
      // initial build and refreshFromConfig()'s re-sync must never drift.
      viewMode: persistedCal.viewMode,
      fullLampViewport: {
        xPercent: persistedCal.fullLampViewportXPercent,
        yPercent: persistedCal.fullLampViewportYPercent,
        sizePercent: persistedCal.fullLampViewportSizePercent
      },
      // PASS 3C — config-derived, same reasoning as fullLampViewport above;
      // kept identical in both createPersonalizerState() and
      // buildConfigDerivedPatch() so they can never drift.
      viewTransitionDurationMs: persistedCal.viewTransitionDurationMs,
      // PASS 3A MANUAL QA CORRECTION PASS, Section 39-45 — config-derived,
      // same reasoning as closeUpVerticalOffsetPercent above (pure CSS
      // presentation, never touches Personalization State Space, never
      // triggers photo reprocessing — see updatePhotoBoundingBoxAppearance()
      // in init()).
      photoOutlineColor: persistedCal.photoOutlineColor,
      photoOutlineFadeInMs: persistedCal.photoOutlineFadeInMs,
      photoOutlinePauseMs: persistedCal.photoOutlinePauseMs,
      photoOutlineFadeOutMs: persistedCal.photoOutlineFadeOutMs,
      // PASS 3A FINAL DEDICATION POSITIONING MODEL — see
      // buildConfigDerivedPatch() above for the field shape/units; kept
      // identical here since createPersonalizerState()'s initial build and
      // refreshFromConfig()'s re-sync must never drift.
      dedicationConfig: {
        centerXRatio: persistedCal.dedicationCenterX,
        line1YSingleRatio: persistedCal.dedicationLine1YSingle,
        togetherLine1YRatio: persistedCal.dedicationTogetherLine1Y,
        togetherLine2YFromBottomRatio: persistedCal.dedicationTogetherLine2YFromBottom,
        splitLine1YRatio: persistedCal.dedicationSplitLine1Y,
        splitLine2YFromBottomRatio: persistedCal.dedicationSplitLine2YFromBottom,
        line1FontSize: persistedCal.line1FontSize,
        line2FontSize: persistedCal.line2FontSize,
        minFontSize: persistedCal.textSizeMin,
        line1MaxChars: persistedCal.line1MaxChars,
        line2MaxChars: persistedCal.line2MaxChars,
        line1TargetWidthRatio: persistedCal.line1TargetWidth,
        line2TargetWidthRatio: persistedCal.line2TargetWidth
      },
      // No-code Scene Asset management — null if the "Visualizer Scene
      // Assets" config block/JSON is missing/malformed, in which case
      // init() falls back to the data-fallback-*-url attributes instead.
      sceneAssets: persistedCal.sceneAssets || null,
      // Two-Line Dedication model — dedicationLine1/Line2 are the
      // CANONICAL customer-entered content (Section 13: explicit state,
      // never one ambiguous variable). `text` is DERIVED — always kept in
      // sync as the serialized combined value (see serializeDedication()
      // below) — and stays the read path for the cart `Dedication`
      // property and every existing diagnostic/audit call site, so none of
      // those needed to change shape (Section 15/16 — cart contract stays
      // exactly one `Dedication` property).
      dedicationLine1: '',
      dedicationLine2: '',
      // PASS 3A FINAL UX PASS, Section 10-13 — the customer's chosen
      // two-line layout preset. Canonical customer-entered state, exactly
      // like dedicationLine1/Line2 above (not config-derived — a Theme
      // Editor section reload/refreshFromConfig() must never touch this).
      // Allowed values: 'together' | 'split' (Section 11 — stable lowercase
      // internal vocabulary). Starts at the documented default ('together',
      // Section 10) and is the ONLY thing a preset click ever changes —
      // nothing else in this object is touched by that write (Section 19).
      // SHARED Dedication placement (semantic): 'below' | 'above'. ONE state for Desktop
      // and Mobile. The legacy cart/order vocabulary (together|split) is only produced at
      // the commit boundary via layoutFromPlacement() and consumed at restore.
      dedicationPlacement: 'below',
      text: '',
      selectedVariant: null,
      layerSettings: {
        photo: { opacity: persistedCal.photoOpacity },
        text: { visible: persistedCal.textLayerVisible, opacity: persistedCal.dedicationOpacity, color: persistedCal.dedicationColor, blendMode: persistedCal.dedicationBlendMode },
        light: { visible: persistedCal.lightLayerVisible, opacity: persistedCal.lightOpacity }
      },
      // 'empty' (no photo) | 'selected' (photo present, idle) |
      // 'uploading' (an Add to Cart / Buy Now commit — i.e. the ONE Mini
      // finalize call — is currently in flight) | 'error' (last commit
      // attempt failed). This is now entirely about the FINAL COMMIT, never
      // about background editing-time upload — Mini has no editing-time
      // presence in this architecture at all.
      uploadStatus: 'empty',
      uploadError: null
    };
    var listeners = [];

    function get() {
      return state;
    }

    function update(partial) {
      Object.assign(state, partial);
      listeners.forEach(function (listener) {
        listener(state);
      });
    }

    function updateLayer(layerName, patch) {
      var next = Object.assign({}, state.layerSettings);
      next[layerName] = Object.assign({}, next[layerName], patch);
      update({ layerSettings: next });
    }

    function subscribe(listener) {
      listeners.push(listener);
      return function unsubscribe() {
        var index = listeners.indexOf(listener);
        if (index !== -1) listeners.splice(index, 1);
      };
    }

    /*
      Theme Editor live-calibration-preview correction — re-reads the
      CURRENT config JSON and merges config-derived fields (see
      buildConfigDerivedPatch's own header) into state, called ONLY from
      EverLeafPersonalizer.init() and ONLY when window.Shopify.designMode
      is true (never on the real customer storefront — see that call site).
      layerSettings is merged per-layer (not replaced wholesale) so a
      customer-entered value on a layer config doesn't own — currently only
      layerSettings.text.line1FontSizeRatio/line2FontSizeRatio, set by
      refitDedicationLines() from the typed Dedication lines — survive
      untouched.
    */
    function refreshFromConfig() {
      var persistedCal = loadProductConfig();
      var patch = buildConfigDerivedPatch(persistedCal);
      var mergedLayerSettings = Object.assign({}, state.layerSettings);
      Object.keys(patch.layerSettings).forEach(function (layerName) {
        mergedLayerSettings[layerName] = Object.assign({}, mergedLayerSettings[layerName], patch.layerSettings[layerName]);
      });
      patch.layerSettings = mergedLayerSettings;
      update(patch);
    }

    return { get: get, update: update, updateLayer: updateLayer, subscribe: subscribe, refreshFromConfig: refreshFromConfig };
  }

  // One shared instance sitewide — Desktop and Mobile personalizer elements
  // (however many are mounted) all read/write the same state.
  window.EverLeafPersonalizerState = createPersonalizerState();

  /*
    PASS 3A FINAL DEDICATION POSITIONING MODEL, Section 16-19 — split,
    INDEPENDENT per-line character maximums, replacing the old single
    shared `maxDedicationChars`. Root cause of "Maximum Characters does not
    update until Save" (Section 18), traced not guessed: the OLD
    `TEXT_INPUT_CONFIG.maxChars` was a plain object literal seeded ONCE
    from state at script-load time — `setMaxTextChars()` existed as a
    push-based updater, but nothing in the file ever actually CALLED it on
    a Theme Editor config refresh, so it silently went stale until the next
    full page load (a genuine Save/navigation). Fixed by deleting that
    whole cache-and-push architecture entirely and reading LIVE from state
    on every call instead — the SAME "live read, never cache" convention
    getImageLimits()/getZoomPivot() already use elsewhere in this file.
    Since bindDedicationInputs() (below) is already re-invoked on every
    Theme Editor config-only-block reload (both via a fresh
    <ever-leaf-personalizer> reconnect AND the shopify:section:load
    listener), this alone makes the DOM `maxlength` attribute update live
    with no Save required, no separate pub/sub layer needed.
  */
  function getMaxCharsForLine1() {
    var cfg = window.EverLeafPersonalizerState.get().dedicationConfig || {};
    return cfg.line1MaxChars != null ? cfg.line1MaxChars : 12;
  }
  function getMaxCharsForLine2() {
    var cfg = window.EverLeafPersonalizerState.get().dedicationConfig || {};
    return cfg.line2MaxChars != null ? cfg.line2MaxChars : 12;
  }

  /*
    Upload Photo — the ONE unified upload/replace action (Finalization pass,
    Section 3-5: Change Photo has been removed; pressing Upload Photo again
    while a photo already exists replaces it via this exact same path,
    resetting the transform to a fresh default-fit and preserving state.text
    — never carrying the old photo's position/scale over). A local File
    straight from OUR OWN hidden native file input (see
    EverLeafCustomerControls), never Mini/FilePond. Implements the
    fast-interactive-then-deferred-HQ pattern (Section 20): the small
    interactive prepared source is built and shown FIRST, and only once
    that's already on screen does the full-resolution HQ source get built,
    asynchronously, off the initial paint.
  */

  /*
    BACKGROUND PHOTO PRE-UPLOAD MICRO-PASS — the ONE authoritative hosted-
    upload lifecycle for the currently-selected customer File, shared by
    both the background pre-upload trigger (handleLocalFileSelected below,
    fire-and-forget, right after the local preview path is set up) and the
    actual commit path (runCommit, inside wireCartCommitFlow) — replacing
    the prior ATC-performance pass's standalone lastUploadedFile/
    lastUploadedHostedUrl cache variables entirely, per this pass's explicit
    "one source of truth" requirement (no second, competing cache).

    State machine, keyed by strict File OBJECT REFERENCE identity (never
    filename/size/lastModified — the browser creates a genuinely NEW File
    object on every fresh selection, even re-selecting the identical file
    from disk, so reference identity is a safe, self-invalidating key):
      IDLE      — no record exists yet for the current File (getRecordFor
                  returns null).
      UPLOADING — one Mini/FilePond finalizeUpload() call is in flight for
                  the current File (record.promise is pending).
      READY     — that exact File has successfully produced a hosted URL
                  (record.status/hostedUrl).
      ERROR     — the upload for that exact File failed; start() is
                  intentionally NOT a no-op in this state, so a later retry
                  (from runCommit) begins a fresh attempt for the SAME File.

    Single-flight is enforced in TWO dimensions:
      1. Per File — start() is a no-op whenever a record already exists for
         the given File with status 'uploading' or 'ready' (never starts a
         second upload for the SAME File — this is what makes the "select,
         then click Add to Cart almost immediately" race safe regardless of
         which call site's start() call happens to run first: whichever
         runs second always finds the first one's record already in place).
      2. Across DIFFERENT Files, GLOBALLY — Mini/FilePond exposes exactly
         ONE shared pond instance (resolvePondInstance() in
         ever-leaf-mini-upload-adapter.js always resolves the SAME
         '.filepond--root'), and finalizeUpload()'s own sanitizeStaleState()
         calls pond.removeFiles() at the START of every call — so two
         concurrent finalizeUpload() calls for two DIFFERENT Files would
         corrupt each other (File B's call would rip the pond out from under
         a still-in-flight File A call). `pondQueue` below is a simple,
         always-settling Promise chain ensuring the actual Mini/FilePond
         call for a NEW File only ever begins once the PREVIOUS File's call
         has fully settled (success or failure) — exactly the "let the old
         upload finish physically, but ignore its result" tradeoff this
         pass's brief explicitly accepts, rather than introducing risky
         cancellation code the adapter does not support. The old File's
         result is still safely ignored for the new File's own lifecycle via
         the `currentFile === file` checks below (a stale-completion guard).

    Never persisted to localStorage/IndexedDB/cookies — session/runtime
    memory only, exactly like the cache this replaces.
  */
  var photoUploadManager = (function () {
    var currentFile = null;
    var status = 'idle'; // idle | uploading | ready | error
    var hostedUrl = '';
    var promise = null;
    var pondQueue = Promise.resolve(); // always settles regardless of the previous attempt's outcome — see header comment, dimension 2

    function runUploadFor(file) {
      var adapter = window.EverLeafMiniUploadAdapter;
      return new Promise(function (resolve, reject) {
        if (!adapter) {
          reject(new Error('Upload service is unavailable right now.'));
          return;
        }
        adapter.finalizeUpload(file, {
          // Fine-grained DEV phase reporting (pondFound/addfile-start/...)
          // is only meaningful to a live diagnostics panel watching ONE
          // particular commit attempt — a purely-background start (or one
          // a later commit only JOINS after it was already running) has no
          // such listener anyway, so this is intentionally a no-op here;
          // runCommit's own diag fields are still fully populated from the
          // resolved/rejected result below, just without the interim phases.
          onStatus: function () {},
          onReady: function (result) {
            resolve((result && result.hostedUrl) || '');
          },
          onError: function (message) {
            reject(new Error(message || 'Upload failed.'));
          }
        });
      });
    }

    function start(file) {
      if (!file) return;
      if (currentFile === file && status !== 'error') return; // already uploading/ready for this exact File — never restart
      currentFile = file;
      status = 'uploading';
      hostedUrl = '';
      var myFile = file;
      var resultPromise = pondQueue.then(function () {
        return runUploadFor(myFile);
      });
      // Keep the shared queue alive regardless of THIS attempt's outcome, so
      // a File selected while this one is still running still gets its turn
      // next, once this one settles either way.
      pondQueue = resultPromise.then(
        function () {},
        function () {}
      );
      promise = resultPromise.then(
        function (url) {
          // Stale-completion guard: only apply if `file` is STILL the
          // tracked one — a later File selection (or Remove Photo) must
          // never let an old completion resurrect as the active record.
          if (currentFile === myFile) {
            status = 'ready';
            hostedUrl = url;
          }
          return url;
        },
        function (err) {
          if (currentFile === myFile) {
            status = 'error';
          }
          throw err;
        }
      );
      // A purely-background attempt (Section 51 — silent/invisible unless
      // Add to Cart actually needs it) may fail before any commit ever
      // joins `promise` — without a handler attached here, that would
      // surface as an unhandled promise rejection (a console error) for a
      // failure the customer never even tried to act on yet. This
      // `.catch()` only marks THIS rejection observed; it does not consume
      // or suppress the rejection for `getRecordFor()`'s own separate
      // `.then()`/`.catch()` call inside runCommit, which still fires
      // independently and normally when a real commit later joins it.
      promise.catch(function () {});
    }

    function getRecordFor(file) {
      if (!file || currentFile !== file) return null;
      return { status: status, hostedUrl: hostedUrl, promise: promise };
    }

    // Remove Photo — an eventual completion/failure of a physically-still-
    // running old upload can never repopulate the active record once
    // invalidated, since currentFile is cleared here and the stale-
    // completion guard above checks against the ORIGINAL File reference,
    // which no longer matches anything once cleared.
    function invalidate() {
      currentFile = null;
      status = 'idle';
      hostedUrl = '';
      promise = null;
    }

    return { start: start, getRecordFor: getRecordFor, invalidate: invalidate };
  })();
  // Identity guard for handleLocalFileSelected()'s decode-FAILURE path only:
  // each selection takes a number, and an older selection's late failure is
  // ignored once a newer selection exists, so it can never surface an error
  // over anything belonging to the newer file.
  var localPhotoSelectionGeneration = 0;
  function handleLocalFileSelected(file, onDecodeFailure) {
    var renderer = window.EverLeafImageRenderer;
    var state = window.EverLeafPersonalizerState;
    if (!renderer) return;
    var selectionGeneration = (localPhotoSelectionGeneration += 1);

    renderer.decodeFileToSource(file).then(function (source) {
    // NOTE — the reset below used to run synchronously BEFORE the async
    // decode. It now runs as the FIRST step of the successful-decode
    // continuation, immediately before the state update further down, so the
    // reset-then-update ordering every subscriber sees is unchanged, while an
    // unreadable file (which never becomes a photo) can no longer reset/
    // replay the hint systems for the photo that is still showing.
    // PHASE 46A/46B/47A — a genuinely new/replaced File is the ONLY thing
    // that resets/replays the Interaction Hints AND View Toggle first-
    // appear systems (Section "NEW/REPLACED PHOTO RESET"). This is the
    // single, authoritative place a new local file enters the system
    // (initial upload or "Change Photo"), so resetting here — before the
    // state update below — is correct regardless of how long HQ-prep takes
    // afterward.
    // handleLocalFileSelected() lives OUTSIDE init()'s own per-instance
    // closure (same file-scope boundary as the existing window.__everLeaf*
    // bridges below — window.__everLeafInteractiveSize/__everLeafPersonalizer
    // Assets), so the reset is exposed the SAME established way rather than
    // reaching for a closure variable that genuinely isn't in scope here.
    // Two independent bridges (Section "NO SHARED TIMER ASSUMPTION") — each
    // system owns its own cancellable delay/generation state.
    if (window.__everLeafResetInteractionHints) window.__everLeafResetInteractionHints();
    if (window.__everLeafResetViewToggleFirstAppear) window.__everLeafResetViewToggleFirstAppear();

      var previous = state.get();
      if (previous.customerSource) renderer.releaseSource(previous.customerSource);

      var width = source.width || source.naturalWidth;
      var height = source.height || source.naturalHeight;
      var aspect = width && height ? width / height : 1;

      // Pass 3A — legacyMaskBounds (Personalization State Space, NOT scene
      // space) is used ONLY to compute a good INITIAL cover-fit (this is a
      // one-time starting-point calculation, not a per-frame clamp — see
      // ever-leaf-image-renderer.js's Center-model header comment). The
      // result is converted to the canonical Center representation and
      // clamped against the simple, explicit Scale/Center limits — the
      // SAME clamp every other entry point (Reset, drag, arrows, zoom)
      // uses.
      var cachedAssets = window.__everLeafPersonalizerAssets;
      var defaultTopLeft =
        cachedAssets && cachedAssets.legacyMaskBounds
          ? renderer.computeDefaultPhotoTransform(width, height, cachedAssets.legacyMaskBounds, renderer.LEAF_LOCAL_SIZE)
          : renderer.DEFAULT_PHOTO_TRANSFORM;
      var uploadLimits = (window.EverLeafPersonalizerState.get().imageLimits) || {};
      var defaultCenterPoint = renderer.topLeftToCenter(defaultTopLeft.x, defaultTopLeft.y, defaultTopLeft.scale, aspect);
      var defaultScale = renderer.clampScaleValue(defaultTopLeft.scale, uploadLimits);
      var defaultCenter = renderer.clampCenterValue(
        { x: defaultCenterPoint.centerX, y: defaultCenterPoint.centerY },
        uploadLimits
      );

      // preparedPhotoHQ is explicitly cleared here (not left stale from a
      // prior photo) so the "settle" subscriber below doesn't fire an HQ
      // render against the OLD photo's prepared canvas while the NEW one is
      // still being prepared — only the fast interactive preview shows
      // during that (very short) window.
      state.update({
        customerFile: file,
        customerSource: source,
        photoAspect: aspect,
        preparedPhotoHQ: null,
        photoCenterX: defaultCenter.x,
        photoCenterY: defaultCenter.y,
        photoScale: defaultScale,
        uploadStatus: 'selected',
        uploadError: null
      });

      // MOBILE IMMERSIVE PRODUCT STAGE — Photo -> Line 1 auto-advance hook
      // (Section 17 of the brief). Fired the instant a NEW local File has
      // decoded and is already reflected in state above — deliberately
      // BEFORE the deferred hosted upload kicked off just below, so the
      // carousel never waits on network completion. Ordinary geometry/
      // tonal changes never reach this line again for the same File (this
      // whole block only runs once per genuinely new/replaced photo,
      // exactly like the Interaction Hints/View Toggle first-appear reset
      // above), and a stale File's own later hosted-URL resolution (PHASE
      // 50D) never re-enters handleLocalFileSelected at all, so it can
      // never re-fire this event either. Presentation-only DOM event, never
      // written to EverLeafPersonalizerState or any cart property.
      document.dispatchEvent(new CustomEvent('ever-leaf:local-photo-ready'));

      // BACKGROUND PHOTO PRE-UPLOAD MICRO-PASS — start the real hosted
      // upload now, quietly, fully in parallel with the interactive-
      // preview-prep work below and everything the customer does
      // afterward (drag/zoom/Dedication/etc. never restart it — see
      // photoUploadManager's own header comment). Deferred one tick (same
      // convention as the HQ-build setTimeout a few lines down) purely so
      // this function's own synchronous work — and therefore the local
      // preview path — can never be blocked even in principle;
      // finalizeUpload()'s real network work is already asynchronous
      // regardless. Never touches window.EverLeafPersonalizerState.
      // uploadStatus (that remains exclusively the cart-commit single-
      // flight/UI flag runCommit owns) — this is intentionally invisible to
      // the customer unless/until Add to Cart actually needs the result.
      setTimeout(function () {
        photoUploadManager.start(file);
      }, 0);

      var tonal = state.get().photoTonal;
      var interactiveMax = window.__everLeafInteractiveSize || MAX_INTERACTIVE_SIZE;
      var preparedPhotoInteractive = renderer.prepareGrayscalePhoto(source, {
        maxDimension: interactiveMax,
        tonal: tonal
      });
      state.update({ preparedPhotoInteractive: preparedPhotoInteractive });

      // Deferred HQ build — off the initial-preview critical path. Bails out
      // if a newer Upload (replace) or Remove has already superseded this
      // source by the time this runs.
      setTimeout(function () {
        if (state.get().customerSource !== source) return;
        var preparedPhotoHQ = renderer.prepareGrayscalePhoto(source, { tonal: state.get().photoTonal });
        if (state.get().customerSource !== source) return;
        state.update({ preparedPhotoHQ: preparedPhotoHQ });
      }, 0);

      savePreviewSession();
    }, function (decodeError) {
      // Unreadable/corrupt image. Nothing was installed before this point, so
      // the previous state (empty, or the previously valid photo) is already
      // intact: no customerSource, no hosted upload, no local-photo-ready
      // event. Only surface the failure, and only if this is still the most
      // recent selection.
      if (selectionGeneration !== localPhotoSelectionGeneration) return;
      if (typeof onDecodeFailure === 'function') onDecodeFailure(decodeError);
    });
  }

  /*
    Theme Editor Preview Session — persists the CURRENT test photo/
    transform/dedication so a Shopify Theme Editor Save (a real navigation,
    unlike an unsaved settings change) doesn't force a re-upload. A pure
    no-op outside the actual Theme Editor (window.EverLeafEditorPreviewSession
    itself gates every call on window.Shopify.designMode; this wrapper adds
    the "don't even bother reading state if the module isn't loaded" guard
    for the case the script isn't present at all, e.g. an older cached
    page). Safe to call liberally — cheap when there's no photo yet, and
    IndexedDB writes are fire-and-forget from the caller's perspective.
  */
  function savePreviewSession() {
    if (!window.EverLeafEditorPreviewSession) return;
    var s = window.EverLeafPersonalizerState.get();
    if (!s.customerFile) return;
    window.EverLeafEditorPreviewSession.save({
      photoFile: s.customerFile,
      fileName: s.customerFile.name || '',
      fileType: s.customerFile.type || '',
      centerX: s.photoCenterX,
      centerY: s.photoCenterY,
      scale: s.photoScale,
      // Two-Line Dedication model (Section 28) — the two explicit lines are
      // the PRIMARY persisted representation now, not a single combined
      // string; see ever-leaf-editor-preview-session.js's own schema
      // comment for the backward-compatible read side of this.
      dedicationLine1: s.dedicationLine1,
      dedicationLine2: s.dedicationLine2,
      // PASS 3A FINAL UX PASS, Section 12/58 — the chosen layout preset
      // must survive Theme Editor Save/test-session restore exactly like
      // the photo transform and Dedication text already do.
      dedicationLayout: layoutFromPlacement(s.dedicationPlacement)
    });
  }

  /*
    Theme Editor Preview Session restore — mirrors handleLocalFileSelected's
    decode/prepare pipeline exactly, EXCEPT the transform/text come from the
    saved session record instead of a freshly-computed default-fit (a
    restore must reproduce exactly where the merchant left off, not reset
    to a default). Re-clamps the restored Center/Scale against the
    CURRENT Scale/Center limits (in case those were also changed since the
    session was saved) via the same clamp functions every other entry
    point uses — never a raw, unclamped write.
  */
  function restoreFromPreviewSession(record) {
    var renderer = window.EverLeafImageRenderer;
    var state = window.EverLeafPersonalizerState;
    if (!renderer || !record || !record.photoFile) return;

    renderer.decodeFileToSource(record.photoFile).then(function (source) {
      // Another upload/restore may have already won the race by the time
      // this async decode resolves — never clobber a newer, real state.
      if (state.get().customerFile) return;

      var width = source.width || source.naturalWidth;
      var height = source.height || source.naturalHeight;
      var aspect = width && height ? width / height : 1;
      var limits = state.get().imageLimits || {};
      var restoredScale = renderer.clampScaleValue(
        typeof record.scale === 'number' ? record.scale : renderer.DEFAULT_PHOTO_TRANSFORM_CENTER.scale,
        limits
      );
      var restoredCenter = renderer.clampCenterValue(
        {
          x: typeof record.centerX === 'number' ? record.centerX : renderer.DEFAULT_PHOTO_TRANSFORM_CENTER.centerX,
          y: typeof record.centerY === 'number' ? record.centerY : renderer.DEFAULT_PHOTO_TRANSFORM_CENTER.centerY
        },
        limits
      );

      // Two-Line Dedication model backward compatibility (Section 28) — an
      // OLD Preview Session record (saved before this pass) only has
      // `dedicationText` (a single combined string, possibly itself
      // containing an embedded newline from an even-older two-line typing
      // convention, though none existed until now — realistically always a
      // single line). A NEW record has `dedicationLine1`/`dedicationLine2`
      // directly. Prefer the new fields when present; fall back to the old
      // single field entirely as Line 1, Line 2 empty — never lose an
      // existing saved test Dedication just because the record's shape
      // predates this pass.
      var restoredLine1 = typeof record.dedicationLine1 === 'string' ? record.dedicationLine1 : record.dedicationText || '';
      var restoredLine2 = typeof record.dedicationLine2 === 'string' ? record.dedicationLine2 : '';
      // PASS 3A FINAL UX PASS, Section 58 — an OLD Preview Session record
      // (saved before this pass) has no `dedicationLayout` key at all;
      // default to 'together' (the documented default) rather than break on
      // a missing field — never resets an already-'split' record, since
      // that key is only ever absent on records this pass could not have
      // written.
      var restoredPlacement = record.dedicationLayout === 'split' ? 'above' : 'below';

      state.update({
        customerFile: record.photoFile,
        customerSource: source,
        photoAspect: aspect,
        preparedPhotoHQ: null,
        photoCenterX: restoredCenter.x,
        photoCenterY: restoredCenter.y,
        photoScale: restoredScale,
        dedicationLine1: restoredLine1,
        dedicationLine2: restoredLine2,
        dedicationPlacement: restoredPlacement,
        text: serializeDedication(restoredLine1, restoredLine2),
        uploadStatus: 'selected',
        uploadError: null
      });
      // Section 27-31 — the DOM inputs and the fitted font-size ratios both
      // need an explicit refresh here: this restore can happen AFTER
      // EverLeafCalibrationPanel already bound the (then-empty) inputs at
      // init time (the real-world ordering — IndexedDB load is always
      // async), so nothing else will otherwise re-sync them. Section 12 —
      // the layout preset UI (visibility + pressed state) needs the same
      // explicit re-sync, for the same reason.
      syncDedicationInputsFromState();
      updateDedicationLayoutUI(state.get());
      refitDedicationLines();

      var tonal = state.get().photoTonal;
      var interactiveMax = window.__everLeafInteractiveSize || MAX_INTERACTIVE_SIZE;
      state.update({
        preparedPhotoInteractive: renderer.prepareGrayscalePhoto(source, { maxDimension: interactiveMax, tonal: tonal })
      });
      setTimeout(function () {
        if (state.get().customerSource !== source) return;
        var preparedPhotoHQ = renderer.prepareGrayscalePhoto(source, { tonal: state.get().photoTonal });
        if (state.get().customerSource !== source) return;
        state.update({ preparedPhotoHQ: preparedPhotoHQ });
      }, 0);
    });
  }

  /*
    Remove Photo -> full personalization reset. Remove Photo (simplified
    under the new architecture) is purely local state; Mini is intentionally
    NOT touched here (it has no editing-time role at all). Any stale
    Mini-owned hosted-image property left over from an earlier interrupted
    commit attempt is cleaned up lazily by sanitizeStaleState() at the START
    of the NEXT actual commit, not here.

    REMOVE PHOTO -> FULL PERSONALIZER RESET pass: Remove now also resets
    Dedication Line 1/2, Material and View Mode, so the personalizer looks
    and behaves like a fresh initial session afterward (the next upload
    starts clean rather than inheriting the removed photo's session). Photo
    position/scale were ALREADY reset to the canonical "no photo yet"
    sentinel (`null`, resolved on read via
    `Object.assign({}, renderer.DEFAULT_PHOTO_TRANSFORM_CENTER, ...)`,
    exactly like createPersonalizerState()'s own bootstrap state) — this is
    the existing, already-correct behavior for photoCenterX/Y/Scale, left
    untouched. Everything else routes through resetPersonalizationAfterPhotoRemoval()
    below, the one authoritative reset path, so Dedication/Material/View
    Mode can never drift out of sync with each other or be reimplemented
    slightly differently at a second call site.
  */
  function removeCurrentPhoto() {
    var renderer = window.EverLeafImageRenderer;
    var s = window.EverLeafPersonalizerState.get();
    if (s.customerSource && renderer) renderer.releaseSource(s.customerSource);

    // BACKGROUND PHOTO PRE-UPLOAD MICRO-PASS — an in-flight or completed
    // background upload for the photo being removed must never resurrect as
    // the active hosted URL for whatever comes next (a new photo, or no
    // photo at all). See photoUploadManager's own header comment.
    photoUploadManager.invalidate();

    window.EverLeafPersonalizerState.update({
      customerFile: null,
      customerSource: null,
      preparedPhotoInteractive: null,
      preparedPhotoHQ: null,
      photoAspect: null,
      photoCenterX: null,
      photoCenterY: null,
      photoScale: null,
      uploadStatus: 'empty',
      uploadError: null
    });
    // Preview Session cleanup (Section 25) — a removed test photo must not
    // silently reappear on the next Theme Editor reload. No-op (gated
    // internally) on the real customer storefront.
    if (window.EverLeafEditorPreviewSession) {
      window.EverLeafEditorPreviewSession.clear();
    }

    resetPersonalizationAfterPhotoRemoval();
  }

  /*
    REMOVE PHOTO -> FULL PERSONALIZER RESET pass — the ONE authoritative
    reset path Remove calls (see removeCurrentPhoto() above). Deliberately
    NOT scattered across separate UI/renderer/form/purchase-state reset
    code: every sub-reset below reuses the SAME setter/state-transition an
    ordinary customer action already uses, so this can never drift out of
    sync with the live editing paths.
      - Dedication: the exact state shape + `text` derivation
        calibrationPanel.onDedicationChange() (live typing) already uses,
        then the existing STATE -> DOM sync (syncDedicationInputsFromState)
        and the existing re-fit path (refitDedicationLines) — both already
        used elsewhere for the identical "content changed" case.
      - Material: resetMaterialToDefault() below, which drives the real
        Shopify variant selection through the SAME swatch-click ->
        sync_variant_selection -> input[name="id"] change cascade PHASE 39/
        45B already wired for a real customer click — no new variant-
        selection code path.
      - View Mode: window.__everLeafResetViewModeToCloseUp, a bridge to
        init()'s own startTransition() (the SAME function the View Toggle
        button itself calls) — same bridge convention as
        window.__everLeafResetInteractionHints/
        window.__everLeafResetViewToggleFirstAppear above.
    Photo position/scale are handled by removeCurrentPhoto() itself (already
    correct — see its own comment) and are not part of this function.
  */
  function resetPersonalizationAfterPhotoRemoval() {
    window.EverLeafPersonalizerState.update({
      dedicationLine1: '',
      dedicationLine2: '',
      text: serializeDedication('', '')
    });
    syncDedicationInputsFromState();
    refitDedicationLines();

    resetMaterialToDefault();

    if (window.__everLeafResetViewModeToCloseUp) {
      window.__everLeafResetViewModeToCloseUp();
    }
  }

  /*
    REMOVE PHOTO -> FULL PERSONALIZER RESET pass — Material component.
    Resets the real Shopify variant selection back to
    `product.selected_or_first_available_variant`: the page's own initial
    default, captured ONCE by Liquid into the `[data-selected-variant]`
    JSON script tag inside `<variant-selects>` (snippets/product-variant-
    picker.liquid) and never rewritten afterward — this page has no
    `<product-info>`/server-fetch re-render path (see that snippet's own
    header comment on `sync_variant_selection`), so that JSON stays a
    reliable frozen snapshot of the fresh-page-load default for the whole
    session. Never invents/hardcodes a material name.

    Drives the reset the SAME way a real customer swatch click already
    does: sets the matching radio/option per option block (in
    `product.options_with_values` / DOM order, matching Shopify's own
    option1/option2/option3 positions) and dispatches a real, bubbling
    `change` event — letting the existing PHASE 39 sync_variant_selection
    script (in that same snippet) resolve the matching variant and update
    `input[name="id"]`, which PHASE 45B's own delegated document `change`
    listener (see onDocumentVariantChange above) already turns into
    applySelectedVariantScene(). No new variant-selection/material code
    path, no change to the variant-picker architecture.
  */
  function resetMaterialToDefault() {
    var root = document.querySelector('variant-selects');
    if (!root) return;
    var dataEl = root.querySelector('[data-selected-variant]');
    if (!dataEl) return;
    var defaultVariant;
    try {
      defaultVariant = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!defaultVariant || defaultVariant.id == null) return;

    var form = document.querySelector('form[id^="product-form-"]');
    var idInput = form ? form.querySelector('input[name="id"]') : null;
    if (idInput && String(idInput.value) === String(defaultVariant.id)) return; // already the default material

    var defaultValues = [defaultVariant.option1, defaultVariant.option2, defaultVariant.option3].filter(function (v) {
      return v != null;
    });
    var optionBlocks = root.querySelectorAll('.product-form__input');
    var changedInput = null;
    for (var i = 0; i < optionBlocks.length && i < defaultValues.length; i++) {
      var targetValue = defaultValues[i];
      var select = optionBlocks[i].querySelector('select');
      if (select) {
        if (select.value !== targetValue) {
          select.value = targetValue;
          changedInput = select;
        }
        continue;
      }
      var radios = optionBlocks[i].querySelectorAll('input[type="radio"]');
      for (var r = 0; r < radios.length; r++) {
        if (radios[r].value === targetValue && !radios[r].checked) {
          radios[r].checked = true;
          changedInput = radios[r];
        }
      }
    }
    if (changedInput) {
      // Bubbles up to <variant-selects>' own delegated listener (updates
      // swatch/aria UI) AND the sync_variant_selection script's root
      // listener (resolves + writes the real variant id) in one dispatch —
      // both already re-scan every currently checked/selected input rather
      // than relying on event.target, so one dispatch covers every option
      // block changed above.
      changedInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
  }

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  // PASS 3C, Section 49 — the one shared ease-in-out function driving both
  // the viewport interpolation and the Blur/Sharp crossfade (never two
  // separate easing curves). Standard cubic ease-in-out: symmetric
  // acceleration in, deceleration out, `f(0)=0`/`f(1)=1` exactly (so an
  // eased transition still lands precisely on its endpoint).
  function easeInOutCubic(x) {
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
  }

  // Pass 3A interaction-model correction — replaces currentTransformFromState.
  // Returns a PARTIAL {centerX, centerY, scale} (only the fields actually
  // set in state) — callers merge this over
  // renderer.DEFAULT_PHOTO_TRANSFORM_CENTER for a fully-resolved transform.
  function currentCenterTransformFromState() {
    var s = window.EverLeafPersonalizerState.get();
    var transform = {};
    if (s.photoCenterX != null) transform.centerX = s.photoCenterX;
    if (s.photoCenterY != null) transform.centerY = s.photoCenterY;
    if (s.photoScale != null) transform.scale = s.photoScale;
    return transform;
  }

  /*
    Tiny performance readout — wraps a render call with performance.now(),
    tracks last/avg/max per tier, and paints into a plain <pre>. No console
    logging.
  */
  function createPerfTracker() {
    var stats = {
      interactive: { last: 0, avg: 0, max: 0, count: 0 },
      hq: { last: 0, avg: 0, max: 0 }
    };
    function record(tier, ms) {
      var s = stats[tier];
      s.last = ms;
      s.max = Math.max(s.max, ms);
      s.count = (s.count || 0) + 1;
      s.avg = s.avg + (ms - s.avg) / s.count;
    }
    return { stats: stats, record: record };
  }

  var EverLeafPersonalizer = /** @class */ (function () {
    function init(el) {
      var canvas = el.querySelector('canvas.ever-leaf-personalizer__canvas');
      if (!canvas) return;

      // Theme Editor live-calibration-preview correction — real customer
      // storefront behavior is COMPLETELY unchanged by this (one config
      // snapshot per real page load, exactly as before this fix): only
      // inside the actual Theme Editor (window.Shopify.designMode, the
      // same guard ever-leaf-editor-preview.js already uses) does every
      // section reload re-sync calibration-derived state (tonal/opacity/
      // scale+position limits/scene asset URLs/text sizing) from the
      // LATEST config — see buildConfigDerivedPatch()/refreshFromConfig()'s
      // own header comments for the root cause this fixes. Customer-
      // entered state (photo, its transform, dedication text, upload
      // status) is never touched by this call. `beforeDesignModeRefresh`
      // is captured so the tone/text-layout dirty-category reprocessing
      // below (after refreshFromConfig has already applied the FRESH
      // Dedication Text Config) can tell whether pixel-
      // level reprocessing is actually needed, rather than assuming it
      // always is.
      var isDesignModeRefresh = !!(window.Shopify && window.Shopify.designMode);
      var beforeDesignModeRefresh = isDesignModeRefresh ? window.EverLeafPersonalizerState.get() : null;
      var beforeTonal = beforeDesignModeRefresh ? beforeDesignModeRefresh.photoTonal : null;
      var beforeDedicationConfig = beforeDesignModeRefresh ? beforeDesignModeRefresh.dedicationConfig : null;
      if (isDesignModeRefresh) {
        window.EverLeafPersonalizerState.refreshFromConfig();
      }

      /*
        Theme Editor Preview Session restore — ONLY when design_mode AND
        there is currently no photo in state (a genuine post-Save/full-
        reload, or a first-ever Theme Editor visit with a leftover session
        from earlier). Never runs on the real customer storefront
        (window.EverLeafEditorPreviewSession itself no-ops there too — this
        is defense in depth). Deliberately does NOT run when
        state.customerFile is already set — that means
        window.EverLeafPersonalizerState survived an unsaved-settings
        section-reload on its own (the already-proven case), and restoring
        from a possibly-older IndexedDB snapshot on top of that would
        actually be a REGRESSION (overwriting newer in-memory state with
        older stored state).
      */
      if (isDesignModeRefresh && window.EverLeafEditorPreviewSession && !window.EverLeafPersonalizerState.get().customerFile) {
        window.EverLeafEditorPreviewSession.load().then(function (record) {
          if (record && !window.EverLeafPersonalizerState.get().customerFile) {
            restoreFromPreviewSession(record);
          }
        });
      }

      var renderer = window.EverLeafImageRenderer;
      var assets = null;
      // PASS 3B — raw SHARED scene images as loaded by lampScene.
      // getLeafSceneAssets() ({ photoMask, light }), kept separately from
      // `assets` (the renderer's own maskAlpha/maskBounds-derived bundle —
      // see renderer.loadPersonalizerAssetsFromImages). PHASE 45B: this no
      // longer carries a lamp Blur/Sharp pair at all — every variant's own
      // Scene pair lives in `variantResolvedImages` below instead, since
      // Gray is no longer a special, always-loaded default (Section 9 of
      // the PHASE 45B brief: "Gray must use the same architecture as every
      // other variant").
      var sharedSceneImages = null;
      var interactiveContext = null;
      // PHASE 45B — VARIANT-OWNED VISUALIZER SCENE ARCHITECTURE.
      // `variantResolvedImages[variantId]` is `{ blurImage, sharpImage }`
      // once that real Shopify variant's Scene pair has finished loading
      // (see lampScene.loadMaterialPair()); `variantLoadPromises[variantId]`
      // is the in-flight promise for a pair not yet resolved, so a second
      // request for the same variant while it's loading reuses the one
      // fetch instead of starting another. `activeVariantId` is the real
      // Shopify variant ID currently being RENDERED (only ever changes
      // atomically, once its pair is fully ready — see
      // applySelectedVariantScene()); `pendingVariantId` is the most
      // recently REQUESTED variant ID, used as a "last request wins" guard
      // so a slow-to-load variant can never clobber a faster one the
      // customer switched to afterward. Keyed by the variant's OWN real
      // numeric ID (as a string, matching input[name="id"].value and the
      // JSON object keys Liquid emits) — never by a material name/key, and
      // never hard-coded (Section 19 of the brief: map from the actual
      // selected Shopify variant, nothing else).
      var variantResolvedImages = {};
      var variantLoadPromises = {};
      var activeVariantId = null;
      var pendingVariantId = null;
      var perf = createPerfTracker();
      var debugPanel = new EverLeafDebugPanel(el, perf);
      var calibrationPanel = new EverLeafCalibrationPanel(el);
      var textSizingPanel = new EverLeafTextSizingPanel(el);
      var customerControls = new EverLeafCustomerControls(el);
      var verboseLogging = false;

      /*
        PHASE 46B/47A — INTERACTION HINTS MOTION / VIEW-STATE VISIBILITY /
        FIRST-APPEAR DELAY. Supersedes PHASE 46A/46A.1's Fade-In-only
        lifecycle.

        Two fully independent DOM elements (snippets/ever-leaf-product-
        visualizer.liquid, [data-ever-leaf-move-hint] and [data-ever-leaf-
        zoom-hint]), inside a dedicated [data-ever-leaf-hints-clip]
        overflow:hidden layer — every dimension/position/style value is
        already resolved server-side into CSS custom properties
        (component-ever-leaf-personalizer.css), so this file holds no
        styling logic of its own; it only toggles ONE attribute per hint,
        `data-ever-leaf-hint-visible` (never `hidden`, which is `display:
        none` and therefore cannot be transitioned/animated).

        Visibility is the AND of two independent, already-authoritative
        signals — never a new, third source of truth: photo-ready (normal
        storefront) OR Theme Editor design-mode preview (PHASE 46A.1's own
        isDesignModeRefresh bypass); AND the SAME real view-state Pass 3C
        already owns (`state.viewMode`, flipped synchronously the instant a
        transition is REQUESTED, never the local `editingEnabled`).

        PHASE 47A adds ONE more layer on top, ONLY for the transition from
        not-shown to shown: a configurable delay before the FIRST reveal
        for the CURRENT photo (Section "EXACT DELAY SEMANTICS" — later
        Close-Up returns for the SAME photo, e.g. after a Full Lamp visit,
        skip the delay entirely). This is tracked with a small per-photo
        generation counter (hintsPhotoGeneration, bumped once per genuinely
        new/replaced local file — window.__everLeafResetInteractionHints
        below is the single authoritative bump point) compared against
        hintsFirstAppearedForGeneration — the exact "small per-photo first-
        appearance token" the brief prefers over a second duplicated
        business-state boolean. A pending delayed reveal is cancellation-
        safe: its callback re-validates both the generation (a newer photo
        supersedes it) and the current shouldShow state (e.g. the customer
        switched to Full Lamp before the delay elapsed) before ever
        touching the DOM — "last/current state wins", never a stale reveal.

        `instant` (the PHASE 46A.1 convention) skips the CSS transition
        AND the first-appear delay entirely via a one-frame `data-ever-
        leaf-hint-instant` pulse — used only for the very first sync after
        init()/a Theme Editor section reload, so a merchant tuning settings
        never sits through a replayed delay+slide merely because Shopify
        re-rendered the section (SECTION REFRESH != NEW PHOTO). A genuine
        local photo selection inside an active design-mode session (NOT a
        bare section reload) still runs the real, non-instant path below.
      */
      var interactionHintEls = [
        el.querySelector('[data-ever-leaf-move-hint]'),
        el.querySelector('[data-ever-leaf-zoom-hint]')
      ].filter(function (elHint) {
        return !!elHint;
      });
      var interactionHintsUnsubscribe = null;
      var hintsFirstAppearDelayMs = parseInt(el.getAttribute('data-ever-leaf-hints-first-appear-delay-ms'), 10) || 0;
      var hintsPhotoGeneration = 0;
      var hintsFirstAppearedForGeneration = -1;
      var hintsFirstAppearTimer = null;

      function cancelHintsFirstAppearTimer() {
        if (hintsFirstAppearTimer) {
          clearTimeout(hintsFirstAppearTimer);
          hintsFirstAppearTimer = null;
        }
      }

      function setInteractionHintsVisible(visible, instant) {
        interactionHintEls.forEach(function (elHint) {
          if (instant) {
            elHint.setAttribute('data-ever-leaf-hint-instant', '');
            // Force a synchronous style flush so the CURRENT (pre-change)
            // position is genuinely painted transition-free before the
            // attribute below changes it, then release next frame.
            void elHint.offsetWidth;
          }
          if (visible) {
            elHint.setAttribute('data-ever-leaf-hint-visible', '');
          } else {
            elHint.removeAttribute('data-ever-leaf-hint-visible');
          }
          elHint.setAttribute('aria-hidden', visible ? 'false' : 'true');
          if (instant) {
            requestAnimationFrame(function () {
              elHint.removeAttribute('data-ever-leaf-hint-instant');
            });
          }
        });
      }

      /* Hints with State Transitions > Interaction Hints > Enable State
         Transition switched OFF are PINNED: the View Toggle must not hide
         them. This is the one place that decides it for the hint elements
         themselves (assets/ever-leaf-mobile-stage.js owns the same rule for
         the slide, and the CSS settled state opts out via the same attribute),
         so the Close-Up factor - and only that factor - is dropped here too.
         The photo requirement below is an INDEPENDENT eligibility rule and
         deliberately still applies, so a pinned Hint is never forced on screen
         when there is no photo to hint about. */
      function hintsTransitionPinned() {
        var stage = el.closest ? el.closest('[data-ever-leaf-mobile-stage]') : null;
        var off = stage ? (stage.getAttribute('data-ever-leaf-state-off') || '') : '';
        return (' ' + off + ' ').indexOf(' hints ') !== -1;
      }
      function computeHintsShouldShow() {
        var s = window.EverLeafPersonalizerState.get();
        var hasPhoto = s.uploadStatus !== 'empty';
        var isCloseUp = s.viewMode === 'close_up';
        return (isDesignModeRefresh || hasPhoto) && (isCloseUp || hintsTransitionPinned());
      }

      // Reveals the hints for the CURRENT photo generation. `instant`
      // always skips the delay (Theme Editor init/reload sync). Otherwise,
      // applies hintsFirstAppearDelayMs only if this generation hasn't
      // appeared yet — a later reveal for the SAME generation (Full Lamp
      // -> Close-Up return) shows immediately with the normal Hint motion
      // duration, no extra delay.
      function revealInteractionHints(instant) {
        cancelHintsFirstAppearTimer();
        var isFirstAppear = hintsFirstAppearedForGeneration !== hintsPhotoGeneration;
        // Interaction Hints/View Toggle resync fix (Mobile Dock Correction
        // Pass 2) — this legacy delay is Desktop only (already labelled as
        // such on the setting itself); on Mobile the Dock's own State
        // Transitions > Interaction Hints delays (First Appearance/Pre-
        // Appear/Pre-Disappear) are the SOLE timing authority. Without this,
        // this timer could fire independently of - and later than - that
        // engine's own WAAPI slide, snapping the hint's position into view
        // with no animation instead of one coordinated reveal.
        var isMobile = !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
        if (instant || isMobile || !isFirstAppear || hintsFirstAppearDelayMs <= 0) {
          hintsFirstAppearedForGeneration = hintsPhotoGeneration;
          setInteractionHintsVisible(true, instant);
          return;
        }
        var myGeneration = hintsPhotoGeneration;
        hintsFirstAppearTimer = setTimeout(function () {
          hintsFirstAppearTimer = null;
          if (myGeneration !== hintsPhotoGeneration) return; // superseded by a newer photo
          if (!computeHintsShouldShow()) return; // state moved away (e.g. Full Lamp) before the delay elapsed
          hintsFirstAppearedForGeneration = myGeneration;
          setInteractionHintsVisible(true, false);
        }, hintsFirstAppearDelayMs);
      }

      // Single combined visibility formula, edge-detected so dedication/
      // lights/outline/pivot/quantity/terms/HQ-responsive rerenders — none
      // of which touch uploadStatus or viewMode — never redundantly
      // re-enter the reveal/hide path.
      var interactionHintsLastShouldShow = null;
      function syncInteractionHintsVisibility(instant) {
        var shouldShow = computeHintsShouldShow();
        if (shouldShow === interactionHintsLastShouldShow) return;
        interactionHintsLastShouldShow = shouldShow;
        if (shouldShow) {
          revealInteractionHints(instant);
        } else {
          cancelHintsFirstAppearTimer();
          setInteractionHintsVisible(false, instant);
        }
      }

      // Bridges handleLocalFileSelected() (defined outside this closure,
      // same file-scope boundary as window.__everLeafInteractiveSize/
      // __everLeafPersonalizerAssets below) into this per-instance state —
      // the exact same window.__everLeaf* pattern already used elsewhere in
      // this file. THE single authoritative point where a genuinely new/
      // replaced local File resets first-appearance eligibility (Section
      // "NEW/REPLACED PHOTO RESET") — bumping the generation immediately
      // cancels any stale pending timer's relevance and makes the next
      // eligible reveal (whenever it happens, even much later after a
      // Full Lamp visit) apply the delay again.
      //
      // Real local file selection is a genuine storefront-only customer
      // action; while in Full Lamp, hints must stay hidden with no
      // premature flash (Section "PHOTO UPLOAD WHILE FULL LAMP") — the
      // eligible first reveal happens naturally later, when the customer
      // returns to Close-Up, via the subscriber below. A REPLACE while
      // ALREADY visible (shouldShow stays true -> true, so the edge-
      // detected sync above would otherwise no-op) forces a genuine
      // hide -> show-with-first-appear-delay cycle so the entrance visibly
      // replays for the new photo. This also intentionally runs in Theme
      // Editor design mode for a GENUINE local file selection (Section
      // "ACTUAL NEW PHOTO IN DESIGN MODE" — "may preview the configured
      // delay + first entrance"), since this bridge is only ever called
      // from handleLocalFileSelected() and never from a bare section
      // reload (that path is syncInteractionHintsVisibility(true) below).
      window.__everLeafResetInteractionHints = function () {
        hintsPhotoGeneration += 1;
        cancelHintsFirstAppearTimer();
        if (!isDesignModeRefresh) {
          var s = window.EverLeafPersonalizerState.get();
          if (s.uploadStatus === 'empty') return;
          if (s.viewMode !== 'close_up') return;
        }
        if (interactionHintsLastShouldShow !== true) return;
        setInteractionHintsVisible(false, true);
        requestAnimationFrame(function () {
          revealInteractionHints(false);
        });
      };

      // Initial sync — always instant (Theme Editor section reloads and a
      // genuine first page load must never replay the delay/slide merely
      // because the section was (re)rendered). Real interactions
      // afterward — a real photo event or a real View Toggle click, both
      // of which update the SAME shared state object — flow through this
      // one subscriber and animate normally, including the first-appear
      // delay when eligible.
      syncInteractionHintsVisibility(true);
      // The formula above reads the pinned switch, and its result is
      // edge-detected - so a change to the switch alone has to re-enter it,
      // or a newly pinned Hint would stay hidden until some other input moved.
      (function watchHintsPinnedSwitch() {
        var stage = el.closest ? el.closest('[data-ever-leaf-mobile-stage]') : null;
        if (!stage || typeof MutationObserver !== 'function') return;
        new MutationObserver(function () { syncInteractionHintsVisibility(true); })
          .observe(stage, { attributes: true, attributeFilter: ['data-ever-leaf-state-off'] });
      })();
      interactionHintsUnsubscribe = window.EverLeafPersonalizerState.subscribe(function () {
        syncInteractionHintsVisibility(false);
      });

      /*
        Pass 3A targeted correction, Section 30 — Scene Asset source status
        (Content vs. bundled fallback, actual dimensions, validation
        warnings) is computed server-side (sections/ever-leaf-product.liquid)
        but was previously only surfaced via console.warn — easy to miss,
        not "genuinely visible". This renders it into a real on-page panel
        instead. Gated the SAME way the panel's own markup is gated
        (snippets/ever-leaf-personalizer-dev-panels.liquid: design_mode AND
        Developer Tools enabled) — root() below is simply absent from the
        DOM on the real storefront, so this is a true no-op there, not just
        hidden by CSS.
      */
      function renderSceneAssetsDiagnostic() {
        var root = document.querySelector('[data-ever-leaf-scene-assets-debug]');
        if (!root) return;
        var sa = window.EverLeafPersonalizerState.get().sceneAssets;
        if (!sa || !sa.sources) {
          root.textContent = 'DEV SCENE ASSETS — not customer-facing\n(no Visualizer Scene Assets config found; using bundled defaults for every layer)';
          return;
        }
        var layers = [
          { key: 'lampBlur', label: 'Lamp Blur' },
          { key: 'lampSharp', label: 'Lamp Sharp' },
          { key: 'photoMask', label: 'Photo Mask' },
          { key: 'lightsOverlay', label: 'Lights Overlay' }
        ];
        var lines = ['DEV SCENE ASSETS — not customer-facing'];
        layers.forEach(function (layer) {
          var source = sa.sources[layer.key] || 'bundled';
          var dims = (sa.dimensions && sa.dimensions[layer.key]) || 'unknown';
          var badge = source === 'content' ? 'CONTENT (merchant-selected)' : 'BUNDLED (theme default)';
          lines.push(layer.label + ': ' + badge + ' — ' + dims);
        });
        if (sa.warnings && sa.warnings.length) {
          lines.push('');
          lines.push('WARNINGS:');
          sa.warnings.forEach(function (w) {
            lines.push('- ' + w);
          });
        }
        root.textContent = lines.join('\n');
      }
      if (isDesignModeRefresh) {
        renderSceneAssetsDiagnostic();
        window.EverLeafPersonalizerState.subscribe(renderSceneAssetsDiagnostic);
      }

      /*
        The photo's own natural aspect ratio, computed ONCE at file-selection
        time from the original decoded source (state.photoAspect) — the same
        canonical value the renderer threads through both the interactive and
        HQ passes (Section 19 fix), so clamping and drawing never disagree
        about geometry. Falls back to 1 (square) before any photo is loaded.
      */
      function getPhotoAspect() {
        var s = window.EverLeafPersonalizerState.get();
        return s.photoAspect || 1;
      }
      // Pass 2A — read live (not cached), same convention as getPhotoAspect
      // above, so a Theme Editor section reload always reflects the CURRENT
      // Visualizer Calibration values.
      function getImageLimits() {
        return window.EverLeafPersonalizerState.get().imageLimits || {};
      }

      /*
        Pass 3A interaction-model correction — THE single place
        photoCenterX/photoCenterY/photoScale ever get written, shared by
        drag, pinch, arrow buttons, zoom buttons, and reset. `candidate` may
        be a partial patch (any subset of centerX/centerY/scale) — missing
        fields fall back to the CURRENT transform, never to a default, so a
        candidate that only sets `scale` (a pure zoom request) leaves
        centerX/centerY completely untouched: Scale and Center are clamped
        completely independently (renderer.clampScaleValue/
        clampCenterValue) — no mask geometry, no scale pivot, nothing
        couples them together anymore. This is what guarantees "scale never
        moves the center, move never changes the scale" — it falls directly
        out of this merge order, not a special case.
      */
      // Press-and-hold pass (2A.1) — TRANSFORM_EPSILON: lets the hold-repeat
      // loop detect a genuine no-op (already at a boundary) and stop
      // itself, instead of re-rendering forever while held at a boundary.
      var TRANSFORM_EPSILON = 1e-9;
      function applyClampedCenterTransform(candidate) {
        if (!assets) return false;
        var current = Object.assign(
          {},
          renderer.DEFAULT_PHOTO_TRANSFORM_CENTER,
          currentCenterTransformFromState()
        );
        var merged = Object.assign({}, current, candidate);
        var limits = getImageLimits();
        var clampedScale = renderer.clampScaleValue(merged.scale, limits);
        var clampedCenter = renderer.clampCenterValue({ x: merged.centerX, y: merged.centerY }, limits);
        var changed =
          Math.abs(clampedCenter.x - current.centerX) > TRANSFORM_EPSILON ||
          Math.abs(clampedCenter.y - current.centerY) > TRANSFORM_EPSILON ||
          Math.abs(clampedScale - current.scale) > TRANSFORM_EPSILON;
        window.EverLeafPersonalizerState.update({
          photoCenterX: clampedCenter.x,
          photoCenterY: clampedCenter.y,
          photoScale: clampedScale
        });
        return changed;
      }

      // Pass 3A targeted correction — config-derived, same live-read
      // convention as getImageLimits() above.
      function getZoomPivot() {
        return window.EverLeafPersonalizerState.get().zoomPivot || { mode: 'image_center', x: 0.5, y: 0.4 };
      }

      /*
        Pass 3A targeted correction — THE single canonical zoom entry
        point, used by every zoom trigger (Zoom buttons/press-and-hold,
        mouse wheel, pinch). Enforces the ordering contract in exactly one
        place: clamp Scale FIRST; if the clamped Scale hasn't actually
        changed, return a COMPLETE no-op immediately — no pivot math runs,
        Center is never touched. This exact ordering is what the ORIGINAL
        (pre-Center-model) drift bug violated; it must never be
        reintroduced. Only once Scale has genuinely changed does this
        branch on zoomPivot.mode:
          - 'image_center' (default; the already-passing behavior) — the
            patch leaves centerX/centerY absent, so
            applyClampedCenterTransform's own merge falls back to the
            CURRENT center unchanged, exactly like Move/Reset already rely
            on. No pivot math at all in this mode.
          - 'custom_pivot' — a pivot-preserving Center is computed from the
            OLD Center/Scale via renderer.computePivotPreservingCenter
            (derived from first principles — see that function's own
            comment in ever-leaf-image-renderer.js), then committed.
        Either branch commits through applyClampedCenterTransform, so the
        resulting Center is independently clamped at its own bounds just
        like any other Center write — a pivot-preserving Center that would
        land outside centerMinX/MaxX/MinY/MaxY is bounded there, never via
        a separate/oscillating clamp path.
      */
      function applyZoom(requestedScale) {
        if (!assets) return false;
        var current = Object.assign({}, renderer.DEFAULT_PHOTO_TRANSFORM_CENTER, currentCenterTransformFromState());
        var limits = getImageLimits();
        var clampedScale = renderer.clampScaleValue(requestedScale, limits);
        if (Math.abs(clampedScale - current.scale) < TRANSFORM_EPSILON) {
          return false; // complete no-op: already at (or unable to move past) this scale limit
        }
        var pivot = getZoomPivot();
        var patch = { scale: clampedScale };
        if (pivot.mode === 'custom_pivot') {
          var pivotCenter = renderer.computePivotPreservingCenter(
            current.centerX,
            current.centerY,
            current.scale,
            clampedScale,
            pivot.x,
            pivot.y
          );
          patch.centerX = pivotCenter.centerX;
          patch.centerY = pivotCenter.centerY;
        }
        return applyClampedCenterTransform(patch);
      }

      // Pass 3A — 2048 Scene Foundation (corrected). The Visualizer's Leaf
      // Base/Photo Mask/Light source images are no longer three standalone
      // 1060 PNGs; they're the FULL, uncropped 2048 Gray lamp scene (see
      // assets/ever-leaf-lamp-scene.js's getLeafSceneAssets — deliberately
      // NOT camera-cropped, so maskBounds/photoX/photoY/photoScale stay
      // fractions of the whole scene, independent of any camera window).
      // Leaf Base is baked into ever-leaf-lamp-gray-blur.png (sharp,
      // pixel-identical to the Sharp asset — only the surrounding lamp
      // differs), matching the locked Blur/focus model for the Leaf
      // Close-Up endpoint. The Leaf Close-Up CAMERA is applied later, only
      // as a final crop step — see renderHQ/scheduleInteractiveRender.
      //
      // No-code Scene Asset management — URLs now come from
      // state.sceneAssets (Shopify Content image picker vs. bundled
      // fallback, already source-resolved + 2048x2048-validated server-side
      // — see sections/ever-leaf-product.liquid). The 3 data-fallback-*-url
      // attributes are a last-resort default used ONLY if sceneAssets ended
      // up null (config script tag missing/malformed) — see that snippet's
      // own comment.
      var sceneAssetsConfig = window.EverLeafPersonalizerState.get().sceneAssets;
      // PHASE 45B — sceneUrls now only carries the two genuinely SHARED
      // assets (variant-specific lamp Blur/Sharp moved to variantScenes,
      // resolved per selected variant below, never loaded unconditionally
      // here).
      var sceneUrls = sceneAssetsConfig
        ? {
            photoMask: sceneAssetsConfig.photoMaskUrl,
            lightsOverlay: sceneAssetsConfig.lightsOverlayUrl
          }
        : {
            photoMask: el.getAttribute('data-fallback-photo-mask-url'),
            lightsOverlay: el.getAttribute('data-fallback-lights-overlay-url')
          };
      // Last-resort bundled lamp pair — used ONLY as a fallback if the
      // ENTIRE `variantScenes` map is missing (e.g. the config script tag
      // itself failed to parse), never as a substitute for one specific
      // variant's individually-incomplete Scene while others are correctly
      // configured (see getVariantSceneUrls()'s own header — Section 24 of
      // the PHASE 45B brief requires blocking, not silently substituting,
      // in that narrower case).
      var fallbackLampUrls = {
        lampBlurUrl: el.getAttribute('data-fallback-lamp-gray-blur-url'),
        lampSharpUrl: el.getAttribute('data-fallback-lamp-gray-sharp-url')
      };

      // Pass 3A correction — camera/personalization separation. The
      // EFFECTIVE camera (below) is Camera Preset A, normalized (0..1 of
      // the full 2048 scene), optionally shifted by the merchant's Close-Up
      // Vertical Offset; it is applied ONLY as a final crop step
      // (lampScene.applyCamera), strictly AFTER the full scene has been
      // personalized/rendered at a stable, camera-independent working
      // resolution. This is what guarantees a future camera change (Pass
      // 3C), OR a merchant tuning the vertical offset live, never needs to
      // rerun grayscale/Color Burn/masking — see applyCamera's own header
      // comment.
      var lampScene = window.EverLeafLampScene;
      /*
        PASS 3A FINAL CONSOLIDATED CALIBRATION + FRAMING CORRECTION,
        Section 38-52 — Close-Up Vertical Offset. Read LIVE (not cached at
        init time, same convention getImageLimits()/getZoomPivot() already
        use) so a Theme Editor change takes effect immediately with no
        Save, and the base Camera A preset (LEAF_CLOSEUP_CAMERA_NORMALIZED)
        itself is NEVER mutated — computeEffectiveCamera() always derives a
        fresh rect from it. This is the ONE place VIEW-space camera
        geometry is resolved; every render call site below, and the Pivot
        marker (Section 48 — it must track the SAME effective camera the
        rendered scene actually uses, never the frozen base preset), reads
        through this same function so they can never drift out of sync
        with each other. Purely VIEW-DIRTY (Section 46-47): reading this
        live value never touches photoTonal/prepared photo tiers/any
        Personalization State Space field — it only changes which rect
        applyCamera() crops, exactly like the underlying camera/
        personalization separation this file's opening comment already
        documents.
      */
      /*
        PASS 3C — ANIMATED SINGLE-VIEWPORT TRANSITION.

        `currentT` is the ONE canonical transition progress value (Section
        4 of the brief): 0 = Close-Up, 1 = Full Lamp, anything in between
        is a live transition frame. It is DELIBERATELY a plain closure
        variable, never routed through window.EverLeafPersonalizerState —
        driving it via state.update() on every animation frame would fire
        every subscriber, including the one below that calls
        onInteractionEnd() (a full HQ render + debug refresh + Preview
        Session save) on EVERY update once a photo exists, which would
        turn a 60fps animation into ~60 HQ renders/second (explicitly
        forbidden — Section 56). `state.viewMode` remains the DISCRETE,
        pub/sub-visible concern (which endpoint is the current
        destination — drives the toggle icon/aria-label and the editing
        gate) and is completely decoupled from this continuous value.
        Initialized ONCE here from the config-resolved initial `viewMode`
        (Section 61 — Static Scene Preview / the current discrete state
        defines only the STARTING point; it never itself triggers an
        animation).
      */
      var currentT = window.EverLeafPersonalizerState.get().viewMode === 'full_lamp' ? 1 : 0;

      // Mobile Stage presentation: publishes the ONE canonical transition
      // progress (0 = Close-Up, 1 = Full Lamp) as a CSS variable so the
      // Mobile visual-surface size can interpolate on the same clock as the
      // viewport/blur transition. Presentation only; no new timer.
      function publishViewProgress() {
        el.style.setProperty('--ever-leaf-view-t', String(currentT));
      }
      publishViewProgress();

      /*
        SINGLE-VIEWPORT ARCHITECTURE. This is the ONE current-viewport
        resolver — not "camera A" vs "camera B" as two separate systems.
        There is one 2048x2048 Scene Space (see ever-leaf-lamp-scene.js's
        own header) and one viewport rectangle concept; the two calibrated
        presets (Close-Up and Full Lamp) are interpolated by `currentT` via
        lampScene.lerpViewportNormalized() — Section 10-12 of the Pass 3C
        brief. At currentT===0 this returns the Close-Up rect EXACTLY (the
        lerp's own 0-multiplier term vanishes); at currentT===1 it returns
        the Full Lamp rect EXACTLY — so both Pass 3B endpoints are frozen
        by construction (Section 7), with no special-case branch needed
        here at all. Every existing caller (interactive/HQ render, Pivot
        marker, Photo Outline bounding box) already reads through this one
        function and needs no per-preset OR per-transition awareness of
        its own (Section 12 — "do NOT implement separate transition math
        inside photo/outline/pivot/etc").

        Full Lamp's own rect is resolved fresh from live config on every
        call (same "live read, never cache" convention
        closeUpVerticalOffsetPercent already uses) and always passes
        through lampScene.clampViewportNormalized() for safety (Section
        58/59 of the Pass 3B brief) — a real saved DEV value of
        x=100%,y=100%,size=100% was found during the Pass 3B correction
        pass and confirmed to safely resolve to the full scene via this
        clamp. Close-Up's own existing computeEffectiveCamera() path (and
        its own internal clamp, and Close-Up Vertical Offset) is completely
        unchanged — Section 8/9 "fresh-read the actual current effective
        Close-Up viewport."
      */
      /*
        VIEW TRANSITION PERFORMANCE PASS — extracted out of getCurrentViewport()
        below, byte-identical logic, only so ensureInteractiveContext() can
        also reach both FIXED endpoint rects directly (see its own comment)
        without duplicating this resolution logic a second time or reading it
        back out of a lerped/interpolated result. getCurrentViewport()'s own
        output is completely unchanged by this extraction.
      */
      function getViewportEndpoints() {
        var s = window.EverLeafPersonalizerState.get();
        // Platform calibration: Desktop and Mobile own independent Close-Up/Full Lamp values,
        // resolved by the ONE existing breakpoint (max-width: 749px). Never by height.
        var isMobileViewport = !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
        /* MOBILE FRAME-OWNED MIGRATION (Model B). On Mobile the visible canvas
           now fills the View Frame, so BOTH presets are built against that
           box's real aspect and therefore cannot stretch the square Scene.
           Desktop passes nothing and keeps its exact previous square cameras. */
        var destAspect = isMobileViewport ? canvasBoxAspect() : 0;
        var closeUpZoom = isMobileViewport ? s.closeUpZoomMobile : 1;
        var closeUpViewport = isMobileViewport
          ? lampScene.computeEffectiveCamera(lampScene.LEAF_CLOSEUP_CAMERA_NORMALIZED, s.closeUpVerticalOffsetPercentMobile, s.closeUpHorizontalOffsetPercentMobile, { destAspect: destAspect, zoom: closeUpZoom })
          : lampScene.applyScaleMultiplier(
              lampScene.computeEffectiveCamera(lampScene.LEAF_CLOSEUP_CAMERA_NORMALIZED, s.closeUpVerticalOffsetPercent, s.closeUpHorizontalOffsetPercent),
              s.closeUpScaleDesktop
            );
        var fl = (isMobileViewport ? s.fullLampViewportMobile : s.fullLampViewport) || {};
        /* MOBILE CAMERA CONTROL UNIFICATION — Mobile Full Lamp is now Zoom +
           composition offsets against the aspect-fit base, identical in model
           to Close-Up. Desktop still passes its own top-left X/Y + Size trio
           through the original builder, unchanged. DESKTOP PRODUCT SECTION
           PASS adds the Scale Multiplier as a pure post-process AFTER the
           existing, byte-unchanged X/Y/Size resolution + clamp above. */
        var fullLampViewport = isMobileViewport
          ? lampScene.buildFullLampCameraNormalized(fl.zoom, fl.horizontalOffsetPercent, fl.verticalOffsetPercent, destAspect)
          : lampScene.applyScaleMultiplier(
              lampScene.clampViewportNormalized(
                lampScene.buildFullLampViewportNormalized(fl.xPercent, fl.yPercent, fl.sizePercent)
              ),
              s.fullLampScaleDesktop
            );
        return { closeUp: closeUpViewport, fullLamp: fullLampViewport };
      }

      function getCurrentViewport() {
        var endpoints = getViewportEndpoints();
        return lampScene.lerpViewportNormalized(endpoints.closeUp, endpoints.fullLamp, currentT);
      }

      /*
        Gray Blur and Gray Sharp are two perfectly registered layers of the
        SAME lamp geometry (Section 19 of the Pass 3C brief), sharing the
        same 2048 scene origin — not "the Close-Up image" and "the
        Full-Lamp image". Their weights are now driven CONTINUOUSLY by the
        same canonical `currentT` the viewport itself uses (Section 20):
        at currentT===0, blurWeight=1/sharpWeight=0 (pure Blur, byte-identical
        to Pass 3B Close-Up); at currentT===1, the reverse (pure Sharp,
        byte-identical to Pass 3B Full Lamp); in between, both vary linearly
        and in lockstep with the SAME progress driving the viewport
        (Section 6/49 — "no desynchronization", one shared progress source).
        The actual real dual-layer alpha compositing lives in
        ever-leaf-image-renderer.js's drawLampLayers() (Section 23) — see
        that function's own header for the crossfade math.

        PASS 3C CORRECTION — REAL BUG FOUND VIA BROWSER DIAGNOSIS (Section
        3-11 of the correction brief): this function previously returned
        `{ lampBlurWeight, lampSharpWeight }`, but drawLampLayers() has
        always read `weights.sharpWeight` (no "lamp" prefix, matching
        DEFAULT_LAMP_WEIGHTS' own `{ blurWeight, sharpWeight }` shape). Since
        `weights.sharpWeight` was therefore ALWAYS `undefined`, drawLampLayers()'s
        own defensive `typeof weights.sharpWeight === 'number'` check always
        failed and silently fell back to `sharpWeight = 0` — Sharp NEVER
        rendered, at ANY progress, including the fully-settled Full Lamp
        endpoint; Blur rendered at full opacity throughout the entire
        animation. A real-browser drawImage-call recording (monkey-patching
        CanvasRenderingContext2D.prototype.drawImage, temporary/diagnostic
        only) proved this conclusively: 55 Blur draws across the whole
        transition, ZERO Sharp draws, including the final HQ settle frame.
        The viewport itself was interpolating correctly the entire time
        (confirmed via the same recording) — this was a pure naming-mismatch
        bug in the crossfade path only, not a caching, timing, or asset-
        readiness issue. The PHASE 42 source-level harness did not catch
        this because it hand-built a weights object containing BOTH naming
        conventions at once (a test-only artifact), rather than calling this
        real function — a real integration gap between an isolated unit test
        and the actual production call site. Fixed by using the SAME field
        names (`blurWeight`/`sharpWeight`) the renderer has always expected.
      */
      function getLampLayerWeights() {
        return { blurWeight: 1 - currentT, sharpWeight: currentT };
      }

      /*
        PHASE 45B — VARIANT-OWNED VISUALIZER SCENE ARCHITECTURE.

        Shopify's selected variant is the SOLE source of truth for which
        Scene renders — never CSS/.active/aria-pressed/swatch appearance,
        and never a hard-coded variant ID or material name. Each real
        variant may reference an "Ever Leaf Visualizer Scene" metaobject via
        its own `custom.visualizer_scene` metafield (Shopify Admin > Custom
        data > Variants); that Scene owns exactly two Shopify File/Image
        references (blur_image, sharp_image). sections/ever-leaf-product.
        liquid resolves this into `sceneAssetsConfig.variantScenes`, a JSON
        object keyed by the variant's own real numeric ID (as a string) —
        this file never branches on "Gray"/"Beige"/"Dark Gray" or any other
        material name at all; it only ever looks up the currently selected
        variant's own ID.
      */

      /*
        Reuses PHASE 39's existing `sync_variant_selection` DOM data
        (snippets/product-variant-picker.liquid) instead of inventing a
        parallel data source: `input[name="id"]` (inside the one shared
        product form — sections/ever-leaf-product.liquid) is kept live-
        synced to the actually-selected real variant by that existing
        mechanism. Returns the plain variant ID string (or null if the form
        genuinely isn't present yet) — no mapping, no normalization, no
        material semantics of any kind.
      */
      function getSelectedVariantId() {
        var input = document.querySelector('form[id^="product-form-"] input[name="id"]');
        return input && input.value ? String(input.value) : null;
      }

      // The selected variant's Blur/Sharp URL pair, resolved from the
      // config bridge's `sceneAssets.variantScenes` map (see sections/
      // ever-leaf-product.liquid) — generated dynamically from
      // product.variants there, so a variant with no Scene reference (or an
      // incomplete one) simply has no entry here; this function returns
      // null for it rather than substituting another variant's artwork
      // (Section 24 of the brief).
      function getVariantSceneUrls(variantId) {
        var variantScenes = (sceneAssetsConfig && sceneAssetsConfig.variantScenes) || null;
        var entry = variantScenes && variantId != null ? variantScenes[variantId] : null;
        if (entry && entry.lampBlurUrl && entry.lampSharpUrl) {
          return { lampBlurUrl: entry.lampBlurUrl, lampSharpUrl: entry.lampSharpUrl };
        }
        // Total config collapse fallback — only reached if the config
        // bridge produced no variantScenes entries at all (never for one
        // specific variant's individually-incomplete Scene while others
        // are configured, which correctly falls through to `return null`
        // below and blocks initialization instead — Section 24).
        if (!variantScenes || !Object.keys(variantScenes).length) {
          return fallbackLampUrls.lampBlurUrl && fallbackLampUrls.lampSharpUrl ? fallbackLampUrls : null;
        }
        return null;
      }

      // Small safe image cache: resolved pairs are cached forever in
      // `variantResolvedImages`; an in-flight load is cached in
      // `variantLoadPromises` so a repeat request (e.g. rapid switching)
      // reuses the same fetch instead of starting a second one. Never
      // called from inside requestAnimationFrame — only from
      // applySelectedVariantScene() and the one-time preloadOtherVariant
      // Scenes() pass below.
      function ensureVariantSceneLoaded(variantId) {
        if (variantResolvedImages[variantId]) return Promise.resolve(variantResolvedImages[variantId]);
        if (variantLoadPromises[variantId]) return variantLoadPromises[variantId];
        var urls = getVariantSceneUrls(variantId);
        if (!urls) return Promise.reject(new Error('EverLeaf: no Scene configured for variant "' + variantId + '"'));
        var promise = lampScene.loadMaterialPair(urls).then(
          function (pair) {
            variantResolvedImages[variantId] = pair;
            return pair;
          },
          function (err) {
            // Failed load: don't cache a rejected promise forever — allow a
            // future retry (e.g. the customer switches away and back) — and
            // never crash or touch personalization/purchase state.
            delete variantLoadPromises[variantId];
            throw err;
          }
        );
        variantLoadPromises[variantId] = promise;
        return promise;
      }

      // The images the renderer should draw RIGHT NOW, shaped to merge
      // directly into the `assets` bundle at each render call site
      // (`leafBase`/`leafBaseSharp` — the SAME two field names
      // renderHQ/refreshDebugStages/ensureInteractiveContext already use,
      // per drawLampLayers()'s existing Blur/Sharp contract). `activeVariantId`
      // is only ever set once its own pair is already resolved (see
      // applySelectedVariantScene()/the initial-load chain below), so this
      // never needs a fallback substitute — returning null here means
      // initialization has not completed yet, which the render call sites'
      // own `if (!assets) return;` guards already handle.
      function getActiveSceneImages() {
        var active = variantResolvedImages[activeVariantId];
        return active ? { leafBase: active.blurImage, leafBaseSharp: active.sharpImage } : null;
      }

      // Preload every OTHER real variant's Scene once the initial scene is
      // stable, generated from `variantScenes` itself (not a fixed material
      // list) so a future variant with a valid Scene naturally participates
      // with zero code changes. Failures are swallowed here on purpose: a
      // customer who never selects that variant should never see a
      // console-noise-free experience turn into an error dialog, and
      // ensureVariantSceneLoaded()'s own promise cache means a later real
      // selection will simply retry the fetch.
      function preloadOtherVariantScenes() {
        var variantScenes = (sceneAssetsConfig && sceneAssetsConfig.variantScenes) || {};
        Object.keys(variantScenes).forEach(function (variantId) {
          ensureVariantSceneLoaded(variantId).catch(function (err) {
            console.warn('EverLeaf: Scene preload failed for variant "' + variantId + '"', err);
          });
        });
      }

      /*
        Atomic Scene switch. Reads the REAL selected Shopify variant,
        resolves its Scene's Blur/Sharp pair (from cache if already
        loaded), and only then flips `activeVariantId` — so a mismatched
        Blur-from-one-variant + Sharp-from-another frame can never render,
        and a slow load never produces a blank frame (the previous Scene
        keeps rendering until the new pair is fully ready). Deliberately
        does NOT touch currentT, the camera/viewport, or any
        Personalization State Space field (photoX/photoY/photoScale/
        dedication/lights/outline/pivot) — it only ever changes which two
        images getActiveSceneImages() returns, then asks the existing Pass
        3C render functions to redraw with them — works identically whether
        called at rest at either endpoint, or while `transitionActive` is
        mid-flight, since it never reads or writes `currentT`/
        `transitionRafId` itself.
      */
      function applySelectedVariantScene() {
        var variantId = getSelectedVariantId();
        pendingVariantId = variantId;
        if (activeVariantId === variantId) return;
        if (variantResolvedImages[variantId]) {
          activeVariantId = variantId;
          renderInteractiveFrame();
          renderHQ();
          refreshDebugStages();
          return;
        }
        ensureVariantSceneLoaded(variantId).then(
          function () {
            // Last-request-wins guard: if the customer switched again while
            // this was loading, a slower earlier request must never clobber
            // the variant they've since moved on to.
            if (pendingVariantId !== variantId || activeVariantId === variantId) return;
            activeVariantId = variantId;
            renderInteractiveFrame();
            renderHQ();
            refreshDebugStages();
          },
          function (err) {
            // Failed Scene load: preserve the current working Scene, log a
            // diagnostic, never crash, never reset personalization, never
            // touch the real Shopify selected variant.
            console.error('EverLeaf: failed to load Scene for variant "' + variantId + '"', err);
          }
        );
      }

      // Reusable OFFSCREEN canvases the full-scene composite is rendered
      // into — never shown directly; only the camera-cropped result of
      // these ever reaches the visible `canvas`. Sized lazily on first use.
      var interactiveOffscreen = document.createElement('canvas');
      var hqOffscreen = document.createElement('canvas');

      /*
        Pass 3A interaction-model correction — the ONE place Center state
        is converted to the legacy TOP-LEFT convention. Used by both cart
        serialization (see LINE_ITEM_PROPERTY_NAMES.photoX/Y below) and
        sceneTransformFromState() (chained into scene space for rendering).
        Never used for clamping — clamping stays purely in Center/Scale
        space (applyClampedCenterTransform above).
      */
      function legacyTopLeftFromState() {
        var s = window.EverLeafPersonalizerState.get();
        var center = Object.assign({}, renderer.DEFAULT_PHOTO_TRANSFORM_CENTER, currentCenterTransformFromState());
        var topLeft = renderer.centerToTopLeft(center.centerX, center.centerY, center.scale, s.photoAspect || 1);
        return { x: topLeft.x, y: topLeft.y, scale: center.scale };
      }

      // Pass 3A coordinate-semantics correction — the ONLY place the
      // (now-derived) legacy transform is converted into Scene Space. Used
      // exclusively at the three actual render calls below (never for
      // clamping, never for state writes, never for cart serialization —
      // cart uses legacyTopLeftFromState() directly, without the scene
      // mapping).
      function sceneTransformFromState() {
        return lampScene.mapTransformToScene(legacyTopLeftFromState());
      }

      // Pass 3A Dedication fix, extended for the PASS 3A FINAL DEDICATION
      // POSITIONING MODEL — the SAME state->scene problem the photo
      // transform had also applied to the Text layer, undetected until
      // manual QA: every Dedication position/size value (centerXRatio/
      // line1YSingleRatio/line1YTwoRatio/line2Y.../fitted line font
      // ratios) is a plain Personalization-State-Space number — exactly
      // the same shape as a photo transform's {x, y, scale} (a point or a
      // linear size, normalized to that space's own canvasSize). Reusing
      // lampScene.mapTransformToScene() unchanged is mathematically correct
      // for this, not a coincidence — see that function's own header. A
      // "scale" mapping (transform.scale * ref.width) is valid for ANY
      // linear size in this space, not just a photo's drawn width, since
      // ref.width === ref.height (PERSONALIZATION_REFERENCE_RECT is
      // square).
      //
      // Section 15 — Line 2's merchant-facing "from bottom" convention is
      // converted to an ordinary "from top" Personalization State Space
      // ratio (`1 - fromBottomRatio`) RIGHT HERE, BEFORE the single
      // Personalization->Scene mapping below — this is the ONE place that
      // inversion happens; renderer.drawDedicationLayer() itself never
      // sees a "from bottom" value at all (see that function's own
      // coordinate-space note).
      //
      // PASS 3A FINAL UX PASS, Section 16 — Two-Line Layout Presets. Which
      // pair of config values feeds the two-line Y math below is now
      // selected live from `s.dedicationLayout` ('split' or the default
      // 'together') — everything downstream of that selection (the
      // mapTransformToScene() calls, the returned shape) is UNCHANGED from
      // the prior single-pair model, and renderer.drawDedicationLayer()
      // itself needs no changes at all: it still just receives one already-
      // resolved, already-scene-mapped line1YTwoRatio/line2YRatio pair, with
      // no knowledge that a preset ever chose between two candidates.
      function sceneTextLayerSettings(s) {
        var textState = (s.layerSettings && s.layerSettings.text) || {};
        var cfg = s.dedicationConfig || {};
        var layout = layoutFromPlacement(s.dedicationPlacement);
        var line1FontSizeRatio =
          textState.line1FontSizeRatio != null ? textState.line1FontSizeRatio : renderer.TEXT_CANONICAL.line1FontSize / renderer.LEAF_LOCAL_SIZE;
        var line2FontSizeRatio =
          textState.line2FontSizeRatio != null ? textState.line2FontSizeRatio : renderer.TEXT_CANONICAL.line2FontSize / renderer.LEAF_LOCAL_SIZE;

        var centerXStateRatio = cfg.centerXRatio != null ? cfg.centerXRatio : renderer.TEXT_CANONICAL.centerXRatio;
        // Above/Below: with ONLY Line 1, Below keeps the lower primary (single-line) position; Above
        // uses the calibrated upper position (the legacy Split Line 1 Y).
        var line1YSingleStateRatio = layout === 'split'
          ? (cfg.splitLine1YRatio != null ? cfg.splitLine1YRatio : renderer.TEXT_CANONICAL.line1YTwoRatio)
          : (cfg.line1YSingleRatio != null ? cfg.line1YSingleRatio : renderer.TEXT_CANONICAL.line1YSingleRatio);
        var line1YTwoStateRatio =
          layout === 'split'
            ? (cfg.splitLine1YRatio != null ? cfg.splitLine1YRatio : renderer.TEXT_CANONICAL.line1YTwoRatio)
            : (cfg.togetherLine1YRatio != null ? cfg.togetherLine1YRatio : renderer.TEXT_CANONICAL.line1YTwoRatio);
        var line2YFromBottomStateRatio =
          layout === 'split'
            ? (cfg.splitLine2YFromBottomRatio != null ? cfg.splitLine2YFromBottomRatio : 1 - renderer.TEXT_CANONICAL.line2YRatio)
            : (cfg.togetherLine2YFromBottomRatio != null ? cfg.togetherLine2YFromBottomRatio : 1 - renderer.TEXT_CANONICAL.line2YRatio);
        var line2YStateRatio = 1 - line2YFromBottomStateRatio;

        // Center X and Line 1's single-line Y share one mapTransformToScene
        // call (also carries Line 1's font-size scale) — X mapping is
        // independent of Y in this affine transform, so its result is
        // reused for every other point below without remapping it.
        var mappedLine1Single = lampScene.mapTransformToScene({
          x: centerXStateRatio,
          y: line1YSingleStateRatio,
          scale: line1FontSizeRatio
        });
        var mappedLine1TwoY = lampScene.mapTransformToScene({ x: 0, y: line1YTwoStateRatio, scale: 0 }).y;
        var mappedLine2Y = lampScene.mapTransformToScene({ x: 0, y: line2YStateRatio, scale: 0 }).y;
        var mappedLine2Scale = lampScene.mapTransformToScene({ x: 0, y: 0, scale: line2FontSizeRatio }).scale;

        return Object.assign({}, textState, {
          centerXRatio: mappedLine1Single.x,
          line1YSingleRatio: mappedLine1Single.y,
          line1YTwoRatio: mappedLine1TwoY,
          line2YRatio: mappedLine2Y,
          line1FontSizeRatio: mappedLine1Single.scale,
          line2FontSizeRatio: mappedLine2Scale
        });
      }

      /* MOBILE FRAME-OWNED MIGRATION (Model B) — the visible destination is no
         longer forced square.

         The Visualizer now IS the View Frame, which is strongly portrait on a
         phone, so a single scalar measured from the box's WIDTH would either
         squash a square raster into a rectangle (CSS stretch) or throw away
         most of the height. This returns the real {width, height} of the box
         the canvas actually fills, at device resolution, bounded by an
         explicit pixel budget so the interactive cost stays in the same order
         as the historical <=640x640 one.

         The OFFSCREEN Scene composite stays square — only this destination is
         rectangular. See lampScene.computeWorkingSize(). */
      function canvasBoxAspect() {
        var wrap = el.querySelector('.ever-leaf-personalizer__canvas-wrap');
        var rect = (wrap || canvas).getBoundingClientRect();
        if (!(rect.width > 0) || !(rect.height > 0)) return 1;
        return rect.width / rect.height;
      }
      function computeInteractiveDisplay() {
        var wrap = el.querySelector('.ever-leaf-personalizer__canvas-wrap');
        var rect = (wrap || canvas).getBoundingClientRect();
        var cssW = rect.width || 480;
        var cssH = rect.height || cssW;
        var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
        var w = cssW * dpr;
        var h = cssH * dpr;
        // Bound the LONGER edge, then keep the aspect exactly.
        var longEdge = Math.max(w, h);
        if (longEdge > MAX_INTERACTIVE_SIZE) {
          var k = MAX_INTERACTIVE_SIZE / longEdge;
          w *= k; h *= k;
        }
        var shortEdge = Math.min(w, h);
        if (shortEdge < MIN_INTERACTIVE_SHORT_EDGE) {
          var k2 = MIN_INTERACTIVE_SHORT_EDGE / shortEdge;
          w *= k2; h *= k2;
        }
        return { width: Math.max(1, Math.round(w)), height: Math.max(1, Math.round(h)) };
      }
      /* HQ destination. Same rectangle as the interactive tier (so the settled
         raster can never jump geometrically against the interactive one) but
         at a higher budget, replacing the old fixed square LEAF_LOCAL_SIZE
         backing store which would have been stretched by CSS into the
         portrait View Frame. */
      function computeHqDisplay() {
        var wrap = el.querySelector('.ever-leaf-personalizer__canvas-wrap');
        var rect = (wrap || canvas).getBoundingClientRect();
        var cssW = rect.width || 480;
        var cssH = rect.height || cssW;
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        var w = cssW * dpr;
        var h = cssH * dpr;
        var longEdge = Math.max(w, h);
        if (longEdge > MAX_HQ_LONG_EDGE) {
          var k = MAX_HQ_LONG_EDGE / longEdge;
          w *= k; h *= k;
        }
        return { width: Math.max(1, Math.round(w)), height: Math.max(1, Math.round(h)) };
      }
      // Kept for callers that still want one scalar (photo preparation budget).
      function computeInteractiveSize() {
        var d = computeInteractiveDisplay();
        return Math.max(d.width, d.height);
      }

      /*
        VIEW TRANSITION PERFORMANCE PASS — workingSize is now derived from
        BOTH fixed Close-Up/Full Lamp endpoint rects (getViewportEndpoints()),
        never from the LIVE interpolated getCurrentViewport() a Close-Up<->
        Full-Lamp transition sweeps through every frame via `currentT`. Each
        endpoint's own required size is computed and clamped exactly as
        before (computeWorkingSize + the same MAX_INTERACTIVE_WORKING_SIZE
        cap); the larger of the two is used, which can never under-resolve
        either endpoint OR any point in between (computeWorkingSize is a
        monotonic function of the camera's own width/height, and
        lerpViewportNormalized interpolates those linearly, so the two
        endpoints already bound every intermediate value). The one-time cost
        is a slightly larger-than-strictly-necessary raster at whichever
        endpoint needed less resolution — never a quality loss.

        Previously this read getCurrentViewport() directly, so `workingSize`
        (and therefore this context's own canvases, INCLUDING the Lamp
        Blur/Sharp/Light caches this pass's own perf fix added — see
        createRenderContext()) changed on nearly every transition frame,
        forcing a full context rebuild — 4-6 fresh 4096px source downscales
        plus several new canvas allocations — on almost every single
        Close-Up<->Full-Lamp animation frame. Pinch/zoom was never affected
        by this (it only ever changes photoScale/photoCenterX/Y, never the
        camera/viewMode getViewportEndpoints() reads), which is exactly why
        that gesture was already fast while the view transition was not.
        `workingSize` is now stable for the ENTIRE transition (only display
        size, per-view merchant calibration, or destAspect can change it —
        none of those move mid-transition), so this context now only ever
        rebuilds on those genuinely infrequent events plus a variant/Scene
        switch (unchanged below), never once per animation frame.
      */
      function ensureInteractiveContext() {
        if (!assets) return null;
        var display = computeInteractiveDisplay();
        var endpoints = getViewportEndpoints();
        var workingSize = Math.max(
          Math.min(MAX_INTERACTIVE_WORKING_SIZE, lampScene.computeWorkingSize(display, endpoints.closeUp)),
          Math.min(MAX_INTERACTIVE_WORKING_SIZE, lampScene.computeWorkingSize(display, endpoints.fullLamp))
        );
        // PASS 3C / ZOOM PERFORMANCE REGRESSION AUDIT — createRenderContext()
        // pre-scales Leaf Base (Blur/Sharp) and Light into cached canvases at
        // `workingSize` (see that function's own header); their WEIGHTED
        // BLEND is still computed live every frame at whatever weight the
        // current transition progress calls for — only which bitmap
        // RESOLUTION they blend from is cached. This context therefore only
        // needs to invalidate on a SIZE or active-Scene change — simpler
        // than the Pass 3B correction's own cache-key, which had to also
        // track "which single image is active" precisely because that pass
        // baked one image in once.
        //
        // PASS 3D, generalized in PHASE 45B — re-introduces that same
        // "which image is active" cache-key, now for VARIANT SCENE identity
        // rather than a Blur/Sharp choice: a variant switch changes which
        // Blur AND Sharp images are live, but never changes `workingSize`,
        // so without this the size-only check above would keep serving the
        // old Scene's images out of the cached context indefinitely.
        //
        // FINAL INTERACTIVE PERFORMANCE PASS — Material switch. A material
        // change is a real Scene-identity change (this if/else-if's second
        // condition), but it is NOT a size change — Photo Mask/Lights
        // Overlay/backdrop are shared by every variant (PHASE 45B) and don't
        // need rebuilding just because Lamp Blur/Sharp did. Previously both
        // conditions funneled into the SAME full createRenderContext() call,
        // so a material switch also needlessly re-downscaled the 4096px
        // Mask and Lights Overlay on every click. Now a size change (or no
        // context yet) still does the full rebuild; a material-only change,
        // with size unchanged, rebuilds ONLY the two Lamp canvases via
        // renderer.updateRenderContextMaterial(), reusing the existing
        // backdrop/mask/light/scratch canvases untouched.
        var activeImages = getActiveSceneImages();
        if (!activeImages) return interactiveContext;
        var sizeChanged = !interactiveContext || interactiveContext.size !== workingSize;
        var materialChanged =
          !sizeChanged &&
          (interactiveContext.__activeBlur !== activeImages.leafBase ||
            interactiveContext.__activeSharp !== activeImages.leafBaseSharp);
        if (sizeChanged) {
          interactiveContext = renderer.createRenderContext(Object.assign({}, assets, activeImages), workingSize);
          interactiveContext.__activeBlur = activeImages.leafBase;
          interactiveContext.__activeSharp = activeImages.leafBaseSharp;
          // Read by handleLocalFileSelected's prepareGrayscalePhoto() call —
          // must track the actual WORKING (full-scene) raster the photo
          // gets composited at, not the smaller on-screen display size, or
          // the interactive preview would be needlessly soft.
          window.__everLeafInteractiveSize = workingSize;
        } else if (materialChanged) {
          interactiveContext = renderer.updateRenderContextMaterial(interactiveContext, Object.assign({}, assets, activeImages));
          interactiveContext.__activeBlur = activeImages.leafBase;
          interactiveContext.__activeSharp = activeImages.leafBaseSharp;
        }
        return interactiveContext;
      }

      // PASS 3C — the interactive render body, extracted out of
      // scheduleInteractiveRender()'s own rAF-coalescing wrapper below so
      // the transition RAF loop (Section 51 — ONE requestAnimationFrame
      // loop total) can call it directly, once per tick, without nesting a
      // second rAF indirection inside its own. Behavior is byte-identical
      // to the pre-Pass-3C body — only extracted, not changed.
      function renderInteractiveFrame() {
        publishViewProgress();
        var context = ensureInteractiveContext();
        if (!context) return;
        var s = window.EverLeafPersonalizerState.get();
        var t0 = performance.now();
        // Stage 1 — personalization (camera-independent): full-scene
        // composite onto the offscreen working canvas.
        renderer.renderInteractive(interactiveOffscreen, context, {
          preparedPhotoSource: s.preparedPhotoInteractive,
          transform: sceneTransformFromState(),
          photoAspect: s.photoAspect || 1,
          dedication: { line1: s.dedicationLine1, line2: s.dedicationLine2 },
          layers: Object.assign({}, s.layerSettings, { text: sceneTextLayerSettings(s) }),
          lampWeights: getLampLayerWeights()
        });
        // Stage 2 — view (camera-only): cheap crop onto the visible
        // canvas. No personalization function is called past this point.
        var display = computeInteractiveDisplay();
        canvas.width = display.width;
        canvas.height = display.height;
        lampScene.applyCamera(interactiveOffscreen, getCurrentViewport(), canvas);
        perf.record('interactive', performance.now() - t0);
        debugPanel.refreshPerfReadout();
      }

      // rAF coalescing: many state updates within one frame (a burst of
      // pointermove events) collapse into a single render on the next frame.
      var frameScheduled = false;
      function scheduleInteractiveRender() {
        if (!assets || frameScheduled) return;
        frameScheduled = true;
        requestAnimationFrame(function () {
          frameScheduled = false;
          renderInteractiveFrame();
        });
      }

      // HQ render: the full personalization pipeline at HQ working
      // resolution, run only once interaction settles, into an offscreen
      // canvas — then camera-cropped onto the visible canvas at
      // LEAF_LOCAL_SIZE (1060), matching the pre-Pass-3 backing resolution
      // exactly. CSS display size is untouched, so there's no visible size
      // jump.
      function renderHQ() {
        if (!assets) return;
        var s = window.EverLeafPersonalizerState.get();
        var t0 = performance.now();
        var camera = getCurrentViewport();
        var hqWorkingSize = Math.min(MAX_HQ_WORKING_SIZE, lampScene.computeWorkingSize(computeHqDisplay(), camera));
        renderer.renderPersonalizedLeaf(hqOffscreen, {
          canvasSize: hqWorkingSize,
          // PASS 3D — the active material's Blur/Sharp pair, not always Gray.
          assets: Object.assign({}, assets, getActiveSceneImages()),
          preparedPhotoSource: s.preparedPhotoHQ,
          transform: sceneTransformFromState(),
          photoAspect: s.photoAspect || 1,
          dedication: { line1: s.dedicationLine1, line2: s.dedicationLine2 },
          layers: Object.assign({}, s.layerSettings, { text: sceneTextLayerSettings(s) }),
          lampWeights: getLampLayerWeights()
        });
        var hqDisplay = computeHqDisplay();
        canvas.width = hqDisplay.width;
        canvas.height = hqDisplay.height;
        lampScene.applyCamera(hqOffscreen, camera, canvas);
        perf.record('hq', performance.now() - t0);
        debugPanel.refreshPerfReadout();
      }

      // Debug stages are comparatively expensive (9 extra canvases) and are
      // never run from the interactive path, and immediately no-op while the
      // debug grid is hidden (hidden by default — see EverLeafDebugPanel).
      // Intermediate stage snapshots (Original/Grayscale/Mask/etc.) remain
      // full-scene/uncropped on purpose — this is DEV-only diagnostic
      // tooling, and showing the whole scene is more useful for debugging
      // than pre-cropping it; only the final on-canvas result is camera-
      // cropped, matching what the customer actually sees.
      function refreshDebugStages() {
        if (!assets || debugPanel.isHidden()) return;
        var debugOptions = debugPanel.getDebugOptions();
        if (!debugOptions) return;
        var s = window.EverLeafPersonalizerState.get();
        var camera = getCurrentViewport();
        var hqWorkingSize = Math.min(MAX_HQ_WORKING_SIZE, lampScene.computeWorkingSize(computeHqDisplay(), camera));
        renderer.renderPersonalizedLeaf(hqOffscreen, {
          canvasSize: hqWorkingSize,
          // PASS 3D — the active material's Blur/Sharp pair, not always Gray.
          assets: Object.assign({}, assets, getActiveSceneImages()),
          preparedPhotoSource: s.preparedPhotoHQ,
          customerSource: s.customerSource,
          transform: sceneTransformFromState(),
          photoAspect: s.photoAspect || 1,
          dedication: { line1: s.dedicationLine1, line2: s.dedicationLine2 },
          layers: Object.assign({}, s.layerSettings, { text: sceneTextLayerSettings(s) }),
          lampWeights: getLampLayerWeights(),
          debug: debugOptions
        });
        var hqDebugDisplay = computeHqDisplay();
        canvas.width = hqDebugDisplay.width;
        canvas.height = hqDebugDisplay.height;
        lampScene.applyCamera(hqOffscreen, camera, canvas);
        debugPanel.showStages(debugOptions.stages);
      }

      // Interaction settling point: render HQ once, then (only if visible)
      // refresh the debug grid — neither runs during active drag/zoom.
      // Also the ONE place Preview Session gets updated after an
      // interaction (Move/Zoom/Reset/drag/wheel/pinch/Dedication text all
      // already call this) — a single hook covers every mutation path
      // rather than one per interaction method. savePreviewSession() is a
      // pure no-op outside the actual Theme Editor.
      function onInteractionEnd() {
        renderHQ();
        refreshDebugStages();
        savePreviewSession();
      }

      /*
        PASS 3C — the customer-facing top-right View Toggle + its single
        RAF-driven animation controller (Section 3-5/25-60 of the brief).

        Architecture: ONE animation owner (`transitionRafId`), time-based
        progress (`transitionStartTime`/`performance.now()`, never a frame
        counter — Section 52), one shared easing (easeInOutCubic) driving
        BOTH the viewport interpolation and the Blur/Sharp crossfade via
        the single `currentT` value `getCurrentViewport()`/
        `getLampLayerWeights()` already read. Reversal is fully supported
        (Section 42's preferred architecture, chosen because this design
        makes it genuinely trivial): pressing the toggle mid-transition
        just retargets `transitionTargetT` and keeps animating from
        whatever `currentT` currently is — never a jump, never a second
        controller. Segment duration scales with the actual distance
        travelled (`configuredDuration * |targetT - startT|`) so a full
        0->1 (or 1->0) run takes exactly the configured duration and a
        partial reversal takes a proportionally shorter time, preserving a
        constant apparent speed rather than a fixed wall-clock time
        regardless of distance.
      */
      var transitionActive = false;
      var transitionRafId = null;
      var transitionStartT = currentT;
      var transitionStartTime = 0;
      var transitionTargetT = currentT;
      // Section 45/46 — editing gate. Read by wirePhotoInteraction's
      // isEditingEnabled hook (gates canvas drag/pinch/wheel) and applied
      // to the Move/Zoom/Reset buttons via customerControls.setEditingEnabled()
      // (native `disabled`, which wireHoldableButton already checks on its
      // own pointerdown handler — Section 46: input gating only, never a
      // state mutation).
      var editingEnabled = true;
      var lastAppliedViewMode = null;
      // Section 61 — distinguishes MY OWN toggle-triggered state.update()
      // (where currentT/transitionActive are already correctly set BEFORE
      // the update, so the subscriber below must NOT also try to "snap")
      // from an EXTERNAL viewMode change (Theme Editor Static Scene
      // Preview / a live config refresh with no full remount) — which
      // never touches currentT at all and must be snapped directly,
      // without animating (Section 61: this only ever defines an
      // endpoint, never triggers a customer-style animated transition).
      var suppressAutoSnap = false;

      // Queried BEFORE updateToggleUI/syncDiscreteViewState's own initial
      // call further below, so that very first sync (Section 58 — correct
      // initial icon at mount) actually finds real elements instead of
      // silently no-op'ing on not-yet-assigned variables.
      var viewToggleButton = el.querySelector('[data-ever-leaf-view-toggle]');
      var viewToggleIconLamp = el.querySelector('[data-ever-leaf-view-toggle-icon-lamp]');
      var viewToggleIconLeaf = el.querySelector('[data-ever-leaf-view-toggle-icon-leaf]');

      /*
        PHASE 47A, Part B — VIEW TOGGLE FIRST-APPEAR FROM ABOVE.

        Fully independent from the Interaction Hints' own first-appear
        system above (Section "NO SHARED TIMER ASSUMPTION" — its own
        cancellable delay/generation state, its own setting, never coupled
        to Interaction Hints Config), though both observe the SAME real
        photo lifecycle via the SAME window.__everLeaf* bridge convention
        from handleLocalFileSelected().

        Critically DIFFERENT from the Hints' visibility rule (Section
        "VIEW TOGGLE DIFFERENT LOGIC FROM HINTS"): once revealed for the
        current photo, the Toggle stays visible through EVERY Close-Up/
        Full Lamp switch — "eligible" below depends ONLY on photo-ready/
        design-mode preview, exactly mirroring the pre-existing `.ever-
        leaf-has-photo` collapse that already governs the whole Visualizer
        (including this button) — never on `state.viewMode`. The entrance
        itself is a one-time `top` slide from fully above the Visualizer
        (component-ever-leaf-personalizer.css's own min-free, block-size-
        driven hidden position) down to the button's own untouched,
        existing final position — never touching PHASE 44's click/select/
        icon/size behavior at all, only this first-appear presentation.
      */
      // Materials/Color freeze fix (Mobile Dock Correction Pass 2) — same
      // listener-leak class PHASE 46A.1 already fixed for Interaction
      // Hints below, found while reproducing the reported white-screen/
      // freeze: on a REAL Theme Editor section reload (this custom
      // element's disconnectedCallback -> connectedCallback -> a fresh
      // init() call), destroy() below did not remove these three — so
      // every reload left one more permanently-live copy of each on
      // `document`/`window`/the persisted state singleton, each still
      // pointing at that OLD, now-detached instance's canvas/closures.
      // The `document` 'change' listener specifically re-runs a full
      // interactive + HQ (1060x1060) render on EVERY variant change it
      // sees — after even a few reloads, one Materials click fired that
      // full render N times over, synchronously, on the main thread:
      // exactly a freeze/white-screen. Captured here so destroy() can
      // remove the exact same function reference from the exact same
      // target it was added to.
      var discreteViewStateUnsubscribe = null;
      var breakpointQuery = null;
      var onBreakpointChange = null;
      var onDocumentVariantChange = null;
      var onWindowResizeForCanvas = null;
      var viewToggleUnsubscribe = null;
      var viewToggleFirstAppearDelayMs = parseInt(el.getAttribute('data-ever-leaf-view-toggle-first-appear-delay-ms'), 10) || 0;
      var viewToggleFirstAppearGeneration = 0;
      var viewToggleFirstAppearedForGeneration = -1;
      var viewToggleFirstAppearTimer = null;
      var viewToggleLastEligible = null;

      function cancelViewToggleFirstAppearTimer() {
        if (viewToggleFirstAppearTimer) {
          clearTimeout(viewToggleFirstAppearTimer);
          viewToggleFirstAppearTimer = null;
        }
      }

      function setViewToggleRevealed(visible, instant) {
        if (!viewToggleButton) return;
        if (instant) {
          viewToggleButton.setAttribute('data-ever-leaf-view-toggle-instant', '');
          void viewToggleButton.offsetWidth;
        }
        if (visible) {
          viewToggleButton.setAttribute('data-ever-leaf-view-toggle-visible', '');
        } else {
          viewToggleButton.removeAttribute('data-ever-leaf-view-toggle-visible');
        }
        if (instant) {
          requestAnimationFrame(function () {
            viewToggleButton.removeAttribute('data-ever-leaf-view-toggle-instant');
          });
        }
      }

      function computeViewToggleEligible() {
        var s = window.EverLeafPersonalizerState.get();
        var hasPhoto = s.uploadStatus !== 'empty';
        return isDesignModeRefresh || hasPhoto;
      }

      // Reveals the Toggle for the CURRENT photo generation, applying the
      // configured delay only on a genuine first appearance — a later
      // sync while already appeared for this generation (any subsequent
      // Close-Up/Full Lamp switch) is handled entirely by CSS simply never
      // being told to hide again (see syncViewToggleFirstAppear — it is
      // never even invoked by viewMode changes at all).
      function revealViewToggleFirstAppear(instant) {
        cancelViewToggleFirstAppearTimer();
        var isFirstAppear = viewToggleFirstAppearedForGeneration !== viewToggleFirstAppearGeneration;
        if (instant || !isFirstAppear || viewToggleFirstAppearDelayMs <= 0) {
          viewToggleFirstAppearedForGeneration = viewToggleFirstAppearGeneration;
          setViewToggleRevealed(true, instant);
          return;
        }
        var myGeneration = viewToggleFirstAppearGeneration;
        viewToggleFirstAppearTimer = setTimeout(function () {
          viewToggleFirstAppearTimer = null;
          if (myGeneration !== viewToggleFirstAppearGeneration) return; // superseded by a newer photo
          if (!computeViewToggleEligible()) return; // no longer eligible before the delay elapsed
          viewToggleFirstAppearedForGeneration = myGeneration;
          setViewToggleRevealed(true, false);
        }, viewToggleFirstAppearDelayMs);
      }

      // Edge-detected on "eligible" ONLY (photo-ready/design-mode) — never
      // on viewMode, so Close-Up/Full Lamp switches never re-enter this at
      // all and the Toggle correctly just stays wherever it already is.
      function syncViewToggleFirstAppear(instant) {
        var eligible = computeViewToggleEligible();
        if (eligible === viewToggleLastEligible) return;
        viewToggleLastEligible = eligible;
        if (eligible) {
          revealViewToggleFirstAppear(instant);
        } else {
          cancelViewToggleFirstAppearTimer();
          setViewToggleRevealed(false, instant);
        }
      }

      // Same bridge convention/reasoning as window.__everLeafResetInteractionHints
      // above — the single authoritative point where a genuinely new/
      // replaced local File resets THIS system's own first-appearance
      // eligibility (Section "VIEW TOGGLE FIRST-APPEAR RESET" — never on
      // variant/dedication/lights/Theme-Editor-rerender/the Toggle's own
      // click). A replace while already eligible/visible forces a genuine
      // hide -> show-with-delay cycle so the entrance visibly replays;
      // this intentionally also runs for a genuine design-mode local file
      // selection (never for a bare section reload, which only ever calls
      // syncViewToggleFirstAppear(true) below).
      window.__everLeafResetViewToggleFirstAppear = function () {
        viewToggleFirstAppearGeneration += 1;
        cancelViewToggleFirstAppearTimer();
        if (viewToggleLastEligible !== true) return;
        setViewToggleRevealed(false, true);
        requestAnimationFrame(function () {
          revealViewToggleFirstAppear(false);
        });
      };

      syncViewToggleFirstAppear(true);
      viewToggleUnsubscribe = window.EverLeafPersonalizerState.subscribe(function () {
        syncViewToggleFirstAppear(false);
      });

      function updateToggleUI(mode) {
        if (!viewToggleButton) return;
        var showLampIcon = mode !== 'full_lamp';
        if (viewToggleIconLamp) viewToggleIconLamp.hidden = !showLampIcon;
        if (viewToggleIconLeaf) viewToggleIconLeaf.hidden = showLampIcon;
        var label = viewToggleButton.getAttribute(showLampIcon ? 'data-label-forward' : 'data-label-backward');
        if (label) {
          viewToggleButton.setAttribute('aria-label', label);
          viewToggleButton.title = label;
        }
      }

      function applyDiscreteViewState(mode) {
        editingEnabled = mode === 'close_up' && !transitionActive;
        if (customerControls && customerControls.setEditingEnabled) {
          customerControls.setEditingEnabled(editingEnabled);
        }
        updateToggleUI(mode);
      }

      function syncDiscreteViewState(s) {
        if (s.viewMode === lastAppliedViewMode) return;
        lastAppliedViewMode = s.viewMode;
        applyDiscreteViewState(s.viewMode);
        if (!suppressAutoSnap && !transitionActive) {
          currentT = s.viewMode === 'full_lamp' ? 1 : 0;
          transitionTargetT = currentT;
          renderInteractiveFrame();
          updatePivotMarker(s);
          updatePhotoBoundingBoxGeometry(s);
        }
      }
      discreteViewStateUnsubscribe = window.EverLeafPersonalizerState.subscribe(syncDiscreteViewState);
      // Crossing the Desktop/Mobile breakpoint swaps the merchant viewport pair; re-render the frame only
      // (no state change, photo/dedication/variant untouched).
      breakpointQuery = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
      if (breakpointQuery) {
        onBreakpointChange = function () {
          renderInteractiveFrame();
          updatePivotMarker(window.EverLeafPersonalizerState.get());
          updatePhotoBoundingBoxGeometry(window.EverLeafPersonalizerState.get());
        };
        if (breakpointQuery.addEventListener) breakpointQuery.addEventListener('change', onBreakpointChange); else if (breakpointQuery.addListener) breakpointQuery.addListener(onBreakpointChange); // eslint-disable-line no-restricted-properties
      }
      // Apply the correct initial icon/gate/aria-label immediately at mount
      // — customerControls (constructed earlier in init(), above) already
      // exists by this point, so this is a real, complete initial
      // application, not a placeholder later overwritten.
      syncDiscreteViewState(window.EverLeafPersonalizerState.get());

      function tickTransition(now) {
        var s = window.EverLeafPersonalizerState.get();
        var durationMs = s.viewTransitionDurationMs;
        var totalDistance = Math.abs(transitionTargetT - transitionStartT);
        var segmentDurationMs = durationMs * totalDistance;
        var elapsed = now - transitionStartTime;
        var raw = segmentDurationMs > 0 ? clamp(elapsed / segmentDurationMs, 0, 1) : 1;
        var eased = easeInOutCubic(raw);
        currentT = transitionStartT + (transitionTargetT - transitionStartT) * eased;
        renderInteractiveFrame();
        updatePivotMarker(window.EverLeafPersonalizerState.get());
        updatePhotoBoundingBoxGeometry(window.EverLeafPersonalizerState.get());
        if (raw < 1) {
          transitionRafId = requestAnimationFrame(tickTransition);
        } else {
          // Section 53 — endpoint snap: land exactly on the target, never
          // an eased-but-not-quite value, then run the real HQ/debug/
          // Preview-Session settle pass (Section 56 — HQ only runs here,
          // at the very end, never once per animation frame).
          currentT = transitionTargetT;
          transitionActive = false;
          transitionRafId = null;
          // Real bug found via browser QA: `state.viewMode` was already set
          // to its final value back at startTransition() time (so the icon
          // flips immediately, per Section 43), meaning
          // syncDiscreteViewState()'s own `viewMode === lastAppliedViewMode`
          // early-return means it never fires again here — so without this
          // explicit re-apply, the editing gate (Move/Zoom/Reset `disabled`)
          // stayed stuck OFF forever after finishing a Close-Up-bound
          // transition, since `transitionActive` only flips back to false
          // at this exact point, AFTER the state.update() that would
          // otherwise have re-computed it. Re-applying directly here is the
          // correct fix — it re-reads the NOW-updated transitionActive.
          applyDiscreteViewState(window.EverLeafPersonalizerState.get().viewMode);
          onInteractionEnd();
        }
      }

      function startTransition(targetT) {
        var newMode = targetT === 1 ? 'full_lamp' : 'close_up';
        if (transitionRafId) {
          cancelAnimationFrame(transitionRafId);
          transitionRafId = null;
        }
        transitionTargetT = targetT;
        var prefersReducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
        var configuredDurationMs = window.EverLeafPersonalizerState.get().viewTransitionDurationMs;
        suppressAutoSnap = true;
        if (prefersReducedMotion || !configuredDurationMs || configuredDurationMs <= 0) {
          // Section 50 — reduced motion: switch directly/essentially
          // instantly, never run the RAF loop at all.
          transitionActive = false;
          currentT = targetT;
          window.EverLeafPersonalizerState.update({ viewMode: newMode });
          renderInteractiveFrame();
          updatePivotMarker(window.EverLeafPersonalizerState.get());
          updatePhotoBoundingBoxGeometry(window.EverLeafPersonalizerState.get());
          onInteractionEnd();
        } else {
          transitionActive = true;
          transitionStartT = currentT;
          transitionStartTime = performance.now();
          window.EverLeafPersonalizerState.update({ viewMode: newMode });
          transitionRafId = requestAnimationFrame(tickTransition);
        }
        suppressAutoSnap = false;
      }

      // REMOVE PHOTO -> FULL PERSONALIZER RESET pass — same bridge
      // convention as window.__everLeafResetInteractionHints/
      // window.__everLeafResetViewToggleFirstAppear above: the single
      // authoritative point resetPersonalizationAfterPhotoRemoval()
      // (module scope, outside this closure) reaches startTransition()
      // through. Routes through the SAME function the View Toggle button
      // itself calls (reduced-motion, in-flight-animation cancellation,
      // and the toggle icon/editing-gate update all come for free) — no
      // separate "instant snap" implementation. Guarded so Remove from an
      // already-Close-Up session is a genuine no-op (Test 3 — no
      // unnecessary transition/render when there is nothing to reverse).
      window.__everLeafResetViewModeToCloseUp = function () {
        if (window.EverLeafPersonalizerState.get().viewMode !== 'close_up') {
          startTransition(0);
        }
      };

      // Section 42 — reversal is driven off `transitionTargetT` (the
      // current DESTINATION), never off `currentT` itself, so a click
      // mid-transition correctly reverses toward the OTHER endpoint from
      // wherever the animation currently is, rather than continuing on.
      function requestViewTransition() {
        startTransition(transitionTargetT === 1 ? 0 : 1);
      }

      if (viewToggleButton) {
        viewToggleButton.addEventListener('click', requestViewTransition);
      }

      // PHASE 45B — the Visualizer's active Scene follows the real Shopify
      // variant selection. Delegated at the document level (not bound
      // directly to the hidden `input[name="id"]`) because Shopify's
      // Section Rendering API can replace the whole product form DOM node
      // (Pass 3A/PHASE 31's own "stale form reattachment" lesson) — a
      // direct binding would silently stop firing after such a swap, while
      // this delegated listener keeps working against whatever
      // `input[name="id"]` currently exists. Fires on the SAME `change`
      // event PHASE 39's sync_variant_selection already dispatches on that
      // input once it resolves a real matching variant (snippets/product-
      // variant-picker.liquid) — no new event, no new data source.
      onDocumentVariantChange = function (event) {
        var target = event.target;
        if (target && target.name === 'id' && target.closest('form[id^="product-form-"]')) {
          applySelectedVariantScene();
        }
      };
      document.addEventListener('change', onDocumentVariantChange);

      /*
        PHASE 45B initial load. Resolves the two SHARED scene assets AND the
        REAL initially selected variant's Scene pair together, so the very
        first render already shows the correct Scene — no "flash of Gray"
        or any other placeholder, since there is no more default variant at
        all (Section 21 of the brief). If the initial variant has no valid
        Scene (missing metafield reference, incomplete pair, or a load
        failure), the whole chain rejects and initialization is correctly
        BLOCKED (Section 24) — `assets` never gets set, nothing renders, and
        a clear diagnostic is logged; the real Shopify selected variant
        itself is never touched.
      */
      var initialVariantId = getSelectedVariantId();
      Promise.all([lampScene.getLeafSceneAssets(sceneUrls), ensureVariantSceneLoaded(initialVariantId)])
        .then(function (results) {
          var shared = results[0];
          var initialPair = results[1];
          sharedSceneImages = shared;
          activeVariantId = initialVariantId;
          pendingVariantId = initialVariantId;
          // renderer.loadPersonalizerAssetsFromImages() expects
          // { leafBase, photoMask, light } — leafBase here is the initial
          // variant's own Blur image, never a fixed Gray default.
          return renderer.loadPersonalizerAssetsFromImages({
            leafBase: initialPair.blurImage,
            photoMask: shared.photoMask,
            light: shared.light
          });
        })
        .then(function (loaded) {
          // Pass 3A coordinate-semantics correction — `loaded.maskBounds` is
          // SCENE-space (fractions of the whole 2048 scene; correct for the
          // renderer's own internal use, e.g. the Full Lamp viewport). Every
          // clamp/default-transform call site in THIS file, however, must
          // keep operating in the pre-existing Personalization State Space
          // (photoX/photoY/photoScale's own historical coordinate
          // convention) — `legacyMaskBounds` is that same mask geometry,
          // inverse-mapped back through PERSONALIZATION_REFERENCE_RECT, so
          // it is numerically equivalent to what computeMaskBounds() would
          // have returned against the legacy 1060 mask before Pass 3A ever
          // existed. See ever-leaf-lamp-scene.js's own header for the full
          // three-space model and calibration derivation.
          loaded.legacyMaskBounds = lampScene.mapLegacyMaskBoundsFromScene(loaded.maskBounds);
          assets = loaded;
          window.__everLeafPersonalizerAssets = loaded; // read by handleLocalFileSelected's default-fit computation
          renderHQ();
          refreshDebugStages();
          // Preload every other real variant's Scene once this initial
          // scene is stable, so switching later is instant.
          preloadOtherVariantScenes();
        })
        .catch(function (err) {
          console.error(
            'EverLeaf: Visualizer initialization blocked — no valid Scene for the initial selected variant ("' +
              initialVariantId +
              '")',
            err
          );
        });

      window.EverLeafPersonalizerState.subscribe(scheduleInteractiveRender);
      // PHASE 50B.1 — this was a bare truthiness check on s.preparedPhotoHQ,
      // not an edge-detection. state.update() is synchronous/unbatched (see
      // update() above), so this subscriber ran on EVERY state update —
      // including every single drag/pinch pointermove, since
      // preparedPhotoHQ is set once at upload/tonal-rebuild time and then
      // stays truthy in state for the rest of the session. That fired a
      // full, synchronous, non-throttled HQ render (onInteractionEnd ->
      // renderHQ ~1060x1060, ~4-8x the interactive tier's pixel count) plus
      // a canvas backing-store reallocation on every pointermove — directly
      // contradicting onInteractionEnd's own header comment ("neither runs
      // during active drag/zoom") and confirmed (PHASE 50B) as the dominant
      // cause of the reported mobile drag/pinch jank. preparedPhotoHQ is a
      // freshly-built canvas/ImageBitmap each time it's actually rebuilt
      // (renderer.prepareGrayscalePhoto(...) — see the three state.update
      // call sites above), and is never touched by a drag/pinch update
      // (those only ever update photoCenterX/photoCenterY/photoScale) — so
      // comparing by reference identity against the last-seen value
      // correctly fires only on a genuine fresh upload/tonal-rebuild,
      // exactly matching this subscriber's own original intent, and never
      // during an ordinary gesture. Matches the existing edge-detection
      // pattern already used elsewhere in this file (e.g.
      // syncDiscreteViewState's `s.viewMode === lastAppliedViewMode`).
      var lastPreparedPhotoHQForSettle = null;
      window.EverLeafPersonalizerState.subscribe(function (s) {
        if (s.preparedPhotoHQ && s.preparedPhotoHQ !== lastPreparedPhotoHQForSettle) {
          lastPreparedPhotoHQForSettle = s.preparedPhotoHQ;
          onInteractionEnd();
        }
      });
      debugPanel.onChange(refreshDebugStages);
      debugPanel.onVerboseChange(function (value) {
        verboseLogging = value;
      });

      /*
        FINAL INTERACTIVE PERFORMANCE PASS — Dedication typing. Root cause:
        this handler fires on EVERY 'input' event (every keystroke — see
        bindDedicationInputs()'s own 'input' listener) and, before this
        pass, called onInteractionEnd() — a full HQ render at up to
        MAX_HQ_WORKING_SIZE, plus refreshDebugStages()/savePreviewSession()
        — synchronously, unconditionally, per character. Drag/pinch/wheel
        already avoid exactly this (releasePointer()'s `wasActive` edge-
        detection at genuine pointerup/pointercancel; the existing
        `wheelSettleTimer` 200ms debounce a few hundred lines up) — typing
        was the one interaction still doing it per-event. The interactive
        preview stays instant either way: state.update() (which drives
        scheduleInteractiveRender(), the cheap RAF-coalesced interactive
        tier) and refitDedicationLines() (font-size fit, needed for that
        same preview to look right immediately) both stay OUTSIDE the
        debounce, unchanged. Only the expensive settle work is deferred —
        same 200ms window the wheel debounce already uses, so a customer who
        pauses typing sees the identical settle behavior a drag/pinch/wheel
        pause already produces. Cart/order values read state.dedicationLine1/
        Line2 directly at commit time (see LINE_ITEM_PROPERTY_NAMES below),
        never the HQ render output, so deferring HQ changes nothing about
        cart/order correctness.
      */
      var dedicationSettleTimer = null;
      calibrationPanel.onDedicationChange(function (line1, line2) {
        window.EverLeafPersonalizerState.update({
          dedicationLine1: line1,
          dedicationLine2: line2,
          text: serializeDedication(line1, line2)
        });
        refitDedicationLines();
        clearTimeout(dedicationSettleTimer);
        dedicationSettleTimer = setTimeout(function () {
          onInteractionEnd();
        }, 200);
      });

      /*
        Photo Tone live-reprocessing fix (Theme Editor only) — root cause:
        Brightness/Contrast/Gamma/Black-White point are baked into
        preparedPhotoInteractive/HQ ONCE, at upload time
        (renderer.prepareGrayscalePhoto, a real pixel loop) — never re-run
        just because state.photoTonal changes. refreshFromConfig() above
        already updates state.photoTonal correctly, but nothing previously
        re-derived the PREPARED BITMAP from it, which is why only Photo
        Opacity (applied at composite time, never baked into pixels — see
        this file's own top header comment) appeared live while tone
        settings needed a Save/re-upload. Fixed here: if tone actually
        changed and a photo is already loaded, rebuild both prepared tiers
        from the SAME original decoded source — no new Upload, no state
        loss — using the exact same renderer.prepareGrayscalePhoto() calls
        handleLocalFileSelected() uses. Text-layout dirty (Dedication Text
        Config) is handled the same way: if a FONT-SIZE-relevant field
        changed (line1FontSize/line2FontSize/minFontSize) and at least one
        line already has content, both lines are re-fit via the exact same
        refitDedicationLines() normal typing uses — the text itself
        (dedicationLine1/Line2) is never touched. Position fields
        (centerXRatio/line1YSingleRatio/line1YTwoRatio/
        line2YFromBottomRatio) need NO re-fit at all — sceneTextLayerSettings()
        reads dedicationConfig live on every render call, so a position-only
        change (including moving Line 1/Line 2 independently, per the PASS
        3A FINAL DEDICATION POSITIONING MODEL) is already live with zero
        extra work (unlike font SIZE, which is cached in layerSettings.text
        specifically so it isn't re-measured every frame).
      */
      if (isDesignModeRefresh) {
        var afterDesignModeRefresh = window.EverLeafPersonalizerState.get();
        var toneChanged =
          beforeTonal &&
          (beforeTonal.brightness !== afterDesignModeRefresh.photoTonal.brightness ||
            beforeTonal.contrast !== afterDesignModeRefresh.photoTonal.contrast ||
            beforeTonal.gamma !== afterDesignModeRefresh.photoTonal.gamma ||
            beforeTonal.blackPoint !== afterDesignModeRefresh.photoTonal.blackPoint ||
            beforeTonal.whitePoint !== afterDesignModeRefresh.photoTonal.whitePoint);
        if (toneChanged && afterDesignModeRefresh.customerSource) {
          var reprocessSource = afterDesignModeRefresh.customerSource;
          var reprocessTonal = afterDesignModeRefresh.photoTonal;
          var reprocessInteractiveMax = window.__everLeafInteractiveSize || MAX_INTERACTIVE_SIZE;
          window.EverLeafPersonalizerState.update({
            preparedPhotoInteractive: renderer.prepareGrayscalePhoto(reprocessSource, {
              maxDimension: reprocessInteractiveMax,
              tonal: reprocessTonal
            }),
            preparedPhotoHQ: renderer.prepareGrayscalePhoto(reprocessSource, { tonal: reprocessTonal })
          });
        }

        var dedicationSizeChanged =
          beforeDedicationConfig &&
          (beforeDedicationConfig.line1FontSize !== afterDesignModeRefresh.dedicationConfig.line1FontSize ||
            beforeDedicationConfig.line2FontSize !== afterDesignModeRefresh.dedicationConfig.line2FontSize ||
            beforeDedicationConfig.minFontSize !== afterDesignModeRefresh.dedicationConfig.minFontSize);
        if (dedicationSizeChanged && (afterDesignModeRefresh.dedicationLine1 || afterDesignModeRefresh.dedicationLine2)) {
          refitDedicationLines();
        }
      }
      // MOVE_STEP/ZOOM_STEP are normalized (fractions of the 1060 logical
      // space), never raw screen pixels — identical behavior regardless of
      // display size, Desktop vs. Mobile, or interactive vs. HQ raster
      // (Section 15/20). Pass 2A — now sourced from the Visualizer
      // Calibration block (window.EverLeafPersonalizerState.imageLimits)
      // instead of a hardcoded literal; defaults reproduce the exact prior
      // values (0.03 / 0.15) byte-for-byte when the merchant hasn't touched
      // the new settings.
      var imageLimits0 = window.EverLeafPersonalizerState.get().imageLimits || {};
      var MOVE_STEP = imageLimits0.moveStep;
      var ZOOM_STEP = imageLimits0.zoomStep;

      /*
        Pass 3A interaction-model correction — the Scale Photo Pivot Modes
        machinery (fixed_visualizer_point / image_center, scalePivotX/Y) is
        RETIRED from the active interaction path entirely: Zoom In/Out (and
        wheel/pinch scale) now always preserves the photo's own CURRENT
        center exactly, with no anchor/pivot concept at all — see
        applyClampedCenterTransform above, which makes this the natural
        result of a scale-only patch rather than a special case needing its
        own resolver. Old scalePivotMode/X/Y Theme Editor settings are
        removed from the active schema (see sections/ever-leaf-product.liquid)
        — any previously-saved values are simply no longer read.
      */

      /*
        DEV-only arrow diagnostics (Phase 4 Correction Pass 2, Task A/Section
        4) — a small, non-spamming readout (updated only on an actual arrow
        click, never per-frame) showing exactly where the chain stands:
        handler fired, before/requested/after-clamp values. Kept even after
        this pass's fix so any FUTURE regression in this area is immediately
        observable without re-adding instrumentation from scratch.
      */
      var lastArrowDirection = null;
      var lastArrowBefore = null;
      var lastArrowRequested = null;
      function fmt(n) {
        return typeof n === 'number' && isFinite(n) ? n.toFixed(4) : String(n);
      }
      function formatArrowDebug() {
        if (!lastArrowDirection) return 'ARROW DEBUG\n(no arrow pressed yet)';
        var after = currentCenterTransformFromState();
        return (
          'ARROW DEBUG\n' +
          'last action: ' + lastArrowDirection.toUpperCase() + '\n' +
          'handler fired: yes\n' +
          'before        centerX: ' + fmt(lastArrowBefore.centerX) + '  centerY: ' + fmt(lastArrowBefore.centerY) + '\n' +
          'requested     centerX: ' + fmt(lastArrowRequested.centerX) + '  centerY: ' + fmt(lastArrowRequested.centerY) + '\n' +
          'after clamp   centerX: ' + fmt(after.centerX) + '  centerY: ' + fmt(after.centerY) + '  scale: ' + fmt(after.scale)
        );
      }
      function logArrowDebug(direction, before, patch) {
        lastArrowDirection = direction;
        lastArrowBefore = before;
        lastArrowRequested = Object.assign({}, before, patch);
        debugPanel.setArrowDebug(formatArrowDebug());
      }
      function logArrowDebugAfter() {
        debugPanel.setArrowDebug(formatArrowDebug());
      }

      // Upload Photo is the ONE unified upload/replace action (Finalization
      // pass, Section 3-5) — the same handler runs whether or not a photo
      // already exists; handleLocalFileSelected() below already implements
      // "replace, reset transform to default-fit, preserve text" uniformly.
      customerControls.onUploadClick(function () {
        customerControls.triggerFilePicker();
      });
      customerControls.onFileSelected(function (file) {
        customerControls.clearLocalFileError();
        handleLocalFileSelected(file, function () {
          customerControls.showLocalFileError();
        });
      });
      customerControls.onRemoveClick(function () {
        removeCurrentPhoto();
      });
      // Pass 3A interaction-model correction — Move touches Center ONLY,
      // never Scale. Passing a patch with only centerX/centerY set leaves
      // `scale` completely absent from `candidate`, so
      // applyClampedCenterTransform's merge falls back to the CURRENT
      // scale unchanged — no separate "don't touch scale" logic needed.
      // MOBILE PERFORMANCE PASS — Move/Zoom previously called the full
      // onInteractionEnd() -> renderHQ() (a ~4-8x-larger-pixel-area canvas
      // composite than the cheap interactive tier, per the comment at
      // onInteractionEnd's own definition) SYNCHRONOUSLY on every single
      // dispatch, including every ~80ms hold-repeat tick. Drag/pinch never
      // had this problem: they already call onInteractionEnd() only once,
      // at gesture release (see wirePhotoInteraction's edge-detected
      // releasePointer()), and wheel-zoom already debounces it 200ms after
      // the last tick (wheelSettleTimer, above). Move/Zoom had no
      // equivalent debounce, so a held button re-ran the expensive HQ
      // render every 80ms — the proven cause of the reported lag. This
      // applies the IDENTICAL 200ms trailing-debounce convention already
      // established for wheel. The cheap interactive-tier repaint (via
      // applyClampedCenterTransform/applyZoom -> state.update() ->
      // scheduleInteractiveRender, RAF-coalesced) still runs synchronously
      // on every tick, unchanged, so visual feedback stays immediate —
      // only the expensive settle-quality render is deferred/coalesced.
      // Phase 50 safety: the persisted photoCenterX/Y/scale state is
      // written synchronously inside applyClampedCenterTransform/applyZoom
      // (state.update()), BEFORE this debounce is even armed — renderHQ()
      // only reads that state to paint a canvas, it never writes back to
      // it, so deferring it cannot leave any cart/ATC-relevant state stale.
      var controlsInteractionSettleTimer = null;
      function scheduleControlsInteractionEnd() {
        clearTimeout(controlsInteractionSettleTimer);
        controlsInteractionSettleTimer = setTimeout(function () {
          onInteractionEnd();
        }, 200);
      }
      customerControls.onMove(function (direction) {
        // PASS 3A FINAL UX PASS, Section 38/46 — called on every tick this
        // callback runs, including every hold-repeat tick (Section 46:
        // "stay visible for entire repeat sequence"); schedulePhotoInteractionHide()
        // below re-arms the SAME timer every tick, so it only actually
        // fires ~400ms after the LAST tick (i.e. after the button is
        // released).
        notePhotoInteractionActivity();
        var t = Object.assign({}, renderer.DEFAULT_PHOTO_TRANSFORM_CENTER, currentCenterTransformFromState());
        var patch = {};
        if (direction === 'up') patch.centerY = t.centerY - MOVE_STEP;
        if (direction === 'down') patch.centerY = t.centerY + MOVE_STEP;
        if (direction === 'left') patch.centerX = t.centerX - MOVE_STEP;
        if (direction === 'right') patch.centerX = t.centerX + MOVE_STEP;
        logArrowDebug(direction, t, patch);
        // Press-and-hold pass (2A.1) — return value lets the hold-repeat
        // loop in EverLeafCustomerControls detect a genuine no-op (already
        // at a position limit) and stop itself; a plain single click/tap
        // ignores the return value entirely, unaffected.
        var changed = applyClampedCenterTransform(patch);
        logArrowDebugAfter();
        scheduleControlsInteractionEnd();
        schedulePhotoInteractionHide();
        return changed;
      });
      // Pass 3A targeted correction — Zoom buttons/press-and-hold now go
      // through the ONE canonical applyZoom() (see its own comment above)
      // instead of resolving/clamping scale locally. In 'image_center'
      // mode this is unchanged behavior (Center untouched); in
      // 'custom_pivot' mode Center moves to keep the configured pivot
      // point visually fixed.
      customerControls.onZoom(function (direction) {
        notePhotoInteractionActivity();
        var t = Object.assign({}, renderer.DEFAULT_PHOTO_TRANSFORM_CENTER, currentCenterTransformFromState());
        if (!assets) {
          scheduleControlsInteractionEnd();
          schedulePhotoInteractionHide();
          return false;
        }
        var requestedScale = direction === 'in' ? t.scale + ZOOM_STEP : t.scale - ZOOM_STEP;
        var changed = applyZoom(requestedScale);
        scheduleControlsInteractionEnd();
        schedulePhotoInteractionHide();
        return changed;
      });
      customerControls.onResetPosition(function () {
        var s = window.EverLeafPersonalizerState.get();
        if (!s.customerFile) return;
        // PASS 3A FINAL UX PASS, Section 38 — Reset genuinely changes
        // Scale/Center just like any other manipulation, so it activates
        // the box the same way a single Move/Zoom click does (show, update
        // geometry via the state.update() below, then schedule the hide).
        notePhotoInteractionActivity();
        // Reads the ORIGINAL decoded source (always available immediately,
        // unlike preparedPhotoHQ which may still be mid-deferred-build) —
        // the exact same default-fit logic as a fresh Upload/Change,
        // restoring defaults without touching the photo itself.
        var width = 0;
        var height = 0;
        if (s.customerSource) {
          width = s.customerSource.width || s.customerSource.naturalWidth;
          height = s.customerSource.height || s.customerSource.naturalHeight;
        }
        var aspect = width && height ? width / height : 1;
        var defaultTopLeft =
          assets && width && height
            ? renderer.computeDefaultPhotoTransform(width, height, assets.legacyMaskBounds, renderer.LEAF_LOCAL_SIZE)
            : renderer.DEFAULT_PHOTO_TRANSFORM;
        var defaultCenter = renderer.topLeftToCenter(defaultTopLeft.x, defaultTopLeft.y, defaultTopLeft.scale, aspect);
        applyClampedCenterTransform({
          centerX: defaultCenter.centerX,
          centerY: defaultCenter.centerY,
          scale: defaultTopLeft.scale
        });
        onInteractionEnd();
        schedulePhotoInteractionHide();
      });

      /*
        Pass 3A targeted correction — Zoom Pivot Mode / Pivot X / Pivot Y
        are RESTORED as an OPTIONAL mode layered on top of the (still
        canonical, still passing) Center/Scale model — see applyZoom()/
        getZoomPivot() above and computePivotPreservingCenter() in
        ever-leaf-image-renderer.js. This is NOT a return to the old
        scale-pivot + mask-cover-position-clamp architecture that caused
        the original drift bug: Center and Scale remain independently
        clamped, and pivot math only ever runs after Scale has already
        genuinely changed. The [data-ever-leaf-pivot-marker] overlay
        remains a calibration-only visual aid (never customer-facing) and
        still defaults `hidden` in its own markup with no reveal toggle
        wired up — restoring the pivot MATH did not require restoring that
        marker UI.
      */

      /*
        MASTER REBUILD — GALLERY <-> VISUALIZER state machine (Section 11).
        Toggles one class on the custom Ever Leaf Product Section's own
        root (`.ever-leaf-product`, replacing the old Dawn `.product`
        lookup now that the Dawn-based transitional layout is retired) so
        CSS alone can swap the custom Gallery for the personalizer canvas
        in the same visual slot with no layout jump, and hide the "Add
        Your Image" CTA in favor of the compact upload-success control.
        Purely a visual state flag — never touches uploadStatus/
        customerFile/any purchase-relevant state, and never affects Add
        to Cart/Buy Now gating (still driven entirely by the existing
        photo-required gate).
      */
      var galleryStateRoot = el.closest('.ever-leaf-product');
      function syncGalleryVisualizerState(s) {
        customerControls.setUploadStatus(s.uploadStatus, s.uploadError, s.customerFile);
        if (galleryStateRoot) {
          galleryStateRoot.classList.toggle('ever-leaf-has-photo', !!s.customerFile);
        }
      }
      window.EverLeafPersonalizerState.subscribe(syncGalleryVisualizerState);
      /*
        THEME EDITOR BUGFIX (Visualizer-must-not-fall-back-to-Gallery brief,
        Section 2/6) — root cause: window.EverLeafPersonalizerState is a
        singleton on `window` (guarded at the top of this file specifically
        so it survives re-execution) and DOES survive a Shopify Theme
        Editor section reload, confirmed via direct reproduction using the
        real Section Rendering API + script re-execution. What did NOT
        survive is this DOM's visual sync: `subscribe()` above only fires
        on the NEXT state.update() call, never retroactively against
        whatever the state ALREADY is at the moment a fresh
        <ever-leaf-personalizer> connects (via connectedCallback -> init).
        On an ordinary first page load this is invisible (state starts
        empty, matching the server-rendered default), but on a section
        reload the surviving state can already be non-empty while the
        fresh Liquid-rendered DOM has no way to know that — leaving Gallery
        visible / Visualizer hidden even though a real photo is still
        loaded. This single explicit sync call (using the state as it
        exists RIGHT NOW, not a future update) is the fix — it runs on
        every init() regardless of Theme Editor vs. real storefront, and
        is a pure no-op on a normal first load (customerFile is already
        null, so toggling 'ever-leaf-has-photo' to false matches the
        existing default). No new state/cache system needed: the existing
        singleton already carries everything (photoX/Y/scale, dedication
        text, uploadStatus) across the reload.
      */
      syncGalleryVisualizerState(window.EverLeafPersonalizerState.get());

      /*
        PASS 3A FINAL CONSOLIDATED UI CORRECTION, Section 5-10 — Custom
        Pivot red marker. A merchant calibration aid only (Section 6 —
        never customer-facing): gated on ALL of window.Shopify.designMode
        (defense in depth — the marker's own markup is additionally wrapped
        in {% if request.design_mode %} in
        snippets/ever-leaf-product-visualizer.liquid, so it is genuinely
        absent from the DOM on the real storefront, not merely hidden),
        Visualizer active (s.customerFile truthy — the SAME flag
        syncGalleryVisualizerState() above already uses to toggle Gallery
        <-> Visualizer), and zoomPivot.mode === 'custom_pivot'.

        Coordinate accuracy (Section 8 — "do NOT approximate... use the
        real coordinate chain"): reuses lampScene.mapTransformToScene()
        (the SAME affine mapping the photo transform and Dedication text
        already go through — see sceneTextLayerSettings()'s own comment)
        to go Personalization State Space -> Scene Space, then
        lampScene.mapPersonalizationPointToCameraNormalized()'s own second
        leg -> Camera A-normalized (0..1 within the cropped view). Camera-
        normalized 0..1 IS exactly a CSS left/top percentage against
        canvas-wrap, since applyCamera() crops+scales that same camera rect
        to fill the canvas 1:1 — no separate DOM/pixel measurement needed,
        and it stays correctly aligned across any resize for free (pure
        CSS percentage, recomputed only when the pivot value itself
        changes, never on resize).
      */
      var pivotMarkerEl = el.querySelector('[data-ever-leaf-pivot-marker]');
      function updatePivotMarker(s) {
        if (!pivotMarkerEl) return;
        var isDesignMode = !!(window.Shopify && window.Shopify.designMode);
        var pivot = s.zoomPivot || {};
        var visible = isDesignMode && !!s.customerFile && pivot.mode === 'custom_pivot';
        if (!visible) {
          pivotMarkerEl.hidden = true;
          return;
        }
        // Section 48 — must track the SAME effective camera the rendered
        // scene actually uses (including any Close-Up Vertical Offset),
        // never the frozen base preset, or the marker would visibly drift
        // away from the real pivot point the instant a merchant tunes the
        // offset.
        var camPoint = lampScene.mapPersonalizationPointToCameraNormalized(pivot.x, pivot.y, getCurrentViewport());
        pivotMarkerEl.style.left = camPoint.x * 100 + '%';
        pivotMarkerEl.style.top = camPoint.y * 100 + '%';
        pivotMarkerEl.hidden = false;
      }
      window.EverLeafPersonalizerState.subscribe(updatePivotMarker);
      updatePivotMarker(window.EverLeafPersonalizerState.get());

      /*
        PASS 3A FINAL UX PASS — Photo Interaction Bounding Box (Section
        31-50 of the brief). Unlike the Pivot Marker above, this is
        CUSTOMER-FACING (never gated on window.Shopify.designMode) and
        shows on the real storefront while the customer manipulates their
        uploaded photo. Two independent concerns, deliberately kept
        separate (Section 51 — "must not conflict, use clear separate
        classes/state"):

        1. GEOMETRY (updatePhotoBoundingBoxGeometry) — subscribed to EVERY
           state update, matching updatePivotMarker's own established
           convention (cheap arithmetic + a few style writes; no photo
           reprocessing, no mask/canvas measurement — Section 47). Uses the
           real photoCenterX/photoCenterY/photoScale/photoAspect ->
           Personalization State Space rect -> Scene Space -> effective
           Camera A pipeline (lampScene.computePhotoBoundingBoxCameraNormalized(),
           reusing the SAME point-mapping the Pivot Marker uses and the
           SAME "scale" mapping leg Dedication's font-size ratios use — see
           that function's own header). `el.hidden` here is the STRUCTURAL
           on/off (Section 50: no photo at all -> never show, regardless of
           interaction) — separate from the `--visible` class below, which
           is the INTERACTION-driven fade in/out.
        2. ACTIVITY (notePhotoInteractionActivity/schedulePhotoInteractionHide)
           — called explicitly from the exact gesture entry/exit points
           Section 38 lists (drag, pinch, wheel, Move/Zoom buttons and
           press-and-hold, Reset) — never from a generic "state changed"
           hook, so an unrelated state write (Dedication typing, initial
           upload's default-fit, a Theme Editor calibration tweak) can never
           spuriously flash the box (Section 50, Section 57 — "Photo bounds
           are unrelated to Dedication").
      */
      var boundingBoxEl = el.querySelector('[data-ever-leaf-photo-bounding-box]');
      var boundingBoxHideTimer = null;
      function updatePhotoBoundingBoxGeometry(s) {
        if (!boundingBoxEl) return;
        if (!s.customerFile || s.photoCenterX == null || s.photoCenterY == null || s.photoScale == null) {
          boundingBoxEl.hidden = true;
          return;
        }
        var rect = lampScene.computePhotoBoundingBoxCameraNormalized(
          s.photoCenterX,
          s.photoCenterY,
          s.photoScale,
          s.photoAspect || 1,
          getCurrentViewport()
        );
        boundingBoxEl.style.left = rect.left * 100 + '%';
        boundingBoxEl.style.top = rect.top * 100 + '%';
        boundingBoxEl.style.width = rect.width * 100 + '%';
        boundingBoxEl.style.height = rect.height * 100 + '%';
        boundingBoxEl.hidden = false;
      }
      window.EverLeafPersonalizerState.subscribe(updatePhotoBoundingBoxGeometry);
      updatePhotoBoundingBoxGeometry(window.EverLeafPersonalizerState.get());

      /*
        PHOTO OUTLINE TIMING MICRO-PASS — Photo Outline Color/Fade-In/Pause/
        Fade-Out calibration, all three timing phases now independently
        merchant-configurable. Pure CSS custom-property writes — never
        touches Personalization State Space, never reprocesses the photo,
        never re-renders the canvas. Subscribed the same way
        updatePhotoBoundingBoxGeometry is, so a Theme Editor
        color/speed/pause change updates the NEXT interaction immediately,
        no Save required. Geometry and activation triggers remain untouched
        this pass (narrow scope).
      */
      function updatePhotoBoundingBoxAppearance(s) {
        if (!boundingBoxEl) return;
        // WAVE 3C — blank = Theme: only set the inline custom property when
        // the merchant supplied a real override; otherwise remove it so the
        // CSS var(--ever-leaf-photo-outline-color, var(--el-control-surface))
        // fallback resolves live from the Product semantic layer instead of
        // a hardcoded literal (Clear/Reset in Theme Editor then genuinely
        // reaches the theme color, matching every other migrated setting).
        if (typeof s.photoOutlineColor === 'string' && s.photoOutlineColor) {
          boundingBoxEl.style.setProperty('--ever-leaf-photo-outline-color', s.photoOutlineColor);
        } else {
          boundingBoxEl.style.removeProperty('--ever-leaf-photo-outline-color');
        }
        var fadeInMs = typeof s.photoOutlineFadeInMs === 'number' && isFinite(s.photoOutlineFadeInMs) ? s.photoOutlineFadeInMs : 100;
        var fadeOutMs = typeof s.photoOutlineFadeOutMs === 'number' && isFinite(s.photoOutlineFadeOutMs) ? s.photoOutlineFadeOutMs : 220;
        boundingBoxEl.style.setProperty('--ever-leaf-photo-outline-fade-in-ms', fadeInMs + 'ms');
        boundingBoxEl.style.setProperty('--ever-leaf-photo-outline-fade-out-ms', fadeOutMs + 'ms');
      }
      window.EverLeafPersonalizerState.subscribe(updatePhotoBoundingBoxAppearance);
      updatePhotoBoundingBoxAppearance(window.EverLeafPersonalizerState.get());

      // Pause Before Fade-Out — the merchant-configured value, read LIVE
      // (never cached at module-load time — same "live read, never cache"
      // convention getImageLimits()/getMaxCharsForLine1() already use)
      // every time a hide is actually scheduled, so a Theme Editor change
      // takes effect on the very next interaction with no reload needed.
      // Falls back to the original hardcoded 400ms if config is somehow
      // missing, preserving the exact prior default.
      function getPhotoOutlinePauseMs() {
        var s = window.EverLeafPersonalizerState.get();
        return typeof s.photoOutlinePauseMs === 'number' && isFinite(s.photoOutlinePauseMs) ? s.photoOutlinePauseMs : 400;
      }

      // Section 39/42 — re-adding this class while a fade-out is already in
      // progress (CSS opacity transition) simply reverses it smoothly; no
      // special-case "reactivation" code needed. Section 50/52 — a no-op
      // whenever the box is structurally hidden (no photo), so an activity
      // call arriving right as a photo is removed can never force it back
      // on.
      function notePhotoInteractionActivity() {
        if (!boundingBoxEl || boundingBoxEl.hidden) return;
        clearTimeout(boundingBoxHideTimer);
        boundingBoxEl.classList.add('ever-leaf-photo-bounding-box--visible');
      }
      // Section 40/43/44/46 — called on every relevant gesture tick
      // (dragged pointermove, wheel tick, hold-repeat tick) ALONGSIDE
      // notePhotoInteractionActivity(), so the timer keeps getting reset for
      // as long as activity continues; it only ever actually fires (fading
      // the box out) the CURRENTLY CONFIGURED Pause duration after the LAST
      // such call.
      function schedulePhotoInteractionHide() {
        if (!boundingBoxEl) return;
        clearTimeout(boundingBoxHideTimer);
        boundingBoxHideTimer = setTimeout(function () {
          boundingBoxEl.classList.remove('ever-leaf-photo-bounding-box--visible');
        }, getPhotoOutlinePauseMs());
      }

      wirePhotoInteraction(el, canvas, {
        onInteractionEnd: onInteractionEnd,
        // PASS 3C, Section 45/46 — gates canvas drag/pinch/wheel start the
        // same way the Move/Zoom/Reset buttons are gated (see
        // customerControls.setEditingEnabled()); read live, never cached,
        // so it reflects the CURRENT transition/view state at the moment
        // a gesture actually begins.
        isEditingEnabled: function () {
          return editingEnabled;
        },
        // PASS 3A FINAL UX PASS, Section 38/43/44 — drag/pinch (pointerdown/
        // pointermove/releasePointer) and wheel all live inside
        // wirePhotoInteraction() itself, so these two are passed as hooks
        // rather than duplicated there.
        notePhotoInteractionActivity: notePhotoInteractionActivity,
        schedulePhotoInteractionHide: schedulePhotoInteractionHide,
        isVerbose: function () {
          return verboseLogging;
        },
        // MOBILE INTERACTION CUMULATIVE pass, Section 9-11 — read live
        // (not cached at init time) so a Theme Editor setting change that
        // triggers a section reload always reflects the CURRENT attribute;
        // also matches this file's existing convention of never caching a
        // DOM read that could change across a reload.
        getMobileDragMode: function () {
          return el.getAttribute('data-mobile-drag-mode') === 'two_fingers' ? 'two_fingers' : 'one_finger';
        },
        applyClampedCenterTransform: applyClampedCenterTransform,
        getCurrentTransform: function () {
          return Object.assign({}, renderer.DEFAULT_PHOTO_TRANSFORM_CENTER, currentCenterTransformFromState());
        },
        // Pass 3A interaction-model correction — pinch's scale component
        // needs a plain clamp (no center involvement) to compute its
        // "basis" scale before adding the independent move delta; this is
        // the ONLY reason wirePhotoInteraction needs a scale-only clamp
        // hook at all (drag/wheel/arrows go through
        // applyClampedCenterTransform directly).
        clampScale: function (requestedScale) {
          return renderer.clampScaleValue(requestedScale, getImageLimits());
        },
        // Pass 3A targeted correction — single-request zoom entry points
        // (mouse wheel) go straight through the canonical applyZoom() so
        // the clamp-first/complete-no-op/pivot-branch ordering lives in
        // exactly one place.
        applyZoom: applyZoom,
        // Pinch is a CONTINUOUS gesture resolved every touchmove from a
        // frozen startTransform basis (see wirePhotoInteraction's own pinch
        // comment) — it can't call applyZoom() directly (that reads the
        // currently-COMMITTED transform, not the frozen pinch basis), so it
        // needs the pivot resolution as its own hook: given the frozen old
        // Center/Scale and the newly-clamped basis Scale, returns the
        // Center pivot math would produce (identical to the OLD center,
        // untouched, in 'image_center' mode — same branch applyZoom() takes
        // — or the pivot-preserving Center in 'custom_pivot' mode). The
        // caller then adds its own independent pan delta on top, exactly
        // mirroring applyZoom()'s "pivot math, then commit" structure.
        resolvePivotCenter: function (oldCenterX, oldCenterY, oldScale, newScale) {
          var pivot = getZoomPivot();
          if (pivot.mode !== 'custom_pivot') {
            return { centerX: oldCenterX, centerY: oldCenterY };
          }
          return renderer.computePivotPreservingCenter(oldCenterX, oldCenterY, oldScale, newScale, pivot.x, pivot.y);
        }
      });

      onWindowResizeForCanvas = debounce(function () {
        ensureInteractiveContext();
      }, 200);
      window.addEventListener('resize', onWindowResizeForCanvas);

      hideOldPocPanelByDefault(el);
      // The DEV CART COMMIT panel now renders OUTSIDE this element (Phase 4
      // Correction Pass 3, Section 21-23 — see
      // snippets/ever-leaf-personalizer-dev-panels.liquid, rendered after
      // the purchase block), so it's found via document, not el.
      wireCartCommitFlow(document.querySelector('[data-ever-leaf-cart-debug]'));

      // PASS 3C, Section 73 — Shopify's Theme Editor can destroy and
      // recreate this element's DOM on a section reload; the returned
      // cleanup cancels any in-flight transition RAF so it can never keep
      // running (and rendering into an offscreen/detached canvas) after
      // disconnect. See customElements.define()'s disconnectedCallback
      // below, the only caller.
      return function destroy() {
        if (transitionRafId) {
          cancelAnimationFrame(transitionRafId);
          transitionRafId = null;
        }
        // PHASE 46A.1 — without this, every Theme Editor section reload
        // (disconnect + reconnect) left the old Interaction Hints
        // subscriber registered on the persistent state singleton forever,
        // accumulating one extra stale listener per reload.
        if (interactionHintsUnsubscribe) {
          interactionHintsUnsubscribe();
          interactionHintsUnsubscribe = null;
        }
        // PHASE 47A — a pending first-appear delay timer must never survive
        // its own element being disconnected (a stale reveal on a detached
        // DOM node is harmless visually, but a leaked timer is still a
        // leaked timer across repeated Theme Editor reloads).
        cancelHintsFirstAppearTimer();
        cancelViewToggleFirstAppearTimer();
        if (viewToggleUnsubscribe) {
          viewToggleUnsubscribe();
          viewToggleUnsubscribe = null;
        }
        // Materials/Color freeze fix — see the declaration comment above
        // (near viewToggleUnsubscribe) for the full root cause. Each of
        // these was previously left registered forever on every Theme
        // Editor section reload; the document-level variant `change`
        // listener specifically is what turned repeated Materials clicks
        // into a multiplying, synchronous full-render freeze.
        if (discreteViewStateUnsubscribe) {
          discreteViewStateUnsubscribe();
          discreteViewStateUnsubscribe = null;
        }
        if (breakpointQuery && onBreakpointChange) {
          if (breakpointQuery.removeEventListener) breakpointQuery.removeEventListener('change', onBreakpointChange); else if (breakpointQuery.removeListener) breakpointQuery.removeListener(onBreakpointChange); // eslint-disable-line no-restricted-properties
          breakpointQuery = null;
          onBreakpointChange = null;
        }
        if (onDocumentVariantChange) {
          document.removeEventListener('change', onDocumentVariantChange);
          onDocumentVariantChange = null;
        }
        if (onWindowResizeForCanvas) {
          window.removeEventListener('resize', onWindowResizeForCanvas);
          onWindowResizeForCanvas = null;
        }
      };
    }

    return { init: init };
  })();

  var addToCartGatingWired = false;

  /*
    ALL SEVEN Shopify line-item properties Ever Leaf owns (Correction Pass —
    "Real Shopify Line Item Properties", Section 2/7/8): the public hosted-
    image link, Dedication text, the photo transform, the config version,
    and Ever Leaf's own private technical copy of the hosted URL. Mini/
    FilePond's ONLY responsibility is upload/storage — it never creates or
    touches any of these (see assets/ever-leaf-mini-upload-adapter.js's
    header comment).
  */
  var LINE_ITEM_PROPERTY_NAMES = {
    upload: 'Upload 1',
    text: 'Dedication',
    photoX: '_ever_leaf_photo_x',
    photoY: '_ever_leaf_photo_y',
    photoScale: '_ever_leaf_photo_scale',
    configVersion: '_ever_leaf_config_version',
    uploadUrl: '_ever_leaf_upload_url',
    // PASS 3A FINAL UX PASS, Section 13-14 — the customer's chosen two-line
    // layout preset changes the customer's INTENDED final personalization
    // (unlike pure Camera/View calibration), so the order must retain it.
    // Private (underscore-prefixed) — never a public/customer-visible
    // property, and never splits or replaces the single public `Dedication`
    // property above. Eighth canonical property (Section 14 — an
    // intentional, documented expansion from seven to eight).
    dedicationLayout: '_ever_leaf_dedication_layout'
  };
  var CONFIG_VERSION = '1';

  /*
    STANDALONE DEV END-TO-END PROPERTY AUDIT pass — exact root cause of the
    duplicate "Upload 1" reaching Buy Now's real Checkout: this removal
    step previously matched by EXACT string only
    (input[name="properties[Upload 1]"]), while an alias-aware normalizer
    (normalizePropertyInnerName, below) already existed in this same file
    — but was only ever wired to the 'formdata' event listener that
    protects ATC's own Dawn /cart/add request. Buy Now's accelerated
    checkout flow never dispatches a native 'submit'/'formdata' event on
    the form at all (it is not a form submission), so that fix could never
    protect it — a stray alias-variant input Mini creates elsewhere
    (different case/whitespace around "Upload 1", e.g. "properties[ Upload
    1 ]") would survive this exact-match removal entirely and sit on the
    form alongside our own canonical input, both carrying the current
    serverId — exactly the "two Upload 1 rows, same URL" symptom observed.
    Fixed: scan every properties[...] input and remove any whose
    NORMALIZED inner name matches, not just an exact string match — reusing
    the SAME normalizer already proven for ATC, so both commit paths now
    obey the identical invariant. Applies to all 7 canonical names
    uniformly (harmless for the other 6 — no known alias risk there, but
    consistent, and no different behavior for them since they have no
    aliases to begin with).
  */
  function removeExistingPropertyInputs(form, propertyName) {
    var targetNormalized = propertyName.trim().toLowerCase();
    var all = form.querySelectorAll('input[name^="properties["]');
    Array.prototype.forEach.call(all, function (el) {
      var inner = normalizePropertyInnerName(el.getAttribute('name') || '');
      if (inner === targetNormalized && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
  }

  function createPropertyInput(form, propertyName, value) {
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[' + propertyName + ']';
    input.value = value != null ? value : '';
    form.appendChild(input);
    return input;
  }

  /*
    Final Correction Pass, Section 6 — normalizes a raw `properties[...]`
    FormData/request key so whitespace/case variants (`properties[Upload 1]`,
    `properties[ Upload 1 ]`, `properties[upload 1]`, `properties[ upload 1 ]`)
    are all recognized as the SAME public upload property. Returns null if
    the key isn't a `properties[...]` key at all.
  */
  function normalizePropertyInnerName(fullKey) {
    var m = /^properties\[(.*)\]$/.exec(fullKey);
    if (!m) return null;
    return m[1].trim().toLowerCase();
  }
  function isUploadAliasKey(fullKey) {
    return normalizePropertyInnerName(fullKey) === LINE_ITEM_PROPERTY_NAMES.upload.toLowerCase();
  }

  /*
    Section 4 — full, unredacted property dump (name AND value, both
    JSON-stringified so leading/trailing whitespace or unusual characters in
    EITHER stay visible rather than silently trimmed by display) from a
    FormData instance. Used at every checkpoint (before resubmit, actual
    request, cart.js) so a mystery 8th property can never hide behind a
    count-only readout again.
  */
  function dumpAllPropertiesFromFormData(fd) {
    var lines = [];
    var entries = typeof fd.entries === 'function' ? Array.from(fd.entries()) : [];
    entries.forEach(function (pair) {
      if (String(pair[0]).indexOf('properties[') === 0) {
        lines.push(JSON.stringify(pair[0]) + ' = ' + JSON.stringify(pair[1]));
      }
    });
    return lines;
  }
  function dumpAllPropertiesFromObject(props) {
    return Object.keys(props || {}).map(function (key) {
      return JSON.stringify(key) + ' = ' + JSON.stringify(props[key]);
    });
  }

  /*
    THE fix for "cart.js showed properties: {}" — Section 6: do NOT rely on
    long-lived inputs created once at page-load/edit-time (the previous
    architecture). Immediately before the approved resubmit, this removes
    ANY existing input for each of our 7 property names (a stale input from
    an earlier commit attempt, a leftover from a previous architecture,
    anything) and creates exactly ONE fresh input per name, directly on the
    VERIFIED-real product form, with the CURRENT PersonalizerState + the
    CURRENT item.serverId. This is the sole source of truth for every
    property Shopify receives — commit-time canonicalization, not
    continuous background sync. Returns the values written, for the DEV
    panel.
  */
  function canonicalizeLineItemProperties(form, hostedUrl) {
    var s = window.EverLeafPersonalizerState.get();
    // Pass 3A interaction-model correction — the cart contract is
    // untouched: `_ever_leaf_photo_x/y` remain the legacy TOP-LEFT
    // convention, never Center. Canonical stored state is now Center, so
    // it's derived here via the same renderer.centerToTopLeft() adapter
    // used for rendering (see legacyTopLeftFromState() in init() — this
    // function lives outside init()'s closure, so it re-derives the same
    // way rather than sharing that helper directly).
    var renderer = window.EverLeafImageRenderer;
    var legacyXY = { x: null, y: null };
    if (s.photoCenterX != null && s.photoCenterY != null && s.photoScale != null && renderer) {
      legacyXY = renderer.centerToTopLeft(s.photoCenterX, s.photoCenterY, s.photoScale, s.photoAspect || 1);
    }
    var values = {};
    values[LINE_ITEM_PROPERTY_NAMES.upload] = hostedUrl || '';
    values[LINE_ITEM_PROPERTY_NAMES.text] = s.text || '';
    values[LINE_ITEM_PROPERTY_NAMES.photoX] = legacyXY.x != null ? legacyXY.x.toFixed(4) : '';
    values[LINE_ITEM_PROPERTY_NAMES.photoY] = legacyXY.y != null ? legacyXY.y.toFixed(4) : '';
    values[LINE_ITEM_PROPERTY_NAMES.photoScale] = s.photoScale != null ? s.photoScale.toFixed(4) : '';
    values[LINE_ITEM_PROPERTY_NAMES.configVersion] = CONFIG_VERSION;
    values[LINE_ITEM_PROPERTY_NAMES.uploadUrl] = hostedUrl || '';
    // Section 15 — always serialized, even when Line 2 is empty (a
    // deterministic convention beats optional-property branching); the
    // layout is visually irrelevant in that case, so 'together' (the
    // documented default) is written regardless of what the customer last
    // clicked while Line 2 had content.
    values[LINE_ITEM_PROPERTY_NAMES.dedicationLayout] = layoutFromPlacement(s.dedicationPlacement);

    Object.keys(values).forEach(function (name) {
      removeExistingPropertyInputs(form, name);
    });
    Object.keys(values).forEach(function (name) {
      createPropertyInput(form, name, values[name]);
    });
    return values;
  }

  /*
    PRODUCT ANALYTICS — provider-neutral domain signal for a successful
    personalization commit (consumed by assets/ever-leaf-analytics.js).
    Called once per successful runCommit, after the stale-photo guard and
    canonicalizeLineItemProperties(), immediately before onCommitted(). The
    detail carries only booleans/categories derived from the same state the
    properties were just written from — never the text, photo, file or URL.
    Synchronous and wrapped: it can never throw into, delay or alter the
    Add to Cart / Buy Now continuation.
  */
  function emitPersonalizationCommitted(source, operationId) {
    try {
      var s = window.EverLeafPersonalizerState.get();
      document.dispatchEvent(
        new CustomEvent('ever-leaf:personalization-committed', {
          detail: {
            source: source,
            operationId: operationId,
            hasDedication: !!(s.text && String(s.text).trim()),
            dedicationLayout: layoutFromPlacement(s.dedicationPlacement)
          }
        })
      );
    } catch (e) {
      /* analytics signal only — never affects the commit */
    }
  }

  /*
    Section 11 — narrow, DEV-diagnostic-only observation of the ACTUAL
    outgoing Dawn /cart/add(.js) request payload, installed once page-wide.
    Never alters behavior: always calls through to the real fetch
    immediately; only inspects a FormData body destined for that endpoint.
  */
  var lastDawnCartAddRequest = null;
  var cartAddFetchObserverInstalled = false;
  function installCartAddFetchObserver() {
    if (cartAddFetchObserverInstalled) return;
    cartAddFetchObserverInstalled = true;
    if (typeof window.fetch !== 'function') return;
    var originalFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (url.indexOf('/cart/add') !== -1 && init && init.body instanceof FormData) {
          var props = {};
          var count = 0;
          var uploadAliasCount = 0;
          var entries = typeof init.body.entries === 'function' ? Array.from(init.body.entries()) : [];
          entries.forEach(function (pair) {
            var m = /^properties\[(.+)\]$/.exec(pair[0]);
            if (m) {
              props[m[1]] = pair[1];
              count++;
              if (isUploadAliasKey(pair[0])) uploadAliasCount++;
            }
          });
          lastDawnCartAddRequest = {
            url: url,
            properties: props,
            count: count,
            uploadAliasCount: uploadAliasCount,
            allProperties: dumpAllPropertiesFromFormData(init.body)
          };
        }
      } catch (e) {
        /* best-effort — never break the real request over a diagnostic */
      }
      return originalFetch.apply(this, arguments);
    };
  }

  /*
    Section 9/12 — cart.js is the ONLY acceptance-proof source (Section 20:
    never trust cart drawer DOM/UI). Fetched after a real cartUpdate.

    FIX (this pass): the previous version matched by variant_id alone,
    which is wrong the moment more than one personalized line of the SAME
    variant exists (exactly the case Test D creates on purpose) — it could
    report an unrelated, older personalization's line as if it were the
    current commit's. Now matches PRIMARILY by
    properties['_ever_leaf_upload_url'] === the CURRENT commit's own
    hostedUrl (unambiguous — every commit has its own distinct hosted URL),
    falling back to "last item with this variant_id" only if no exact
    hostedUrl match is found (e.g. hostedUrl unknown for some reason).
  */
  function reportCartJs(diag, refreshDiagnostics, variantId, expectedUploadUrl) {
    diag.cartJsCheckedThisAttempt = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var items = cart.items || [];
        var line = null;
        if (expectedUploadUrl) {
          for (var i = items.length - 1; i >= 0; i--) {
            var itemProps = items[i].properties || {};
            if (itemProps[LINE_ITEM_PROPERTY_NAMES.uploadUrl] === expectedUploadUrl) {
              line = items[i];
              break;
            }
          }
        }
        if (!line) {
          var matches = items.filter(function (item) {
            return String(item.variant_id) === String(variantId);
          });
          line = matches.length ? matches[matches.length - 1] : null;
        }
        var props = (line && line.properties) || {};
        diag.cartJsMatchedByUploadUrl = !!(line && expectedUploadUrl && props[LINE_ITEM_PROPERTY_NAMES.uploadUrl] === expectedUploadUrl);
        diag.cartJsPropertiesCount = Object.keys(props).filter(function (k) {
          return props[k] != null && props[k] !== '';
        }).length;
        diag.cartJsUpload1 = props[LINE_ITEM_PROPERTY_NAMES.upload] || '';
        diag.cartJsDedication = props[LINE_ITEM_PROPERTY_NAMES.text] || '';
        diag.cartJsPhotoX = props[LINE_ITEM_PROPERTY_NAMES.photoX] || '';
        diag.cartJsPhotoY = props[LINE_ITEM_PROPERTY_NAMES.photoY] || '';
        diag.cartJsPhotoScale = props[LINE_ITEM_PROPERTY_NAMES.photoScale] || '';
        diag.cartJsConfigVersion = props[LINE_ITEM_PROPERTY_NAMES.configVersion] || '';
        diag.cartJsUploadUrl = props[LINE_ITEM_PROPERTY_NAMES.uploadUrl] || '';
        diag.cartJsLineKey = (line && line.key) || '';
        diag.cartJsLineQuantity = line ? line.quantity : null;
        diag.cartJsAllProperties = dumpAllPropertiesFromObject(props);
        refreshDiagnostics();
      })
      .catch(function () {
        /* best-effort diagnostic only */
      });
  }

  /*
    THE final-commit boundary (Phase 4 correction pass 2, Sections 7-16).
    Wires exactly two things, once per page:

    1. Add to Cart — a capturing-phase 'submit' listener registered on
       DOCUMENT (not on the form itself). This is the actual fix for
       Correction Pass 2's Task B: a listener registered directly on `form`
       fires in the same AT_TARGET phase as Dawn's own product-form.js
       listener (also registered directly on the form), and same-element
       listeners run in REGISTRATION ORDER regardless of capture/bubble —
       so whichever of the two scripts happened to load/register first would
       win, non-deterministically. A capturing listener on DOCUMENT always
       runs before ANY listener on a descendant (the form), matching the
       exact technique ever-leaf-terms-consent.js already uses for the same
       reason. The user's actual click is intercepted, Mini finalize runs,
       and only on success is the SAME form resubmitted programmatically
       (form.requestSubmit(addButton) — WITH the original button as explicit
       submitter, see Section 13 below) with a one-shot "already committed"
       flag so this listener steps aside and lets Dawn's own product-form.js
       handle the real AJAX add-to-cart exactly as it always has (step 9 —
       "submit the existing Shopify product form normally"). Never
       reimplements cart AJAX.

       Section 13 (explicit submitter): form.requestSubmit() with NO argument
       produces a SubmitEvent with submitter === null. Dawn's own
       onSubmitHandler (assets/product-form.js) never reads event.submitter
       at all — it only checks `this.submitButton.getAttribute('aria-disabled')`
       — so a missing submitter does not break Dawn's flow. It DOES matter for
       ever-leaf-terms-consent.js's OWN gating (see that file's Defense 1,
       fixed in this same pass to key off event.submitter): passing the real
       addButton as submitter is the more correct/explicit choice and costs
       nothing, so it's used here even though Dawn itself doesn't require it.

    2. Buy Now — the exact same interception pattern, generalized to a
       capturing-phase 'click' listener on the [data-ever-leaf-consent-gate]
       wrapper around {{ form | payment_button }}. A capturing-phase
       stopImmediatePropagation on an ancestor halts the event before it
       ever reaches the wallet button's own click handler, so this is a
       real, retryable interception rather than the "can't intercept the
       wallet button" workaround considered earlier — on success the ORIGINAL
       clicked element is re-clicked programmatically (same one-shot-flag
       technique) so Shopify's own accelerated checkout proceeds unmodified.
       UNVERIFIED without a live browser: this assumes payment_button's own
       navigation is driven by a real bubble-phase 'click' handler on (or
       under) the gate element rather than 'pointerdown' or some other event
       — flagged for live Dev confirmation.

    Add to Cart depends ONLY on a photo existing; Buy Now depends on BOTH a
    photo existing AND the existing terms checkbox (Section: reuse, never
    duplicate) being checked — the strict State A/B/C gating table.

    `diagnosticsEl`, when provided, is the DEV CART COMMIT panel's <pre> —
    internal-only, never shown to customers (Section 8/27).
  */
  var PHOTO_BLOCKED_MARKER = 'data-ever-leaf-photo-blocked';

  function wireCartCommitFlow(diagnosticsEl) {
    if (addToCartGatingWired) return;
    installCartAddFetchObserver();

    /*
      Section 14-16 (this pass) — ROBUST copy diagnostics. Fixes the real
      observed failure: after interacting with Buy Now/accelerated
      checkout, focus can be left inside an iframe/shadow host, and both the
      Clipboard API and execCommand('copy') can silently fail when the
      document/target element isn't genuinely focused. Three tiers, in
      order, exactly per spec:
        A. Clipboard API (navigator.clipboard.writeText) — button first
           reclaims focus onto itself (a plain, always-available capability
           regardless of what an iframe/shadow host currently holds), since
           the API can reject on an unfocused document.
        B. Hidden off-screen textarea + document.execCommand('copy') — same
           focus-reclaim reasoning; readonly to avoid mobile keyboard popups.
        C. Manual fallback — a VISIBLE, selectable, readonly textarea with
           an explicit "select and press Ctrl+C" message, so the user is
           NEVER left needing to reconstruct diagnostics from a screenshot.
      Never mutates diagnostics values themselves — only ever reads
      diagnosticsEl.textContent fresh at click time. Reports its own outcome
      via diag.copyMethod/copySuccess/copyError (Section 16) — internal only.
    */
    var copyButton = document.querySelector('[data-ever-leaf-cart-debug-copy]');
    var copyFallbackMessage = document.querySelector('[data-ever-leaf-cart-debug-copy-message]');
    var copyFallbackTextarea = document.querySelector('[data-ever-leaf-cart-debug-copy-fallback]');

    function showManualCopyFallback(text) {
      if (!copyFallbackTextarea || !copyFallbackMessage) return;
      copyFallbackTextarea.value = text;
      copyFallbackTextarea.hidden = false;
      copyFallbackMessage.hidden = false;
      copyFallbackMessage.textContent = 'Copy failed — select the text below and press Ctrl+C';
      try {
        copyFallbackTextarea.focus();
        copyFallbackTextarea.select();
      } catch (e) {
        /* best-effort — the field is still visible and manually selectable either way */
      }
    }
    function hideManualCopyFallback() {
      if (copyFallbackTextarea) copyFallbackTextarea.hidden = true;
      if (copyFallbackMessage) copyFallbackMessage.hidden = true;
    }

    if (copyButton && diagnosticsEl) {
      var copyResetTimer = null;
      copyButton.addEventListener('click', function () {
        var text = diagnosticsEl.textContent || '';
        hideManualCopyFallback();

        function showResult(label, method, success, error) {
          diag.copyMethod = method;
          diag.copySuccess = success;
          diag.copyError = error || '';
          refreshDiagnostics();
          clearTimeout(copyResetTimer);
          var original = 'Copy diagnostics';
          copyButton.textContent = label;
          copyResetTimer = setTimeout(function () {
            copyButton.textContent = original;
          }, 1500);
        }

        function tryExecCommandFallback() {
          try {
            var textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.readOnly = true;
            textarea.style.position = 'fixed';
            textarea.style.top = '0';
            textarea.style.left = '0';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            // Reclaim focus into OUR OWN document/element first — a stuck
            // Buy Now iframe/shadow focus is exactly what breaks this
            // without it (Section 14's observed bug).
            textarea.focus();
            textarea.select();
            var ok = !!(document.execCommand && document.execCommand('copy'));
            document.body.removeChild(textarea);
            if (ok) {
              showResult('Copied!', 'execCommand', true, '');
            } else {
              showManualCopyFallback(text);
              showResult('Copy failed', 'manual-fallback', false, 'execCommand returned false');
            }
          } catch (e) {
            showManualCopyFallback(text);
            showResult('Copy failed', 'manual-fallback', false, (e && e.message) || 'execCommand threw');
          }
        }

        // Reclaim focus onto the button itself before attempting either
        // automated method — always safe/available even if the page's
        // active element is currently an iframe or a shadow host.
        try {
          copyButton.focus();
        } catch (e) {
          /* best-effort */
        }

        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          navigator.clipboard.writeText(text).then(
            function () {
              showResult('Copied!', 'clipboard-api', true, '');
            },
            function (err) {
              tryExecCommandFallback();
              if (diag.copyMethod !== 'execCommand') {
                // tryExecCommandFallback already reported its own outcome;
                // this only fires if it somehow didn't (defensive).
                diag.copyError = diag.copyError || ((err && err.message) || 'clipboard write rejected');
              }
            }
          );
        } else {
          tryExecCommandFallback();
        }
      });
    }

    /*
      "Copy Buy Now report" — a SEPARATE button/mechanism from Copy
      Diagnostics above (that one is locked/PASS, untouched here). Copies
      only the Buy Now-relevant subset (gesture/commit/replay/accelerated
      checkout structure) instead of the entire DEV CART COMMIT dump, so a
      manual tester doesn't have to paste ATC/FormData/cart.js sections that
      aren't relevant to a Buy Now report. Built independently from current
      diag fields/state at click time — never touches diagnosticsEl or the
      other button's fallback UI.
    */
    var copyButtonBuyNow = document.querySelector('[data-ever-leaf-cart-debug-copy-buynow]');
    var copyBuyNowFallbackMessage = document.querySelector('[data-ever-leaf-cart-debug-copy-buynow-message]');
    var copyBuyNowFallbackTextarea = document.querySelector('[data-ever-leaf-cart-debug-copy-buynow-fallback]');

    function buildBuyNowReportText() {
      var s = window.EverLeafPersonalizerState.get();
      var termsOk = checkbox ? checkbox.checked : true;
      var pv = diag.propertyValues || {};
      function yn(v) {
        return v == null ? '(none yet)' : v ? 'yes' : 'no';
      }
      var isBuyNowAttempt = diag.currentAttemptSource === 'buy-now';
      var noShopifyRequestYetNote = isBuyNowAttempt
        ? 'note: Buy Now never submits through Dawn /cart/add — request/cartUpdate/cart.js below are ALWAYS "no" for a Buy Now attempt unless something has gone wrong; they are not evidence of a property failure.\n'
        : '';
      return (
        'EVER LEAF — BUY NOW REPORT (DEV, not customer-facing)\n' +
        '--- STATE ---\n' +
        'customerFile: ' + (s.customerFile ? 'yes' : 'no') + '\n' +
        'terms checked: ' + String(termsOk) + '\n' +
        'Buy Now blocked: ' + (gate ? String(gate.classList.contains('ever-leaf-photo-required-blocked')) : 'n/a') + '\n' +
        'Upload 1: ' + (pv[LINE_ITEM_PROPERTY_NAMES.upload] || '(none yet)') + '\n' +
        '--- CURRENT COMMIT ---\n' +
        'attempt id: ' + (diag.currentAttemptId || '(none yet)') + '\n' +
        'source: ' + (diag.currentAttemptSource || '(none yet)') + '\n' +
        'current serverId: ' + (diag.serverId || '(none yet)') + '\n' +
        'request observed this attempt: ' + yn(diag.dawnRequestObserved) + '\n' +
        'cartUpdate this attempt: ' + yn(diag.cartUpdateSeen) + '\n' +
        'cart.js checked this attempt: ' + yn(diag.cartJsCheckedThisAttempt) + '\n' +
        noShopifyRequestYetNote +
        '--- BUY NOW ---\n' +
        'buy now commit source: ' + (diag.buyNowCommitSource || '(none yet)') + '\n' +
        'runCommit entered: ' + yn(diag.buyNowRunCommitEntered) + '\n' +
        'final FormData ready: ' + yn(diag.buyNowFinalFormDataReady) + '\n' +
        'last Buy Now error: ' + (diag.buyNowLastError || '(none)') + '\n' +
        '--- BUY NOW GESTURE ---\n' +
        'pointerdown observed: ' + yn(diag.buyNowPointerdownObserved) + '\n' +
        'pointerdown target: ' + (diag.buyNowPointerdownTarget || '(none yet)') + '\n' +
        'pointerId: ' + (diag.buyNowPointerId == null ? '(none yet)' : diag.buyNowPointerId) + '\n' +
        'pointerType: ' + (diag.buyNowPointerType || '(none yet)') + '\n' +
        'pointerup observed: ' + yn(diag.buyNowPointerupObserved) + '\n' +
        'same pointer: ' + (diag.buyNowSamePointer == null ? '(none yet)' : yn(diag.buyNowSamePointer)) + '\n' +
        'movement px: ' + (diag.buyNowMovementPx == null ? '(none yet)' : diag.buyNowMovementPx) + '\n' +
        'pointercancel observed: ' + yn(diag.buyNowPointercancelObserved) + '\n' +
        'gesture accepted: ' + yn(diag.buyNowGestureAccepted) + '\n' +
        'gesture rejected reason: ' + (diag.buyNowGestureRejectedReason || '(none)') + '\n' +
        'button state at gesture acceptance (moment A): ' + formatButtonSnapshot(diag.buyNowGestureButtonState) + '\n' +
        '(the above is FROZEN for this attempt — a later activation while committing is recorded separately below, never overwriting it)\n' +
        'secondary activation observed: ' + yn(diag.buyNowSecondaryActivationObserved) + '\n' +
        'secondary activation event type: ' + (diag.buyNowSecondaryActivationEventType || '(none)') + '\n' +
        'secondary activation target: ' + (diag.buyNowSecondaryActivationTarget || '(none)') + '\n' +
        'secondary activation rejected reason: ' + (diag.buyNowSecondaryActivationRejectedReason || '(none)') + '\n' +
        '--- BUY NOW REPLAY ---\n' +
        'Mini PROCESSING_COMPLETE: ' + yn(diag.buyNowMiniProcessingComplete) + '\n' +
        'seven properties ready: ' + yn(diag.buyNowSevenPropertiesReady) + '\n' +
        'current real button re-resolved: ' + yn(diag.buyNowButtonReResolved) + '\n' +
        'native replay .click() invoked: ' + yn(diag.buyNowReplayClickInvoked) + '\n' +
        'approved replay observed: ' + yn(diag.buyNowApprovedContinuationInvoked) + '\n' +
        'Ever Leaf stepped aside: ' + yn(diag.buyNowOriginalActionInvoked) + '\n' +
        'navigation started (pagehide, best-effort): ' + (diag.buyNowNavigationStarted == null ? '(unknown/pending)' : yn(diag.buyNowNavigationStarted)) + '\n' +
        '--- BUY NOW EXECUTION PATH ---\n' +
        formatBuyNowExecutionPathText(diag.buyNowExecutionEnvironment) + '\n' +
        '--- BUY NOW CANONICAL PROPERTIES ---\n' +
        formatBuyNowPropertyAuditText(diag.buyNowPropertyAudit) + '\n' +
        '--- BUY NOW REPLAY TARGET ---\n' +
        'selector: ' + BUY_NOW_BUTTON_SELECTOR + '\n' +
        'raw selector match count: ' + (diag.buyNowReplayRawSelectorMatchCount == null ? '(none yet)' : diag.buyNowReplayRawSelectorMatchCount) + '\n' +
        'attempts used (of ' + BUY_NOW_REPLAY_RETRY_MAX_ATTEMPTS + '): ' + (diag.buyNowReplayAttemptsUsed == null ? '(none yet)' : diag.buyNowReplayAttemptsUsed) + '\n' +
        'original pointerdown button connected: ' + (diag.buyNowReplayOriginalConnected == null ? '(none yet)' : yn(diag.buyNowReplayOriginalConnected)) + '\n' +
        'fresh current button state (moment B): ' + formatButtonSnapshot(diag.buyNowReplayButtonState) + '\n' +
        'same node as original: ' + (diag.buyNowReplaySameNodeAsOriginal == null ? '(none yet)' : yn(diag.buyNowReplaySameNodeAsOriginal)) + '\n' +
        'uploadStatus: ' + s.uploadStatus + '\n' +
        'Ever Leaf gating active (before release): ' + (diag.buyNowReplayEverLeafGatingActive == null ? '(none yet)' : yn(diag.buyNowReplayEverLeafGatingActive)) + '\n' +
        'Ever Leaf gating temporarily released: ' + (diag.buyNowReplayGatingReleased == null ? '(none yet)' : yn(diag.buyNowReplayGatingReleased)) + '\n' +
        'Shopify disabled/blocking detected: ' + diag.buyNowReplayShopifyBlocking + '\n' +
        'resolver rejection reason: ' + (diag.buyNowReplayRejectionReason || '(none)') + '\n' +
        '--- CLICK METHOD IDENTITY ---\n' +
        'typeof button.click: ' + (diag.buyNowClickTypeofClick || '(none yet)') + '\n' +
        'has own click property: ' + (diag.buyNowClickHasOwnProperty == null ? '(none yet)' : yn(diag.buyNowClickHasOwnProperty)) + '\n' +
        'own property descriptor: ' + (diag.buyNowClickOwnDescriptor || '(none — inherited, no own property)') + '\n' +
        'button.click === HTMLElement.prototype.click: ' + (diag.buyNowClickIsHTMLElementPrototype == null ? '(none yet)' : yn(diag.buyNowClickIsHTMLElementPrototype)) + '\n' +
        'button.click === HTMLButtonElement.prototype.click: ' + (diag.buyNowClickIsHTMLButtonElementPrototype == null ? '(none yet)' : yn(diag.buyNowClickIsHTMLButtonElementPrototype)) + '\n' +
        'click implementation owner: ' + (diag.buyNowClickImplementationOwner || '(none yet)') + '\n' +
        'description: ' + (diag.buyNowClickDescription || '(none yet)') + '\n' +
        'current replay method: ' + (diag.buyNowReplayMethodUsed || '(none yet)') + '\n' +
        'native prototype fallback eligible: ' + (diag.buyNowNativePrototypeFallbackEligible == null ? '(none yet)' : yn(diag.buyNowNativePrototypeFallbackEligible)) + '\n' +
        'native prototype fallback tested: ' + yn(diag.buyNowNativePrototypeFallbackTested) + '\n' +
        'native prototype fallback click observed: ' + (diag.buyNowNativePrototypeFallbackClickObserved == null ? '(none yet)' : yn(diag.buyNowNativePrototypeFallbackClickObserved)) + '\n' +
        (diag.buyNowNativePrototypeFallbackTested
          ? 'prototype attempt — button click observed: ' + yn(diag.buyNowProtoReplayButtonClickObserved) + '\n' +
            'prototype attempt — gate capture observed: ' + yn(diag.buyNowProtoReplayGateCaptureObserved) + '\n' +
            'prototype attempt — gate bubble observed: ' + yn(diag.buyNowProtoReplayGateBubbleObserved) + '\n' +
            'prototype attempt — document capture observed: ' + yn(diag.buyNowProtoReplayDocumentCaptureObserved) + '\n' +
            'prototype attempt — document bubble observed: ' + yn(diag.buyNowProtoReplayDocumentBubbleObserved) + '\n' +
            'prototype attempt — defaultPrevented: ' + yn(diag.buyNowProtoReplayDefaultPrevented) + '\n'
          : '') +
        '--- EFFECTIVE BUTTON STATE ---\n' +
        'button.disabled: ' + (diag.buyNowDisabledProperty == null ? '(none yet)' : yn(diag.buyNowDisabledProperty)) + '\n' +
        "button.matches(':disabled'): " + (diag.buyNowMatchesDisabled == null ? '(none yet)' : yn(diag.buyNowMatchesDisabled)) + '\n' +
        "button.matches(':enabled'): " + (diag.buyNowMatchesEnabled == null ? '(none yet)' : yn(diag.buyNowMatchesEnabled)) + '\n' +
        'disabled fieldset ancestor: ' + (diag.buyNowDisabledFieldset || '(none)') + '\n' +
        'button.inert: ' + (diag.buyNowInertSelf == null ? '(none yet)' : yn(diag.buyNowInertSelf)) + '\n' +
        'inert ancestor: ' + (diag.buyNowInertAncestor || '(none)') + '\n' +
        'hidden ancestor: ' + (diag.buyNowHiddenAncestor || '(none)') + '\n' +
        'aria-hidden ancestor: ' + (diag.buyNowAriaHiddenAncestor || '(none)') + '\n' +
        'form id: ' + (diag.buyNowFormId || '(none yet)') + '\n' +
        'form.checkValidity(): ' + (diag.buyNowFormCheckValidity == null ? '(n/a — no form)' : yn(diag.buyNowFormCheckValidity)) + '\n' +
        'button.type: ' + (diag.buyNowButtonType || '(none yet)') + '\n' +
        'control native click observed: ' + (diag.buyNowControlButtonClickObserved == null ? '(none yet)' : yn(diag.buyNowControlButtonClickObserved)) + '\n' +
        'isolated clone native click observed: ' + (diag.buyNowCloneButtonClickObserved == null ? '(not tested)' : yn(diag.buyNowCloneButtonClickObserved)) + '\n' +
        'FINAL CONCLUSION: ' + (diag.buyNowFinalConclusion || '(not yet determined)') + '\n' +
        '--- SYNTHETIC CLICK DELIVERY (ordinary button.click() attempt) ---\n' +
        'approval before .click: ' + (diag.buyNowReplayApprovalBeforeClick == null ? '(none yet)' : yn(diag.buyNowReplayApprovalBeforeClick)) + '\n' +
        'button click observed: ' + (diag.buyNowReplayButtonClickObserved == null ? '(none yet)' : yn(diag.buyNowReplayButtonClickObserved)) + '\n' +
        'button event isTrusted: ' + (diag.buyNowReplayButtonEventIsTrusted == null ? '(none yet)' : yn(diag.buyNowReplayButtonEventIsTrusted)) + '\n' +
        'gate capture observed: ' + (diag.buyNowReplayGateCaptureObserved == null ? '(none yet)' : yn(diag.buyNowReplayGateCaptureObserved)) + '\n' +
        'gate bubble observed: ' + (diag.buyNowReplayGateBubbleObserved == null ? '(none yet)' : yn(diag.buyNowReplayGateBubbleObserved)) + '\n' +
        'document capture observed: ' + (diag.buyNowReplayDocumentCaptureObserved == null ? '(none yet)' : yn(diag.buyNowReplayDocumentCaptureObserved)) + '\n' +
        'document bubble observed: ' + (diag.buyNowReplayDocumentBubbleObserved == null ? '(none yet)' : yn(diag.buyNowReplayDocumentBubbleObserved)) + '\n' +
        'defaultPrevented: ' + (diag.buyNowReplayDefaultPrevented == null ? '(none yet)' : yn(diag.buyNowReplayDefaultPrevented)) + '\n' +
        'event.target: ' + (diag.buyNowReplayEventTarget || '(none yet)') + '\n' +
        'event.currentTarget (at button probe): ' + (diag.buyNowReplayEventCurrentTarget || '(none yet)') + '\n' +
        'event.eventPhase (at button probe): ' + (diag.buyNowReplayEventPhaseAtButton == null ? '(none yet)' : diag.buyNowReplayEventPhaseAtButton) + '\n' +
        'approval during event (read inside button probe): ' + (diag.buyNowReplayApprovalDuringEvent == null ? '(none yet)' : yn(diag.buyNowReplayApprovalDuringEvent)) + '\n' +
        'approval after .click returned: ' + (diag.buyNowReplayApprovalAfterClick == null ? '(none yet)' : yn(diag.buyNowReplayApprovalAfterClick)) + '\n' +
        'approved replay listener target: [data-ever-leaf-consent-gate] (capturing)\n' +
        'approved replay listener phase: capture\n' +
        'approved replay observed (listener recognized buyNowApproved): ' + yn(diag.buyNowApprovedContinuationInvoked) + '\n' +
        'Ever Leaf stepped aside: ' + yn(diag.buyNowOriginalActionInvoked) + '\n' +
        'Shopify button state after replay: ' + formatButtonSnapshot(inspectBuyNowButtonState(resolveRealBuyNowButton())) + '\n' +
        'navigation signal: ' + (diag.buyNowNavigationStarted == null ? '(unknown/pending)' : yn(diag.buyNowNavigationStarted)) + '\n' +
        '--- ACCELERATED CHECKOUT STRUCTURE ---\n' +
        acceleratedCheckoutStructureText()
      );
    }

    function showManualCopyFallbackBuyNow(text) {
      if (!copyBuyNowFallbackTextarea || !copyBuyNowFallbackMessage) return;
      copyBuyNowFallbackTextarea.value = text;
      copyBuyNowFallbackTextarea.hidden = false;
      copyBuyNowFallbackMessage.hidden = false;
      copyBuyNowFallbackMessage.textContent = 'Copy failed — select the text below and press Ctrl+C';
      try {
        copyBuyNowFallbackTextarea.focus();
        copyBuyNowFallbackTextarea.select();
      } catch (e) {
        /* best-effort */
      }
    }
    function hideManualCopyFallbackBuyNow() {
      if (copyBuyNowFallbackTextarea) copyBuyNowFallbackTextarea.hidden = true;
      if (copyBuyNowFallbackMessage) copyBuyNowFallbackMessage.hidden = true;
    }

    if (copyButtonBuyNow) {
      var copyBuyNowResetTimer = null;
      copyButtonBuyNow.addEventListener('click', function () {
        var text = buildBuyNowReportText();
        hideManualCopyFallbackBuyNow();

        function showResult(label) {
          clearTimeout(copyBuyNowResetTimer);
          var original = 'Copy Buy Now report';
          copyButtonBuyNow.textContent = label;
          copyBuyNowResetTimer = setTimeout(function () {
            copyButtonBuyNow.textContent = original;
          }, 1500);
        }

        function tryExecCommandFallback() {
          try {
            var textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.readOnly = true;
            textarea.style.position = 'fixed';
            textarea.style.top = '0';
            textarea.style.left = '0';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            var ok = !!(document.execCommand && document.execCommand('copy'));
            document.body.removeChild(textarea);
            if (ok) {
              showResult('Copied!');
            } else {
              showManualCopyFallbackBuyNow(text);
              showResult('Copy failed');
            }
          } catch (e) {
            showManualCopyFallbackBuyNow(text);
            showResult('Copy failed');
          }
        }

        try {
          copyButtonBuyNow.focus();
        } catch (e) {
          /* best-effort */
        }

        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          navigator.clipboard.writeText(text).then(
            function () {
              showResult('Copied!');
            },
            function () {
              tryExecCommandFallback();
            }
          );
        } else {
          tryExecCommandFallback();
        }
      });
    }

    /*
      "Copy last Buy Now report" / "Clear last Buy Now report" — PERSIST
      SUCCESSFUL DIAGNOSTICS ACROSS CHECKOUT NAVIGATION pass. Reads the
      sessionStorage snapshot written by persistBuyNowDiagnosticSnapshot()
      (see that function above) immediately before the last Buy Now
      handoff, so it can be inspected AFTER returning to the Product Page
      from Shopify Checkout in the same tab/session — the live in-memory
      `diag` used by "Copy Buy Now report" above does not survive that
      navigation. Independent mechanism/fallback UI from both copy buttons
      above; never touches diagnosticsEl.
    */
    /*
      DEV DIAGNOSTICS UI FIX pass — these two buttons now live in the DEV
      CHECKOUT DIAGNOSTICS panel (assets/ever-leaf-terms-consent.js's
      ensureConsentPanel(), the panel actually visible on the Product Page
      runtime), not in the liquid-rendered DEV CART COMMIT panel. That
      panel is created by an entirely separate `defer` script whose
      relative execution order vs. this one is not guaranteed, so the
      lookup below is resolved with a short, bounded
      requestAnimationFrame retry instead of a single synchronous
      querySelector — once the target panel exists (whichever script
      built it first), the buttons are found and wired exactly as before.
      No new click-handler logic — same handlers as always.
    */
    var copyButtonLastBuyNow = null;
    var clearButtonLastBuyNow = null;
    var copyLastBuyNowFallbackMessage = null;
    var copyLastBuyNowFallbackTextarea = null;

    function formatLastBuyNowReportText() {
      var raw;
      try {
        raw = window.sessionStorage.getItem(EVER_LEAF_BUY_NOW_STORAGE_KEY);
      } catch (e) {
        raw = null;
      }
      if (!raw) return null;

      var snap;
      try {
        snap = JSON.parse(raw);
      } catch (e) {
        return null;
      }
      if (!snap) return null;

      function yn(v) {
        return v == null ? '(unknown)' : v ? 'yes' : 'no';
      }
      function pf(v) {
        return v == null ? '(unknown)' : v ? 'PASS' : 'FAIL';
      }
      var env = snap.environment || {};
      var xp = snap.executionPath || {};
      var props = snap.properties;
      var img = snap.imageIdentity || {};
      var a = props && props.assertions ? props.assertions : {};
      var names = LINE_ITEM_PROPERTY_NAMES;
      var counts = props ? props.counts || {} : {};
      // The persisted snapshot deliberately holds only "is non-empty"
      // booleans, never property values (see buildBuyNowPersistedSnapshot).
      var present = props ? props.valuePresent || {} : {};
      function has(k) {
        return present[k] ? 'present' : '(none)';
      }

      var out = 'EVER LEAF — LAST BUY NOW REPORT\n';
      out += '--- ATTEMPT ---\n';
      out += 'timestamp: ' + (snap.timestamp || '(unknown)') + '\n';
      out += '--- ENVIRONMENT ---\n';
      out += 'liquid request.design_mode: ' + (env.liquidDesignMode == null ? '(unknown)' : yn(env.liquidDesignMode)) + '\n';
      out += 'Shopify.designMode: ' + (env.shopifyDesignMode == null ? '(unknown)' : yn(env.shopifyDesignMode)) + '\n';
      out += 'top-level: ' + (env.topLevel == null ? '(unknown)' : yn(env.topLevel)) + '\n';
      out += 'inside iframe: ' + (env.insideIframe == null ? '(unknown)' : yn(env.insideIframe)) + '\n';
      out += 'current URL at persist time: ' + (env.currentUrl || '(unknown)') + '\n';
      out += 'dev build marker: ' + (env.devBuildMarker || '(unknown)') + '\n';
      out += 'consent runtime version: ' + (env.consentRuntimeVersion || '(unknown)') + '\n';
      out += '--- BUY NOW EXECUTION PATH ---\n';
      out += 'original trusted gesture observed: ' + yn(xp.originalGestureObserved) + '\n';
      out += 'original event isTrusted: ' + yn(xp.originalEventIsTrusted) + '\n';
      out += 'Ever Leaf intercepted: ' + yn(xp.everLeafIntercepted) + '\n';
      out += 'runCommit("buy-now") executed: ' + yn(xp.runCommitExecuted) + '\n';
      out += 'Mini processing started: ' + yn(xp.miniProcessingStarted) + '\n';
      out += 'Mini processing completed: ' + yn(xp.miniProcessingCompleted) + '\n';
      out += 'authoritative serverId: ' + (xp.serverIdPresent ? 'present' : '(none)') + '\n';
      out += 'canonicalization executed: ' + yn(xp.canonicalizationExecuted) + '\n';
      out += 'Shopify continuation mechanism: ' + (xp.continuationMechanism || '(unknown)') + '\n';
      out += 'button.click replay attempted: ' + yn(xp.replayClickAttempted) + '\n';
      out += 'replay target: ' + (xp.replayTargetDescriptor || '(unknown)') + ', connected: ' + yn(xp.replayTargetConnected) + ', disabled: ' + yn(xp.replayTargetDisabled) + '\n';
      out += 'navigation signal observed: ' + yn(xp.navigationSignalObserved) + (xp.navigationSignalType ? ' (' + xp.navigationSignalType + ')' : '') + '\n';
      out += '--- CANONICAL PROPERTIES ---\n';
      if (props) {
        out += 'total canonical count: ' + props.totalCanonicalCount + '\n';
        out += names.upload + ': count=' + (counts.upload == null ? 0 : counts.upload) + ' value: ' + has('upload') + '\n';
        out += names.text + ': count=' + (counts.text == null ? 0 : counts.text) + ' value: ' + has('text') + '\n';
        out += names.photoX + ': count=' + (counts.photoX == null ? 0 : counts.photoX) + ' value: ' + has('photoX') + '\n';
        out += names.photoY + ': count=' + (counts.photoY == null ? 0 : counts.photoY) + ' value: ' + has('photoY') + '\n';
        out += names.photoScale + ': count=' + (counts.photoScale == null ? 0 : counts.photoScale) + ' value: ' + has('photoScale') + '\n';
        out += names.configVersion + ': count=' + (counts.configVersion == null ? 0 : counts.configVersion) + ' value: ' + has('configVersion') + '\n';
        out += names.uploadUrl + ': count=' + (counts.uploadUrl == null ? 0 : counts.uploadUrl) + ' value: ' + has('uploadUrl') + '\n';
        out += 'unexpected Ever Leaf properties: ' + props.unexpectedCount + '\n';
        (props.unexpectedNames || []).forEach(function (name) {
          out += '  unexpected property name: ' + name + '\n';
        });
        out += '--- ASSERTIONS ---\n';
        out += 'A. Upload 1 count = 1: ' + pf(a.uploadCountOne) + '\n';
        out += 'B. Dedication count = 1: ' + pf(a.textCountOne) + '\n';
        out += 'C. _ever_leaf_photo_x count = 1: ' + pf(a.photoXCountOne) + '\n';
        out += 'D. _ever_leaf_photo_y count = 1: ' + pf(a.photoYCountOne) + '\n';
        out += 'E. _ever_leaf_photo_scale count = 1: ' + pf(a.photoScaleCountOne) + '\n';
        out += 'F. _ever_leaf_config_version count = 1: ' + pf(a.configVersionCountOne) + '\n';
        out += 'G. _ever_leaf_upload_url count = 1: ' + pf(a.uploadUrlCountOne) + '\n';
        out += 'G2. _ever_leaf_dedication_layout count = 1: ' + pf(a.dedicationLayoutCountOne) + '\n';
        out += 'H. total canonical count = 8: ' + pf(a.totalCanonicalComplete) + '\n';
        out += 'I. Upload 1 === _ever_leaf_upload_url: ' + pf(a.uploadMatchesUploadUrl) + '\n';
        out += 'J. Upload 1 === current serverId: ' + pf(a.uploadMatchesServerId) + '\n';
        out += 'K. Dedication === live state: ' + pf(a.textMatchesState) + '\n';
        out += 'L. _ever_leaf_photo_x === live state: ' + pf(a.photoXMatchesState) + '\n';
        out += 'M. _ever_leaf_photo_y === live state: ' + pf(a.photoYMatchesState) + '\n';
        out += 'N. _ever_leaf_photo_scale === live state: ' + pf(a.photoScaleMatchesState) + '\n';
        out += 'O. _ever_leaf_config_version === "1": ' + pf(a.configVersionCorrect) + '\n';
        out += 'P. no unexpected Ever Leaf properties: ' + pf(a.noUnexpected) + '\n';
        out += '--- IMAGE IDENTITY ---\n';
        out += 'customer file present: ' + yn(img.filePresent) + '\n';
        out += 'authoritative serverId: ' + (img.serverIdPresent ? 'present' : '(none)') + '\n';
        out += 'Upload 1: ' + (img.upload1Present ? 'present' : '(none)') + '\n';
        out += '_ever_leaf_upload_url: ' + (img.uploadUrlPresent ? 'present' : '(none)') + '\n';
        out += 'Upload 1 === serverId: ' + pf(img.upload1MatchesServerId) + '\n';
        out += '_ever_leaf_upload_url === serverId: ' + pf(img.uploadUrlMatchesServerId) + '\n';
        out += 'Upload 1 === _ever_leaf_upload_url: ' + pf(img.upload1MatchesUploadUrl) + '\n';
        out += 'OVERALL: BUY NOW CANONICAL PROPERTY SNAPSHOT = ' + (props.overallPass ? 'PASS' : 'FAIL') + '\n';
      } else {
        out += '(no property audit was captured for this attempt)\n';
      }
      return out;
    }

    function showManualCopyFallbackLastBuyNow(text) {
      if (!copyLastBuyNowFallbackTextarea || !copyLastBuyNowFallbackMessage) return;
      copyLastBuyNowFallbackTextarea.value = text;
      copyLastBuyNowFallbackTextarea.hidden = false;
      copyLastBuyNowFallbackMessage.hidden = false;
      copyLastBuyNowFallbackMessage.textContent = 'Copy failed — select the text below and press Ctrl+C';
      try {
        copyLastBuyNowFallbackTextarea.focus();
        copyLastBuyNowFallbackTextarea.select();
      } catch (e) {
        /* best-effort */
      }
    }
    function hideManualCopyFallbackLastBuyNow() {
      if (copyLastBuyNowFallbackTextarea) copyLastBuyNowFallbackTextarea.hidden = true;
      if (copyLastBuyNowFallbackMessage) copyLastBuyNowFallbackMessage.hidden = true;
    }
    function showNoStoredMessageLastBuyNow() {
      if (!copyLastBuyNowFallbackMessage) return;
      copyLastBuyNowFallbackTextarea && (copyLastBuyNowFallbackTextarea.hidden = true);
      copyLastBuyNowFallbackMessage.hidden = false;
      copyLastBuyNowFallbackMessage.textContent = 'No stored Buy Now attempt found in this tab/session.';
    }

    function wireLastBuyNowReportButtons() {
    if (copyButtonLastBuyNow) {
      var copyLastBuyNowResetTimer = null;
      copyButtonLastBuyNow.addEventListener('click', function () {
        var text = formatLastBuyNowReportText();
        hideManualCopyFallbackLastBuyNow();

        if (!text) {
          showNoStoredMessageLastBuyNow();
          return;
        }

        function showResult(label) {
          clearTimeout(copyLastBuyNowResetTimer);
          var original = 'Copy last Buy Now report';
          copyButtonLastBuyNow.textContent = label;
          copyLastBuyNowResetTimer = setTimeout(function () {
            copyButtonLastBuyNow.textContent = original;
          }, 1500);
        }

        function tryExecCommandFallback() {
          try {
            var textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.readOnly = true;
            textarea.style.position = 'fixed';
            textarea.style.top = '0';
            textarea.style.left = '0';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            var ok = !!(document.execCommand && document.execCommand('copy'));
            document.body.removeChild(textarea);
            if (ok) {
              showResult('Copied!');
            } else {
              showManualCopyFallbackLastBuyNow(text);
              showResult('Copy failed');
            }
          } catch (e) {
            showManualCopyFallbackLastBuyNow(text);
            showResult('Copy failed');
          }
        }

        try {
          copyButtonLastBuyNow.focus();
        } catch (e) {
          /* best-effort */
        }

        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          navigator.clipboard.writeText(text).then(
            function () {
              showResult('Copied!');
            },
            function () {
              tryExecCommandFallback();
            }
          );
        } else {
          tryExecCommandFallback();
        }
      });
    }

    if (clearButtonLastBuyNow) {
      var clearLastBuyNowResetTimer = null;
      clearButtonLastBuyNow.addEventListener('click', function () {
        try {
          window.sessionStorage.removeItem(EVER_LEAF_BUY_NOW_STORAGE_KEY);
        } catch (e) {
          /* diagnostics-only */
        }
        hideManualCopyFallbackLastBuyNow();
        clearTimeout(clearLastBuyNowResetTimer);
        var original = 'Clear last Buy Now report';
        clearButtonLastBuyNow.textContent = 'Cleared!';
        clearLastBuyNowResetTimer = setTimeout(function () {
          clearButtonLastBuyNow.textContent = original;
        }, 1500);
      });
    }
    }

    function resolveLastBuyNowReportButtons(attemptsLeft) {
      copyButtonLastBuyNow = document.querySelector('[data-ever-leaf-cart-debug-copy-last-buynow]');
      clearButtonLastBuyNow = document.querySelector('[data-ever-leaf-cart-debug-clear-last-buynow]');
      copyLastBuyNowFallbackMessage = document.querySelector('[data-ever-leaf-cart-debug-copy-last-buynow-message]');
      copyLastBuyNowFallbackTextarea = document.querySelector('[data-ever-leaf-cart-debug-copy-last-buynow-fallback]');
      if (copyButtonLastBuyNow || clearButtonLastBuyNow) {
        wireLastBuyNowReportButtons();
        return;
      }
      if (attemptsLeft > 0) {
        requestAnimationFrame(function () {
          resolveLastBuyNowReportButtons(attemptsLeft - 1);
        });
      }
    }
    // ~5s bounded at 60fps — diagnostics-only, never blocks/affects Buy Now.
    resolveLastBuyNowReportButtons(300);

    /*
      Section 5 — identify the EXACT form the real Add to Cart button
      belongs to, not merely "the first form[data-type=...] found in the
      document" (which would be wrong if more than one exists — e.g. a
      collection-page quick-add card using the same buy-buttons.liquid
      snippet). `button.form` is the browser's own authoritative answer per
      the HTML spec (works even across a form="" attribute), so it wins if
      it ever disagrees with the querySelector guess.
    */
    var candidateForms = Array.prototype.slice.call(document.querySelectorAll('form[data-type="add-to-cart-form"]'));
    var form = null;
    var addButton = null;
    candidateForms.forEach(function (candidate) {
      if (addButton) return;
      var btn = candidate.querySelector('button[name="add"]');
      if (btn) {
        form = candidate;
        addButton = btn;
      }
    });
    if (!form || !addButton) return;
    var formMismatch = !!(addButton.form && addButton.form !== form);
    if (formMismatch) form = addButton.form;
    addToCartGatingWired = true;

    // Preserves any pre-existing disabled state (sold out / unavailable
    // variant, set server-side by buy-buttons.liquid) — our gating only
    // ever ADDS a reason to stay disabled, never removes one that was
    // already there for an unrelated reason (Section 14 — ownership rule).
    var initiallyDisabled = !!(addButton && addButton.disabled);

    var consentRoot = form.closest('[data-ever-leaf-consent-root]');
    var gate = consentRoot ? consentRoot.querySelector('[data-ever-leaf-consent-gate]') : null;
    var checkbox = consentRoot ? consentRoot.querySelector('[data-ever-leaf-consent-checkbox]') : null;

    /*
      DEV CART COMMIT diagnostic state (Correction Pass 3, Section 10) —
      every field the brief's panel spec asks for, tracked explicitly so the
      exact stopping point of a failed commit is always visible. Never
      affects gating/commit logic itself, purely observational.
    */
    var diag = {
      // Section 19-20 (this pass) — per-commit-attempt identity, so stale
      // data from a PREVIOUS attempt (of either source) can never be
      // mistaken for the current one's evidence.
      currentAttemptId: 0,
      currentAttemptSource: '',
      cartJsCheckedThisAttempt: false,
      atcFirstSubmitIntercepted: false,
      // Section 5 — real form identification.
      candidateFormCount: candidateForms.length,
      formMismatch: formMismatch,
      // PASS 3A MANUAL QA CORRECTION PASS, Section 15 — true once the
      // captured form/addButton have been re-resolved at least once
      // because a Theme Editor section reload replaced the original DOM
      // node. Internal-only visibility into the fix above.
      formReattachedAfterSectionReload: false,
      pondFound: null, // null = unknown yet, true/false once known
      pondCleanup: 'idle', // started | done | error
      pondAddFile: 'not started', // started | resolved | error
      pondConfig: '',
      pondConfigSuspect: '',
      pondStatusCode: null,
      fileStatusCode: null,
      processFileCalled: false,
      processFileResolved: false,
      processFileError: '',
      serverId: '', // Correction Pass 6 — item.serverId, the authoritative hosted URL
      hostedUrlAccepted: false,
      // Correction Pass 7, Section 3/12 — snapshot taken in the capturing
      // 'submit' listener's APPROVED branch, immediately before stepping
      // aside, so it reflects the exact DOM/FormData state Dawn is about to
      // read.
      publicUploadInputsBeforeDawn: null,
      publicUploadFormDataEntriesBeforeDawn: null,
      everLeafOwnedUploadInputs: null,
      miniOrOtherUploadInputs: null,
      finalPublicUploadCount: null, // re-scanned once cartUpdate/cartError lands
      // Section 19 — the 7 canonical property values as WE just wrote them.
      propertyValues: null,
      // Section 10 — FormData(form) presence check immediately before
      // requestSubmit(), PLUS (this pass, Section 4) the full raw dump and
      // the normalized-upload-alias count (must be exactly 1 — Section 8).
      formDataPresence: null,
      formDataAllProperties: null,
      formDataNormalizedUploadCount: null,
      // Section 11 — the ACTUAL outgoing Dawn /cart/add(.js) request payload.
      dawnRequestObserved: false,
      dawnRequestEndpoint: '',
      dawnRequestPropertiesCount: null,
      dawnRequestValues: null,
      dawnRequestAllProperties: null,
      dawnRequestNormalizedUploadCount: null,
      // Section 12 — cart.js, the only real acceptance-proof source.
      cartJsPropertiesCount: null,
      cartJsUpload1: '',
      cartJsDedication: '',
      cartJsPhotoX: '',
      cartJsPhotoY: '',
      cartJsPhotoScale: '',
      cartJsConfigVersion: '',
      cartJsUploadUrl: '',
      cartJsLineKey: '',
      cartJsLineQuantity: null,
      cartJsAllProperties: null,
      cartJsMatchedByUploadUrl: false, // Section 9 — false means the variant-only fallback was used
      finalizeUpload: 'idle', // pending | resolved | rejected | cache-hit | joined-upload
      approvalFlag: false,
      requestSubmitCalled: false,
      approvedResubmitObserved: false,
      everLeafSteppedAside: false,
      termsConsentSteppedAside: false,
      dawnSubmitReached: false,
      cartUpdateSeen: false,
      cartErrorSeen: false,
      lastCommitError: '',
      // Section 14/15 — Buy Now specific.
      buyNowClickObserved: false,
      buyNowCommitSource: '', // 'add-to-cart' | 'buy-now', whichever last ran runCommit
      buyNowClickedElement: '',
      buyNowRunCommitEntered: false,
      buyNowFinalFormDataReady: false,
      buyNowApprovedContinuationInvoked: false,
      buyNowOriginalActionInvoked: false,
      buyNowNavigationStarted: null, // null = unknown yet, true/false once known
      buyNowLastError: '',
      // NEXT 1 pass — pointer-gesture Buy Now activation (H.4/H.5). The
      // 'click' interception above is now unreachable in practice (see
      // H.4 — click is never observed) but its fields are left in place
      // as a defensive fallback path (keyboard Enter/Space activation
      // still generates a real click with no pointerdown/pointerup).
      buyNowPointerdownObserved: false,
      buyNowPointerdownTarget: '',
      buyNowPointerId: null,
      buyNowPointerType: '',
      buyNowPointerupObserved: false,
      buyNowSamePointer: null,
      buyNowMovementPx: null,
      buyNowPointercancelObserved: false,
      buyNowGestureAccepted: false,
      buyNowGestureRejectedReason: '',
      buyNowMiniProcessingComplete: false,
      buyNowSevenPropertiesReady: false,
      buyNowButtonReResolved: false,
      buyNowReplayClickInvoked: false,
      // REPLAY TARGET RESOLUTION pass (this pass) — Section 8/10/27:
      // moment-A (gesture acceptance) button snapshot, plus moment-B
      // (replay-time) resolution evidence, kept SEPARATE from the gesture
      // resolver's own pass/fail fields above.
      buyNowGestureButtonState: null, // inspectBuyNowButtonState() snapshot at pointerup acceptance
      buyNowReplayRawSelectorMatchCount: null,
      buyNowReplayOriginalConnected: null,
      buyNowReplaySameNodeAsOriginal: null,
      buyNowReplayButtonState: null, // inspectBuyNowButtonState() snapshot at replay time
      buyNowReplayEverLeafGatingActive: null,
      buyNowReplayGatingReleased: null,
      buyNowReplayShopifyBlocking: 'unknown',
      buyNowReplayRejectionReason: '',
      buyNowReplayAttemptsUsed: null,
      // SYNTHETIC CLICK DELIVERY pass (this pass) — Section 3/9: a SECONDARY
      // activation attempt observed while a commit is already in flight
      // (e.g. a real user re-tap). Kept fully SEPARATE from the primary
      // buyNowGesture*/buyNowGestureAccepted fields above so a secondary
      // attempt can never overwrite the current attempt's own frozen result.
      buyNowSecondaryActivationObserved: false,
      buyNowSecondaryActivationEventType: '',
      buyNowSecondaryActivationTarget: '',
      buyNowSecondaryActivationRejectedReason: '',
      // Section 4-8/16 — exact delivery path of the replayed .click(), via
      // temporary one-shot probes installed only around that one call.
      buyNowReplayApprovalBeforeClick: null,
      buyNowReplayApprovalDuringEvent: null,
      buyNowReplayApprovalAfterClick: null,
      buyNowReplayButtonClickObserved: null,
      buyNowReplayButtonEventIsTrusted: null,
      buyNowReplayGateCaptureObserved: null,
      buyNowReplayGateBubbleObserved: null,
      buyNowReplayDocumentCaptureObserved: null,
      buyNowReplayDocumentBubbleObserved: null,
      buyNowReplayDefaultPrevented: null,
      buyNowReplayEventTarget: '',
      buyNowReplayEventCurrentTarget: '',
      buyNowReplayEventPhaseAtButton: null,
      buyNowReplayMethodUsed: '',
      // PURCHASE FLOW CONSOLIDATED PASS — Section 6-12: click method
      // identity, checked once per replay before deciding whether the ONE
      // allowed alternative (HTMLElement.prototype.click.call) is even
      // eligible to try.
      buyNowClickTypeofClick: '',
      buyNowClickHasOwnProperty: null,
      buyNowClickOwnDescriptor: null,
      buyNowClickIsHTMLElementPrototype: null,
      buyNowClickIsHTMLButtonElementPrototype: null,
      buyNowClickImplementationOwner: '',
      buyNowClickDescription: '',
      buyNowNativePrototypeFallbackEligible: null,
      buyNowNativePrototypeFallbackTested: false,
      buyNowNativePrototypeFallbackClickObserved: null,
      buyNowProtoReplayButtonClickObserved: null,
      buyNowProtoReplayButtonEventIsTrusted: null,
      buyNowProtoReplayGateCaptureObserved: null,
      buyNowProtoReplayGateBubbleObserved: null,
      buyNowProtoReplayDocumentCaptureObserved: null,
      buyNowProtoReplayDocumentBubbleObserved: null,
      buyNowProtoReplayDefaultPrevented: null,
      buyNowProtoReplayApprovalDuringEvent: null,
      buyNowFinalConclusion: '',
      // PURCHASE FLOW CONSOLIDATED PASS II — Section 3/8: effective
      // disabled/inert state, checked before the conclusion is allowed to
      // say "unsuitable" rather than merely "unresolved".
      buyNowEffectiveDisabled: null,
      buyNowDisabledProperty: null,
      buyNowMatchesDisabled: null,
      buyNowMatchesEnabled: null,
      buyNowDisabledFieldset: '',
      buyNowInertSelf: null,
      buyNowInertAncestor: '',
      buyNowHiddenAncestor: '',
      buyNowAriaHiddenAncestor: '',
      buyNowFormId: '',
      buyNowFormCheckValidity: null,
      buyNowButtonType: '',
      buyNowControlButtonClickObserved: null,
      buyNowCloneButtonClickObserved: null,
      // STANDALONE DEV END-TO-END PROPERTY AUDIT pass — Section 6/11-13:
      // the full pre-handoff property-control audit, captured once per
      // commit, immediately before the native replay is attempted.
      buyNowPropertyAudit: null,
      buyNowExecutionEnvironment: null,
      buyNowOriginalGestureIsTrusted: null,
      // This pass, Section 5 — accelerated checkout structural diagnostics
      // (recomputed fresh on every refreshDiagnostics() call, not cached,
      // since the component may render asynchronously after page load).
      acceleratedHostFound: false,
      acceleratedHostTag: '',
      acceleratedHostClass: '',
      acceleratedHostAttributes: '',
      acceleratedShadowRootState: 'none',
      acceleratedIframeCount: 0,
      acceleratedIframeDetails: [],
      acceleratedVisibleButtonFound: false,
      acceleratedVisibleButtonLocation: 'unknown',
      // Scoped, non-noisy interaction observers around the gate area only.
      documentClickSeesGate: false,
      documentPointerdownSeesGate: false,
      documentPointerupSeesGate: false,
      hostPointerdownSeen: false,
      hostPointerupSeen: false,
      focusBeforeInteraction: '(none observed)',
      focusAfterInteraction: '(none observed)',
      // This pass, Section 16 — Copy Diagnostics status.
      copyMethod: '', // 'clipboard-api' | 'execCommand' | 'manual-fallback' | 'none'
      copySuccess: null,
      copyError: ''
    };

    function refreshDiagnostics() {
      if (!diagnosticsEl) return;
      var s = window.EverLeafPersonalizerState.get();
      var adapter = window.EverLeafMiniUploadAdapter;
      var hasPhoto = !!s.customerFile;
      var termsOk = checkbox ? checkbox.checked : true;
      var pondStatusLabel =
        diag.pondStatusCode == null ? '(none yet)' : diag.pondStatusCode + ' / ' + (adapter ? adapter.pondStatusName(diag.pondStatusCode) : '?');
      var fileStatusLabel =
        diag.fileStatusCode == null ? '(none yet)' : diag.fileStatusCode + ' / ' + (adapter ? adapter.fileStatusName(diag.fileStatusCode) : '?');
      var pv = diag.propertyValues || {};
      var fdp = diag.formDataPresence || {};
      var dr = diag.dawnRequestValues || {};
      function yn(v) {
        return v == null ? '(none yet)' : v ? 'yes' : 'no';
      }
      diagnosticsEl.textContent =
        'DEV CART COMMIT — not customer-facing\n' +
        'customerFile: ' + (hasPhoto ? 'yes' : 'no') + '\n' +
        '--- REAL FORM (Section 5) ---\n' +
        'real form found: ' + (form ? 'yes' : 'no') + '\n' +
        'candidate form count: ' + diag.candidateFormCount + '\n' +
        'add button belongs to real form: ' + (diag.formMismatch ? 'no (corrected via addButton.form)' : 'yes') + '\n' +
        'form action: ' + (form ? form.action : 'n/a') + '\n' +
        'form method: ' + (form ? form.method : 'n/a') + '\n' +
        'form id/class: ' + (form ? (form.id || '(no id)') + ' / ' + (form.className || '(no class)') : 'n/a') + '\n' +
        'Add to Cart button found: ' + (addButton ? 'yes' : 'no') + '\n' +
        'Add to Cart disabled: ' + (addButton ? String(addButton.disabled) : 'n/a') + '\n' +
        'Ever Leaf photo gate: ' + (addButton && addButton.getAttribute(PHOTO_BLOCKED_MARKER) === 'true' ? 'blocked' : 'allowed') + '\n' +
        'ATC first submit intercepted: ' + (diag.atcFirstSubmitIntercepted ? 'yes' : 'no') + '\n' +
        '--- MINI (storage only) ---\n' +
        'pond found: ' + (diag.pondFound === null ? 'n/a' : diag.pondFound ? 'yes' : 'no') + '\n' +
        'pond cleanup: ' + diag.pondCleanup + '\n' +
        'pond config: ' + (diag.pondConfig || '(none yet)') + '\n' +
        (diag.pondConfigSuspect ? 'pond config SUSPECT: ' + diag.pondConfigSuspect + '\n' : '') +
        'pond status: ' + pondStatusLabel + '\n' +
        'pond.addFile: ' + diag.pondAddFile + '\n' +
        'file status: ' + fileStatusLabel + '\n' +
        'processFile called: ' + (diag.processFileCalled ? 'yes' : 'no') + '\n' +
        'processFile resolved: ' + (diag.processFileResolved ? 'yes' : 'no') + '\n' +
        'processFile error: ' + (diag.processFileError || '(none)') + '\n' +
        'serverId: ' + (diag.serverId || '(none yet)') + '\n' +
        'hosted URL accepted: ' + (diag.hostedUrlAccepted ? 'yes' : 'no') + '\n' +
        '--- PROPERTY INPUTS (Ever Leaf canonicalized, Section 6) ---\n' +
        'Upload 1: ' + (pv[LINE_ITEM_PROPERTY_NAMES.upload] || '(none yet)') + '\n' +
        'Dedication: ' + (pv[LINE_ITEM_PROPERTY_NAMES.text] != null ? '"' + pv[LINE_ITEM_PROPERTY_NAMES.text] + '"' : '(none yet)') + '\n' +
        'photoX: ' + (pv[LINE_ITEM_PROPERTY_NAMES.photoX] || '(none yet)') + '\n' +
        'photoY: ' + (pv[LINE_ITEM_PROPERTY_NAMES.photoY] || '(none yet)') + '\n' +
        'photoScale: ' + (pv[LINE_ITEM_PROPERTY_NAMES.photoScale] || '(none yet)') + '\n' +
        'config version: ' + (pv[LINE_ITEM_PROPERTY_NAMES.configVersion] || '(none yet)') + '\n' +
        'private upload URL: ' + (pv[LINE_ITEM_PROPERTY_NAMES.uploadUrl] || '(none yet)') + '\n' +
        '--- FORMDATA BEFORE RESUBMIT (Section 10) ---\n' +
        'Upload 1 present: ' + yn(fdp.upload) + '\n' +
        'Dedication present: ' + yn(fdp.text) + '\n' +
        'photoX present: ' + yn(fdp.photoX) + '\n' +
        'photoY present: ' + yn(fdp.photoY) + '\n' +
        'photoScale present: ' + yn(fdp.photoScale) + '\n' +
        'config version present: ' + yn(fdp.configVersion) + '\n' +
        'private upload URL present: ' + yn(fdp.uploadUrl) + '\n' +
        'normalized public Upload count (must be 1 — Section 8): ' + (diag.formDataNormalizedUploadCount == null ? '(none yet)' : diag.formDataNormalizedUploadCount) + '\n' +
        'FORMDATA ALL PROPERTIES (Section 4):\n' + (diag.formDataAllProperties && diag.formDataAllProperties.length ? diag.formDataAllProperties.map(function (l) { return '  ' + l; }).join('\n') : '  (none yet)') + '\n' +
        '--- ACTUAL REQUEST (Section 11) ---\n' +
        'Dawn cart request observed: ' + (diag.dawnRequestObserved ? 'yes' : 'no') + '\n' +
        'request endpoint: ' + (diag.dawnRequestEndpoint || '(none yet)') + '\n' +
        'request properties count: ' + (diag.dawnRequestPropertiesCount == null ? '(none yet)' : diag.dawnRequestPropertiesCount) + '\n' +
        'request Upload 1: ' + (dr[LINE_ITEM_PROPERTY_NAMES.upload] || '(none yet)') + '\n' +
        'request Dedication: ' + (dr[LINE_ITEM_PROPERTY_NAMES.text] != null ? '"' + dr[LINE_ITEM_PROPERTY_NAMES.text] + '"' : '(none yet)') + '\n' +
        'request photoX: ' + (dr[LINE_ITEM_PROPERTY_NAMES.photoX] || '(none yet)') + '\n' +
        'request photoY: ' + (dr[LINE_ITEM_PROPERTY_NAMES.photoY] || '(none yet)') + '\n' +
        'request photoScale: ' + (dr[LINE_ITEM_PROPERTY_NAMES.photoScale] || '(none yet)') + '\n' +
        'request config version: ' + (dr[LINE_ITEM_PROPERTY_NAMES.configVersion] || '(none yet)') + '\n' +
        'request private upload URL: ' + (dr[LINE_ITEM_PROPERTY_NAMES.uploadUrl] || '(none yet)') + '\n' +
        'request normalized public Upload count (must be 1): ' + (diag.dawnRequestNormalizedUploadCount == null ? '(none yet)' : diag.dawnRequestNormalizedUploadCount) + '\n' +
        'REQUEST ALL PROPERTIES (Section 4):\n' + (diag.dawnRequestAllProperties && diag.dawnRequestAllProperties.length ? diag.dawnRequestAllProperties.map(function (l) { return '  ' + l; }).join('\n') : '  (none yet)') + '\n' +
        '--- CART.JS (Section 12 — authoritative) ---\n' +
        'cartUpdate: ' + (diag.cartUpdateSeen ? 'yes' : 'no') + '\n' +
        'cartError: ' + (diag.cartErrorSeen ? 'yes' : 'no') + '\n' +
        'matched by current _ever_leaf_upload_url (Section 9): ' + (diag.cartJsMatchedByUploadUrl ? 'yes' : 'no (used variant-id fallback)') + '\n' +
        'cart.js properties count: ' + (diag.cartJsPropertiesCount == null ? '(pending cartUpdate)' : diag.cartJsPropertiesCount) + '\n' +
        'cart.js Upload 1: ' + (diag.cartJsUpload1 || '(none yet)') + '\n' +
        'cart.js Dedication: ' + (diag.cartJsDedication || '(none yet)') + '\n' +
        'cart.js photoX: ' + (diag.cartJsPhotoX || '(none yet)') + '\n' +
        'cart.js photoY: ' + (diag.cartJsPhotoY || '(none yet)') + '\n' +
        'cart.js photoScale: ' + (diag.cartJsPhotoScale || '(none yet)') + '\n' +
        'cart.js config version: ' + (diag.cartJsConfigVersion || '(none yet)') + '\n' +
        'cart.js private upload URL: ' + (diag.cartJsUploadUrl || '(none yet)') + '\n' +
        'cart line key: ' + (diag.cartJsLineKey || '(none yet)') + '\n' +
        'cart line quantity: ' + (diag.cartJsLineQuantity == null ? '(none yet)' : diag.cartJsLineQuantity) + '\n' +
        'CART.JS ALL PROPERTIES (Section 4):\n' + (diag.cartJsAllProperties && diag.cartJsAllProperties.length ? diag.cartJsAllProperties.map(function (l) { return '  ' + l; }).join('\n') : '  (none yet)') + '\n' +
        '--- DUPLICATE-UPLOAD TRACKING (Correction Pass 7 diagnostics) ---\n' +
        'public Upload inputs before Dawn: ' + (diag.publicUploadInputsBeforeDawn == null ? '(none yet)' : diag.publicUploadInputsBeforeDawn) + '\n' +
        'public Upload FormData entries before Dawn: ' + (diag.publicUploadFormDataEntriesBeforeDawn == null ? '(none yet)' : diag.publicUploadFormDataEntriesBeforeDawn) + '\n' +
        'Ever Leaf-owned Upload inputs: ' + (diag.everLeafOwnedUploadInputs == null ? '(none yet)' : diag.everLeafOwnedUploadInputs) + '\n' +
        'Mini/other Upload inputs: ' + (diag.miniOrOtherUploadInputs == null ? '(none yet)' : diag.miniOrOtherUploadInputs) + '\n' +
        'final public Upload count: ' + (diag.finalPublicUploadCount == null ? '(pending cartUpdate/cartError)' : diag.finalPublicUploadCount) + '\n' +
        '--- COMMIT FLOW ---\n' +
        'finalizeUpload: ' + diag.finalizeUpload + '\n' +
        'approval flag: ' + String(diag.approvalFlag) + '\n' +
        'requestSubmit(addButton): ' + (diag.requestSubmitCalled ? 'called' : 'not called') + '\n' +
        'approved resubmit observed: ' + (diag.approvedResubmitObserved ? 'yes' : 'no') + '\n' +
        'Ever Leaf stepped aside: ' + (diag.everLeafSteppedAside ? 'yes' : 'no') + '\n' +
        'terms-consent stepped aside: ' + (diag.termsConsentSteppedAside ? 'yes' : 'no') + '\n' +
        'Dawn submit reached: ' + (diag.dawnSubmitReached ? 'yes' : 'no') + '\n' +
        'last commit error: ' + (diag.lastCommitError || '(none)') + '\n' +
        '--- CURRENT COMMIT (Section 19-20 — per-attempt, never stale) ---\n' +
        'attempt id: ' + (diag.currentAttemptId || '(none yet)') + '\n' +
        'source: ' + (diag.currentAttemptSource || '(none yet)') + '\n' +
        'current serverId: ' + (diag.serverId || '(none yet)') + '\n' +
        'request observed this attempt: ' + (diag.dawnRequestObserved ? 'yes' : 'no') + '\n' +
        'cartUpdate this attempt: ' + (diag.cartUpdateSeen ? 'yes' : 'no') + '\n' +
        'cart.js checked this attempt: ' + (diag.cartJsCheckedThisAttempt ? 'yes' : 'no') + '\n' +
        '--- BUY NOW (Section 14) ---\n' +
        'Buy Now click observed: ' + (diag.buyNowClickObserved ? 'yes' : 'no') + '\n' +
        'actual clicked element: ' + (diag.buyNowClickedElement || '(none yet)') + '\n' +
        'buy now commit source: ' + (diag.buyNowCommitSource || '(none yet)') + '\n' +
        'runCommit entered: ' + (diag.buyNowRunCommitEntered ? 'yes' : 'no') + '\n' +
        '(customerFile / Mini pond found / PROCESSING_COMPLETE / serverId / canonical properties created: see MINI + PROPERTY INPUTS sections above — shared with Add to Cart)\n' +
        'final FormData ready: ' + (diag.buyNowFinalFormDataReady ? 'yes' : 'no') + '\n' +
        'approved continuation invoked: ' + (diag.buyNowApprovedContinuationInvoked ? 'yes' : 'no') + '\n' +
        'original dynamic checkout action invoked: ' + (diag.buyNowOriginalActionInvoked ? 'yes' : 'no') + '\n' +
        'navigation started (best-effort pagehide signal): ' + (diag.buyNowNavigationStarted == null ? '(unknown — no navigation observed within the timeout)' : diag.buyNowNavigationStarted ? 'yes' : 'no') + '\n' +
        'last Buy Now error: ' + (diag.buyNowLastError || '(none)') + '\n' +
        'terms checked: ' + String(termsOk) + '\n' +
        'Buy Now blocked: ' + (gate ? String(gate.classList.contains('ever-leaf-photo-required-blocked')) : 'n/a') + '\n' +
        '--- BUY NOW GESTURE (NEXT 1 — pointer activation) ---\n' +
        'real Buy Now button found: ' + (resolveRealBuyNowButton() ? 'yes' : 'no') + '\n' +
        'real button selector: ' + BUY_NOW_BUTTON_SELECTOR + '\n' +
        'pointerdown observed: ' + (diag.buyNowPointerdownObserved ? 'yes' : 'no') + '\n' +
        'pointerdown target: ' + (diag.buyNowPointerdownTarget || '(none yet)') + '\n' +
        'pointerId: ' + (diag.buyNowPointerId == null ? '(none yet)' : diag.buyNowPointerId) + '\n' +
        'pointerType: ' + (diag.buyNowPointerType || '(none yet)') + '\n' +
        'pointerup observed: ' + (diag.buyNowPointerupObserved ? 'yes' : 'no') + '\n' +
        'same pointer: ' + (diag.buyNowSamePointer == null ? '(none yet)' : diag.buyNowSamePointer ? 'yes' : 'no') + '\n' +
        'movement px: ' + (diag.buyNowMovementPx == null ? '(none yet)' : diag.buyNowMovementPx) + '\n' +
        'pointercancel observed: ' + (diag.buyNowPointercancelObserved ? 'yes' : 'no') + '\n' +
        'gesture accepted: ' + (diag.buyNowGestureAccepted ? 'yes' : 'no') + '\n' +
        'gesture rejected reason: ' + (diag.buyNowGestureRejectedReason || '(none)') + '\n' +
        'button state at gesture acceptance (moment A): ' + formatButtonSnapshot(diag.buyNowGestureButtonState) + '\n' +
        '(the above is FROZEN for this attempt once a commit starts — a later activation while committing never overwrites it; see SECONDARY ACTIVATION below)\n' +
        '--- SECONDARY ACTIVATION (Section 3/9 — this pass) ---\n' +
        'secondary activation observed: ' + (diag.buyNowSecondaryActivationObserved ? 'yes' : 'no') + '\n' +
        'secondary activation event type: ' + (diag.buyNowSecondaryActivationEventType || '(none)') + '\n' +
        'secondary activation target: ' + (diag.buyNowSecondaryActivationTarget || '(none)') + '\n' +
        'secondary activation rejected reason: ' + (diag.buyNowSecondaryActivationRejectedReason || '(none)') + '\n' +
        '--- BUY NOW REPLAY (NEXT 1) ---\n' +
        'Mini PROCESSING_COMPLETE (this buy-now commit): ' + (diag.buyNowMiniProcessingComplete ? 'yes' : 'no') + '\n' +
        'seven canonical properties ready: ' + (diag.buyNowSevenPropertiesReady ? 'yes' : 'no') + '\n' +
        'approval flag: ' + String(diag.buyNowReplayClickInvoked) + '\n' +
        'current real button re-resolved: ' + (diag.buyNowButtonReResolved ? 'yes' : 'no') + '\n' +
        'native replay .click() invoked: ' + (diag.buyNowReplayClickInvoked ? 'yes' : 'no') + '\n' +
        'approved replay observed: ' + (diag.buyNowApprovedContinuationInvoked ? 'yes' : 'no') + '\n' +
        'Ever Leaf stepped aside: ' + (diag.buyNowOriginalActionInvoked ? 'yes' : 'no') + '\n' +
        'Shopify continuation observed (best-effort, see navigation below): ' + (diag.buyNowApprovedContinuationInvoked ? (diag.buyNowNavigationStarted ? 'yes' : '(pending/unknown)') : 'n/a') + '\n' +
        '--- BUY NOW EXECUTION PATH (this pass) ---\n' +
        formatBuyNowExecutionPathText(diag.buyNowExecutionEnvironment) + '\n' +
        '--- BUY NOW CANONICAL PROPERTIES (this pass) ---\n' +
        formatBuyNowPropertyAuditText(diag.buyNowPropertyAudit) + '\n' +
        '--- BUY NOW REPLAY TARGET (Section 8/10/27 — this pass) ---\n' +
        'selector: ' + BUY_NOW_BUTTON_SELECTOR + '\n' +
        'raw selector match count: ' + (diag.buyNowReplayRawSelectorMatchCount == null ? '(none yet)' : diag.buyNowReplayRawSelectorMatchCount) + '\n' +
        'attempts used (of ' + BUY_NOW_REPLAY_RETRY_MAX_ATTEMPTS + '): ' + (diag.buyNowReplayAttemptsUsed == null ? '(none yet)' : diag.buyNowReplayAttemptsUsed) + '\n' +
        'original pointerdown button connected: ' + (diag.buyNowReplayOriginalConnected == null ? '(none yet)' : diag.buyNowReplayOriginalConnected ? 'yes' : 'no') + '\n' +
        'fresh current button state (moment B): ' + formatButtonSnapshot(diag.buyNowReplayButtonState) + '\n' +
        'same node as original: ' + (diag.buyNowReplaySameNodeAsOriginal == null ? '(none yet)' : diag.buyNowReplaySameNodeAsOriginal ? 'yes' : 'no') + '\n' +
        'uploadStatus: ' + s.uploadStatus + '\n' +
        'Ever Leaf gating active (before release): ' + (diag.buyNowReplayEverLeafGatingActive == null ? '(none yet)' : diag.buyNowReplayEverLeafGatingActive ? 'yes' : 'no') + '\n' +
        'Ever Leaf gating temporarily released: ' + (diag.buyNowReplayGatingReleased == null ? '(none yet)' : diag.buyNowReplayGatingReleased ? 'yes' : 'no') + '\n' +
        'Shopify disabled/blocking detected: ' + diag.buyNowReplayShopifyBlocking + '\n' +
        'resolver rejection reason: ' + (diag.buyNowReplayRejectionReason || '(none)') + '\n' +
        '--- CLICK METHOD IDENTITY (Section 6/32 — this pass) ---\n' +
        'typeof button.click: ' + (diag.buyNowClickTypeofClick || '(none yet)') + '\n' +
        'has own click property: ' + (diag.buyNowClickHasOwnProperty == null ? '(none yet)' : diag.buyNowClickHasOwnProperty ? 'yes' : 'no') + '\n' +
        'own property descriptor: ' + (diag.buyNowClickOwnDescriptor || '(none — inherited, no own property)') + '\n' +
        'button.click === HTMLElement.prototype.click: ' + (diag.buyNowClickIsHTMLElementPrototype == null ? '(none yet)' : diag.buyNowClickIsHTMLElementPrototype ? 'yes' : 'no') + '\n' +
        'button.click === HTMLButtonElement.prototype.click: ' + (diag.buyNowClickIsHTMLButtonElementPrototype == null ? '(none yet)' : diag.buyNowClickIsHTMLButtonElementPrototype ? 'yes' : 'no') + '\n' +
        'click implementation owner: ' + (diag.buyNowClickImplementationOwner || '(none yet)') + '\n' +
        'description: ' + (diag.buyNowClickDescription || '(none yet)') + '\n' +
        'current replay method: ' + (diag.buyNowReplayMethodUsed || '(none yet)') + '\n' +
        'native prototype fallback eligible: ' + (diag.buyNowNativePrototypeFallbackEligible == null ? '(none yet)' : diag.buyNowNativePrototypeFallbackEligible ? 'yes' : 'no') + '\n' +
        'native prototype fallback tested: ' + (diag.buyNowNativePrototypeFallbackTested ? 'yes' : 'no') + '\n' +
        'native prototype fallback click observed: ' + (diag.buyNowNativePrototypeFallbackClickObserved == null ? '(none yet)' : diag.buyNowNativePrototypeFallbackClickObserved ? 'yes' : 'no') + '\n' +
        '--- EFFECTIVE BUTTON STATE (Section 3/8/24 — this pass) ---\n' +
        'button.disabled: ' + (diag.buyNowDisabledProperty == null ? '(none yet)' : diag.buyNowDisabledProperty ? 'yes' : 'no') + '\n' +
        "button.matches(':disabled'): " + (diag.buyNowMatchesDisabled == null ? '(none yet)' : diag.buyNowMatchesDisabled ? 'yes' : 'no') + '\n' +
        "button.matches(':enabled'): " + (diag.buyNowMatchesEnabled == null ? '(none yet)' : diag.buyNowMatchesEnabled ? 'yes' : 'no') + '\n' +
        'disabled fieldset ancestor: ' + (diag.buyNowDisabledFieldset || '(none)') + '\n' +
        'button.inert: ' + (diag.buyNowInertSelf == null ? '(none yet)' : diag.buyNowInertSelf ? 'yes' : 'no') + '\n' +
        'inert ancestor: ' + (diag.buyNowInertAncestor || '(none)') + '\n' +
        'hidden ancestor: ' + (diag.buyNowHiddenAncestor || '(none)') + '\n' +
        'aria-hidden ancestor: ' + (diag.buyNowAriaHiddenAncestor || '(none)') + '\n' +
        'form id: ' + (diag.buyNowFormId || '(none yet)') + '\n' +
        'form.checkValidity(): ' + (diag.buyNowFormCheckValidity == null ? '(n/a — no form)' : diag.buyNowFormCheckValidity ? 'yes' : 'no') + '\n' +
        'button.type: ' + (diag.buyNowButtonType || '(none yet)') + '\n' +
        'control native click observed: ' + (diag.buyNowControlButtonClickObserved == null ? '(none yet)' : diag.buyNowControlButtonClickObserved ? 'yes' : 'no') + '\n' +
        'isolated clone native click observed: ' + (diag.buyNowCloneButtonClickObserved == null ? '(not tested)' : diag.buyNowCloneButtonClickObserved ? 'yes' : 'no') + '\n' +
        'FINAL CONCLUSION: ' + (diag.buyNowFinalConclusion || '(not yet determined)') + '\n' +
        '--- SYNTHETIC CLICK DELIVERY (Section 4-8/16 — ordinary button.click() attempt) ---\n' +
        'approval before .click: ' + (diag.buyNowReplayApprovalBeforeClick == null ? '(none yet)' : diag.buyNowReplayApprovalBeforeClick ? 'yes' : 'no') + '\n' +
        'button click observed: ' + (diag.buyNowReplayButtonClickObserved == null ? '(none yet)' : diag.buyNowReplayButtonClickObserved ? 'yes' : 'no') + '\n' +
        'button event isTrusted: ' + (diag.buyNowReplayButtonEventIsTrusted == null ? '(none yet)' : diag.buyNowReplayButtonEventIsTrusted ? 'yes' : 'no') + '\n' +
        'gate capture observed: ' + (diag.buyNowReplayGateCaptureObserved == null ? '(none yet)' : diag.buyNowReplayGateCaptureObserved ? 'yes' : 'no') + '\n' +
        'gate bubble observed: ' + (diag.buyNowReplayGateBubbleObserved == null ? '(none yet)' : diag.buyNowReplayGateBubbleObserved ? 'yes' : 'no') + '\n' +
        'document capture observed: ' + (diag.buyNowReplayDocumentCaptureObserved == null ? '(none yet)' : diag.buyNowReplayDocumentCaptureObserved ? 'yes' : 'no') + '\n' +
        'document bubble observed: ' + (diag.buyNowReplayDocumentBubbleObserved == null ? '(none yet)' : diag.buyNowReplayDocumentBubbleObserved ? 'yes' : 'no') + '\n' +
        'defaultPrevented: ' + (diag.buyNowReplayDefaultPrevented == null ? '(none yet)' : diag.buyNowReplayDefaultPrevented ? 'yes' : 'no') + '\n' +
        'event.target: ' + (diag.buyNowReplayEventTarget || '(none yet)') + '\n' +
        'event.currentTarget (at button probe): ' + (diag.buyNowReplayEventCurrentTarget || '(none yet)') + '\n' +
        'event.eventPhase (at button probe): ' + (diag.buyNowReplayEventPhaseAtButton == null ? '(none yet)' : diag.buyNowReplayEventPhaseAtButton) + '\n' +
        'approval during event (read inside button probe): ' + (diag.buyNowReplayApprovalDuringEvent == null ? '(none yet)' : diag.buyNowReplayApprovalDuringEvent ? 'yes' : 'no') + '\n' +
        'approval after .click returned: ' + (diag.buyNowReplayApprovalAfterClick == null ? '(none yet)' : diag.buyNowReplayApprovalAfterClick ? 'yes' : 'no') + '\n' +
        'approved replay listener target: [data-ever-leaf-consent-gate] (capturing)\n' +
        'approved replay listener phase: capture\n' +
        'Shopify button state after replay: ' + formatButtonSnapshot(inspectBuyNowButtonState(resolveRealBuyNowButton())) + '\n' +
        '--- ACCELERATED CHECKOUT STRUCTURE (Section 3/5) ---\n' +
        acceleratedCheckoutStructureText() + '\n' +
        '--- COPY DIAGNOSTICS (Section 16) ---\n' +
        'copy method: ' + (diag.copyMethod || 'none') + '\n' +
        'copy success: ' + (diag.copySuccess == null ? '(not attempted yet)' : diag.copySuccess ? 'yes' : 'no') + '\n' +
        'copy error: ' + (diag.copyError || '(none)');
    }

    function acceleratedCheckoutStructureText() {
      var info = inspectAcceleratedCheckoutHost();
      if (!info) return 'accelerated checkout host found: no (no [data-ever-leaf-consent-gate] on this page)';
      var iframeLines = info.iframeDetails.length
        ? info.iframeDetails
            .map(function (f, i) {
              return '  iframe #' + i + ': src=' + f.src + ' title=' + f.title + ' name=' + f.name;
            })
            .join('\n')
        : '  (none)';
      return (
        'accelerated checkout host found: ' + (info.hostFound ? 'yes' : 'no') + '\n' +
        'host tag: ' + info.hostTag + '\n' +
        'host class: ' + info.hostClass + '\n' +
        'host attributes: ' + info.hostAttributes + '\n' +
        'shadow root: ' + info.shadowRootState + '\n' +
        'iframe count: ' + info.iframeCount + '\n' +
        iframeLines + '\n' +
        'visible internal button found: ' + (info.visibleButtonFound ? 'yes' : 'no') + '\n' +
        'internal clickable location: ' + info.visibleButtonLocation + '\n' +
        'document click sees Buy Now: ' + (diag.documentClickSeesGate ? 'yes' : 'no') + '\n' +
        'document pointerdown sees Buy Now: ' + (diag.documentPointerdownSeesGate ? 'yes' : 'no') + '\n' +
        'document pointerup sees Buy Now: ' + (diag.documentPointerupSeesGate ? 'yes' : 'no') + '\n' +
        'host pointerdown seen: ' + (diag.hostPointerdownSeen ? 'yes' : 'no') + '\n' +
        'host pointerup seen: ' + (diag.hostPointerupSeen ? 'yes' : 'no') + '\n' +
        'focus before interaction: ' + diag.focusBeforeInteraction + '\n' +
        'focus after interaction: ' + diag.focusAfterInteraction
      );
    }

    function updateButtonGating() {
      var s = window.EverLeafPersonalizerState.get();
      var hasPhoto = !!s.customerFile;
      var committing = s.uploadStatus === 'uploading';
      var termsOk = checkbox ? checkbox.checked : true;

      if (addButton) {
        var weBlock = !hasPhoto || committing;
        if (weBlock) {
          addButton.disabled = true;
          addButton.setAttribute(PHOTO_BLOCKED_MARKER, 'true');
        } else if (addButton.getAttribute(PHOTO_BLOCKED_MARKER) === 'true') {
          // Only clear the disabled state THIS script itself applied —
          // never force-enable a button Dawn/liquid disabled for its own
          // reason (sold out / unavailable variant).
          addButton.removeAttribute(PHOTO_BLOCKED_MARKER);
          if (!initiallyDisabled) addButton.disabled = false;
        }
      }
      if (gate) {
        var buyNowBlocked = !hasPhoto || !termsOk || committing;
        gate.classList.toggle('ever-leaf-photo-required-blocked', buyNowBlocked);
        gate.setAttribute('aria-disabled', buyNowBlocked ? 'true' : 'false');
      }
      refreshDiagnostics();
    }

    window.EverLeafPersonalizerState.subscribe(updateButtonGating);
    if (checkbox) checkbox.addEventListener('change', updateButtonGating);
    updateButtonGating();

    /*
      DESKTOP PRODUCT SECTION PASS — Task 6, "No Photo = Disable
      Personalization". ONE canonical photo-ready check (`!!s.customerFile`,
      the exact same source of truth updateButtonGating() above already
      uses for Add to Cart/Buy Now), reused here to gate Dedication
      (Line 1/Line 2/Together-Separate) and Materials (the real Shopify
      variant radios) — nothing else has ever disabled these before this
      pass. Native `disabled` on real <input>/<button> elements blocks
      pointer AND keyboard interaction automatically (no separate keydown
      guards needed), and leaves the real Shopify variant selection/value
      completely untouched — this never fakes or replaces it, only blocks
      interaction with it. Shared between Desktop and Mobile by
      construction (one singleton state, one set of DOM nodes reparented —
      never cloned — onto the Mobile Stage), matching every other gate in
      this file; Upload/Remove already funnel through customerFile via
      handleLocalFileSelected()/removeCurrentPhoto(), so this subscription
      re-runs automatically on every Upload/Remove/Re-upload with no extra
      wiring, exactly like updateButtonGating() above.
    */
    function updatePersonalizationGating() {
      var s = window.EverLeafPersonalizerState.get();
      var hasPhoto = !!s.customerFile;
      document.querySelectorAll('[data-ever-leaf-cal-text-line1], [data-ever-leaf-cal-text-line2]').forEach(function (input) {
        input.disabled = !hasPhoto;
      });
      document.querySelectorAll('[data-ever-leaf-dedication-placement-toggle]').forEach(function (button) {
        button.disabled = !hasPhoto;
      });
      document.querySelectorAll('.ever-leaf-product__materials input[type="radio"]').forEach(function (radio) {
        radio.disabled = !hasPhoto;
      });
    }
    window.EverLeafPersonalizerState.subscribe(updatePersonalizationGating);
    updatePersonalizationGating();
    // A Theme Editor "config-only" block reload (settings change, no full
    // <ever-leaf-personalizer> reconnect) can replace the Dedication/
    // Materials markup with fresh, un-gated nodes — same reasoning as
    // bindDedicationInputs() re-running on shopify:section:load above; a
    // genuine no-op on the real storefront (design-mode gated).
    document.addEventListener('shopify:section:load', function () {
      if (!window.Shopify || !window.Shopify.designMode) return;
      updatePersonalizationGating();
    });

    /*
      PHASE 50A.3 — click acknowledgement pulse. A native 'click' event on
      addButton itself fires exactly once per real mouse click, touch tap, OR
      keyboard activation (Enter/Space on a focused <button> — the browser's
      own standard behavior; no separate keyboard handling needed). It is
      the one event in this whole architecture that is guaranteed to come
      ONLY from a real physical user gesture: PHASE 50A.2's approved internal
      resubmit calls `form.requestSubmit(addButton)`, which dispatches a
      'submit' event on the FORM — it does not, and cannot, synthesize a
      'click' event on the button (confirmed: requestSubmit() is not
      HTMLElement.click(), and nothing else in this file ever calls
      addButton.click() or dispatches a synthetic 'click' on it). So this
      listener needs no isTrusted check, no coordination with the commit
      state machine, and cannot double-fire from the internal handoff.
      Re-triggering the CSS animation on rapid repeated real clicks (so each
      new click gets its own pulse, not a no-op if the previous one is still
      finishing) needs the class removed and a synchronous reflow forced
      before re-adding it — the standard technique for restarting a CSS
      animation. Purely cosmetic: does not touch aria-disabled, disabled,
      .loading, the spinner, or any commit-flow state.
    */
    addButton.addEventListener('click', function () {
      addButton.classList.remove('ever-leaf-atc--click-pulse');
      void addButton.offsetWidth;
      addButton.classList.add('ever-leaf-atc--click-pulse');
    });
    // Housekeeping only — the animation's own fill behavior already returns
    // the button to transform:none the instant it finishes, so this has no
    // visual effect either way; it just keeps the DOM tidy rather than
    // leaving a permanently-stale trigger class behind after the first click.
    addButton.addEventListener('animationend', function (event) {
      if (event.animationName === 'ever-leaf-atc-click-pulse') addButton.classList.remove('ever-leaf-atc--click-pulse');
    });

    /*
      PHASE 50A.1 — Add to Cart immediate feedback. PHASE 50A's diagnosis
      found the FIRST customer click is correctly received and starts real
      work immediately, but Dawn's own visible loading treatment
      (.loading class + .loading__spinner reveal, in assets/product-form.js)
      is architecturally gated behind the SECOND, internally-approved
      resubmit — measured ~1.8s of near-silence (only a faint disabled-
      opacity dim) before the customer sees any strong feedback.

      Fix: apply the SAME .loading/.loading__spinner treatment Dawn already
      uses, ourselves, the instant we intercept the first submit — BEFORE
      awaiting any Mini/FilePond/hosted-URL work. Ownership then hands off
      to Dawn at the approved resubmit (see the two PHASE 50A.2 comments at
      that call site below — PHASE 50A.1's original assumption that Dawn
      would simply "re-apply the same state, idempotently" was wrong in two
      separate, independently-confirmed ways: (1) Dawn's own onSubmitHandler
      bails out via its OWN re-entrancy guard (`if (aria-disabled === 'true')
      return`) if it ever sees aria-disabled already 'true', which is exactly
      what PHASE 50A.1 left behind; (2) when the background photo pre-upload
      has already finished by click time, the resubmit happens SYNCHRONOUSLY,
      still nested inside the original submit event's own dispatch, and the
      WHATWG form-submission spec silently no-ops a second requestSubmit()
      while the first is still "firing submission events" for that same
      form). Dawn's own existing .finally() cleanup remains the single source
      of truth for removing the loading state after success/error, exactly as
      before. Nothing about the commit state machine, photoUploadManager,
      FilePond, line-item properties, or the resubmit mechanism itself is
      touched.

      everLeafLoadingActive tracks whether THIS code currently owns the
      loading state (true only between our own early apply and the moment
      ownership passes to Dawn) — required so a later, unrelated state
      change (e.g. a separate Buy Now failure) can never yank the loading
      state out from under Dawn's own legitimate in-flight ATC submission.
    */
    var everLeafLoadingActive = false;

    /*
      ADD TO CART SINGLE-FLIGHT LOCK. Root cause of the reproduced rapid-double
      Add to Cart (2 real /cart/add fetches, quantity 2): once our approved
      resubmit has handed off to Dawn, NOTHING of ours guarded the button any
      more — uploadStatus is back to 'selected', addToCartApproved was cleared
      by the approved submit, everLeafLoadingActive is false — and Dawn's only
      lock is `aria-disabled="true"`, which a click still passes through
      (aria-disabled is not native `disabled`). So a second activation reached
      the capturing interceptor below, was treated as a brand-new FIRST
      activation, ran a second commit (cache-hit), and that commit's
      onCommitted() did `removeAttribute('aria-disabled')` — erasing Dawn's own
      in-flight marker — so Dawn accepted the second submit too.

      `addToCartInFlight` is explicit operation ownership: acquired
      synchronously on the first accepted activation (before any async work, so
      already-queued duplicate submit events are swallowed), held through the
      commit, the approved resubmit and Dawn's own /cart/add, and released only
      on a genuine end: commit failure, stale-photo discard, cart error,
      Dawn finishing (its aria-disabled marker clearing), or the resubmit never
      reaching Dawn. `addToCartCommitted` marks the point our commit finished
      and Dawn owns the operation.
    */
    var addToCartInFlight = false;
    var addToCartCommitted = false;
    var addToCartResubmitIssuing = false;
    var addToCartIdleObserver = null;

    function releaseAddToCartLock() {
      addToCartInFlight = false;
      addToCartCommitted = false;
      if (addToCartIdleObserver) {
        addToCartIdleObserver.disconnect();
        addToCartIdleObserver = null;
      }
    }

    // Dawn's own onSubmitHandler sets aria-disabled="true" synchronously when it
    // takes the approved submit and removes it in its .finally() once the
    // /cart/add fetch chain (success OR failure) is fully done — the canonical
    // "Dawn is finished" signal. Event-driven (no polling, no timers).
    function releaseAddToCartLockWhenDawnIdle() {
      var btn = addButton;
      function dawnBusy() {
        return !!(btn && btn.isConnected && btn.getAttribute('aria-disabled') === 'true');
      }
      if (!dawnBusy() || typeof MutationObserver !== 'function') {
        releaseAddToCartLock();
        return;
      }
      if (addToCartIdleObserver) addToCartIdleObserver.disconnect();
      addToCartIdleObserver = new MutationObserver(function () {
        if (!dawnBusy()) releaseAddToCartLock();
      });
      addToCartIdleObserver.observe(btn, { attributes: true, attributeFilter: ['aria-disabled'] });
    }

    // bfcache restore: a frozen in-flight operation must not survive as a lock.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted && addToCartInFlight) {
        clearEarlyAtcLoadingState();
        everLeafLoadingActive = false;
        releaseAddToCartLock();
      }
    });

    function applyEarlyAtcLoadingState() {
      if (!addButton) return;
      addButton.setAttribute('aria-disabled', 'true');
      addButton.classList.add('loading');
      var spinner = addButton.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    }

    function clearEarlyAtcLoadingState() {
      if (!addButton) return;
      addButton.classList.remove('loading');
      var spinner = addButton.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
      // Deliberately never forces aria-disabled/disabled here — updateButtonGating()
      // (subscribed above, and re-run by the very state.update() call that accompanies
      // every failure path below) remains the single source of truth for whether the
      // button should end up enabled or stay disabled (still committing, or no photo).
    }

    // Failure-only cleanup: if OUR OWN early loading state is still active and the
    // commit we started transitions to 'error' (upload failure, hosted-URL rejection,
    // missing adapter — every runCommit() failure path already calls
    // state.update({uploadStatus:'error',...})), clear it. Never fires on the SUCCESS
    // transition ('uploading' -> 'selected'), which is handled by the synchronous
    // handoff in the interceptor itself, not here — avoids any hide-then-Dawn-reshows
    // flicker on the success path.
    window.EverLeafPersonalizerState.subscribe(function (s) {
      if (everLeafLoadingActive && s.uploadStatus === 'error') {
        clearEarlyAtcLoadingState();
        everLeafLoadingActive = false;
      }
      // Commit-phase failure only: once Dawn owns the operation (committed),
      // its own cart error/finish releases the lock instead, so an unrelated
      // error (e.g. Buy Now) cannot reopen the duplicate window mid-request.
      if (addToCartInFlight && !addToCartCommitted && s.uploadStatus === 'error') {
        releaseAddToCartLock();
      }
    });

    // Confirms the ACTUAL result of Dawn's own AJAX add-to-cart (Section 17
    // — Mini success / requestSubmit / no-exception are each individually
    // NOT success; a real cart mutation is), via Dawn's existing pub/sub bus
    // (assets/pubsub.js + assets/constants.js, both classic non-module
    // scripts loaded sitewide in layout/theme.liquid, so
    // `subscribe`/`PUB_SUB_EVENTS` are real global identifiers by the time
    // this deferred script runs). Matched by variant id so an unrelated
    // add-to-cart elsewhere on the page never marks OUR commit as done.
    // Only counts a cartUpdate/cartError as "ours" while a resubmit WE
    // triggered is actually in flight — set true right when
    // form.requestSubmit() is called below, cleared as soon as either event
    // lands or a fresh commit attempt starts. Otherwise an unrelated
    // add-to-cart elsewhere for the SAME variant (e.g. a quick-add) could
    // falsely mark our own diagnostic as confirmed.
    var awaitingCartConfirmation = false;
    // The CURRENT commit's own hosted URL — set in onReady, read by both the
    // FormData-level canonicalization (Section 6) and the cart.js line
    // matcher (Section 9), so both always operate on THIS commit's value
    // even if a later commit starts before this one's async work finishes.
    var lastCommittedHostedUrl = '';
    // Section 19-20 (this pass) — monotonic id, incremented once per actual
    // commit attempt (inside runCommit, past its early-return guards) so the
    // DEV panel can always tell CURRENT-attempt evidence from a previous
    // attempt's leftover values.
    var commitAttemptId = 0;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (!awaitingCartConfirmation) return;
        var variantInput = form.querySelector('input[name="id"]');
        if (variantInput && event && event.productVariantId && String(event.productVariantId) !== String(variantInput.value)) return;
        awaitingCartConfirmation = false;
        diag.cartUpdateSeen = true;
        // Section 12 — re-scan AFTER the real cart mutation, for comparison
        // against the "before Dawn" snapshot (catches a duplicate that only
        // Dawn's own fetch/response ever revealed).
        diag.finalPublicUploadCount = scanPublicUploadState().domCount;
        refreshDiagnostics();
        // Section 12/20 — cart.js is the ONLY acceptance-proof source; never
        // trust the cart drawer's own DOM/UI rendering. Matched primarily by
        // THIS commit's own hostedUrl (Section 9 fix), captured at commit
        // time so a later, separate commit can never shift which URL we're
        // looking for while this fetch is still in flight.
        var variantInputForCartJs = form.querySelector('input[name="id"]');
        reportCartJs(diag, refreshDiagnostics, variantInputForCartJs ? variantInputForCartJs.value : null, lastCommittedHostedUrl);
      });
      subscribe(PUB_SUB_EVENTS.cartError, function (event) {
        if (!awaitingCartConfirmation) return;
        var variantInput = form.querySelector('input[name="id"]');
        if (variantInput && event && event.productVariantId && String(event.productVariantId) !== String(variantInput.value)) return;
        awaitingCartConfirmation = false;
        diag.cartErrorSeen = true;
        diag.finalPublicUploadCount = scanPublicUploadState().domCount;
        diag.lastCommitError = (event && (event.message || event.errors)) || 'Shopify rejected the add-to-cart request.';
        // Section 18 — retry behavior: a real Dawn-side cart error (e.g. an
        // inventory race lost after Mini already succeeded) restores Add to
        // Cart / keeps the current photo+personalization intact for a retry,
        // exactly like any other failure path below.
        // Customer-facing text: only a message Shopify itself returned (it is served in the storefront's
        // language). Never our own English fallback — null lets setUploadStatus() show the localized
        // generic message (data-i18n-upload-error). The English text stays in diag for the DEV panel.
        window.EverLeafPersonalizerState.update({ uploadStatus: 'error', uploadError: event && typeof event.message === 'string' && event.message ? event.message : null });
        releaseAddToCartLock();
        refreshDiagnostics();
      });
    }

    /*
      Shared shape for both commit flows (Correction Pass 6): disable both
      buttons, sanitize + inject the CURRENT customerFile into Mini, drive it
      to FileStatus.PROCESSING_COMPLETE, then treat `item.serverId` as the
      authoritative hosted URL — the adapter itself creates the ONE
      `properties[Upload 1]` input and verifies exactly one populated upload
      property survives before ever calling onReady. Only then is
      `onCommitted()` invoked. On ANY failure (Mini error, processing
      timeout, invalid/empty serverId, property count != 1), Add to Cart is
      restored, the current photo/personalization is left untouched, and a
      compact error is surfaced — never submits an invalid configuration.
    */
    /*
      PHASE 50D — Photo Commit Atomicity / Stale Upload Guard.

      Root cause (confirmed directly against this file, not assumed):
      `record.promise` (captured once, below, at the moment THIS commit
      starts) is a direct reference to the raw promise chain returned by
      photoUploadManager.start()/runUploadFor() — see that function's own
      `.then(function (url) { if (currentFile === myFile) { status = ...;
      hostedUrl = url; } return url; }, ...)`. That internal
      `currentFile === myFile` check only gates whether the MANAGER's own
      cached status/hostedUrl fields get updated when a LATER, separate
      runCommit call later asks getRecordFor() — it does NOT gate the
      promise's own resolution value. So if the customer replaces or
      removes the photo while THIS commit's upload is still in flight, the
      promise this commit already attached its own .then() to (a few lines
      below, and in the cache-hit branch further down) still resolves with
      the ORIGINAL (now-stale) photo's real hosted URL, and
      canonicalizeLineItemProperties() (inside onHostedUrlReady) reads
      Dedication/layout/photo position/scale FRESH from PersonalizerState
      at that moment — i.e. whatever the CURRENT (new) photo's geometry
      happens to be. Left unguarded, that combination writes the stale
      photo's hosted URL alongside the new photo's geometry into the same
      cart line: Photo A's image, Photo B's position/scale.

      Fix: capture `committedFile` (below, unchanged) as this commit's own
      identity, and re-check it — via simple File-object reference
      equality, the SAME proven, self-invalidating pattern
      photoUploadManager itself already uses for its own staleness guard
      (see that function's header comment: two different File selections,
      even of the identical file from disk, are always different objects,
      so reference identity needs no separate revision counter/token) —
      at the very top of BOTH onHostedUrlReady and onHostedUrlFailed below,
      before either does anything else. A mismatch means the customer has
      since selected a different photo or removed it (removeCurrentPhoto()
      sets customerFile to null, which also fails this same identity
      check) — in that case this resolution is stale and is discarded
      entirely: no diag/property mutation, no state.update(), no
      onCommitted() call (so no approved resubmit, no /cart/add, no Buy Now
      replay), just the caller's own onStale() cleanup hook (each of the
      two call sites below/elsewhere supplies its own — the ATC
      interceptor clears its early-loading spinner state, Buy Now clears
      its own in-flight guard) so the customer is never shown a misleading
      "upload failed" message for a photo they already moved on from, and
      the CURRENT (new) photo's own state is never touched by this stale
      resolution.

      Deliberately NOT guarded: geometry (drag/zoom), Dedication, and
      variant changes to the SAME photo while a commit for that same photo
      is pending. canonicalizeLineItemProperties() already reads all of
      those fresh at resolution time (unchanged by this pass) — since the
      photo identity itself hasn't changed, this always describes one
      coherent, single-photo configuration (just its latest position/text
      rather than a stale one), never a cross-photo mix. Only a genuine
      photo replacement/removal is the category-C risk this guard exists
      for.
    */
    function runCommit(source, onCommitted, onStale) {
      var state = window.EverLeafPersonalizerState;
      var s = state.get();
      if (!s.customerFile) {
        diag.lastCommitError = 'No photo selected.';
        if (source === 'buy-now') buyNowActivationInFlight = false;
        refreshDiagnostics();
        return;
      }
      if (s.uploadStatus === 'uploading') return; // already committing

      // Section 19-20 — a NEW commit attempt starts here: stamp its
      // identity and reset the fields that belong to "the current attempt's
      // evidence" (shared by both Add to Cart and Buy Now), so a PREVIOUS
      // attempt's request/cartUpdate/cart.js/replay values can never be
      // mistaken for this attempt's. Static structure diagnostics
      // (accelerated checkout host inspection etc.) are untouched — those
      // aren't per-attempt. ATC's own submit listener already resets its
      // OWN additional fields before calling this — safe/idempotent overlap.
      commitAttemptId++;
      var analyticsOperationId = commitAttemptId;
      diag.currentAttemptId = commitAttemptId;
      diag.currentAttemptSource = source;
      diag.buyNowCommitSource = source;
      diag.serverId = '';
      diag.hostedUrlAccepted = false;
      diag.propertyValues = null;
      diag.dawnRequestObserved = false;
      diag.dawnRequestEndpoint = '';
      diag.dawnRequestPropertiesCount = null;
      diag.dawnRequestValues = null;
      diag.dawnRequestAllProperties = null;
      diag.dawnRequestNormalizedUploadCount = null;
      diag.cartUpdateSeen = false;
      diag.cartErrorSeen = false;
      diag.cartJsCheckedThisAttempt = false;
      diag.cartJsPropertiesCount = null;
      diag.cartJsUpload1 = '';
      diag.cartJsDedication = '';
      diag.cartJsPhotoX = '';
      diag.cartJsPhotoY = '';
      diag.cartJsPhotoScale = '';
      diag.cartJsConfigVersion = '';
      diag.cartJsUploadUrl = '';
      diag.cartJsLineKey = '';
      diag.cartJsLineQuantity = null;
      diag.cartJsAllProperties = null;
      diag.cartJsMatchedByUploadUrl = false;

      state.update({ uploadStatus: 'uploading', uploadError: null });
      diag.lastCommitError = '';
      diag.finalizeUpload = 'pending';
      refreshDiagnostics();

      var adapter = window.EverLeafMiniUploadAdapter;
      if (!adapter) {
        diag.finalizeUpload = 'rejected';
        diag.lastCommitError = 'Upload service is unavailable right now.';
        // uploadError stays null: the customer sees the localized generic message (data-i18n-upload-error);
        // the specific English reason above remains in diag.lastCommitError (DEV panel only).
        state.update({ uploadStatus: 'error', uploadError: null });
        refreshDiagnostics();
        return;
      }

      var committedFile = s.customerFile;

      // canonicalizeLineItemProperties() still reads Dedication/layout/
      // photo position/scale fresh from PersonalizerState every time it is
      // called below (current state always wins, whether the hosted URL
      // came from a background pre-upload, a joined in-flight upload, or a
      // fresh cold one), and `form` here is whatever the submit
      // interceptor's stale-form-reattachment fix already resolved to the
      // CURRENT live form for this attempt — none of the three paths below
      // ever skip canonicalization or change WHICH form gets canonicalized.
      function isCommitStale() {
        return state.get().customerFile !== committedFile;
      }

      function onHostedUrlReady(hostedUrl) {
        // PHASE 50D stale-commit guard — see this function's own header
        // comment above runCommit() for the full root-cause explanation.
        // Checked first, before any diag/property/state mutation.
        if (isCommitStale()) {
          diag.finalizeUpload = 'stale-commit-discarded';
          refreshDiagnostics();
          if (onStale) onStale();
          return;
        }
        diag.serverId = hostedUrl;
        diag.hostedUrlAccepted = true;
        lastCommittedHostedUrl = hostedUrl;
        diag.propertyValues = canonicalizeLineItemProperties(form, hostedUrl);
        diag.finalizeUpload = 'resolved';
        if (diag.buyNowCommitSource === 'buy-now') diag.buyNowFinalFormDataReady = true;
        state.update({ uploadStatus: 'selected', uploadError: null });
        refreshDiagnostics();
        emitPersonalizationCommitted(source, analyticsOperationId);
        onCommitted();
      }

      function onHostedUrlFailed(message) {
        // PHASE 50D stale-commit guard — a stale photo's upload failure
        // must never surface as an error for whatever photo is current
        // now (the customer already moved on from it).
        if (isCommitStale()) {
          diag.finalizeUpload = 'stale-commit-discarded';
          refreshDiagnostics();
          if (onStale) onStale();
          return;
        }
        diag.finalizeUpload = 'rejected';
        diag.lastCommitError = message || 'Upload failed.';
        if (diag.buyNowCommitSource === 'buy-now') {
          diag.buyNowLastError = diag.lastCommitError;
          // Section 14 — error/retry: clear the in-flight guard so a fresh
          // Buy Now gesture can retry without re-selecting Photo.
          buyNowActivationInFlight = false;
        }
        // `message` is the upload adapter's raw English/technical reason (e.g. FilePond status text). It is kept
        // in diag.lastCommitError for the DEV panel but must not reach customers: null → the localized generic
        // message (data-i18n-upload-error, Hebrew on a Hebrew storefront).
        state.update({ uploadStatus: 'error', uploadError: null });
        refreshDiagnostics();
      }

      // BACKGROUND PHOTO PRE-UPLOAD MICRO-PASS — photoUploadManager (module-
      // level, see its own header comment) is now the ONE authoritative
      // upload lifecycle for the current File, whether or not a background
      // upload already started for it.
      var record = photoUploadManager.getRecordFor(committedFile);
      if (!record || record.status === 'error') {
        // IDLE fallback (Section 13 — background pre-upload never started
        // for this File, e.g. the adapter wasn't ready at selection time) or
        // ERROR retry (Section 12 — a prior attempt for this exact File
        // failed): start (or restart) exactly one fresh attempt.
        // photoUploadManager.start() is itself a no-op if a record already
        // exists in 'uploading'/'ready' state, so this call is always safe.
        photoUploadManager.start(committedFile);
        record = photoUploadManager.getRecordFor(committedFile);
      }

      if (record.status === 'ready') {
        // READY case (Section 10) — skip Mini/FilePond entirely, using the
        // already-confirmed hosted URL. This is the fast path — the same
        // shape as the prior pass's warm-cache hit.
        diag.finalizeUpload = 'cache-hit';
        onHostedUrlReady(record.hostedUrl);
        return;
      }

      // UPLOADING case (Section 11) — join the SAME in-flight operation
      // (whether it was already running quietly in the background, or was
      // just started/restarted above), rather than ever starting a second,
      // concurrent finalizeUpload() call for this File.
      diag.finalizeUpload = 'joined-upload';
      record.promise.then(
        function (hostedUrl) {
          onHostedUrlReady(hostedUrl);
        },
        function (err) {
          onHostedUrlFailed(err && err.message);
        }
      );
    }

    /*
      Correction Pass 7, Section 2-4/12 — the exact instrumentation that
      root-caused the duplicate "Upload 1": scans the WHOLE document for
      every `properties[Upload*]` input right at the moment Dawn is about to
      read them, and separately builds `new FormData(form)` at that same
      instant (the only way to know what Dawn's own later `new
      FormData(this.form)` call — reading the identical, unchanged DOM —
      will see). Distinguishes an Ever-Leaf-owned input (marked via the
      adapter's OWNED_PROPERTY_MARKER, unused as of this pass but checked for
      forward-compatibility / the Section 8 fallback) from anything else
      (Mini's or otherwise).
    */
    function scanPublicUploadState() {
      var adapter = window.EverLeafMiniUploadAdapter;
      var selector = (adapter && adapter.UPLOAD_PROPERTY_SELECTOR) || 'input[name^="properties[Upload"]';
      var ownedMarker = (adapter && adapter.OWNED_PROPERTY_MARKER) || 'data-ever-leaf-owned-upload-property';
      var domInputs = Array.prototype.slice.call(document.querySelectorAll(selector));
      var owned = domInputs.filter(function (el) {
        return el.getAttribute(ownedMarker) === 'true';
      }).length;

      var formDataCount = 0;
      if (form) {
        var fd = new FormData(form);
        var entries = typeof fd.entries === 'function' ? Array.from(fd.entries()) : [];
        entries.forEach(function (pair) {
          if (String(pair[0]).indexOf('properties[Upload') === 0) formDataCount++;
        });
      }

      return {
        domCount: domInputs.length,
        formDataCount: formDataCount,
        owned: owned,
        other: domInputs.length - owned
      };
    }

    /*
      Section 10 — proves whether FormData(form), built at THIS exact
      instant (immediately before requestSubmit), actually contains each of
      our 7 properties. Since nothing mutates the form between this check
      and Dawn's own later `new FormData(this.form)` call in the SAME
      synchronous event dispatch, this is authoritative for what Dawn will
      see.
    */
    function checkFormDataPresence(fd) {
      var entries = typeof fd.entries === 'function' ? Array.from(fd.entries()) : [];
      var seen = {};
      entries.forEach(function (pair) {
        seen[pair[0]] = true;
      });
      return {
        upload: !!seen['properties[' + LINE_ITEM_PROPERTY_NAMES.upload + ']'],
        text: !!seen['properties[' + LINE_ITEM_PROPERTY_NAMES.text + ']'],
        photoX: !!seen['properties[' + LINE_ITEM_PROPERTY_NAMES.photoX + ']'],
        photoY: !!seen['properties[' + LINE_ITEM_PROPERTY_NAMES.photoY + ']'],
        photoScale: !!seen['properties[' + LINE_ITEM_PROPERTY_NAMES.photoScale + ']'],
        configVersion: !!seen['properties[' + LINE_ITEM_PROPERTY_NAMES.configVersion + ']'],
        uploadUrl: !!seen['properties[' + LINE_ITEM_PROPERTY_NAMES.uploadUrl + ']']
      };
    }

    /*
      Final Correction Pass, Section 3-8 — THE fix for the intermittent
      duplicate: real Dev evidence showed an 8th property reaching the
      actual /cart/add request and cart.js even though our OWN FormData
      check (built the same way, on the same form) only ever saw our 7 —
      which is exactly what happens if something adds its entry via the
      standard `formdata` DOM event (fired by the browser every time a
      FormData object is constructed from a form) rather than by inserting a
      DOM node: invisible to any DOM/queried-FormData check, but present in
      EVERY FormData instance built from this form afterward, including
      Dawn's own real one.

      This listener runs on every `formdata` dispatch for our form (cheap;
      in practice that's only our own diagnostic checks and Dawn's real
      submit-time one, since Mini/editing never triggers a FormData
      construction) and canonicalizes IN PLACE: removes any entry whose
      normalized inner name matches "upload 1" (Section 6 — case/whitespace-
      insensitive, so `properties[ Upload 1 ]`/`properties[upload 1]`/etc.
      all count), then sets exactly one correct
      `properties[Upload 1] = <current hostedUrl>`. Registered on DOCUMENT
      in the BUBBLE phase (capture:false) specifically so it fires AFTER any
      listener registered directly on the form itself (AT_TARGET always
      completes before bubbling reaches an ancestor), regardless of
      script-load order — the same "run last" guarantee Section 7 asks for,
      achieved without needing to know whether such a listener even exists.
      Never touches `_ever_leaf_upload_url` (Section 6's explicit
      requirement) or any other Ever Leaf property.
    */
    document.addEventListener(
      'formdata',
      function (event) {
        if (event.target !== form) return;
        var fd = event.formData;
        var toDelete = [];
        var keysIterator = typeof fd.keys === 'function' ? Array.from(fd.keys()) : [];
        keysIterator.forEach(function (key) {
          if (isUploadAliasKey(key) && key !== 'properties[' + LINE_ITEM_PROPERTY_NAMES.upload + ']') {
            toDelete.push(key);
          }
        });
        toDelete.forEach(function (key) {
          fd.delete(key);
        });
        fd.set('properties[' + LINE_ITEM_PROPERTY_NAMES.upload + ']', lastCommittedHostedUrl || '');
      },
      false
    );

    var addToCartApproved = false;
    document.addEventListener(
      'submit',
      function (event) {
        // PASS 3A MANUAL QA CORRECTION PASS, Section 15 — root cause of the
        // confirmed real-browser regression "Add to Cart adds nothing at
        // all, no Dedication, no properties": `form`/`addButton` above are
        // resolved exactly ONCE, at wire time (this whole function is
        // guarded by `addToCartGatingWired` to run only once ever per
        // page). The Shopify Theme Editor's Section Rendering API replaces
        // this ENTIRE section's DOM — including the purchase form — on ANY
        // settings change to ANY block within it, not just the purchase
        // block itself (e.g. testing the new Dedication Text Config
        // Together/Split fields, or the Photo Outline calibration this
        // same pass adds, both live in this section). Once that happens,
        // the real submit's `event.target` is the NEW live form, which
        // structurally IS the correct Add to Cart form but is never
        // `=== form` (the old, now-detached node) — the old reference-
        // identity check silently failed forever after, stepping aside and
        // letting Dawn's OWN unintercepted handler submit the fresh form
        // with none of our properties ever written (exactly "nothing
        // there"). Fixed: gate structurally (is this actually an Ever Leaf
        // add-to-cart form?) instead of by stale reference identity, and
        // re-resolve `form`/`addButton` to the CURRENT live node whenever
        // they differ. `form`/`addButton` are ordinary function-scoped
        // `var`s that every other closure in this function (scanPublicUploadState,
        // the `formdata`/bubble-phase listeners below, reportCartJs's own
        // form lookups) already reads by reference, not by value — so this
        // one reassignment keeps ALL of them correct for the rest of the
        // page's life, not just this one attempt, with no other code
        // needing to change.
        if (!event.target || typeof event.target.getAttribute !== 'function' || event.target.getAttribute('data-type') !== 'add-to-cart-form') return;
        if (event.target !== form) {
          form = event.target;
          addButton = form.querySelector('button[name="add"]') || addButton;
          diag.formReattachedAfterSectionReload = true;
          // The previous form (and any operation on it) is gone.
          releaseAddToCartLock();
        }
        // Only the submit WE issue (flagged for the synchronous duration of our
        // own requestSubmit call) may be treated as the approved resubmit — a
        // physical activation that lands between approval and our deferred
        // resubmit must not consume the approval.
        if (addToCartApproved && addToCartResubmitIssuing) {
          // Section 13 — approved resubmit: detect, clear, and step aside
          // completely. NOT preventDefault, NOT stop(Immediate)Propagation,
          // NOT another upload.
          addToCartApproved = false;
          diag.approvalFlag = false;
          diag.approvedResubmitObserved = true;
          diag.everLeafSteppedAside = true;
          var snap = scanPublicUploadState();
          diag.publicUploadInputsBeforeDawn = snap.domCount;
          diag.publicUploadFormDataEntriesBeforeDawn = snap.formDataCount;
          diag.everLeafOwnedUploadInputs = snap.owned;
          diag.miniOrOtherUploadInputs = snap.other;
          // This new FormData(form) call itself dispatches 'formdata' on
          // `form` (see the listener above) BEFORE it returns, so `fdNow`
          // already reflects the canonicalized (exactly-one-Upload-1) state
          // — Section 4/10's "dump ALL properties" therefore shows the
          // truth Dawn's own later FormData construction will also see.
          var fdNow = new FormData(form);
          diag.formDataPresence = checkFormDataPresence(fdNow);
          diag.formDataAllProperties = dumpAllPropertiesFromFormData(fdNow);
          var normalizedUploadCount = 0;
          Array.from(fdNow.keys()).forEach(function (key) {
            if (isUploadAliasKey(key)) normalizedUploadCount++;
          });
          diag.formDataNormalizedUploadCount = normalizedUploadCount;
          refreshDiagnostics();
          // Section 11 — the wrapped fetch() captures the outgoing request
          // synchronously inside Dawn's own onSubmitHandler, which runs
          // immediately after this listener returns (same event dispatch);
          // a 0ms timeout is enough slack to read it back right after.
          setTimeout(function () {
            if (lastDawnCartAddRequest) {
              diag.dawnRequestObserved = true;
              diag.dawnRequestEndpoint = lastDawnCartAddRequest.url;
              diag.dawnRequestPropertiesCount = lastDawnCartAddRequest.count;
              diag.dawnRequestValues = lastDawnCartAddRequest.properties;
              diag.dawnRequestAllProperties = lastDawnCartAddRequest.allProperties;
              diag.dawnRequestNormalizedUploadCount = lastDawnCartAddRequest.uploadAliasCount;
              refreshDiagnostics();
            }
          }, 0);
          return;
        }
        // Single-flight: a second activation while an operation is in flight
        // (committing, approved-but-not-yet-resubmitted, or Dawn's /cart/add
        // pending) is swallowed here — never reaches Dawn, never starts a
        // second commit, never touches aria-disabled.
        if (addToCartInFlight) {
          event.preventDefault();
          event.stopImmediatePropagation();
          diag.duplicateAddToCartSwallowed = (diag.duplicateAddToCartSwallowed || 0) + 1;
          return;
        }
        addToCartInFlight = true;
        addToCartCommitted = false;
        diag.atcFirstSubmitIntercepted = true;
        diag.buyNowCommitSource = 'add-to-cart';
        // A fresh attempt — reset the confirmation-tracking fields so a
        // PRIOR attempt's cartUpdate/cartError can never bleed into this
        // one's diagnostic readout.
        diag.cartUpdateSeen = false;
        diag.cartErrorSeen = false;
        diag.approvedResubmitObserved = false;
        diag.everLeafSteppedAside = false;
        diag.termsConsentSteppedAside = false;
        diag.dawnSubmitReached = false;
        diag.fileStatusCode = null;
        diag.processFileCalled = false;
        diag.processFileResolved = false;
        diag.processFileError = '';
        diag.serverId = '';
        diag.hostedUrlAccepted = false;
        diag.publicUploadInputsBeforeDawn = null;
        diag.publicUploadFormDataEntriesBeforeDawn = null;
        diag.everLeafOwnedUploadInputs = null;
        diag.miniOrOtherUploadInputs = null;
        diag.finalPublicUploadCount = null;
        diag.propertyValues = null;
        diag.formDataPresence = null;
        diag.dawnRequestObserved = false;
        diag.dawnRequestEndpoint = '';
        diag.dawnRequestPropertiesCount = null;
        diag.dawnRequestValues = null;
        lastDawnCartAddRequest = null;
        event.preventDefault();
        event.stopImmediatePropagation();
        // PHASE 50A.1 — apply Dawn's own loading treatment immediately, before
        // awaiting any Mini/FilePond/hosted-URL work (see this function's own
        // header comment above for the full ownership/handoff/cleanup model).
        applyEarlyAtcLoadingState();
        everLeafLoadingActive = true;
        refreshDiagnostics();
        var commitAttemptBefore = commitAttemptId;
        runCommit('add-to-cart', function () {
          addToCartCommitted = true; // our commit is done; Dawn owns the operation from here
          diag.approvalFlag = true;
          diag.requestSubmitCalled = true;
          refreshDiagnostics();
          addToCartApproved = true;
          awaitingCartConfirmation = true;
          everLeafLoadingActive = false;
          // PHASE 50A.2 — Dawn's own onSubmitHandler (assets/product-form.js)
          // starts with `if (this.submitButton.getAttribute('aria-disabled')
          // === 'true') return;` — a re-entrancy guard against a SECOND real
          // Dawn submission while its own fetch is still in flight. PHASE
          // 50A.1 left `aria-disabled="true"` set (from applyEarlyAtcLoadingState
          // above) all the way through to this resubmit, which made Dawn's
          // guard mistake OUR marker for its own in-flight submission and
          // return immediately — before handleErrorMessage(), before its own
          // aria-disabled/.loading (harmless — already set), and critically
          // before fetch(routes.cart_add_url, ...). Net effect: the spinner
          // showed, but the real cart request never happened. Clearing the
          // attribute here (not just setting it 'false' — Dawn's check is a
          // strict 'true' string match either way, but removing is cleanest)
          // lets Dawn's guard read false and actually proceed. This is
          // read+cleared synchronously, inside the same call stack that
          // immediately invokes requestSubmit below — the browser never gets
          // a chance to paint between clearing it and Dawn re-setting it two
          // lines into its own handler, so there is no visible flicker.
          // `.loading`/`.loading__spinner` are deliberately left untouched
          // (still applied) so the button never visually looks idle.
          // Native `addButton.disabled` is not the blocker here — the state
          // subscriber chain above (state.update({uploadStatus:'selected'})
          // inside runCommit's own success path, called before this callback
          // ever runs) already synchronously cleared it via updateButtonGating()
          // — but it's asserted defensively in case that ordering ever changes.
          addButton.removeAttribute('aria-disabled');
          if (addButton.disabled) addButton.disabled = false;
          // PHASE 50A.2, second finding — when the background photo pre-upload
          // has already finished by the time the customer clicks, runCommit's
          // 'ready'/cache-hit path (see runCommit above) calls onHostedUrlReady
          // — and therefore this whole callback, including requestSubmit()
          // below — SYNCHRONOUSLY, still nested inside the call stack of the
          // ORIGINAL submit event's own dispatch (the one this capturing
          // listener is currently handling). Per the WHATWG HTML forms spec,
          // a <form> tracks a "firing submission events" flag for the
          // duration of one submission's event dispatch; requestSubmit()/
          // submit() silently no-op if that flag is still true for the same
          // form — i.e. a form cannot be told to submit again while it is
          // still in the middle of dispatching its current submit event.
          // Confirmed empirically: calling Dawn's onSubmitHandler directly
          // (bypassing event dispatch) always works; calling
          // form.requestSubmit() synchronously from here silently does
          // nothing whenever the cache-hit path was taken (reproduced
          // consistently), while the async/joined-upload path (which resolves
          // via a Promise .then(), already outside the original dispatch's
          // call stack by the time it runs) was unaffected. Deferring by one
          // macrotask guarantees the original dispatch has fully unwound
          // (flag cleared) before this resubmit, for BOTH paths uniformly —
          // a single setTimeout(0) tick is imperceptible and .loading/the
          // spinner stay applied throughout, so there is no visible idle
          // flash in that gap.
          setTimeout(function () {
            if (typeof form.requestSubmit !== 'function') {
              form.submit();
              return;
            }
            var issued = false;
            addToCartResubmitIssuing = true;
            try {
              form.requestSubmit(addButton || undefined);
              issued = true;
            } finally {
              addToCartResubmitIssuing = false;
              if (!issued) {
                addToCartApproved = false;
                clearEarlyAtcLoadingState();
                releaseAddToCartLock();
              }
            }
            // requestSubmit() dispatches synchronously, so by now the approved
            // branch above has consumed the approval (and Dawn's handler has
            // run). Still approved => the submit never happened (e.g. blocked
            // by form validation): nothing is in flight, so clean up.
            if (addToCartApproved) {
              addToCartApproved = false;
              clearEarlyAtcLoadingState();
              releaseAddToCartLock();
              return;
            }
            releaseAddToCartLockWhenDawnIdle();
          }, 0);
        }, function () {
          // PHASE 50D — stale-commit cleanup. This photo was replaced or
          // removed before its upload resolved; the resolution itself was
          // already discarded (see runCommit's own header comment), so
          // this only needs to clear OUR early-loading visual state (the
          // spinner AND the aria-disabled marker PHASE 50A.1/50A.2 applied
          // immediately on click — both, not just the spinner class: an
          // earlier version of this cleanup only cleared the class,
          // leaving aria-disabled="true" behind, which the button's own
          // CSS treats identically to a real disabled state (dimmed,
          // cursor:not-allowed) — confirmed live via the deterministic
          // A->B race test, which caught exactly this gap) — never touched
          // Dawn, never approved a resubmit, so there is nothing else to
          // undo. updateButtonGating() (already subscribed elsewhere)
          // independently re-derives the correct native disabled state
          // from whatever the CURRENT photo/state is, which was never
          // touched by this stale resolution.
          clearEarlyAtcLoadingState();
          addButton.removeAttribute('aria-disabled');
          everLeafLoadingActive = false;
          releaseAddToCartLock();
          refreshDiagnostics();
        });
        // PHASE 50A.1 failure cleanup — covers runCommit's own SYNCHRONOUS
        // bail-outs (e.g. no photo selected), which return before ever
        // calling state.update({uploadStatus:'uploading',...}) and therefore
        // never reach the async 'error' subscription added above. If we
        // still own the loading state right after this synchronous call
        // returns, AND no resubmit was approved, AND the commit never
        // actually entered its 'uploading' state, nothing else will ever
        // clean this up — do it now. Never fires on the real async path
        // (whether it succeeds or later fails), since runCommit sets
        // uploadStatus to 'uploading' synchronously before doing any async
        // work whenever it doesn't bail out early.
        if (everLeafLoadingActive && !addToCartApproved && window.EverLeafPersonalizerState.get().uploadStatus !== 'uploading') {
          clearEarlyAtcLoadingState();
          everLeafLoadingActive = false;
        }
        // runCommit returned without starting a commit attempt (no photo, or
        // another flow already 'uploading'): nothing was started, so this
        // activation must not leave the lock held.
        if (commitAttemptId === commitAttemptBefore && !addToCartCommitted) {
          clearEarlyAtcLoadingState();
          everLeafLoadingActive = false;
          releaseAddToCartLock();
        }
      },
      true
    );

    // Section 16 — proves the approved resubmit actually reached Dawn's own
    // handler without inspecting/modifying assets/product-form.js: this is a
    // BUBBLE-phase (not capturing) listener on document, so for a submit
    // whose target is `form` it only runs AFTER the full capture phase and
    // the form's own AT_TARGET listeners (Dawn's onSubmitHandler included)
    // have already executed — Dawn's handler only calls preventDefault(),
    // never stop(Immediate)Propagation(), so its having run is exactly what
    // allows this listener to fire at all. On the FIRST (intercepted) submit
    // this never fires, because our own capturing interceptor above already
    // called stopImmediatePropagation() before the event could reach here.
    document.addEventListener(
      'submit',
      function (event) {
        if (event.target !== form) return;
        diag.termsConsentSteppedAside = true;
        diag.dawnSubmitReached = true;
        refreshDiagnostics();
      },
      false
    );

    function describeClickedElement(el) {
      if (!el || el.nodeType !== 1) return '(none)';
      var tag = el.tagName.toLowerCase();
      var cls = el.className ? '.' + String(el.className).trim().split(/\s+/).join('.') : '';
      var name = el.getAttribute && el.getAttribute('name');
      var type = el.getAttribute && el.getAttribute('type');
      return tag + cls + (name ? '[name="' + name + '"]' : '') + (type ? '[type="' + type + '"]' : '');
    }

    /*
      Sections 3-5 (this pass) — inspects the ACTUAL rendered accelerated
      checkout markup inside the gate, without mutating anything. Recomputed
      fresh on every refreshDiagnostics() call (cheap — a handful of
      querySelector calls, no event listeners), never cached, since Shopify
      may render/replace this content asynchronously after page load.

      A null `host.shadowRoot` is intentionally ambiguous ("closed-or-none")
      — that's not a bug in this code, it's the browser deliberately hiding
      that information from us; a CLOSED shadow root is indistinguishable
      from no shadow root at all via any public API. That ambiguity is
      itself useful diagnostic evidence, not a gap to work around.
    */
    function inspectAcceleratedCheckoutHost() {
      if (!gate) return null;
      var host = gate.firstElementChild;
      var result = {
        hostFound: !!host,
        hostTag: host ? host.tagName.toLowerCase() : '(none)',
        hostClass: host ? host.className || '(no class)' : '(none)',
        hostAttributes: host
          ? Array.prototype.map
              .call(host.attributes || [], function (a) {
                return a.name + '="' + a.value + '"';
              })
              .join(' ') || '(none)'
          : '(none)',
        shadowRootState: 'none',
        iframeCount: 0,
        iframeDetails: [],
        visibleButtonFound: false,
        visibleButtonLocation: 'unknown'
      };
      if (!host) return result;

      if (host.shadowRoot) {
        result.shadowRootState = 'open';
        var innerControls = host.shadowRoot.querySelectorAll('button, [role="button"], a');
        if (innerControls.length) {
          result.visibleButtonFound = true;
          result.visibleButtonLocation = 'shadow DOM (open)';
        }
      } else if (typeof host.attachShadow === 'function') {
        result.shadowRootState = 'closed-or-none (cannot distinguish from outside)';
      }

      var iframes = gate.querySelectorAll('iframe');
      result.iframeCount = iframes.length;
      Array.prototype.forEach.call(iframes, function (frame) {
        result.iframeDetails.push({
          src: frame.getAttribute('src') || '(none)',
          title: frame.getAttribute('title') || '(none)',
          name: frame.getAttribute('name') || '(none)'
        });
      });
      if (iframes.length && !result.visibleButtonFound) {
        result.visibleButtonFound = true;
        result.visibleButtonLocation = 'iframe (' + iframes.length + ') — a cross-origin iframe cannot be inspected further or intercepted from this page';
      }
      if (!result.visibleButtonFound) {
        var normalControls = gate.querySelectorAll('button, [role="button"], a');
        if (normalControls.length) {
          result.visibleButtonFound = true;
          result.visibleButtonLocation = 'normal DOM';
        }
      }
      return result;
    }

    // Scoped, non-noisy interaction observers (Section 5) — filtered to the
    // gate area only, never a global/unscoped trace. Used purely to
    // determine WHERE in the event sequence (if anywhere) a real Buy Now
    // activation becomes observable from the parent document.
    document.addEventListener(
      'click',
      function (event) {
        if (gate && (event.target === gate || gate.contains(event.target))) {
          diag.documentClickSeesGate = true;
          refreshDiagnostics();
        }
      },
      true
    );
    document.addEventListener(
      'pointerdown',
      function (event) {
        if (gate && (event.target === gate || gate.contains(event.target))) {
          if (!diag.documentPointerdownSeesGate) {
            // First observed interaction in this attempt — snapshot
            // whatever was focused right before it, for comparison against
            // focusAfterInteraction (the focusin listener below).
            diag.focusBeforeInteraction = describeClickedElement(document.activeElement);
          }
          diag.documentPointerdownSeesGate = true;
          refreshDiagnostics();
        }
      },
      true
    );
    document.addEventListener(
      'pointerup',
      function (event) {
        if (gate && (event.target === gate || gate.contains(event.target))) {
          diag.documentPointerupSeesGate = true;
          refreshDiagnostics();
        }
      },
      true
    );
    // focusin bubbles and — critically — fires on the parent document even
    // when focus moves INTO a cross-origin iframe (the iframe element
    // itself becomes document.activeElement) or a closed shadow host. This
    // is the one signal Section 6 asks for that survives even the
    // cross-origin-iframe case, though — being only "focus moved here", not
    // "user activated it" — it cannot itself be used to pause/prevent the
    // original action.
    document.addEventListener('focusin', function (event) {
      if (gate && (event.target === gate || gate.contains(event.target))) {
        diag.focusAfterInteraction = describeClickedElement(event.target);
        refreshDiagnostics();
      }
    });
    /*
      NEXT 1 (this pass) — Buy Now activation via a validated pointer
      gesture, replacing the 'click'-based interception above. H.4's live
      evidence proved: pointerdown/pointerup on the real Buy Now button ARE
      observable via a capturing listener on the gate; a native 'click' is
      NOT (something suppresses it before it's ever generated — most likely
      Shopify's own pointerup handler on the real button calling
      preventDefault(), which per the Pointer Events spec cancels the
      follow-up click; NOT treated as proven, just the most consistent
      explanation for the proven facts). This section intercepts at the
      LATEST proven-observable point (a capturing pointerup listener on the
      gate) instead of guessing where Shopify's own trigger actually lives.

      The real button is NEVER cached across gestures — Shopify may
      re-render this markup, so every gesture re-resolves it fresh
      (Section 4 of the brief).
    */
    var BUY_NOW_BUTTON_SELECTOR = '.shopify-payment-button .shopify-payment-button__button.shopify-payment-button__button--unbranded';
    var BUY_NOW_MOVE_THRESHOLD_PX = 10; // small, internal only — never exposed to customers

    function resolveRealBuyNowButton() {
      if (!gate) return null;
      var btn = gate.querySelector(BUY_NOW_BUTTON_SELECTOR);
      if (!btn || btn.tagName !== 'BUTTON') return null;
      if (btn.disabled) return null;
      if (!btn.getClientRects().length) return null; // not currently rendered/visible
      return btn;
    }

    /*
      REPLAY TARGET RESOLUTION pass (Section 8/10-13) — a SEPARATE concept
      from resolveRealBuyNowButton() above, which stays exactly as-is and is
      used ONLY to validate the customer's original pointer gesture (Section
      14 — do not weaken that). This function answers a different question:
      "what is the CURRENT real Shopify Buy Now button we should replay
      activation on, right now, after a successful commit?" It must:
        - NEVER require identity with the original gesture's button (Section
          13 — Shopify may legitimately re-render it during the async gap);
          identity is only ever recorded for diagnostics, never enforced.
        - NEVER reject merely because Ever Leaf's OWN temporary gating
          (the .ever-leaf-photo-required-blocked class on `gate`) happens to
          still be applied (Section 10/12) — that class only sets opacity/
          pointer-events on OUR wrapper div, never anything Shopify-owned,
          and is explicitly, defensively cleared by
          releaseEverLeafReplayGating() before this runs.
        - ALWAYS respect a genuinely Shopify-owned disabled state (the
          button's own native `disabled` property) — never overridden
          (Section 11).
    */
    function inspectBuyNowButtonState(el) {
      if (!el) return null;
      var cs = null;
      try {
        cs = window.getComputedStyle(el);
      } catch (e) {
        cs = null;
      }
      return {
        tag: el.tagName ? el.tagName.toLowerCase() : '(none)',
        cls: el.className || '(no class)',
        isConnected: !!el.isConnected,
        disabledProperty: !!el.disabled,
        disabledAttribute: el.hasAttribute && el.hasAttribute('disabled'),
        ariaDisabled: el.getAttribute ? el.getAttribute('aria-disabled') : null,
        hidden: !!el.hidden,
        display: cs ? cs.display : '(n/a)',
        visibility: cs ? cs.visibility : '(n/a)',
        pointerEvents: cs ? cs.pointerEvents : '(n/a)',
        hasOffsetParent: !!el.offsetParent
      };
    }

    function formatButtonSnapshot(snap) {
      if (!snap) return '(none yet)';
      return (
        'tag=' + snap.tag + ' class=' + snap.cls + ' isConnected=' + snap.isConnected + ' disabled(property)=' + snap.disabledProperty + ' disabled(attribute)=' + snap.disabledAttribute + ' aria-disabled=' + (snap.ariaDisabled == null ? '(none)' : snap.ariaDisabled) + ' hidden=' + snap.hidden + ' display=' + snap.display + ' visibility=' + snap.visibility + ' pointer-events=' + snap.pointerEvents + ' offsetParent=' + (snap.hasOffsetParent ? 'present' : 'null')
      );
    }

    // STANDALONE DEV END-TO-END PROPERTY AUDIT pass — shared text
    // formatters, used by BOTH the main DEV CART COMMIT panel and Copy Buy
    // Now report, so the two never drift out of sync.
    function formatBuyNowExecutionPathText(env) {
      var lines = [];
      lines.push('standalone / Theme Editor: ' + (env ? (env.insideIframe ? 'Theme Editor / iframe' : 'standalone top-level') : '(none yet)'));
      lines.push('Shopify.designMode: ' + (env && env.shopifyDesignMode != null ? env.shopifyDesignMode : '(unavailable)'));
      lines.push('current URL: ' + (env ? env.currentUrl : '(none yet)'));
      lines.push('original user event isTrusted: ' + (diag.buyNowOriginalGestureIsTrusted == null ? '(none yet)' : diag.buyNowOriginalGestureIsTrusted));
      lines.push('Ever Leaf intercepted original Buy Now: yes');
      lines.push('runCommit("buy-now") executed: ' + (diag.buyNowRunCommitEntered ? 'yes' : 'no'));
      lines.push('Mini processing executed during Buy Now: ' + (diag.buyNowMiniProcessingComplete ? 'yes' : 'no'));
      lines.push('current customerFile present: ' + (window.EverLeafPersonalizerState.get().customerFile ? 'yes' : 'no'));
      lines.push('current Mini serverId ready: ' + (diag.hostedUrlAccepted ? 'yes' : 'no'));
      lines.push('serverId: ' + (diag.serverId || '(none yet)'));
      lines.push('canonicalization executed: ' + (diag.buyNowSevenPropertiesReady ? 'yes' : 'no'));
      lines.push('Shopify continuation mechanism: ' + (diag.buyNowReplayMethodUsed || '(none yet)'));
      lines.push('programmatic Shopify button click attempted: ' + (diag.buyNowReplayClickInvoked ? 'yes' : 'no'));
      lines.push('programmatic Shopify click observed (approved replay): ' + (diag.buyNowApprovedContinuationInvoked ? 'yes' : 'no'));
      lines.push('real checkout navigation observed: ' + (diag.buyNowNavigationStarted == null ? '(unknown/pending)' : diag.buyNowNavigationStarted ? 'yes' : 'no'));
      return lines.join('\n');
    }

    function formatBuyNowPropertyAuditText(audit) {
      if (!audit) return '(not yet run this session — commit a Buy Now attempt first)';
      var lines = [];
      lines.push('expected property count: 7');
      lines.push('actual Ever Leaf property count: ' + audit.totalCanonical);
      lines.push('Upload 1 count: ' + audit.counts.upload);
      lines.push('Dedication count: ' + audit.counts.text);
      lines.push('_ever_leaf_photo_x count: ' + audit.counts.photoX);
      lines.push('_ever_leaf_photo_y count: ' + audit.counts.photoY);
      lines.push('_ever_leaf_photo_scale count: ' + audit.counts.photoScale);
      lines.push('_ever_leaf_config_version count: ' + audit.counts.configVersion);
      lines.push('_ever_leaf_upload_url count: ' + audit.counts.uploadUrl);
      lines.push('unexpected Ever Leaf property count: ' + audit.unexpected.length);
      audit.unexpected.forEach(function (c, i) {
        lines.push('  unexpected #' + i + ': ' + JSON.stringify(c.rawName) + ' = ' + JSON.stringify(c.value));
      });
      lines.push('Upload 1 value: ' + (audit.values.upload || '(empty)'));
      lines.push('_ever_leaf_upload_url value: ' + (audit.values.uploadUrl || '(empty)'));
      lines.push('Dedication value: "' + audit.values.text + '"');
      lines.push('_ever_leaf_photo_x value: ' + (audit.values.photoX || '(empty)'));
      lines.push('_ever_leaf_photo_y value: ' + (audit.values.photoY || '(empty)'));
      lines.push('_ever_leaf_photo_scale value: ' + (audit.values.photoScale || '(empty)'));
      lines.push('_ever_leaf_config_version value: ' + (audit.values.configVersion || '(empty)'));
      lines.push('current authoritative serverId: ' + (audit.currentServerId || '(empty)'));
      lines.push('--- ALL properties[...] CONTROLS FOUND ON FORM ---');
      audit.controls.forEach(function (c, i) {
        lines.push(
          '  #' + i + ': raw=' + JSON.stringify(c.rawName) + ' normalized=' + JSON.stringify(c.normalizedName) + ' value=' + JSON.stringify(c.value) + ' tag=' + c.tag + ' type=' + c.type + ' hidden=' + c.hidden + ' disabled=' + c.disabled + ' connected=' + c.connected
        );
      });
      lines.push('--- ASSERTIONS ---');
      var labels = {
        uploadCountOne: 'A. Upload 1 count === 1',
        textCountOne: 'B. Dedication count === 1',
        photoXCountOne: 'C. photo_x count === 1',
        photoYCountOne: 'D. photo_y count === 1',
        photoScaleCountOne: 'E. photo_scale count === 1',
        configVersionCountOne: 'F. config_version count === 1',
        uploadUrlCountOne: 'G. upload_url count === 1',
        dedicationLayoutCountOne: 'G2. dedication_layout count === 1',
        totalCanonicalComplete: 'H. total canonical properties === 8',
        uploadMatchesUploadUrl: 'I. Upload 1 URL === _ever_leaf_upload_url',
        uploadMatchesServerId: 'J. Upload 1 URL === current authoritative serverId',
        textMatchesState: 'K. Dedication === current PersonalizerState text',
        photoXMatchesState: 'L. photo_x === current normalized PersonalizerState photoX',
        photoYMatchesState: 'M. photo_y === current normalized PersonalizerState photoY',
        photoScaleMatchesState: 'N. photo_scale === current normalized PersonalizerState photoScale',
        configVersionCorrect: 'O. config_version === "1"',
        dedicationLayoutValid: 'O2. dedication_layout is "together" or "split"',
        noUnexpected: 'P. no unexpected/stray Ever-Leaf-family properties'
      };
      Object.keys(labels).forEach(function (key) {
        lines.push('  ' + labels[key] + ': ' + (audit.assertions[key] ? 'PASS' : 'FAIL'));
      });
      lines.push('BUY NOW CANONICAL PROPERTY SNAPSHOT: ' + (audit.overallPass ? 'PASS' : 'FAIL'));
      return lines.join('\n');
    }

    function releaseEverLeafReplayGating() {
      // Only ever touches OUR OWN wrapper div's own class/attribute — never
      // anything Shopify-owned (Section 11). Idempotent/defensive: by the
      // time a commit's onReady fires, state.update() has already reverted
      // uploadStatus away from 'uploading', which already re-runs
      // updateButtonGating() via the state subscription and normally clears
      // this class on its own — this is a safety net, not the primary fix.
      if (!gate) return false;
      var wasActive = gate.classList.contains('ever-leaf-photo-required-blocked');
      if (wasActive) {
        gate.classList.remove('ever-leaf-photo-required-blocked');
        gate.setAttribute('aria-disabled', 'false');
      }
      return wasActive;
    }

    function resolveBuyNowReplayButton(originalButton) {
      var matches = gate ? gate.querySelectorAll(BUY_NOW_BUTTON_SELECTOR) : [];
      var rawCount = matches.length;
      var btn = rawCount ? matches[0] : null;
      var snapshot = inspectBuyNowButtonState(btn);
      var rejectionReason = '';
      var shopifyBlocking = 'unknown';

      if (!btn || btn.tagName !== 'BUTTON') {
        btn = null;
        rejectionReason = 'no element matched the selector under the gate';
      } else if (!btn.isConnected) {
        rejectionReason = 'matched element is not connected to the document';
        btn = null;
      } else if (btn.disabled) {
        // Native disabled property — treated as Shopify-owned and NEVER
        // overridden (Section 11), regardless of Ever Leaf's own gating.
        rejectionReason = 'button is natively disabled (respected as Shopify-owned state, not overridden)';
        shopifyBlocking = 'yes';
        btn = null;
      } else {
        shopifyBlocking = 'no';
      }

      return {
        rawCount: rawCount,
        button: btn,
        snapshot: snapshot,
        rejectionReason: rejectionReason,
        shopifyBlocking: shopifyBlocking,
        sameAsOriginal: !!(btn && originalButton && btn === originalButton),
        originalConnected: !!(originalButton && originalButton.isConnected)
      };
    }

    // Tracked in-progress gesture: { pointerId, pointerType, startX, startY, button }.
    var buyNowGesture = null;
    // One-shot: recognizes OUR OWN replayed .click() so Ever Leaf steps
    // aside instead of re-intercepting it (Section 10/11).
    var buyNowApproved = false;
    // Double-activation guard (Section 15) — blocks a second gesture from
    // starting a second upload/commit/replay while one is already running.
    var buyNowActivationInFlight = false;

    function beginBuyNowActivation(sourceButton, eventType) {
      // Section 16 — re-validate at ACTIVATION time, never trust state
      // captured earlier in the gesture (photo/Terms may have changed).
      var s = window.EverLeafPersonalizerState.get();
      var hasPhoto = !!s.customerFile;
      var termsOk = checkbox ? checkbox.checked : true;
      var stillEnabled = !!sourceButton && !sourceButton.disabled;

      if (buyNowActivationInFlight || s.uploadStatus === 'uploading') {
        // Section 3/9 (this pass) — a commit is ALREADY running. This is a
        // SECONDARY activation attempt (e.g. a real user re-tap while
        // waiting) and must NEVER overwrite the CURRENT/original attempt's
        // own (frozen) gesture-result fields — that was the exact root
        // cause of the reported "gesture accepted: no" / "runCommit
        // entered: yes" contradiction. Recorded separately instead.
        diag.buyNowSecondaryActivationObserved = true;
        diag.buyNowSecondaryActivationEventType = eventType || '(unknown)';
        diag.buyNowSecondaryActivationTarget = describeClickedElement(sourceButton);
        diag.buyNowSecondaryActivationRejectedReason = 'a Buy Now commit is already in progress';
        refreshDiagnostics();
        return;
      }
      if (!hasPhoto || !termsOk || !stillEnabled) {
        diag.buyNowGestureRejectedReason = !hasPhoto ? 'no photo at activation time' : !termsOk ? 'Terms not checked at activation time' : 'button no longer enabled at activation time';
        refreshDiagnostics();
        return;
      }

      diag.buyNowGestureAccepted = true;
      diag.buyNowGestureRejectedReason = '';
      diag.buyNowSecondaryActivationObserved = false;
      diag.buyNowSecondaryActivationEventType = '';
      diag.buyNowSecondaryActivationTarget = '';
      diag.buyNowSecondaryActivationRejectedReason = '';
      diag.buyNowClickObserved = false;
      diag.buyNowClickedElement = describeClickedElement(sourceButton);
      diag.buyNowRunCommitEntered = false;
      diag.buyNowFinalFormDataReady = false;
      diag.buyNowSevenPropertiesReady = false;
      diag.buyNowMiniProcessingComplete = false;
      diag.buyNowApprovedContinuationInvoked = false;
      diag.buyNowOriginalActionInvoked = false;
      diag.buyNowButtonReResolved = false;
      diag.buyNowReplayClickInvoked = false;
      diag.buyNowNavigationStarted = null;
      diag.buyNowLastError = '';
      diag.buyNowReplayRawSelectorMatchCount = null;
      diag.buyNowReplayOriginalConnected = null;
      diag.buyNowReplaySameNodeAsOriginal = null;
      diag.buyNowReplayButtonState = null;
      diag.buyNowReplayEverLeafGatingActive = null;
      diag.buyNowReplayGatingReleased = null;
      diag.buyNowReplayShopifyBlocking = 'unknown';
      diag.buyNowReplayRejectionReason = '';
      diag.buyNowReplayAttemptsUsed = null;
      buyNowActivationInFlight = true;
      diag.buyNowRunCommitEntered = true;
      refreshDiagnostics();

      runCommit('buy-now', function () {
        diag.buyNowSevenPropertiesReady = true;
        // Section 11 — the pre-handoff snapshot, captured immediately after
        // canonicalization and BEFORE any replay attempt.
        diag.buyNowPropertyAudit = auditBuyNowPropertyControls();
        diag.buyNowExecutionEnvironment = collectBuyNowExecutionEnvironment();
        refreshDiagnostics();
        attemptBuyNowReplay(sourceButton, BUY_NOW_REPLAY_RETRY_MAX_ATTEMPTS);
      }, function () {
        // PHASE 50D — stale-commit cleanup, mirroring onHostedUrlFailed's
        // own existing error-path cleanup exactly (same guard variable),
        // since a stale resolution is functionally "this attempt is over"
        // from Buy Now's perspective too — just without a misleading error
        // message, since the customer already moved on to a different
        // photo. No replay was ever attempted (attemptBuyNowReplay is only
        // reached from the onCommitted callback above, which this stale
        // path never calls), so there is nothing to undo there.
        buyNowActivationInFlight = false;
        refreshDiagnostics();
      });
    }

    /*
      Section 8/10-13/16-17 — replay-target resolution, run ONLY after a
      successful commit (Mini + 7 canonical properties already done). Bounded
      retry (not indefinite polling): the evidence from the prior manual test
      showed the button becomes resolvable AGAIN a short time later ("later
      structure diagnostics can see the button again" — Section 9 of the
      brief), consistent with a brief Shopify-owned busy/transition state
      rather than a permanent block; this re-queries the live DOM fresh on
      each attempt (never caches), same as Section 13 requires. If every
      attempt is exhausted, this is a genuine, reported failure — never
      forced/overridden (Section 11), never a second hack (Section 17).
    */
    var BUY_NOW_REPLAY_RETRY_INTERVAL_MS = 150;
    var BUY_NOW_REPLAY_RETRY_MAX_ATTEMPTS = 8; // ~1.2s total worst case

    function attemptBuyNowReplay(originalButton, attemptsLeft) {
      diag.buyNowReplayAttemptsUsed = BUY_NOW_REPLAY_RETRY_MAX_ATTEMPTS - attemptsLeft + 1;
      diag.buyNowReplayGatingReleased = releaseEverLeafReplayGating();
      diag.buyNowReplayEverLeafGatingActive = !!(gate && gate.classList.contains('ever-leaf-photo-required-blocked'));

      var resolved = resolveBuyNowReplayButton(originalButton);
      diag.buyNowReplayRawSelectorMatchCount = resolved.rawCount;
      diag.buyNowReplayButtonState = resolved.snapshot;
      diag.buyNowReplaySameNodeAsOriginal = resolved.sameAsOriginal;
      diag.buyNowReplayOriginalConnected = resolved.originalConnected;
      diag.buyNowReplayShopifyBlocking = resolved.shopifyBlocking;
      diag.buyNowReplayRejectionReason = resolved.rejectionReason;
      refreshDiagnostics();

      if (resolved.button) {
        diag.buyNowButtonReResolved = true;
        dispatchInstrumentedBuyNowReplay(resolved.button);
        return;
      }

      if (attemptsLeft > 1) {
        setTimeout(function () {
          attemptBuyNowReplay(originalButton, attemptsLeft - 1);
        }, BUY_NOW_REPLAY_RETRY_INTERVAL_MS);
        return;
      }

      // Retries exhausted — genuine failure. Never force/override Shopify's
      // own state (Section 11); restore normal gating and allow retry
      // without re-selecting the photo (Section 28).
      diag.buyNowButtonReResolved = false;
      diag.buyNowLastError = 'Real Buy Now button could not be re-resolved for replay (' + (resolved.rejectionReason || 'unknown reason') + ').';
      buyNowActivationInFlight = false;
      window.EverLeafPersonalizerState.update({ uploadStatus: 'selected', uploadError: null });
      refreshDiagnostics();
    }

    /*
      Section 6 — click METHOD IDENTITY, checked once per replay before
      deciding anything: is button.click the ordinary native
      HTMLElement.prototype.click, or has Shopify/another script overridden
      it on this instance? Short, safe description only — never dumps
      function source.
    */
    function inspectClickMethodIdentity(button) {
      var info = {
        typeofClick: typeof button.click,
        hasOwnClick: false,
        ownDescriptor: null,
        isHTMLElementPrototypeClick: false,
        isHTMLButtonElementPrototypeClick: false,
        owner: 'unknown',
        description: ''
      };
      try {
        info.hasOwnClick = Object.prototype.hasOwnProperty.call(button, 'click');
      } catch (e) {
        /* best-effort */
      }
      try {
        var desc = Object.getOwnPropertyDescriptor(button, 'click');
        info.ownDescriptor = desc ? typeof desc.value + (desc.writable ? ' writable' : '') + (desc.configurable ? ' configurable' : '') : null;
      } catch (e) {
        /* best-effort */
      }
      try {
        info.isHTMLElementPrototypeClick = button.click === HTMLElement.prototype.click;
      } catch (e) {
        /* best-effort */
      }
      try {
        info.isHTMLButtonElementPrototypeClick = typeof HTMLButtonElement !== 'undefined' && button.click === HTMLButtonElement.prototype.click;
      } catch (e) {
        /* best-effort */
      }
      if (info.hasOwnClick) {
        info.owner = 'own property (instance-level — overridden/monkey-patched)';
      } else {
        try {
          var proto = Object.getPrototypeOf(button);
          while (proto) {
            if (Object.prototype.hasOwnProperty.call(proto, 'click')) {
              info.owner = (proto.constructor && proto.constructor.name) || '(anonymous prototype)';
              break;
            }
            proto = Object.getPrototypeOf(proto);
          }
        } catch (e) {
          /* best-effort */
        }
      }
      info.description = info.hasOwnClick
        ? 'OVERRIDDEN — instance owns its own "click" property, not inherited'
        : info.isHTMLElementPrototypeClick
        ? 'native — button.click is exactly HTMLElement.prototype.click'
        : 'non-standard — inherited "click" is not HTMLElement.prototype.click (owner: ' + info.owner + ')';
      return info;
    }

    /*
      SYNTHETIC CLICK DELIVERY pass — proves, rather than assumes, exactly
      what happens to a replay click: temporary one-shot probes at every
      checkpoint between the button itself and document, in both capture and
      bubble phase where applicable, installed ONLY around this one call and
      fully removed afterward (never a permanent/noisy global listener).
    */
    function installReplayProbes(button) {
      var probe = {
        buttonClickObserved: false,
        buttonEventIsTrusted: null,
        gateCaptureObserved: false,
        gateBubbleObserved: false,
        documentCaptureObserved: false,
        documentBubbleObserved: false,
        defaultPrevented: null,
        eventTarget: '',
        eventCurrentTarget: '',
        eventPhaseAtButton: null,
        approvalDuringEvent: null
      };
      function onButton(event) {
        probe.buttonClickObserved = true;
        probe.buttonEventIsTrusted = event.isTrusted;
        probe.eventTarget = describeClickedElement(event.target);
        probe.eventCurrentTarget = describeClickedElement(event.currentTarget);
        probe.eventPhaseAtButton = event.eventPhase;
        probe.approvalDuringEvent = buyNowApproved;
      }
      function onGateCapture() {
        probe.gateCaptureObserved = true;
      }
      function onGateBubble() {
        probe.gateBubbleObserved = true;
      }
      function onDocCapture() {
        probe.documentCaptureObserved = true;
      }
      function onDocBubble(event) {
        probe.documentBubbleObserved = true;
        probe.defaultPrevented = event.defaultPrevented;
      }
      button.addEventListener('click', onButton, { once: true, capture: false });
      if (gate) {
        gate.addEventListener('click', onGateCapture, { once: true, capture: true });
        gate.addEventListener('click', onGateBubble, { once: true, capture: false });
      }
      document.addEventListener('click', onDocCapture, { once: true, capture: true });
      document.addEventListener('click', onDocBubble, { once: true, capture: false });
      return {
        probe: probe,
        removeAll: function () {
          button.removeEventListener('click', onButton, false);
          if (gate) {
            gate.removeEventListener('click', onGateCapture, true);
            gate.removeEventListener('click', onGateBubble, false);
          }
          document.removeEventListener('click', onDocCapture, true);
          document.removeEventListener('click', onDocBubble, false);
        }
      };
    }

    function recordReplayProbeResults(probe, isProto) {
      var prefix = isProto ? 'buyNowProtoReplay' : 'buyNowReplay';
      diag[prefix + 'ButtonClickObserved'] = probe.buttonClickObserved;
      diag[prefix + 'ButtonEventIsTrusted'] = probe.buttonEventIsTrusted;
      diag[prefix + 'GateCaptureObserved'] = probe.gateCaptureObserved;
      diag[prefix + 'GateBubbleObserved'] = probe.gateBubbleObserved;
      diag[prefix + 'DocumentCaptureObserved'] = probe.documentCaptureObserved;
      diag[prefix + 'DocumentBubbleObserved'] = probe.documentBubbleObserved;
      diag[prefix + 'DefaultPrevented'] = probe.defaultPrevented;
      diag[prefix + 'ApprovalDuringEvent'] = probe.approvalDuringEvent;
      if (!isProto) {
        diag.buyNowReplayEventTarget = probe.eventTarget;
        diag.buyNowReplayEventCurrentTarget = probe.eventCurrentTarget;
        diag.buyNowReplayEventPhaseAtButton = probe.eventPhaseAtButton;
      }
    }

    function probeSawNothing(probe) {
      return !probe.buttonClickObserved && !probe.gateCaptureObserved && !probe.gateBubbleObserved && !probe.documentCaptureObserved && !probe.documentBubbleObserved;
    }

    /*
      Sections 6-12 — the full decision tree: identify the click method,
      try the ordinary button.click() (instrumented), and ONLY if that
      produces NOTHING observable AND the identity check proves button.click
      is not the standard native implementation, try the ONE allowed
      alternative (HTMLElement.prototype.click.call). Never tried as mere
      experimentation (Section 8) — gated strictly on that evidence.
    */
    /*
      Section 3 — EFFECTIVE disabled/inert state. `button.disabled === false`
      does not by itself prove the control is genuinely clickable: a
      disabled ancestor <fieldset>, an inert ancestor, or a hidden/
      aria-hidden ancestor can all suppress a real user's activation while
      leaving the element's own `disabled` property untouched. Checked
      BEFORE any conclusion is allowed to call native replay "unsuitable".
    */
    function inspectEffectiveDisabledState(button) {
      var info = {
        disabled: !!button.disabled,
        matchesDisabled: false,
        matchesEnabled: false,
        disabledFieldset: '',
        isInert: false,
        inertAncestor: '',
        hiddenAncestor: '',
        ariaHiddenAncestor: '',
        formId: '(none)',
        formCheckValidity: null,
        type: button.type || ''
      };
      try {
        info.matchesDisabled = button.matches(':disabled');
      } catch (e) {
        /* :disabled is invalid on some non-form-associated elements — n/a here */
      }
      try {
        info.matchesEnabled = button.matches(':enabled');
      } catch (e) {
        /* best-effort */
      }
      try {
        var fs = button.closest('fieldset[disabled]');
        info.disabledFieldset = fs ? describeClickedElement(fs) : '';
      } catch (e) {
        /* best-effort */
      }
      try {
        info.isInert = !!button.inert;
      } catch (e) {
        /* best-effort — `inert` IDL not supported in every browser */
      }
      try {
        var inertAnc = button.closest('[inert]');
        info.inertAncestor = inertAnc ? describeClickedElement(inertAnc) : '';
      } catch (e) {
        /* best-effort */
      }
      try {
        var hiddenAnc = button.closest('[hidden]');
        info.hiddenAncestor = hiddenAnc ? describeClickedElement(hiddenAnc) : '';
      } catch (e) {
        /* best-effort */
      }
      try {
        var ariaHiddenAnc = button.closest('[aria-hidden="true"]');
        info.ariaHiddenAncestor = ariaHiddenAnc ? describeClickedElement(ariaHiddenAnc) : '';
      } catch (e) {
        /* best-effort */
      }
      try {
        // Section 6 (Consolidated Pass III) — NEVER read `.id` directly off
        // a <form>: HTMLFormElement is [OverrideBuiltins], so a named child
        // control (e.g. this exact form's own `input[name="id"]`, the
        // variant id field) shadows the inherited Element.id IDL property
        // and `.id` returns that INPUT ELEMENT instead of the id string —
        // exactly the "[object HTMLInputElement]" diagnostic bug reported.
        // getAttribute('id') goes through the generic Element method, which
        // named-property lookup never shadows.
        info.formId = button.form ? button.form.getAttribute('id') || '(no id attribute)' : '(none)';
        info.formCheckValidity = button.form ? button.form.checkValidity() : null;
      } catch (e) {
        /* best-effort */
      }
      return info;
    }

    /*
      Section 5 — a plain, off-screen, customer-invisible <button> created
      solely to prove the diagnostic probe mechanism AND this browser/
      document environment correctly observe an ordinary HTMLElement.click()
      call. Never touches Shopify markup, never triggers any Shopify action.
      Removed immediately after the single call.
    */
    function runControlButtonSanityTest() {
      var observed = false;
      var control = document.createElement('button');
      control.type = 'button';
      control.setAttribute('aria-hidden', 'true');
      control.tabIndex = -1;
      control.style.position = 'fixed';
      control.style.top = '-9999px';
      control.style.left = '-9999px';
      document.body.appendChild(control);
      function onClick() {
        observed = true;
      }
      control.addEventListener('click', onClick, { once: true });
      control.click();
      control.removeEventListener('click', onClick);
      document.body.removeChild(control);
      return observed;
    }

    /*
      Section 6 — an OFFLINE clone of the real Shopify button, stripped of
      every identifying id/name/data/form attribute so Shopify's own JS can
      never bind to or recognize it, never inserted into the real
      [data-ever-leaf-consent-gate] markup. Diagnostic-only: proves whether
      the real button's OWN markup/attributes (independent of Shopify's live
      JS instance) are themselves inert to .click(), never used as a
      checkout continuation mechanism.
    */
    function runClonedButtonSanityTest(realButton) {
      try {
        var clone = realButton.cloneNode(true);
        clone.removeAttribute('id');
        clone.removeAttribute('name');
        clone.removeAttribute('form');
        Array.prototype.slice.call(clone.attributes).forEach(function (attr) {
          if (/^data-/i.test(attr.name)) clone.removeAttribute(attr.name);
        });
        clone.disabled = false;
        clone.setAttribute('aria-hidden', 'true');
        clone.tabIndex = -1;
        clone.style.position = 'fixed';
        clone.style.top = '-9999px';
        clone.style.left = '-9999px';
        document.body.appendChild(clone);
        var observed = false;
        function onClick() {
          observed = true;
        }
        clone.addEventListener('click', onClick, { once: true });
        clone.click();
        clone.removeEventListener('click', onClick);
        document.body.removeChild(clone);
        return observed;
      } catch (e) {
        return null; // could not be tested
      }
    }

    /*
      STANDALONE DEV END-TO-END PROPERTY AUDIT pass — Section 6/11-13. Full
      enumeration of every properties[...] control on the REAL form,
      captured immediately before Buy Now hands off to Shopify, so the
      exact pre-handoff payload is provable rather than assumed. Uses the
      SAME normalizePropertyInnerName() the duplicate-Upload fix above
      relies on, so "count" here reflects the alias-aware truth, not a
      naive exact-name count that could itself hide a duplicate.
    */
    function auditBuyNowPropertyControls() {
      var s = window.EverLeafPersonalizerState.get();
      var allEls = Array.prototype.slice.call(form.querySelectorAll('input[name^="properties["]'));
      var controls = allEls.map(function (el) {
        var rawName = el.getAttribute('name') || '';
        return {
          rawName: rawName,
          trimmedName: rawName.trim(),
          normalizedName: normalizePropertyInnerName(rawName),
          value: el.value,
          tag: el.tagName.toLowerCase(),
          type: el.type || '',
          hidden: el.type === 'hidden' || el.hidden,
          disabled: !!el.disabled,
          connected: el.isConnected
        };
      });

      function matchesFor(canonicalName) {
        var target = canonicalName.trim().toLowerCase();
        return controls.filter(function (c) {
          return c.normalizedName === target;
        });
      }
      function countFor(canonicalName) {
        return matchesFor(canonicalName).length;
      }
      function valueFor(canonicalName) {
        var m = matchesFor(canonicalName);
        return m.length ? m[m.length - 1].value : '';
      }

      var names = LINE_ITEM_PROPERTY_NAMES;
      // PASS 3A MANUAL QA CORRECTION PASS, Section 17 — audited every
      // hardcoded property enumeration in this file for staleness after
      // the 7->8 canonical-property expansion. This one WAS stale (missing
      // dedicationLayout entirely, so `totalCanonical`/`totalSeven` below
      // silently ignored the 8th property instead of counting it) — fixed
      // by deriving counts/values from Object.keys(names) generically
      // instead of a hand-maintained list, so a FUTURE 9th property can
      // never cause this same drift again. Confirmed this function itself
      // is diagnostic-only (feeds the DEV "BUY NOW CANONICAL PROPERTY
      // SNAPSHOT" panel text) and never gates/blocks/removes anything on
      // the real commit path — the staleness was a diagnostic-accuracy gap,
      // not the cause of the real ATC/Buy Now regression (see the actual
      // root cause and fix documented at wireCartCommitFlow's submit
      // interceptor).
      var counts = {};
      var values = {};
      Object.keys(names).forEach(function (key) {
        counts[key] = countFor(names[key]);
        values[key] = valueFor(names[key]);
      });
      var totalCanonical = Object.keys(counts).reduce(function (sum, key) {
        return sum + counts[key];
      }, 0);
      var knownNormalizedNames = Object.keys(names).map(function (k) {
        return names[k].trim().toLowerCase();
      });
      var unexpected = controls.filter(function (c) {
        return knownNormalizedNames.indexOf(c.normalizedName) === -1;
      });

      var currentServerId = diag.serverId || '';
      // Pass 3A interaction-model correction — same legacy top-left
      // derivation canonicalizeLineItemProperties() uses, so this DEV
      // diagnostic compares the actual submitted cart value against the
      // correct expected value rather than a stale/nonexistent s.photoX.
      var diagRenderer = window.EverLeafImageRenderer;
      var expectedLegacyXY = { x: null, y: null };
      if (s.photoCenterX != null && s.photoCenterY != null && s.photoScale != null && diagRenderer) {
        expectedLegacyXY = diagRenderer.centerToTopLeft(s.photoCenterX, s.photoCenterY, s.photoScale, s.photoAspect || 1);
      }
      var expectedPhotoX = expectedLegacyXY.x != null ? expectedLegacyXY.x.toFixed(4) : '';
      var expectedPhotoY = expectedLegacyXY.y != null ? expectedLegacyXY.y.toFixed(4) : '';
      var expectedPhotoScale = s.photoScale != null ? s.photoScale.toFixed(4) : '';

      var assertions = {
        uploadCountOne: counts.upload === 1,
        textCountOne: counts.text === 1,
        photoXCountOne: counts.photoX === 1,
        photoYCountOne: counts.photoY === 1,
        photoScaleCountOne: counts.photoScale === 1,
        configVersionCountOne: counts.configVersion === 1,
        uploadUrlCountOne: counts.uploadUrl === 1,
        dedicationLayoutCountOne: counts.dedicationLayout === 1,
        totalCanonicalComplete: totalCanonical === Object.keys(names).length,
        uploadMatchesUploadUrl: values.upload === values.uploadUrl,
        uploadMatchesServerId: !!currentServerId && values.upload === currentServerId,
        textMatchesState: values.text === (s.text || ''),
        photoXMatchesState: values.photoX === expectedPhotoX,
        photoYMatchesState: values.photoY === expectedPhotoY,
        photoScaleMatchesState: values.photoScale === expectedPhotoScale,
        configVersionCorrect: values.configVersion === CONFIG_VERSION,
        dedicationLayoutValid: values.dedicationLayout === 'together' || values.dedicationLayout === 'split',
        noUnexpected: unexpected.length === 0
      };
      var overallPass = Object.keys(assertions).every(function (k) {
        return assertions[k];
      });

      return {
        controls: controls,
        counts: counts,
        totalCanonical: totalCanonical,
        unexpected: unexpected,
        values: values,
        currentServerId: currentServerId,
        assertions: assertions,
        overallPass: overallPass
      };
    }

    // Section 2/10 — standalone storefront vs Theme Editor, the exact
    // distinction that revised the previous "delayed replay unsuitable"
    // conclusion. Deliberately independent of ever-leaf-terms-consent.js's
    // own environment helper (different file, different concern) — small,
    // self-contained duplication is safer here than a cross-file coupling.
    function collectBuyNowExecutionEnvironment() {
      var insideIframe = false;
      try {
        insideIframe = window.top !== window.self;
      } catch (e) {
        insideIframe = true; // cross-origin ancestor access throws -> some iframe
      }
      return {
        shopifyDesignMode: window.Shopify && typeof window.Shopify.designMode === 'boolean' ? window.Shopify.designMode : null,
        topLevel: !insideIframe,
        insideIframe: insideIframe,
        currentUrl: window.location.href
      };
    }

    /*
      PERSIST SUCCESSFUL DIAGNOSTICS ACROSS CHECKOUT NAVIGATION pass —
      diagnostics-only. Buy Now successfully navigates the browser away
      from the Product Page, so the "Copy Buy Now report" button (read from
      live in-memory `diag`) is unreachable afterward. This writes the SAME
      evidence into sessionStorage, synchronously, immediately before each
      handoff attempt, so it survives navigation and can be read back (and
      copied) after returning to the Product Page in the same tab/session.
      Every write is wrapped in try/catch and never allowed to affect Buy
      Now's own behavior — if sessionStorage throws (quota, privacy mode,
      etc.), the diagnostic is silently skipped and Buy Now continues
      exactly as it already does.
    */
    var EVER_LEAF_BUY_NOW_STORAGE_KEY = 'everLeafLastBuyNowDiagnostic';

    function buildBuyNowPersistedSnapshot() {
      var s = window.EverLeafPersonalizerState.get();
      var audit = diag.buyNowPropertyAudit;
      var env = diag.buyNowExecutionEnvironment;
      var replaySnap = diag.buyNowReplayButtonState;

      /*
        PRIVACY — this object is JSON-serialized into sessionStorage, so it is
        an explicit ALLOWLIST of non-customer diagnostic metadata only:
        booleans, counts, assertion results, identifiers of code paths. It
        must never carry customer personalization content: no Dedication
        text, file name/size, hosted photo URL / serverId, Upload 1 /
        _ever_leaf_upload_url values, or any raw line-item-property value
        (property VALUES are reduced to "is non-empty" booleans below). The
        live in-memory `diag` object and DEV panel still hold the raw values;
        only what is returned here is persisted.
      */
      function presenceOf(v) {
        return !!v;
      }
      function originAndPathOnly(u) {
        // Query strings / hashes can carry arbitrary customer-adjacent data.
        try {
          var parsed = new URL(u, window.location.href);
          return parsed.origin + parsed.pathname;
        } catch (e) {
          return '';
        }
      }
      var valuePresence = null;
      if (audit) {
        valuePresence = {};
        Object.keys(audit.values).forEach(function (k) {
          valuePresence[k] = presenceOf(audit.values[k]);
        });
      }

      return {
        timestamp: new Date().toISOString(),
        environment: {
          liquidDesignMode: typeof window.EverLeafRequestDesignMode === 'boolean' ? window.EverLeafRequestDesignMode : null,
          shopifyDesignMode: env ? env.shopifyDesignMode : null,
          topLevel: env ? env.topLevel : null,
          insideIframe: env ? env.insideIframe : null,
          currentUrl: originAndPathOnly(env ? env.currentUrl : window.location.href),
          devBuildMarker: window.EverLeafDevBuildMarker || null,
          consentRuntimeVersion: window.EverLeafConsentRuntimeVersion || null
        },
        executionPath: {
          originalGestureObserved: !!diag.buyNowGestureAccepted,
          originalEventIsTrusted: diag.buyNowOriginalGestureIsTrusted,
          everLeafIntercepted: true,
          runCommitExecuted: !!diag.buyNowRunCommitEntered,
          miniProcessingStarted: diag.pondFound !== null || diag.pondAddFile !== 'not started',
          miniProcessingCompleted: !!diag.buyNowMiniProcessingComplete,
          serverIdPresent: presenceOf(diag.serverId),
          canonicalizationExecuted: !!diag.buyNowSevenPropertiesReady,
          continuationMechanism: diag.buyNowReplayMethodUsed || '',
          replayClickAttempted: !!diag.buyNowReplayClickInvoked,
          replayTargetDescriptor: replaySnap ? replaySnap.tag + (replaySnap.cls ? '.' + replaySnap.cls : '') : null,
          replayTargetConnected: replaySnap ? true : null, // only ever populated from a resolved, connected button
          replayTargetDisabled: replaySnap ? replaySnap.disabledProperty : null,
          navigationSignalObserved: false,
          navigationSignalType: ''
        },
        properties: audit
          ? {
              totalCanonicalCount: audit.totalCanonical,
              counts: audit.counts,
              valuePresent: valuePresence,
              unexpectedCount: audit.unexpected.length,
              // property NAMES only (a field name is code, never customer content)
              unexpectedNames: audit.unexpected.map(function (c) {
                return String(c.rawName);
              }),
              assertions: audit.assertions,
              overallPass: audit.overallPass
            }
          : null,
        imageIdentity: {
          filePresent: !!s.customerFile,
          serverIdPresent: presenceOf(diag.serverId),
          upload1Present: audit ? presenceOf(audit.values.upload) : null,
          uploadUrlPresent: audit ? presenceOf(audit.values.uploadUrl) : null,
          upload1MatchesServerId: audit ? audit.assertions.uploadMatchesServerId : null,
          uploadUrlMatchesServerId: audit ? !!(diag.serverId && audit.values.uploadUrl === diag.serverId) : null,
          upload1MatchesUploadUrl: audit ? audit.assertions.uploadMatchesUploadUrl : null
        }
      };
    }

    function installBuyNowNavigationSignalListeners() {
      function updateNavigationSignal(type) {
        try {
          var raw = window.sessionStorage.getItem(EVER_LEAF_BUY_NOW_STORAGE_KEY);
          if (!raw) return;
          var stored = JSON.parse(raw);
          if (stored && stored.executionPath) {
            stored.executionPath.navigationSignalObserved = true;
            stored.executionPath.navigationSignalType = type;
            window.sessionStorage.setItem(EVER_LEAF_BUY_NOW_STORAGE_KEY, JSON.stringify(stored));
          }
        } catch (e) {
          /* diagnostics-only — never block navigation */
        }
      }
      // Section 10 — optional/non-blocking, one-shot, never preventDefault,
      // never delays navigation.
      window.addEventListener(
        'beforeunload',
        function () {
          updateNavigationSignal('beforeunload');
        },
        { once: true }
      );
      window.addEventListener(
        'pagehide',
        function () {
          updateNavigationSignal('pagehide');
        },
        { once: true }
      );
    }

    function persistBuyNowDiagnosticSnapshot() {
      try {
        var snapshot = buildBuyNowPersistedSnapshot();
        window.sessionStorage.setItem(EVER_LEAF_BUY_NOW_STORAGE_KEY, JSON.stringify(snapshot));
        installBuyNowNavigationSignalListeners();
      } catch (e) {
        /* diagnostics-only — Buy Now must continue normally regardless */
      }
    }

    function resetBuyNowInFlight() {
      buyNowApproved = false;
      buyNowActivationInFlight = false;
      window.EverLeafPersonalizerState.update({ uploadStatus: 'selected', uploadError: null });
    }

    function dispatchInstrumentedBuyNowReplay(button) {
      var identity = inspectClickMethodIdentity(button);
      diag.buyNowClickTypeofClick = identity.typeofClick;
      diag.buyNowClickHasOwnProperty = identity.hasOwnClick;
      diag.buyNowClickOwnDescriptor = identity.ownDescriptor;
      diag.buyNowClickIsHTMLElementPrototype = identity.isHTMLElementPrototypeClick;
      diag.buyNowClickIsHTMLButtonElementPrototype = identity.isHTMLButtonElementPrototypeClick;
      diag.buyNowClickImplementationOwner = identity.owner;
      diag.buyNowClickDescription = identity.description;
      diag.buyNowNativePrototypeFallbackTested = false;
      diag.buyNowNativePrototypeFallbackClickObserved = null;

      // Section 3 — effective disabled/inert state, recorded up front,
      // BEFORE any replay attempt or conclusion.
      var effective = inspectEffectiveDisabledState(button);
      diag.buyNowDisabledProperty = effective.disabled;
      diag.buyNowMatchesDisabled = effective.matchesDisabled;
      diag.buyNowMatchesEnabled = effective.matchesEnabled;
      diag.buyNowDisabledFieldset = effective.disabledFieldset;
      diag.buyNowInertSelf = effective.isInert;
      diag.buyNowInertAncestor = effective.inertAncestor;
      diag.buyNowHiddenAncestor = effective.hiddenAncestor;
      diag.buyNowAriaHiddenAncestor = effective.ariaHiddenAncestor;
      diag.buyNowFormId = effective.formId;
      diag.buyNowFormCheckValidity = effective.formCheckValidity;
      diag.buyNowButtonType = effective.type;
      var effectivelyDisabled = !!(effective.matchesDisabled || effective.disabledFieldset || effective.isInert || effective.inertAncestor);
      diag.buyNowEffectiveDisabled = effectivelyDisabled;

      // Section 5 — control-button sanity test: proves the probe mechanism
      // and this document/browser correctly observe an ordinary click()
      // BEFORE trusting a "no click observed" result on the real button.
      diag.buyNowControlButtonClickObserved = runControlButtonSanityTest();
      refreshDiagnostics();

      if (effectivelyDisabled) {
        // Section 8 — an effective-disabled/inert state, not Shopify click
        // suppression, is the actual blocker. Do NOT attempt replay at all.
        diag.buyNowFinalConclusion =
          'EFFECTIVE DISABLED/INERT STATE DETECTED — this, not Shopify click suppression, is the actual blocker (matches(:disabled)=' +
          effective.matchesDisabled +
          ', disabled fieldset=' +
          (effective.disabledFieldset || 'none') +
          ', inert=' +
          effective.isInert +
          ', inert ancestor=' +
          (effective.inertAncestor || 'none') +
          ').';
        diag.buyNowLastError = diag.buyNowFinalConclusion;
        resetBuyNowInFlight();
        refreshDiagnostics();
        return;
      }

      // --- Attempt 1: ordinary button.click(), the normal mechanism. ---
      var attempt1 = installReplayProbes(button);
      buyNowApproved = true;
      diag.buyNowReplayApprovalBeforeClick = buyNowApproved;
      diag.buyNowReplayClickInvoked = true;
      diag.buyNowReplayMethodUsed = 'button.click()';
      refreshDiagnostics();
      persistBuyNowDiagnosticSnapshot();
      button.click();
      // .click() dispatches and returns fully synchronously — every probe
      // that was going to fire already has by this line (Section 7: the
      // real application approval was already true before this call; only
      // the diagnostic "before" snapshot was previously taken one line too
      // early — fixed here by sampling it right after setting the flag).
      diag.buyNowReplayApprovalAfterClick = buyNowApproved;
      recordReplayProbeResults(attempt1.probe, false);
      attempt1.removeAll();
      refreshDiagnostics();

      if (!probeSawNothing(attempt1.probe)) {
        diag.buyNowNativePrototypeFallbackEligible = false;
        diag.buyNowFinalConclusion = 'native replay delivered a click event somewhere in the chain — see SYNTHETIC CLICK DELIVERY for exactly where; native Shopify Buy Now still viable.';
        refreshDiagnostics();
        return;
      }

      // Section 6 — an isolated, off-DOM clone of the REAL button (stripped
      // of every identifying attribute so Shopify's JS can never bind to
      // it), tested only for completeness/diagnostic value, never used as a
      // continuation mechanism.
      diag.buyNowCloneButtonClickObserved = runClonedButtonSanityTest(button);
      refreshDiagnostics();

      // Section 8 — revised conclusion logic: "unsuitable" requires ALL SIX
      // criteria to hold. Anything less is reported as unresolved, not a
      // false-confident final answer.
      var nativeConfirmed = identity.isHTMLElementPrototypeClick;
      var controlProvedMechanismWorks = diag.buyNowControlButtonClickObserved === true;

      if (!nativeConfirmed) {
        // Section 8/11 — eligible: button.click is proven NOT the standard
        // native implementation, and the ordinary call produced nothing
        // observable. Exactly ONE alternative native test, per the brief.
        diag.buyNowNativePrototypeFallbackEligible = true;
        diag.buyNowNativePrototypeFallbackTested = true;
        var attempt2 = installReplayProbes(button);
        buyNowApproved = true;
        diag.buyNowReplayMethodUsed = 'HTMLElement.prototype.click.call(button)';
        refreshDiagnostics();
        persistBuyNowDiagnosticSnapshot();
        HTMLElement.prototype.click.call(button);
        diag.buyNowReplayApprovalAfterClick = buyNowApproved;
        recordReplayProbeResults(attempt2.probe, true);
        attempt2.removeAll();
        diag.buyNowNativePrototypeFallbackClickObserved = attempt2.probe.buttonClickObserved;

        if (!probeSawNothing(attempt2.probe)) {
          // Section 11 — override conclusively proven as the blocker.
          diag.buyNowFinalConclusion = 'override confirmed as the blocker — HTMLElement.prototype.click.call(button) delivered a click where button.click() did not.';
          refreshDiagnostics();
          return;
        }
        // Section 10 — no more synthetic activation experiments. Criterion
        // 1 (native identity) was never true here, so per Section 8 this
        // can NEVER be reported as the exact "unsuitable" conclusion string
        // — only as unresolved.
        diag.buyNowFinalConclusion = 'UNRESOLVED — MORE RUNTIME EVIDENCE REQUIRED (button.click is overridden; neither the override nor HTMLElement.prototype.click.call() produced an observable click)';
        resetBuyNowInFlight();
        refreshDiagnostics();
        return;
      }

      diag.buyNowNativePrototypeFallbackEligible = false;

      if (!controlProvedMechanismWorks) {
        // Criterion 5 unmet — cannot trust the "zero events" reading yet.
        diag.buyNowFinalConclusion =
          'UNRESOLVED — MORE RUNTIME EVIDENCE REQUIRED (the control-button sanity test itself did not confirm a normal click() is observable in this environment, so the real button producing nothing cannot yet be trusted as meaningful)';
        resetBuyNowInFlight();
        refreshDiagnostics();
        return;
      }

      // All six Section 8 criteria satisfied: native identity confirmed,
      // not effectively disabled/inert, control button proved the
      // mechanism works, and the real button still produced zero events.
      diag.buyNowFinalConclusion = 'NATIVE SHOPIFY BUY NOW REPLAY UNSUITABLE FOR THIS ASYNC PERSONALIZATION FLOW';
      resetBuyNowInFlight();
      refreshDiagnostics();
    }

    if (gate) {
      gate.addEventListener(
        'pointerdown',
        function (event) {
          diag.hostPointerdownSeen = true;
          var btn = resolveRealBuyNowButton();
          var targetsButton = !!(btn && (event.target === btn || btn.contains(event.target)));

          if (buyNowActivationInFlight) {
            // Section 3/9 — a commit is already running. NEVER touch the
            // CURRENT attempt's own (frozen) gesture-result fields here;
            // record as a secondary activation instead, and never track a
            // gesture for it (there is nothing to replay/commit again).
            buyNowGesture = null;
            if (targetsButton) {
              diag.buyNowSecondaryActivationObserved = true;
              diag.buyNowSecondaryActivationEventType = 'pointerdown';
              diag.buyNowSecondaryActivationTarget = describeClickedElement(event.target);
              diag.buyNowSecondaryActivationRejectedReason = 'a Buy Now commit is already in progress';
              refreshDiagnostics();
            }
            return;
          }

          if (targetsButton) {
            buyNowGesture = {
              pointerId: event.pointerId,
              pointerType: event.pointerType,
              startX: event.clientX,
              startY: event.clientY,
              button: btn
            };
            diag.buyNowPointerdownObserved = true;
            diag.buyNowPointerdownTarget = describeClickedElement(event.target);
            diag.buyNowPointerId = event.pointerId;
            diag.buyNowPointerType = event.pointerType;
            diag.buyNowPointerupObserved = false;
            diag.buyNowSamePointer = null;
            diag.buyNowMovementPx = null;
            diag.buyNowPointercancelObserved = false;
            diag.buyNowGestureAccepted = false;
            diag.buyNowGestureRejectedReason = '';
          } else {
            // pointerdown outside the real button — not a tracked gesture.
            buyNowGesture = null;
          }
          refreshDiagnostics();
        },
        true
      );

      gate.addEventListener(
        'pointercancel',
        function (event) {
          if (buyNowGesture && event.pointerId === buyNowGesture.pointerId) {
            diag.buyNowPointercancelObserved = true;
            diag.buyNowGestureRejectedReason = 'pointercancel';
            buyNowGesture = null; // Section 6 — clear tracked state, do NOT commit or replay.
            refreshDiagnostics();
          }
        },
        true
      );

      gate.addEventListener(
        'pointerup',
        function (event) {
          diag.hostPointerupSeen = true;

          var g = buyNowGesture;
          buyNowGesture = null; // one-shot per pointerdown regardless of outcome

          if (!g || event.pointerId !== g.pointerId) {
            refreshDiagnostics();
            return; // not our tracked Buy Now gesture — step aside entirely
          }

          diag.buyNowPointerupObserved = true;
          diag.buyNowSamePointer = true;

          var dx = event.clientX - g.startX;
          var dy = event.clientY - g.startY;
          diag.buyNowMovementPx = Math.round(Math.sqrt(dx * dx + dy * dy));

          if (diag.buyNowMovementPx > BUY_NOW_MOVE_THRESHOLD_PX) {
            diag.buyNowGestureRejectedReason = 'movement exceeded threshold (drag, not a tap)';
            refreshDiagnostics();
            return;
          }

          var currentButton = resolveRealBuyNowButton();
          var landedOnTrackedButton = event.target === g.button || g.button.contains(event.target);
          if (!currentButton) {
            diag.buyNowGestureRejectedReason = 'real Buy Now button no longer resolvable at pointerup';
            refreshDiagnostics();
            return;
          }
          if (currentButton !== g.button || !landedOnTrackedButton) {
            diag.buyNowGestureRejectedReason = 'pointerup target/control mismatch (possible re-render or stray pointer)';
            refreshDiagnostics();
            return;
          }

          // Section 8 — moment A: the real button's full state right at
          // gesture acceptance, for comparison against moment B (replay
          // time, captured inside attemptBuyNowReplay).
          diag.buyNowGestureButtonState = inspectBuyNowButtonState(g.button);
          diag.buyNowOriginalGestureIsTrusted = event.isTrusted;

          // Accepted gesture — hold Shopify's own activation right here, at
          // the latest proven-observable point in the sequence (Section 7).
          // Narrowest available prevention: this SAME event, nothing global.
          event.preventDefault();
          event.stopImmediatePropagation();
          refreshDiagnostics();

          beginBuyNowActivation(g.button, 'pointerup');
        },
        true
      );

      // The synthetic replay's own 'click' (freshButton.click() above)
      // bubbles through here and is recognized via the one-shot flag, then
      // stepped aside completely — no preventDefault/stopPropagation, no
      // second upload, no second runCommit (Section 10/11). A genuine,
      // non-replay click that still reaches this point (e.g. keyboard
      // Enter/Space activation, which never generates pointerdown/pointerup)
      // is treated as a fallback activation path through the SAME
      // validation, so keyboard access isn't silently broken by this rework.
      gate.addEventListener(
        'click',
        function (event) {
          if (buyNowApproved) {
            buyNowApproved = false;
            diag.buyNowApprovedContinuationInvoked = true;
            diag.buyNowOriginalActionInvoked = true;
            refreshDiagnostics();
            // Best-effort "did navigation actually start" signal — the
            // accelerated checkout normally navigates away; if the page is
            // still here after a few seconds, something stopped it.
            var navTimer = setTimeout(function () {
              if (diag.buyNowNavigationStarted === null) {
                diag.buyNowNavigationStarted = false;
                buyNowActivationInFlight = false;
                refreshDiagnostics();
              }
            }, 4000);
            window.addEventListener(
              'pagehide',
              function () {
                clearTimeout(navTimer);
                diag.buyNowNavigationStarted = true;
                buyNowActivationInFlight = false;
                refreshDiagnostics();
              },
              { once: true }
            );
            return;
          }
          diag.buyNowClickObserved = true;
          event.preventDefault();
          event.stopImmediatePropagation();
          refreshDiagnostics();
          var fallbackTarget = (event.target && event.target.closest && event.target.closest(BUY_NOW_BUTTON_SELECTOR)) || resolveRealBuyNowButton();
          beginBuyNowActivation(fallbackTarget, 'click');
        },
        true
      );
    }
  }

  /*
    The old floating Original/Grayscale POC panel (ever-leaf-image-renderer-
    poc.js) is created dynamically on first upload and appended to
    document.body — it doesn't exist at mount time. A MutationObserver
    catches its insertion so it can be hidden by default (Section 16) without
    modifying that file. A checkbox in the calibration panel opts back in.
  */
  function hideOldPocPanelByDefault(el) {
    var showToggle = el.querySelector('[data-ever-leaf-show-old-poc]');
    function applyState() {
      var panel = document.getElementById('ever-leaf-renderer-poc');
      if (panel) panel.hidden = !(showToggle && showToggle.checked);
    }
    var observer = new MutationObserver(applyState);
    observer.observe(document.body, { childList: true });
    applyState();
    if (showToggle) showToggle.addEventListener('change', applyState);
  }

  /*
    Pass 3A interaction-model correction — computePivotPreservingScale() and
    resolveScaleUpdate() are RETIRED. The user-observed "drift/bounce near
    scale min/max" (photoX creeping further negative on every click while
    pinned at MAX — a genuine escalating creep, not a one-time clip) was
    this pivot-preserving machinery interacting with the mask-cover clamp:
    solving x/y for one scale, then having clampPhotoTransform silently
    clip x/y into a DIFFERENT box for the actually-applied (clamped) scale.
    The new model has no anchor/pivot concept at all — scale changes only
    ever touch `scale`, so there is nothing for a clamp mismatch to act on.
    SCALE_EPSILON is kept (same value, same purpose: letting the hold-
    repeat loop detect a genuine no-op at a hard scale limit and stop
    itself) — used directly in the onZoom handler in init() above.
  */
  var SCALE_EPSILON = 1e-9;

  /*
    Drag-to-move, two-finger pinch-to-zoom, and desktop mouse-wheel zoom for
    the Photo layer only — the one layer the customer is allowed to
    manipulate. Coordinates are converted from on-screen pixels to the
    canvas's own normalized (0..1) space so the same state works regardless
    of the current raster resolution. Every mutation goes through
    `hooks.applyClampedCenterTransform` — the SAME clamp-bounds logic used
    by the arrow/zoom buttons.

    Pass 3A interaction-model correction — drag/pinch/wheel below no longer
    resolve any scale anchor/pivot at all. Drag and the pinch MOVE
    component translate Center directly (translation is anchor-agnostic —
    moving by (dx, dy) is identical whether you track top-left or center).
    Wheel and the pinch SCALE component only ever set `scale`, leaving
    Center completely untouched — "zoom preserves center" falls directly
    out of applyClampedCenterTransform's merge order.
  */
  function wirePhotoInteraction(el, canvas, hooks) {
    var state = window.EverLeafPersonalizerState;
    var pointers = {}; // id -> { x, y, type }
    var dragLast = null; // single-pointer (mouse) drag only
    var pinch = null; // { startDistance, startMidpoint:{x,y}, startTransform:{x,y,scale} }

    function pointerIds() {
      return Object.keys(pointers);
    }

    function pointerDistance() {
      var ids = pointerIds();
      var a = pointers[ids[0]];
      var b = pointers[ids[1]];
      return Math.hypot(a.x - b.x, a.y - b.y);
    }

    function pointerMidpoint() {
      var ids = pointerIds();
      var a = pointers[ids[0]];
      var b = pointers[ids[1]];
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    }

    /*
      MOBILE TOUCH MODEL pass, Section 18-24 — audit of the PRIOR pinch
      implementation (documented rather than deleted silently):

      The old startPinch() captured the two-finger MIDPOINT, converted it to
      canvas-normalized coordinates, and recorded `contentFracX/contentFracY`
      — the fraction of the drawn photo sitting under that midpoint at
      gesture start. Every subsequent pointermove recomputed the CURRENT
      (live) midpoint and re-solved x/y so that same content fraction
      stayed under the CURRENT midpoint. The midpoint itself WAS the scale
      anchor, and — because it was recomputed live — any drift in the
      midpoint (fingers panning together while pinching) directly
      translated the photo as a side effect, entangled with scale.

      MODEL (Pass 3A interaction-model correction): scale and move are two
      fully INDEPENDENT signals from the SAME two-finger gesture, computed
      from one STABLE start snapshot (never incrementally compounded):
        - SCALE magnitude: distance(now)/distance(start) against
          startTransform.scale — no anchor/pivot resolution at all anymore
          (the old pivot-preserving math is retired file-wide).
        - MOVE: midpoint(now) - midpoint(start), converted from canvas
          pixels to the same normalized units Center already uses (divide
          by rect.width/height — identical conversion to the existing
          one-finger drag path), added on top of startTransform's own
          center.
      Both land in ONE applyClampedCenterTransform({scale, centerX,
      centerY}) call per pointermove. Computing everything from the
      immutable startTransform/startMidpoint snapshot instead of a live-
      transform chain removes incremental-rounding-drift risk — every
      pointermove during one gesture is an independent, idempotent
      recomputation from the same fixed inputs plus the current finger
      positions, not a running accumulation.
    */
    function startPinch() {
      pinch = {
        startDistance: pointerDistance(),
        startMidpoint: pointerMidpoint(),
        startTransform: hooks.getCurrentTransform()
      };
    }

    canvas.addEventListener('pointerdown', function (event) {
      if (!state.get().customerFile) return;
      if (hooks.isEditingEnabled && !hooks.isEditingEnabled()) return;
      // PASS 3A FINAL UX PASS, Section 38/43/45 — covers both drag start
      // AND pinch start (both begin here, at the SAME pointerdown handler).
      if (hooks.notePhotoInteractionActivity) hooks.notePhotoInteractionActivity();
      canvas.setPointerCapture(event.pointerId);
      pointers[event.pointerId] = { x: event.clientX, y: event.clientY, type: event.pointerType };

      var ids = pointerIds();
      if (ids.length === 1) {
        // MOBILE INTERACTION CUMULATIVE pass, Section 9-10/16 — a real
        // mouse ALWAYS drags with one pointer (Section 15: Desktop never
        // depends on the Mobile Drag Mode setting). A touch/pen pointer
        // only drags when the merchant's Mobile Drag Mode is 'one_finger'
        // (Section 10) — pen is deliberately treated the same as touch
        // here (Section 16: no concrete reason found to special-case it,
        // and pen input is realistically only ever used on a touchscreen
        // device alongside finger gestures, so it should follow whichever
        // policy the merchant chose for that device). In 'two_fingers'
        // mode a lone touch/pen point is left untouched entirely (no
        // dragLast), matching the previously-verified "one finger does
        // nothing" behavior. The pointer is still tracked above regardless
        // of mode, so a SECOND finger arriving can compute a correct start
        // distance/midpoint.
        var singlePointerDrags = event.pointerType === 'mouse' || hooks.getMobileDragMode() === 'one_finger';
        dragLast = singlePointerDrags ? { x: event.clientX, y: event.clientY } : null;
        pinch = null;
      } else if (ids.length === 2) {
        dragLast = null;
        startPinch();
      }
    });

    canvas.addEventListener('pointermove', function (event) {
      if (!pointers[event.pointerId]) return;
      pointers[event.pointerId] = { x: event.clientX, y: event.clientY, type: event.pointerType };

      var rect = canvas.getBoundingClientRect();
      var count = pointerIds().length;

      // PASS 3A FINAL UX PASS, Section 40/45 — keep the box visible (reset
      // the hide timer) for as long as a tracked drag/pinch continues; a
      // stray pointermove with no active drag/pinch (count doesn't match
      // either branch below) intentionally does NOT re-trigger this.
      if ((count === 1 && dragLast) || (count === 2 && pinch)) {
        if (hooks.notePhotoInteractionActivity) hooks.notePhotoInteractionActivity();
      }

      if (count === 1 && dragLast) {
        // Reaches here for: any mouse drag (always), OR a touch/pen drag
        // when Mobile Drag Mode = one_finger (Section 10) — never a lone
        // touch/pen point in two_fingers mode (dragLast stays null for
        // those, set above in pointerdown). Translation is anchor-
        // agnostic — moving Center by (dx, dy) is visually identical to
        // moving top-left by (dx, dy); no scale touched at all.
        var dxPx = event.clientX - dragLast.x;
        var dyPx = event.clientY - dragLast.y;
        dragLast = { x: event.clientX, y: event.clientY };
        var t = hooks.getCurrentTransform();
        hooks.applyClampedCenterTransform({
          centerX: t.centerX + dxPx / rect.width,
          centerY: t.centerY + dyPx / rect.height
        });
        if (hooks.isVerbose()) {
          console.log('[EverLeaf][verbose] drag', { dxPx: dxPx, dyPx: dyPx });
        }
      } else if (count === 2 && pinch) {
        // Pass 3A targeted correction — scale, pivot, and pan are three
        // independent reads of the same gesture, all resolved from the
        // frozen pinch.startTransform/startMidpoint snapshot (never the
        // live-committed transform — a continuous gesture must not
        // re-resolve against its own prior frame's output, or drift would
        // compound frame over frame). clampScaleValue has no null/no-op
        // signal (it always returns a valid in-range number), so the
        // clamped scale IS the basis. hooks.resolvePivotCenter() returns
        // the frozen start Center UNCHANGED in 'image_center' mode (same
        // as before this correction) or the pivot-preserving Center in
        // 'custom_pivot' mode — either way the independent pan delta is
        // then added on top of THAT, never on top of the raw start Center.
        var distance = pointerDistance();
        var requestedScale = pinch.startTransform.scale * (distance / pinch.startDistance);
        var basisScale = hooks.clampScale(requestedScale);
        var pivotCenter = hooks.resolvePivotCenter(
          pinch.startTransform.centerX,
          pinch.startTransform.centerY,
          pinch.startTransform.scale,
          basisScale
        );

        var mid = pointerMidpoint();
        var moveDxNorm = (mid.x - pinch.startMidpoint.x) / rect.width;
        var moveDyNorm = (mid.y - pinch.startMidpoint.y) / rect.height;

        hooks.applyClampedCenterTransform({
          scale: basisScale,
          centerX: pivotCenter.centerX + moveDxNorm,
          centerY: pivotCenter.centerY + moveDyNorm
        });
      }
    });

    function releasePointer(event) {
      var wasActive = !!pointers[event.pointerId];
      delete pointers[event.pointerId];
      var remaining = pointerIds();
      if (remaining.length === 1) {
        // Dropped from two fingers/pointers to one — MOBILE INTERACTION
        // CUMULATIVE pass, Section 10-11: the surviving pointer resumes a
        // live drag if it's a mouse (always) OR a touch/pen point AND the
        // merchant's Mobile Drag Mode is 'one_finger'. In 'two_fingers'
        // mode a lone surviving touch/pen stays a no-op — it must not
        // silently start dragging just because a second finger lifted.
        var survivor = pointers[remaining[0]];
        var survivorDrags = survivor.type === 'mouse' || hooks.getMobileDragMode() === 'one_finger';
        dragLast = survivorDrags ? { x: survivor.x, y: survivor.y } : null;
        pinch = null;
      } else if (remaining.length === 0) {
        dragLast = null;
        pinch = null;
      }
      if (wasActive && hooks.onInteractionEnd) hooks.onInteractionEnd();
      // PASS 3A FINAL UX PASS, Section 43/45 — schedule the delayed fade
      // once the drag/pinch actually ends (not immediately hidden — Section
      // 43: "Do not hide immediately on pointerup"). A no-op release (a
      // pointer that was never actually tracked) must not touch the timer.
      if (wasActive && hooks.schedulePhotoInteractionHide) hooks.schedulePhotoInteractionHide();
    }

    canvas.addEventListener('pointerup', releasePointer);
    canvas.addEventListener('pointercancel', releasePointer);

    // Desktop convenience: mouse wheel also zooms. Debounced settle so a
    // burst of wheel events triggers the HQ render/debug refresh only once.
    // preventDefault is scoped to the canvas element only — page scroll
    // anywhere else is completely unaffected.
    //
    // Pass 3A targeted correction — wheel now goes through the ONE
    // canonical hooks.applyZoom() (same function the Zoom buttons use),
    // so the clamp-first/complete-no-op/pivot-branch ordering lives in
    // exactly one place. In 'image_center' mode this is unchanged behavior
    // (Center left untouched); the wheel's OWN scale-amount-per-tick
    // (1.05/0.95) is unaffected either way — only whether Center may also
    // move has changed, and only when a merchant explicitly configures
    // Custom Pivot.
    var wheelSettleTimer = null;
    canvas.addEventListener(
      'wheel',
      function (event) {
        if (!state.get().customerFile) return;
        if (hooks.isEditingEnabled && !hooks.isEditingEnabled()) return;
        event.preventDefault();
        // PASS 3A FINAL UX PASS, Section 44 — each meaningful wheel tick
        // shows/keeps the box visible AND independently re-arms its own
        // ~400ms hide timer (schedulePhotoInteractionHide has its own
        // internal clearTimeout, so a burst of ticks just keeps pushing it
        // out — never firing early, never stacking multiple timers).
        if (hooks.notePhotoInteractionActivity) hooks.notePhotoInteractionActivity();
        var t = hooks.getCurrentTransform();
        var requestedScale = t.scale * (event.deltaY < 0 ? 1.05 : 0.95);
        hooks.applyZoom(requestedScale);
        if (hooks.schedulePhotoInteractionHide) hooks.schedulePhotoInteractionHide();
        clearTimeout(wheelSettleTimer);
        wheelSettleTimer = setTimeout(function () {
          if (hooks.onInteractionEnd) hooks.onInteractionEnd();
        }, 200);
      },
      { passive: false }
    );
  }

  /*
    Real customer-facing controls (Phase 4, Section 43; Change Photo removed
    in the Finalization pass — Upload Photo is now the ONE unified upload/
    replace action, per Section 3-5): Upload/Remove, the four directional
    arrows, the two zoom buttons, and Reset Position. Purely functional/
    temporary layout — visual design is a later phase. Purely a no-op if the
    host markup doesn't include [data-ever-leaf-customer-controls].
  */
  /*
    MASTER REBUILD (Section 54) — DOCUMENT-WIDE ADAPTER, not a rewrite.
    The custom Ever Leaf Product Section physically splits these controls
    across independent Shopify blocks (Upload/Dedication in the
    Information column; Move/Zoom/Reset stay with the Visual block) — real
    Shopify blocks render at fixed template positions and cannot be pulled
    across DOM subtrees with CSS alone (the brief explicitly forbids
    reaching for the old display:contents/grid-order tricks to fake that).
    Rather than requiring one shared `[data-ever-leaf-customer-controls]`
    ancestor, every control is now resolved independently by its own
    unique data-attribute, document-wide — the exact same
    `document.querySelector` adapter pattern already used elsewhere in
    this file for the DEV panels (which have always rendered outside
    <ever-leaf-personalizer> — see EverLeafCalibrationPanel below). No
    click handler, state transition, or gating logic changed — only HOW
    each element is found.
  */
  function EverLeafCustomerControls(el) {
    var uploadButton = document.querySelector('[data-ever-leaf-upload]');
    var removeButton = document.querySelector('[data-ever-leaf-remove]');
    var moveButtons = document.querySelectorAll('[data-ever-leaf-move]');
    var zoomButtons = document.querySelectorAll('[data-ever-leaf-zoom]');
    /*
      Product Visual Architecture Refactor — Mobile Horizontal control
      layout mode can render a SECOND real Reset button and a SECOND
      [data-ever-leaf-photo-controls] panel (the Grid panel always exists;
      Horizontal renders alongside it, CSS-toggled — see
      component-ever-leaf-personalizer.css). Both instances must be wired/
      toggled the same way moveButtons/zoomButtons already are (querySelectorAll
      + forEach), or whichever one is NOT first in the DOM would silently
      stay dead/stuck-hidden. At most one instance is ever visible/
      focusable at a time (the other is `display:none`, which removes it
      from the accessibility tree and tab order), so wiring both is safe.
    */
    var resetButtons = document.querySelectorAll('[data-ever-leaf-reset-position]');
    var statusText = document.querySelector('[data-ever-leaf-upload-status]');
    var photoControlsPanels = document.querySelectorAll('[data-ever-leaf-photo-controls]');
    // Visual Pass 01, Section 26-28 — Upload success layout reads the
    // real selected File's own .name/.size (never inferred from the
    // hosted URL, never fabricated). Read-only: nothing here touches
    // Mini/FilePond/the upload adapter itself.
    var uploadFilenameEl = document.querySelector('[data-ever-leaf-upload-filename]');
    var uploadFilesizeEl = document.querySelector('[data-ever-leaf-upload-filesize]');
    // OUR OWN hidden native file input — the ONLY file-selection surface in
    // the whole editing flow. Mini/FilePond's own picker is never opened
    // from here (Phase 4 correction pass, Section 1). Stays inside the
    // Visual block's <ever-leaf-personalizer> element (it's invisible, so
    // its physical DOM position never matters), found the same
    // document-wide way for consistency.
    var fileInput = document.querySelector('[data-ever-leaf-native-file-input]');

    var moveListeners = [];
    var zoomListeners = [];
    var uploadListeners = [];
    var removeListeners = [];
    var resetListeners = [];
    var fileSelectedListeners = [];

    /*
      PASS 3C, Section 45/46 — Full Lamp / during-transition editing gate.
      Native `disabled` on the real Move/Zoom/Reset buttons: `disabled`
      already blocks both pointer AND keyboard (Enter/Space) activation by
      itself, and wireHoldableButton's own pointerdown handler already
      checks `if (button.disabled) return;` (pre-existing, unrelated to
      this pass) — so gating here needs no changes to the button wiring
      itself. Input gating ONLY (Section 46): never touches photoCenterX/Y/
      Scale, Pivot, Outline state, or any other personalization value.
      Canvas drag/pinch/wheel are gated separately via wirePhotoInteraction's
      own isEditingEnabled hook (see its wiring in init()).
    */
    this.setEditingEnabled = function (enabled) {
      function apply(list) {
        list.forEach(function (button) {
          button.disabled = !enabled;
        });
      }
      apply(moveButtons);
      apply(zoomButtons);
      apply(resetButtons);
    };

    this.triggerFilePicker = function () {
      if (!fileInput) return;
      // Reset first so re-selecting the SAME filename still fires 'change'
      // (pressing Upload Photo again to replace with an identical file must
      // still count as a new selection).
      fileInput.value = '';
      fileInput.click();
    };
    this.onFileSelected = function (fn) {
      fileSelectedListeners.push(fn);
    };
    if (fileInput) {
      fileInput.addEventListener('change', function () {
        var file = fileInput.files && fileInput.files[0];
        if (!file) return;
        fileSelectedListeners.forEach(function (fn) {
          fn(file);
        });
      });
    }

    if (uploadButton) {
      uploadButton.addEventListener('click', function () {
        uploadListeners.forEach(function (fn) {
          fn();
        });
      });
    }
    if (removeButton) {
      removeButton.addEventListener('click', function () {
        removeListeners.forEach(function (fn) {
          fn();
        });
      });
    }
    /*
      Press-and-hold pass (2A.1) — Move Up/Down/Left/Right and Zoom In/Out
      gain continuous repeat while held; Reset (wired further below, plain
      click only) deliberately does NOT. Interaction timing only (hold
      delay / repeat cadence) — never merchant-configurable, never touches
      Visualizer Calibration.

      dispatchMove/dispatchZoom are the ONE authoritative action per
      direction — the exact same moveListeners/zoomListeners pub/sub a
      plain click already used, now also reused by the hold-repeat loop
      below. Their boolean return (true = state actually changed) lets the
      hold loop detect "already at a limit" and stop itself, per the
      brief's preferred behavior (Section 10) rather than looping forever
      on a no-op.
    */
    var HOLD_DELAY_MS = 300; // first repeat begins this long after press
    var HOLD_REPEAT_MS = 80; // then repeats at this constant cadence (no acceleration)
    // Section 19 — at most ONE hold loop active across all six buttons at
    // any time. If a second button is somehow pressed while another is
    // still held (e.g. multi-touch), the first is stopped before the
    // second starts — never two accumulating timers.
    var stopAnyActiveHold = null;

    function dispatchMove(direction) {
      var changed = false;
      moveListeners.forEach(function (fn) {
        if (fn(direction)) changed = true;
      });
      return changed;
    }
    function dispatchZoom(direction) {
      var changed = false;
      zoomListeners.forEach(function (fn) {
        if (fn(direction)) changed = true;
      });
      return changed;
    }

    /*
      Shared by every Move/Zoom button — one wiring function, not six copies.
      `dispatchAction` is dispatchMove or dispatchZoom (closed over `arg`,
      the direction); it is the SAME function a plain tap and a hold-repeat
      tick both call — no parallel/duplicated action logic anywhere.

      Double-step prevention (Section 6 of the brief): pointerdown fires the
      action immediately AND sets `suppressNextClick`. The native `click`
      that follows a real pointer interaction (mouse or touch) checks that
      flag and no-ops when set — it was already handled. Keyboard Enter/
      Space activation never dispatches PointerEvents at all, so it always
      reaches the click handler with the flag clear and fires normally,
      unaffected — no separate keyboard code path needed.

      `suppressNextClick` is reset two ways, both handled: (a) the click
      handler itself consumes and clears it on the normal path, and (b) as
      a safety net, `stopHold()` also clears it on the next tick, so a
      cancelled interaction (pointercancel — where the browser never fires
      a following click at all, e.g. the gesture turned into a page
      scroll) can never leave it stuck "true" and wrongly swallow some
      later, unrelated keyboard click on the same button.
    */
    function wireHoldableButton(button, dispatchAction, arg) {
      var holdTimer = null;
      var repeatTimer = null;
      var suppressNextClick = false;
      var activePointerId = null;

      function stopHold() {
        if (holdTimer) {
          clearTimeout(holdTimer);
          holdTimer = null;
        }
        if (repeatTimer) {
          clearInterval(repeatTimer);
          repeatTimer = null;
        }
        if (activePointerId !== null) {
          try {
            button.releasePointerCapture(activePointerId);
          } catch (e) {
            // Capture already released/invalid — nothing to clean up.
          }
          activePointerId = null;
        }
        if (stopAnyActiveHold === stopHold) stopAnyActiveHold = null;
        setTimeout(function () {
          suppressNextClick = false;
        }, 0);
      }

      function startRepeating() {
        holdTimer = null;
        repeatTimer = setInterval(function () {
          var changed = dispatchAction(arg);
          if (!changed) stopHold(); // at a limit — nothing left to repeat
        }, HOLD_REPEAT_MS);
      }

      button.addEventListener('pointerdown', function (event) {
        // Left mouse button only; every touch/pen contact is already
        // "primary". Ignore anything else (e.g. a right-click).
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (button.disabled) return;
        if (stopAnyActiveHold) stopAnyActiveHold(); // stop any other button's hold first
        stopAnyActiveHold = stopHold;
        activePointerId = event.pointerId;
        try {
          // Section 8 — keeps the hold alive if the pointer drifts slightly
          // outside the button's physical bounds while held.
          button.setPointerCapture(event.pointerId);
        } catch (e) {
          // Pointer capture isn't available/failed — hold-repeat still
          // works via the listeners below, just without drift tolerance.
        }
        suppressNextClick = true;
        dispatchAction(arg); // immediate first step, same as a plain tap
        holdTimer = setTimeout(startRepeating, HOLD_DELAY_MS);
      });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(function (evtName) {
        button.addEventListener(evtName, stopHold);
      });
      // Section 19 — if focus/visibility is lost mid-hold (e.g. the user
      // switches app/tab while still physically pressing), stop rather
      // than let a detached interval keep running.
      window.addEventListener('blur', stopHold);
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stopHold();
      });

      button.addEventListener('click', function () {
        if (suppressNextClick) {
          suppressNextClick = false;
          return; // already handled by pointerdown above
        }
        dispatchAction(arg); // keyboard Enter/Space (no pointer events fired)
      });
    }

    moveButtons.forEach(function (button) {
      var direction = button.getAttribute('data-ever-leaf-move');
      wireHoldableButton(button, dispatchMove, direction);
    });
    zoomButtons.forEach(function (button) {
      var direction = button.getAttribute('data-ever-leaf-zoom');
      wireHoldableButton(button, dispatchZoom, direction);
    });
    // Reset deliberately stays plain single-action click only (Section 15
    // of the brief) — never wired through wireHoldableButton/repeatable.
    resetButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        resetListeners.forEach(function (fn) {
          fn();
        });
      });
    });

    /*
      Reflects uploadStatus into the UI: Upload Photo stays visible in every
      state (it's the unified upload/replace action — Section 4); Remove +
      movement/zoom controls only once a photo exists; a plain status line
      for uploading/ready/error (Section 10 — no raw FilePond/technical
      detail surfaced to the customer).
    */
    // Visual Pass 01, Section 28 — clean "842 KB" / "1.8 MB" formatting,
    // never a raw byte count. Real file size only; no fake metadata.
    function formatFileSize(bytes) {
      if (typeof bytes !== 'number' || !isFinite(bytes) || bytes < 0) return '';
      if (bytes < 1024) return bytes + ' B';
      var kb = bytes / 1024;
      if (kb < 1024) return Math.round(kb) + ' KB';
      var mb = kb / 1024;
      return (Math.round(mb * 10) / 10) + ' MB';
    }

    /*
      Local-file decode failure (unreadable/corrupt image). This is a
      PRESENTATION-ONLY message: it is deliberately not written to
      EverLeafPersonalizerState, because 'error' there means "has a photo"
      to several consumers (`uploadStatus !== 'empty'`), which would leave a
      false personalization state after a file that never became a photo. It
      stays visible while the upload status it was raised against is
      unchanged (e.g. through Dedication typing), and is dropped as soon as
      the status changes (a valid photo lands, Remove, Add to Cart starts) or
      the customer picks another file.
    */
    var localFileError = null;
    var lastStatusArgs = { status: 'empty', errorMessage: null, file: null };
    this.showLocalFileError = function () {
      var fallback = 'We couldn’t read that image — please choose a different photo.';
      localFileError = {
        status: lastStatusArgs.status,
        message: (statusText && statusText.dataset.i18nDecodeError) || fallback
      };
      this.setUploadStatus(lastStatusArgs.status, lastStatusArgs.errorMessage, lastStatusArgs.file);
    };
    this.clearLocalFileError = function () {
      if (!localFileError) return;
      localFileError = null;
      this.setUploadStatus(lastStatusArgs.status, lastStatusArgs.errorMessage, lastStatusArgs.file);
    };

    this.setUploadStatus = function (status, errorMessage, file) {
      lastStatusArgs = { status: status, errorMessage: errorMessage, file: file };
      if (localFileError && localFileError.status !== status) localFileError = null;
      var hasPhoto = status !== 'empty';
      // Wave 3B5B.2 — CSS-visible marker only, no new state, no behavior change.
      if (removeButton) removeButton.setAttribute('data-upload-status', localFileError ? 'error' : status);
      if (removeButton) removeButton.hidden = !hasPhoto;
      photoControlsPanels.forEach(function (panel) {
        panel.hidden = !hasPhoto;
      });
      if (statusText) {
        // PHASE 50 — these 3 strings are localized in Liquid (Hebrew/English,
        // same request.locale.iso_code pattern used project-wide) and passed
        // in via data-i18n-* attributes on this same element; JS itself does
        // no locale detection. Falls back to the pre-existing English literal
        // only if the attribute is ever missing (defensive, not expected).
        var messages = {
          empty: '',
          selected: '',
          uploading: statusText.dataset.i18nUploading || 'Uploading…',
          ready: statusText.dataset.i18nReady || 'Ready',
          error: errorMessage || statusText.dataset.i18nUploadError || 'Upload failed — please try again.'
        };
        statusText.textContent = localFileError ? localFileError.message : messages[status] || '';
      }
      if (uploadFilenameEl) {
        uploadFilenameEl.textContent = file && file.name ? file.name : '';
      }
      if (uploadFilesizeEl) {
        uploadFilesizeEl.textContent = file && typeof file.size === 'number' ? formatFileSize(file.size) : '';
      }
    };

    this.onUploadClick = function (fn) {
      uploadListeners.push(fn);
    };
    this.onRemoveClick = function (fn) {
      removeListeners.push(fn);
    };
    this.onMove = function (fn) {
      moveListeners.push(fn);
    };
    this.onZoom = function (fn) {
      zoomListeners.push(fn);
    };
    this.onResetPosition = function (fn) {
      resetListeners.push(fn);
    };
  }

  /*
    Two-Line Dedication model, Section 31 — THE single DOM synchronization
    path from state to the two Dedication `<input>` elements. Never mutates
    state, never dispatches a fake `input` event, never duplicates a
    listener. Called (a) at first-bind time inside EverLeafCalibrationPanel
    below, and (b) after restoreFromPreviewSession() applies a restored
    Preview Session record to state — covering BOTH DOM orderings the brief
    calls out (Section 29: inputs bind first then IndexedDB restore later
    — the actual real-world ordering, since restore is always async — or,
    defensively, the reverse). Document-wide resolution (not scoped to
    `el`), same convention textInput/textCounter always used.
  */
  function syncDedicationInputsFromState() {
    var s = window.EverLeafPersonalizerState.get();
    var line1Input = document.querySelector('[data-ever-leaf-cal-text-line1]');
    var line2Input = document.querySelector('[data-ever-leaf-cal-text-line2]');
    if (line1Input) line1Input.value = s.dedicationLine1 || '';
    if (line2Input) line2Input.value = s.dedicationLine2 || '';
  }

  /*
    PASS 3A FINAL UX PASS, Section 5/10/11 — Two-Line Layout Presets UI
    sync: STATE -> DOM only (never the reverse), same one-way-sync
    convention syncDedicationInputsFromState() above already uses. Shows/
    hides the whole preset selector purely from whether Line 2 currently
    has content (Section 5 — no preset choice is meaningful with a single
    line), and keeps each option's `aria-pressed` in sync with
    `s.dedicationLayout`. Document-wide resolution, module-level (not
    per-instance) — same reasoning as bindDedicationInputs()/
    notifyDedicationChange() below: the preset buttons live in the same
    independent Dedication block DOM subtree as the two text inputs, so
    they need the identical re-sync-on-every-reload treatment.
  */
  function updateDedicationLayoutUI(s) {
    var placement = s.dedicationPlacement === 'above' ? 'above' : 'below';
    var groups = document.querySelectorAll('[data-ever-leaf-dedication-placement-group]');
    groups.forEach(function (g) {
      // Single-control Placement Toggle (one button, two states), mirroring
      // the View Toggle's own icon-swap + label-swap model. This function
      // stays READ-ONLY with respect to state: it reflects whatever
      // `dedicationPlacement` currently is and never writes it, so a
      // re-render / Theme Editor reload / photo change can never overwrite
      // an already-active selection with the markup's static default.
      var toggle = g.querySelector('[data-ever-leaf-dedication-placement-toggle]');
      if (!toggle) return;
      var label = toggle.getAttribute(placement === 'above' ? 'data-label-above' : 'data-label-below') || '';
      var previous = toggle.getAttribute('data-ever-leaf-placement-state');
      // Settle silently (no directional animation replay) whenever this is
      // a re-sync rather than a real user-driven change - same "paint the
      // start state, release next frame" technique the View Toggle and the
      // Interaction Hints already use.
      if (previous !== placement && previous !== null && !toggle.dataset.everLeafPlacementUserChange) {
        toggle.setAttribute('data-ever-leaf-placement-instant', '');
        void toggle.offsetWidth;
      }
      delete toggle.dataset.everLeafPlacementUserChange;
      toggle.setAttribute('data-ever-leaf-placement-state', placement);
      g.querySelectorAll('[data-ever-leaf-placement-icon]').forEach(function (icon) {
        icon.hidden = icon.getAttribute('data-ever-leaf-placement-icon') !== placement;
      });
      var text = g.querySelector('[data-ever-leaf-placement-text]');
      if (text) text.textContent = label;
      var base = toggle.getAttribute('data-placement-aria-base');
      if (!base) {
        base = (toggle.getAttribute('aria-label') || '').split(':')[0];
        toggle.setAttribute('data-placement-aria-base', base);
      }
      toggle.setAttribute('aria-label', base ? base + ': ' + label : label);
      if (toggle.hasAttribute('data-ever-leaf-placement-instant')) {
        requestAnimationFrame(function () {
          toggle.removeAttribute('data-ever-leaf-placement-instant');
        });
      }
    });
  }
  // Legacy cart/order vocabulary bridge: Below <-> together, Above <-> split.
  function layoutFromPlacement(p) {
    return p === 'above' ? 'split' : 'together';
  }
  window.EverLeafPersonalizerState.subscribe(updateDedicationLayoutUI);

  /*
    PASS 3A FINAL CONSOLIDATED CALIBRATION + FRAMING CORRECTION, Section
    30-34 — Dedication DOM-after-Save, re-audited. Root cause traced (not
    guessed) via the actual DOM architecture: the Dedication inputs
    (snippets/ever-leaf-product-dedication.liquid) are a SEPARATE DOM
    subtree/block from <ever-leaf-personalizer> (see the MASTER REBUILD
    note — they live independently in the Information column). The
    previous pass's fix only re-ran Dedication input binding/seeding from
    INSIDE EverLeafCalibrationPanel's constructor, itself only invoked from
    <ever-leaf-personalizer>'s own connectedCallback -> init(). If Shopify's
    Section Rendering API ever replaces the Dedication block's DOM
    independently of <ever-leaf-personalizer> reconnecting (a real
    possibility for a section containing multiple independent blocks),
    nothing re-runs EverLeafCalibrationPanel at all for those fresh nodes —
    they'd sit unbound and un-synced indefinitely, exactly matching "Canvas
    survives (state was never lost), inputs go empty (nothing ever finds
    the new nodes)".

    Fixed by making Dedication input binding/sync a MODULE-LEVEL concern,
    entirely decoupled from any one <ever-leaf-personalizer> instance's
    lifecycle (Section 33): `bindDedicationInputs()` (idempotent — safe to
    call as often as needed) is invoked from (a) EverLeafCalibrationPanel's
    constructor (covers <ever-leaf-personalizer> reconnecting), AND (b) a
    document-level `shopify:section:load` listener below (covers the
    Dedication block's own DOM being replaced independently — Shopify's
    own documented Theme Editor lifecycle event, already used elsewhere in
    this theme for the same class of problem — see
    ever-leaf-editor-preview.js). `dedicationChangeListeners` is therefore
    also module-level (not per-instance) — there is only ever one real
    "a Dedication line changed" concern in this app, regardless of how many
    times EverLeafCalibrationPanel gets constructed.
  */
  var dedicationChangeListeners = [];
  function notifyDedicationChange() {
    var line1Input = document.querySelector('[data-ever-leaf-cal-text-line1]');
    var line2Input = document.querySelector('[data-ever-leaf-cal-text-line2]');
    var line1 = line1Input ? line1Input.value : '';
    var line2 = line2Input ? line2Input.value : '';
    dedicationChangeListeners.forEach(function (fn) {
      fn(line1, line2);
    });
  }

  /*
    Applies the CURRENT configured max to ONE live input's native
    `maxlength` attribute ONLY — Section 20 explicitly changes prior
    behavior here: a merchant lowering the max in Theme Editor calibration
    must NOT silently erase a longer existing (test) value. `maxlength`
    updates immediately (so no FURTHER typing can exceed it — Section 21),
    but an already-longer value is left alone. Design-mode-only diagnostic
    (never customer-facing) warns if the current value already exceeds the
    new max, matching this file's existing lightweight console.warn
    convention for non-blocking calibration issues (see the Scene Asset
    warnings in loadProductConfig()).
  */
  function applyMaxLengthOnly(input, max) {
    if (!input) return;
    input.maxLength = max;
    if (input.value.length > max && window.Shopify && window.Shopify.designMode) {
      if (window.console && console.warn) {
        console.warn('[Ever Leaf Dedication] current value (' + input.value.length + ' chars) exceeds the configured maximum (' + max + ') — not truncated automatically; further typing is capped at the new limit.');
      }
    }
  }

  // Character limit (Section 16-17) — Line 1 and Line 2 each have their
  // OWN independent configured maximum, not a shared/global one: changing
  // one line's max never affects the other line's max.

  /*
    Finds the CURRENT Line 1/Line 2 input nodes (Section 33 — "current
    section DOM only", never a stale reference held from a replaced
    section) and binds exactly one 'input' listener to each, guarded by a
    per-node dataset flag so calling this repeatedly (on every
    EverLeafCalibrationPanel construction AND every shopify:section:load)
    never double-binds an already-bound node — a genuinely fresh node
    (from either a <ever-leaf-personalizer> reconnect or an independent
    Dedication block replace) never carries the flag, so it always gets
    bound exactly once, regardless of which code path found it first.
  */
  function bindDedicationInputs() {
    var line1Input = document.querySelector('[data-ever-leaf-cal-text-line1]');
    var line2Input = document.querySelector('[data-ever-leaf-cal-text-line2]');
    if (line1Input && !line1Input.dataset.everLeafDedicationBound) {
      line1Input.dataset.everLeafDedicationBound = 'true';
      line1Input.addEventListener('input', function () {
        // Section 21 — genuine customer typing always enforces THIS line's
        // own current configured max, read live (never a stale cache).
        var max = getMaxCharsForLine1();
        if (line1Input.value.length > max) {
          line1Input.value = line1Input.value.slice(0, max);
        }
        notifyDedicationChange();
      });
    }
    if (line2Input && !line2Input.dataset.everLeafDedicationBound) {
      line2Input.dataset.everLeafDedicationBound = 'true';
      line2Input.addEventListener('input', function () {
        var max = getMaxCharsForLine2();
        if (line2Input.value.length > max) {
          line2Input.value = line2Input.value.slice(0, max);
        }
        notifyDedicationChange();
      });
    }

    // Dedication Text Config fix (state->DOM synchronization, Section
    // 27-34) — root cause of "text disappears when Dedication Text Config
    // changes" (and, extended by this pass, "Dedication input is empty
    // after Save" — see restoreFromPreviewSession()'s own call to this same
    // sync function): a Theme Editor settings/section change replaces
    // this block's DOM, so these are fresh <input> nodes with an empty
    // .value (Liquid never pre-fills them) — even though
    // state.dedicationLine1/Line2 were never touched (buildConfigDerivedPatch
    // never includes them; they survive in the singleton exactly like
    // photoCenterX/Y do). The Canvas render already reads state directly so
    // it stayed correct, but the INPUTS visibly went blank. Seeding via the
    // SAME syncDedicationInputsFromState() a Preview Session restore also
    // uses — STATE populates DOM here, never the reverse (Section 30/34) —
    // is the fix; a pure no-op on a genuinely fresh page load, where state
    // is already ''.
    syncDedicationInputsFromState();
    // Section 18-19 — live maxlength update, no Save required: this whole
    // function already re-runs on every Theme Editor config-only-block
    // reload (both via a fresh <ever-leaf-personalizer> reconnect AND the
    // shopify:section:load listener below), and getMaxCharsForLine1/2()
    // read state LIVE (never cached) — so simply re-applying here on every
    // call is sufficient; no separate push/pub-sub layer is needed.
    applyMaxLengthOnly(line1Input, getMaxCharsForLine1());
    applyMaxLengthOnly(line2Input, getMaxCharsForLine2());

    // PASS 3A FINAL UX PASS, Section 4/10/19 — the two Layout preset
    // buttons live in the SAME independent Dedication block DOM subtree as
    // the two text inputs above, so they need the identical idempotent-
    // rebind treatment (a Theme Editor reload can replace this whole block,
    // fresh nodes with no dataset flag). A click ONLY ever writes
    // `dedicationLayout` — nothing else in state — satisfying Section 19
    // ("must not change Dedication text/Font Size/Maximum Characters/photo
    // transform/opacity/Center X"). The Visualizer updates immediately with
    // no extra wiring: sceneTextLayerSettings() already reads
    // s.dedicationLayout live on every render, and this state.update() call
    // alone triggers the existing scheduleInteractiveRender subscriber.
    document.querySelectorAll('[data-ever-leaf-dedication-placement-group]').forEach(function (group) {
      if (group.dataset.everLeafPlacementBound) return;
      group.dataset.everLeafPlacementBound = 'true';
      var placementToggle = group.querySelector('[data-ever-leaf-dedication-placement-toggle]');
      var setPlacement = function (value, userDriven) {
        // Only the semantic placement is ever written here - never the
        // dedication text, font size, character limits, photo transform,
        // opacity or Center X. The legacy cart/order vocabulary bridge
        // (layoutFromPlacement: Below -> together, Above -> split) and the
        // persisted property names are untouched by this control.
        if (userDriven && placementToggle) placementToggle.dataset.everLeafPlacementUserChange = 'true';
        window.EverLeafPersonalizerState.update({ dedicationPlacement: value });
      };
      if (placementToggle) {
        // ONE control, two states: each activation flips Below <-> Above.
        placementToggle.addEventListener('click', function () {
          var cur = window.EverLeafPersonalizerState.get().dedicationPlacement === 'above' ? 'above' : 'below';
          setPlacement(cur === 'above' ? 'below' : 'above', true);
        });
        // Native <button> already handles Enter/Space. Arrow keys are kept
        // as a directional convenience that matches the control's own
        // up/down language, and they set an explicit state rather than
        // flipping, so repeated presses are idempotent.
        placementToggle.addEventListener('keydown', function (ev) {
          var k = ev.key;
          var next = null;
          if (k === 'ArrowUp') next = 'above';
          else if (k === 'ArrowDown') next = 'below';
          if (next) {
            ev.preventDefault();
            setPlacement(next, true);
          }
        });
      }
    });
    // Section 12 — structural re-sync (visibility + pressed state) on every
    // rebind, same reasoning as syncDedicationInputsFromState() above: a
    // freshly re-rendered selector element must reflect CURRENT state
    // immediately, not wait for the next unrelated state.update().
    updateDedicationLayoutUI(window.EverLeafPersonalizerState.get());
  }

  /*
    Section 33 — re-bind/re-sync on EVERY section reload the Theme Editor
    fires, independent of whether <ever-leaf-personalizer> itself happens
    to reconnect for that particular reload. window.Shopify.designMode-
    gated defense in depth, matching this file's existing convention —
    shopify:section:load only ever fires inside the real Theme Editor
    iframe anyway, so this is a genuine no-op on the real storefront
    regardless.
  */
  document.addEventListener('shopify:section:load', function () {
    if (!window.Shopify || !window.Shopify.designMode) return;
    bindDedicationInputs();
  });

  /*
    INTERNAL CONFIGURATION ARCHITECTURE pass — the old DEV Photo Calibration
    window this constructor used to drive has been retired; photo tonal/
    opacity/pivot/light values now come from the "Visualizer Calibration"
    Shopify block (see CALIBRATION_DEFAULTS/loadProductConfig() above). This
    constructor's only remaining real job is binding the customer Dedication
    inputs (Two-Line Dedication model) — delegated to the module-level
    bindDedicationInputs()/dedicationChangeListeners above (Section 30-34)
    so <ever-leaf-personalizer> reconnecting is just ONE of the paths that
    keeps them in sync, not the only one — kept as its own function (rather
    than inlined) so the call sites in EverLeafPersonalizer.init don't need
    to change shape.
  */
  function EverLeafCalibrationPanel(el) {
    bindDedicationInputs();

    // Replaces (never accumulates) — dedicationChangeListeners is
    // module-level and would otherwise grow by one every time
    // <ever-leaf-personalizer> reconnects (a fresh EverLeafCalibrationPanel
    // per init() call, each registering its own closure), causing every
    // later Dedication keystroke to fire N stale handlers instead of just
    // the current one. Only the MOST RECENT registration (the currently
    // live <ever-leaf-personalizer> instance) is ever meaningful.
    this.onDedicationChange = function (fn) {
      dedicationChangeListeners = [fn];
    };

    // Photo opacity/tonal/pivot/light/text-visibility are now fixed for the
    // session from the Shopify-configured "Visualizer Calibration" block
    // (read once into state at init — see createPersonalizerState()/
    // loadProductConfig()). The DEV Photo Calibration window that used to
    // let a developer live-tune these is retired; these accessors remain as
    // permanent no-ops purely so the existing internal call sites
    // (onTonalChange/onOpacityChange/etc. in EverLeafPersonalizer.init)
    // don't need to change shape.
    this.onTonalChange = function () {};
    this.onOpacityChange = function () {};
    this.onLightChange = function () {};
    this.onTextVisibleChange = function () {};
    this.onReset = function () {};
    this.setValues = function () {};
  }

  /*
    INTERNAL CONFIGURATION ARCHITECTURE pass — the old DEV Text Sizes window
    this constructor used to drive has been retired; Dedication Text Config
    values (now the PASS 3A FINAL DEDICATION POSITIONING MODEL — fully
    independent per-line Y positions, no Group Y/Line Gap) come from the
    "Dedication Text Config" Shopify block (see
    CALIBRATION_DEFAULTS/loadProductConfig() above, consumed via
    refitDedicationLines()/getMaxCharsForLine1()/getMaxCharsForLine2() at
    init). Kept as a function (permanent no-op) only so existing call sites
    don't need to change shape.
  */
  function EverLeafTextSizingPanel(el) {
    this.onChange = function () {};
    this.onReset = function () {};
    this.setValues = function () {};
  }

  /*
    Temporary DEV-only diagnostic panel (Sections 15/17). Hidden by default —
    see the "Hide debug grid" checkbox's default-checked state in the liquid
    markup. Only activates if the host markup includes an element carrying
    data-ever-leaf-debug. Debug stages and the perf readout are only updated
    when explicitly requested — never from the interactive render path, and
    getDebugOptions() returns null (a hard no-op) whenever hidden.

    Root is found via document, NOT scoped to `el` (Phase 4 Correction Pass
    3, Section 21-23) — renders in ever-leaf-personalizer-dev-panels.liquid
    now, after the purchase block.
  */
  function EverLeafDebugPanel(el, perf) {
    var root = document.querySelector('[data-ever-leaf-debug]');
    var listeners = [];
    var verboseListeners = [];

    function onChange(fn) {
      listeners.push(fn);
    }
    function onVerboseChange(fn) {
      verboseListeners.push(fn);
    }
    function notify() {
      listeners.forEach(function (fn) {
        fn();
      });
    }

    if (!root) {
      this.getDebugOptions = function () {
        return null;
      };
      this.showStages = function () {};
      this.onChange = onChange;
      this.onVerboseChange = onVerboseChange;
      this.isHidden = function () {
        return true;
      };
      this.refreshPerfReadout = function () {};
      this.setArrowDebug = function () {};
      return;
    }

    var compositeSelect = root.querySelector('[data-ever-leaf-debug-composite]');
    var refreshButton = root.querySelector('[data-ever-leaf-debug-refresh]');
    var stagesContainer = root.querySelector('[data-ever-leaf-debug-stages]');
    var hideToggle = root.querySelector('[data-ever-leaf-debug-hide]');
    var verboseToggle = root.querySelector('[data-ever-leaf-debug-verbose]');
    var perfReadout = root.querySelector('[data-ever-leaf-debug-perf]');
    var body = root.querySelector('[data-ever-leaf-debug-body]');
    var arrowDebugReadout = root.querySelector('[data-ever-leaf-arrow-debug]');

    function applyHiddenState() {
      if (body) body.hidden = hideToggle ? hideToggle.checked : false;
    }

    if (compositeSelect) compositeSelect.addEventListener('change', notify);
    if (refreshButton) refreshButton.addEventListener('click', notify);
    if (verboseToggle) {
      verboseToggle.addEventListener('change', function () {
        verboseListeners.forEach(function (fn) {
          fn(verboseToggle.checked);
        });
      });
    }
    if (hideToggle) {
      hideToggle.addEventListener('change', function () {
        applyHiddenState();
        if (!hideToggle.checked) notify(); // coming back visible — refresh once
      });
    }
    applyHiddenState(); // hidden by default, per the checkbox's checked="checked" markup

    this.isHidden = function () {
      return !!(hideToggle && hideToggle.checked);
    };

    this.getDebugOptions = function () {
      if (this.isHidden()) return null;
      return {
        stages: [],
        compositeMode: compositeSelect ? compositeSelect.value : 'color-burn',
        // Text/Light visibility now live in the always-available Photo
        // Calibration panel (state.layerSettings) — the debug grid only
        // controls which Color Burn mode Stage 8 compares against.
        disableText: false,
        disableLight: false
      };
    }.bind(this);

    this.showStages = function (stages) {
      if (!stagesContainer || !stages) return;
      stagesContainer.innerHTML = '';
      stages
        .slice()
        .sort(function (a, b) {
          return parseFloat(a.label) - parseFloat(b.label);
        })
        .forEach(function (entry) {
          var figure = document.createElement('figure');
          figure.className = 'ever-leaf-debug__figure';
          var caption = document.createElement('figcaption');
          caption.textContent = entry.label;
          var previewCanvas = document.createElement('canvas');
          previewCanvas.width = entry.canvas.width;
          previewCanvas.height = entry.canvas.height;
          previewCanvas.className = 'ever-leaf-debug__canvas';
          previewCanvas.getContext('2d').drawImage(entry.canvas, 0, 0);
          figure.appendChild(previewCanvas);
          figure.appendChild(caption);
          stagesContainer.appendChild(figure);
        });
    };

    this.refreshPerfReadout = function () {
      if (!perfReadout) return;
      var i = perf.stats.interactive;
      var hq = perf.stats.hq;
      perfReadout.textContent =
        'INTERACTIVE\n' +
        '  resolution: ' + (window.__everLeafInteractiveSize || '?') + 'x' + (window.__everLeafInteractiveSize || '?') + '\n' +
        '  last: ' + i.last.toFixed(1) + 'ms  avg: ' + i.avg.toFixed(1) + 'ms  max: ' + i.max.toFixed(1) + 'ms\n\n' +
        'HQ\n' +
        '  resolution: 1060x1060\n' +
        '  last: ' + hq.last.toFixed(1) + 'ms';
    };

    this.setArrowDebug = function (text) {
      if (arrowDebugReadout) arrowDebugReadout.textContent = text;
    };

    this.onChange = onChange;
    this.onVerboseChange = onVerboseChange;
  }

  customElements.define(
    'ever-leaf-personalizer',
    class extends HTMLElement {
      connectedCallback() {
        // PASS 3C, Section 73 — capture init()'s returned cleanup so a
        // later disconnect (Theme Editor section reload/removal) can
        // cancel any in-flight transition RAF loop.
        this._everLeafDestroy = EverLeafPersonalizer.init(this);
      }
      disconnectedCallback() {
        if (typeof this._everLeafDestroy === 'function') {
          this._everLeafDestroy();
          this._everLeafDestroy = null;
        }
      }
    }
  );
})();
